import React, { useState, useEffect, useRef } from 'react';
import { format } from 'date-fns';
import { formatInTimeZone, fromZonedTime } from 'date-fns-tz';
import axios from 'axios';
import { Heart, CheckCircle, XCircle, Clock, ArrowLeft, ArrowRight, Calendar, Edit2, Trash2, CalendarPlus, CalendarX, DollarSign, CreditCard, RefreshCw, Mail, Lock, LogOut, Settings, Send, ChevronDown, Plus, Users, Copy, FileText, Download, Search, X, AlertCircle, Loader2, ExternalLink, BookOpen, ArrowRightLeft, GripVertical, Globe, LockKeyhole, MapPin, EyeOff, RotateCcw, Eye, Key, ShoppingCart, Wrench, Store, ShoppingBag, Info, TrendingUp, Package, UserPlus, Tag, CheckCircle2, History, Edit, BarChart3, MoreHorizontal, Save, Inbox, User, AlertTriangle, Camera, Minus, Play, Check, Pause, Paperclip, Upload, UserCheck, Square, Layers, StopCircle, Building2, Filter, FileCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger, DropdownMenuSeparator, DropdownMenuSub, DropdownMenuSubTrigger, DropdownMenuSubContent, DropdownMenuLabel } from '@/components/ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { arrayMove, SortableContext, sortableKeyboardCoordinates, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { formatPhoneNumber } from '../utils/formatters';
import { TodoList } from '@/components/TodoList';
// Stripe Elements imports removed - payments are handled by POSSystem and other dedicated components

// Extracted dialog components
import PasswordChangeDialog from '@/components/admin/PasswordChangeDialog';
import CreateBookingDialog from '@/components/admin/CreateBookingDialog';
import { StudentEmailButton, GroupEmailButton } from '@/components/admin/StudentEmailResend';
import PendingQueueSidebar from '@/components/admin/PendingQueueSidebar';
import EmailTemplateEditor from '@/components/admin/EmailTemplateEditor';
import POSSystem from '@/components/admin/POSSystem';
import WaitlistManager from '@/components/admin/WaitlistManager';
import InvoiceSystem from '@/components/admin/InvoiceSystem';
import DistanceCalculator from '@/components/admin/DistanceCalculator';
import ReportsDashboard from '@/components/admin/ReportsDashboard';
import QueuePanel from '@/components/admin/QueuePanel';
import RescheduleDashboard from '@/components/admin/RescheduleDashboard';
import RentalManagement from '@/components/admin/RentalManagement';
import RecurringClientsManager from '@/components/admin/RecurringClientsManager';
import StoreManager from '@/components/admin/StoreManager';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper function to copy text to clipboard with fallback
const copyToClipboard = async (text, successMessage = 'Copied!') => {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      toast.success(successMessage);
    } else {
      // Fallback for older browsers or restricted contexts
      const textarea = document.createElement('textarea');
      textarea.value = text;
      textarea.style.position = 'fixed';
      textarea.style.opacity = '0';
      document.body.appendChild(textarea);
      textarea.select();
      document.execCommand('copy');
      document.body.removeChild(textarea);
      toast.success(successMessage);
    }
  } catch (err) {
    console.error('Failed to copy:', err);
    toast.error('Failed to copy to clipboard');
  }
};

// Sortable Class Type Item for drag-and-drop reordering
const SortableClassTypeItem = ({ ct, onEdit, onToggleActive, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: ct.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div 
      ref={setNodeRef}
      style={style}
      className={`p-4 flex items-center justify-between hover:bg-slate-700/30 transition-colors ${!ct.is_active ? 'opacity-50' : ''} ${isDragging ? 'bg-slate-700/50' : ''}`}
      data-testid={`class-type-row-${ct.id}`}
    >
      {/* Drag Handle */}
      <div 
        {...attributes} 
        {...listeners}
        className="cursor-grab active:cursor-grabbing p-1 mr-2 text-slate-500 hover:text-white"
        title="Drag to reorder"
      >
        <GripVertical className="h-5 w-5" />
      </div>
      
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-slate-200">{ct.name}</span>
          {!ct.is_active && (
            <Badge className="bg-red-900/50 text-red-400 border-red-700 text-xs">Inactive</Badge>
          )}
          {ct.base_price && (
            <Badge className="bg-green-900/50 text-green-400 border-green-700 text-xs">
              ${ct.base_price}{ct.additional_student_price && ` / $${ct.additional_student_price}`}
            </Badge>
          )}
        </div>
        <div className="flex items-center gap-4 text-sm text-white mt-1">
          <span>{ct.duration_hours}h</span>
          <span>SKU: {ct.sku}</span>
          <span className="text-teal-400">
            {ct.expiration_months ? `Expires: ${ct.expiration_months} months` : 'No Expiration'}
          </span>
        </div>
        {ct.description && (
          <p className="text-xs text-slate-500 mt-1 truncate">{ct.description}</p>
        )}
      </div>
      
      <div className="flex items-center gap-1 ml-4">
        <Button
          onClick={() => onEdit(ct)}
          variant="ghost"
          size="sm"
          className="text-teal-400 hover:text-teal-300 hover:bg-teal-900/30"
          title="Edit"
          data-testid={`edit-class-type-${ct.id}`}
        >
          <Edit2 className="h-4 w-4" />
        </Button>
        <Button
          onClick={() => onToggleActive(ct)}
          variant="ghost"
          size="sm"
          className={ct.is_active 
            ? "text-amber-400 hover:text-amber-300 hover:bg-amber-900/30"
            : "text-green-400 hover:text-green-300 hover:bg-green-900/30"
          }
          title={ct.is_active ? 'Deactivate' : 'Activate'}
          data-testid={`toggle-class-type-${ct.id}`}
        >
          {ct.is_active ? <XCircle className="h-4 w-4" /> : <CheckCircle className="h-4 w-4" />}
        </Button>
        <Button
          onClick={() => onDelete(ct.id)}
          variant="ghost"
          size="sm"
          className="text-red-400 hover:text-red-300 hover:bg-red-900/30"
          title="Delete"
          data-testid={`delete-class-type-${ct.id}`}
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

// Sortable Category Item for drag-and-drop reordering
const SortableCategoryItem = ({ category, onEdit, onDelete }) => {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: category.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  return (
    <div 
      ref={setNodeRef}
      style={style}
      className={`flex items-center justify-between p-3 bg-slate-700/50 rounded-lg border border-slate-600 ${isDragging ? 'border-teal-500 shadow-lg' : ''}`}
    >
      {/* Drag Handle */}
      <div 
        {...attributes} 
        {...listeners}
        className="cursor-grab active:cursor-grabbing p-1 mr-2 text-slate-500 hover:text-white"
        title="Drag to reorder"
      >
        <GripVertical className="h-4 w-4" />
      </div>
      
      <div className="flex-1">
        <p className="font-medium text-white">{category.name}</p>
        <p className="text-xs text-slate-400">slug: {category.slug}</p>
      </div>
      
      <div className="flex gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onEdit(category)}
          className="text-teal-400 hover:text-teal-300 hover:bg-slate-600"
        >
          <Edit className="h-4 w-4" />
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => onDelete(category.id)}
          className="text-red-400 hover:text-red-300 hover:bg-slate-600"
        >
          <Trash2 className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};

// Note: Admin payment processing is handled via POSSystem component
// The AdminStripeSubmitButton component was removed as it used invalid Stripe imports

// Login Component
const LoginScreen = ({ onLogin }) => {
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/login`, { password });
      if (response.data.success) {
        localStorage.setItem('adminToken', response.data.token);
        onLogin(response.data.token);
        toast.success('Welcome to Admin Dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-900 flex items-center justify-center p-4">
      <Card className="w-full max-w-md p-8 bg-slate-800 border-slate-700 shadow-lg" data-testid="login-card">
        <div className="text-center mb-8">
          <div className="h-16 w-16 rounded-full bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center mx-auto mb-4">
            <Heart className="h-8 w-8 text-white" />
          </div>
          <h1 className="text-2xl font-bold text-slate-100 mb-2">Admin Dashboard</h1>
          <p className="text-white">Enter your password to continue</p>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <Label htmlFor="password" className="text-white">Password</Label>
            <div className="relative mt-1">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-500" />
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter admin password"
                className="pl-10 bg-slate-700 border-teal-600 text-slate-100 placeholder:text-slate-500"
                data-testid="password-input"
                required
              />
            </div>
          </div>
          <Button 
            type="submit" 
            className="w-full bg-teal-500 hover:bg-teal-400 text-slate-900"
            disabled={loading}
            data-testid="login-button"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </Button>
        </form>
        
        <div className="mt-6 pt-6 border-t border-slate-700 text-center">
          <a 
            href="/"
            className="inline-flex items-center gap-2 text-white hover:text-teal-400 transition-colors"
            data-testid="back-to-booking-link"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Booking Form
          </a>
        </div>
      </Card>
    </div>
  );
};

const AdminDashboard = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [authChecking, setAuthChecking] = useState(true);
  const [bookings, setBookings] = useState([]);
  const [filter, setFilter] = useState('all');
  const [searchDate, setSearchDate] = useState('');
  const [loading, setLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [editingBooking, setEditingBooking] = useState(null);
  const [editDate, setEditDate] = useState('');
  const [editStartTime, setEditStartTime] = useState('');
  const [editEndTime, setEditEndTime] = useState('');
  const [calendarConnected, setCalendarConnected] = useState(false);
  const [showAuthUrl, setShowAuthUrl] = useState(false);
  const [authUrl, setAuthUrl] = useState('');
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [showCreateBooking, setShowCreateBooking] = useState(false);
  const [showPastClasses, setShowPastClasses] = useState(false);
  const [calendarViewMode, setCalendarViewMode] = useState(true); // Default to Calendar View
  
  // Expanded booking state (only one at a time)
  const [expandedBookingId, setExpandedBookingId] = useState(null);
  
  // Change Class Type state
  const [showChangeClassType, setShowChangeClassType] = useState(false);
  const [changeClassTypeBooking, setChangeClassTypeBooking] = useState(null);
  const [newClassTypeId, setNewClassTypeId] = useState('');
  const [classTypeChangeReason, setClassTypeChangeReason] = useState('');
  const [changingClassType, setChangingClassType] = useState(false);
  const [classTypesWithPrices, setClassTypesWithPrices] = useState([]);
  const [loadingPrices, setLoadingPrices] = useState(false);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const BOOKINGS_PER_PAGE = 10;
  
  // Notes dialog state
  const [showNotesDialog, setShowNotesDialog] = useState(false);
  const [notesBookingId, setNotesBookingId] = useState(null);
  const [notesText, setNotesText] = useState('');
  const [notesSaving, setNotesSaving] = useState(false);
  
  // Edit Students dialog state
  const [showEditStudents, setShowEditStudents] = useState(false);
  const [editStudentsBooking, setEditStudentsBooking] = useState(null);
  const [editStudentsList, setEditStudentsList] = useState([]);
  const [newStudent, setNewStudent] = useState({ firstName: '', lastName: '', email: '', phone: '', notes: '', paymentStatus: 'paid' });
  const [studentSaving, setStudentSaving] = useState(false);
  const [editingStudentIndex, setEditingStudentIndex] = useState(null); // Track which student index is being edited
  const [editingStudentOriginalEmail, setEditingStudentOriginalEmail] = useState(null); // Store original email for API call
  
  // Digital Test Dialog state (BLS classes only)
  const [showDigitalTestDialog, setShowDigitalTestDialog] = useState(false);
  const [digitalTestBooking, setDigitalTestBooking] = useState(null);
  const [digitalTestStudents, setDigitalTestStudents] = useState([]); // Student statuses
  const [selectedTestStudents, setSelectedTestStudents] = useState([]); // Selected student indices
  const [sendingDigitalTests, setSendingDigitalTests] = useState(false);
  
  // Edit DateTime dialog state
  const [showEditDateTime, setShowEditDateTime] = useState(false);
  const [editDateTimeBooking, setEditDateTimeBooking] = useState(null);
  const [editTime, setEditTime] = useState('');
  const [dateTimeSaving, setDateTimeSaving] = useState(false);
  
  // Email log state
  const [showEmailLog, setShowEmailLog] = useState(false);
  const [emailLogBooking, setEmailLogBooking] = useState(null);
  const [emailLogs, setEmailLogs] = useState([]);
  const [emailLogLoading, setEmailLogLoading] = useState(false);
  
  // Signature view state
  const [viewSignatureData, setViewSignatureData] = useState(null);
  
  // Sign-In Password Dialog state
  const [showSignInPasswordDialog, setShowSignInPasswordDialog] = useState(false);
  const [signInPassword, setSignInPassword] = useState('');
  const [signInPasswordLoading, setSignInPasswordLoading] = useState(false);
  
  // AHA Credentials state
  const [showAhaCredentialsDialog, setShowAhaCredentialsDialog] = useState(false);
  const [ahaUsername, setAhaUsername] = useState('');
  const [ahaPassword, setAhaPassword] = useState('');
  const [ahaCredentialsLoading, setAhaCredentialsLoading] = useState(false);
  const [ahaCredentialsConfigured, setAhaCredentialsConfigured] = useState(false);
  const [ahaTestingLogin, setAhaTestingLogin] = useState(false);
  
  // AHA Product Links state
  const [showAhaProductLinksDialog, setShowAhaProductLinksDialog] = useState(false);
  const [ahaProductLinks, setAhaProductLinks] = useState([]);
  const [ahaProductLinksLoading, setAhaProductLinksLoading] = useState(false);
  const [editingAhaLink, setEditingAhaLink] = useState(null);
  const [newAhaLink, setNewAhaLink] = useState({
    name: '',
    product_type: 'ebook',
    aha_product_name: '',
    class_type_ids: [],
    is_active: true
  });
  
  // BLS Test Link state (static/always present)
  const [blsTestLink, setBlsTestLink] = useState('');
  const [blsTestLinkSaving, setBlsTestLinkSaving] = useState(false);
  
  // Rejection dialog state (choose refund or no refund)
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [rejectBooking, setRejectBooking] = useState(null);
  
  // Delete/Hide dialog state
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteBooking, setDeleteBooking] = useState(null);
  
  // Fee Waivers dialog state
  const [showFeeWaiversDialog, setShowFeeWaiversDialog] = useState(false);
  const [feeWaiversBooking, setFeeWaiversBooking] = useState(null);
  const [feeWaiversLoading, setFeeWaiversLoading] = useState(false);
  
  // Payment mode state
  const [paymentMode, setPaymentMode] = useState('live');
  const [paymentModeLoading, setPaymentModeLoading] = useState(false);

  // Class Types management state
  const [showClassTypes, setShowClassTypes] = useState(false);
  const [showCategoryManager, setShowCategoryManager] = useState(false);
  const [categoryFormData, setCategoryFormData] = useState({ name: '', slug: '', sort_order: 0 });
  const [editingCategory, setEditingCategory] = useState(null);
  const [categorySaving, setCategorySaving] = useState(false);
  const [adminClassTypes, setAdminClassTypes] = useState([]);
  const [classTypesLoading, setClassTypesLoading] = useState(false);
  const [editingClassType, setEditingClassType] = useState(null);
  const [showAddClassForm, setShowAddClassForm] = useState(false);
  const [classTypeForm, setClassTypeForm] = useState({
    id: '',
    name: '',
    duration_hours: 4,
    sku: '',
    description: '',
    is_active: true,
    sort_order: 0,
    expiration_months: '',
    ecard_product_number: '',
    ecard_product_url: '',
    ecard_min_order: 1,
    course_category: '',
    paperwork_template: '',
    ebook_product_id: '',
    base_price: '',
    additional_student_price: ''
  });
  const [classTypeSaving, setClassTypeSaving] = useState(false);
  const [courseCategories, setCourseCategories] = useState([]);
  const [paperworkTemplateOptions, setPaperworkTemplateOptions] = useState([]);
  const [ebookProductOptions, setEbookProductOptions] = useState([]);
  const classTypesScrollRef = useRef(null);
  const classTypesListRef = useRef(null); // Ref for the actual scrollable list
  const classTypesScrollPositionRef = useRef(0);
  
  // eCard needs summary for header indicator
  const [ecardNeedsSummary, setEcardNeedsSummary] = useState({ total_needed: 0, by_product: [] });
  
  // eCard ordering state
  const [showEcardOrdering, setShowEcardOrdering] = useState(false);
  const [ecardNeeds, setEcardNeeds] = useState([]);
  const [ecardPrices, setEcardPrices] = useState({});
  const [ecardOrderQuantities, setEcardOrderQuantities] = useState({});
  const [ecardInventory, setEcardInventory] = useState({}); // Current inventory by class type
  const [ecardSupplierSettings, setEcardSupplierSettings] = useState({ username: '', password: '', configured: false, has_payment_card: false, card_last_four: '', tax_exempt: false, auto_order_mode: 'manual' });
  const [ecardLoading, setEcardLoading] = useState(false);
  const [ecardOrdering, setEcardOrdering] = useState(false);
  const [ecardCartResult, setEcardCartResult] = useState(null); // Result after generating cart
  const [showEcardSettings, setShowEcardSettings] = useState(false);
  const [ecardSettingsForm, setEcardSettingsForm] = useState({ username: '', password: '' });
  const [ecardDontAdjustInventory, setEcardDontAdjustInventory] = useState(false); // Skip inventory tracking
  const [editingInventory, setEditingInventory] = useState(null); // class_type_id being edited
  const [editInventoryValue, setEditInventoryValue] = useState(0);
  const [showCardSettings, setShowCardSettings] = useState(false);
  const [showSupplierSettings, setShowSupplierSettings] = useState(false);
  const [cardSettingsForm, setCardSettingsForm] = useState({ card_number: '', card_expiry: '', card_cvv: '', tax_exempt: true });
  const [showStudentsNeedingEcards, setShowStudentsNeedingEcards] = useState(false);
  const [studentsNeedingEcards, setStudentsNeedingEcards] = useState([]);
  const [markingEcardStudent, setMarkingEcardStudent] = useState(null); // {booking_id, student_index}
  const [studentsWithEcards, setStudentsWithEcards] = useState([]);
  const [showStudentsWithEcards, setShowStudentsWithEcards] = useState(false);
  const [showStudentEcardManager, setShowStudentEcardManager] = useState(false); // Separate dialog for managing student ecards
  const [showAdminTasks, setShowAdminTasks] = useState(false); // Admin Tasks dialog
  
  // AHA Verification Modal state
  const [ahaVerifyModal, setAhaVerifyModal] = useState({
    show: false,
    bookingId: null,
    studentIndex: null,
    student: null,
    enteredName: '',
    ahaName: '',
    currentStatus: false
  });
  
  // Enrollware Integration state
  const [showEnrollwareSettings, setShowEnrollwareSettings] = useState(false);
  const [enrollwareSettings, setEnrollwareSettings] = useState({ username: '', configured: false, last_updated: null });
  const [enrollwareSettingsForm, setEnrollwareSettingsForm] = useState({ username: '', password: '' });
  const [enrollwareLoading, setEnrollwareLoading] = useState(false);
  const [enrollwareTestResult, setEnrollwareTestResult] = useState(null);
  
  // QuickBooks Integration state
  const [qboStatus, setQboStatus] = useState({ connected: false, realm_id: null, reporting_mode: 'woocommerce' });
  const [qboLoading, setQboLoading] = useState(false);
  const [showQboSettings, setShowQboSettings] = useState(false);
  
  // Production Sync state
  const [showProductionSync, setShowProductionSync] = useState(false);
  const [productionSyncForm, setProductionSyncForm] = useState({ 
    production_mongo_url: '', 
    production_db_name: 'biggestlittleclassscheduler',
    password: ''
  });
  const [productionSyncing, setProductionSyncing] = useState(false);
  const [productionSyncResult, setProductionSyncResult] = useState(null);
  
  // eCard Order History state
  const [showOrderHistory, setShowOrderHistory] = useState(false);
  const [orderHistory, setOrderHistory] = useState([]);
  const [orderHistoryLoading, setOrderHistoryLoading] = useState(false);
  const [orderHistorySearch, setOrderHistorySearch] = useState('');
  const [placingOrder, setPlacingOrder] = useState(false); // For automated Playwright checkout
  const [orderResult, setOrderResult] = useState(null); // Result of automated order
  const [cachedPrices, setCachedPrices] = useState({ by_url: {}, by_product_number: {}, last_sync: null }); // Cached supplier prices
  const [refreshingPrices, setRefreshingPrices] = useState(false); // For manual price refresh

  // Weekly Calendar state
  const [calendarWeekStart, setCalendarWeekStart] = useState(() => {
    // Start from today (current date on far left)
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return now;
  });
  const [calendarEvents, setCalendarEvents] = useState([]);
  const [calendarLoading, setCalendarLoading] = useState(false);
  const [availableCalendars, setAvailableCalendars] = useState([]);
  const [selectedCalendars, setSelectedCalendars] = useState([]); // Empty means all
  const [showOnlyClassesAndRentals, setShowOnlyClassesAndRentals] = useState(false); // Filter for classes and rental pickups/dropoffs only

  // Pending Queue sidebar state
  const [showPendingQueue, setShowPendingQueue] = useState(false);

  // Send Queue panel state (eBooks and Digital Tests)
  const [showSendQueue, setShowSendQueue] = useState(false);
  const [queueSummary, setQueueSummary] = useState({ queued: 0, processing: 0, failed: 0, total_pending: 0 });

  // Email Template Editor state
  const [showEmailTemplateEditor, setShowEmailTemplateEditor] = useState(false);

  // Max students edit state
  const [editingMaxStudents, setEditingMaxStudents] = useState(null); // booking id
  const [newMaxStudents, setNewMaxStudents] = useState(10);
  
  // Location name edit state for public classes
  const [editingLocationName, setEditingLocationName] = useState(null); // booking id
  const [newLocationName, setNewLocationName] = useState('');
  
  // Reference code edit state
  const [editingReferenceCode, setEditingReferenceCode] = useState(null); // booking id
  const [newReferenceCode, setNewReferenceCode] = useState('');
  
  // Address edit state
  const [editingAddress, setEditingAddress] = useState(null); // booking id
  const [addressForm, setAddressForm] = useState({
    location_name: '',
    street: '',
    city: '',
    state: '',
    zip: ''
  });
  const [recurringClientLocations, setRecurringClientLocations] = useState([]);
  
  // POS System state
  const [showPOS, setShowPOS] = useState(false);
  const [showWaitlist, setShowWaitlist] = useState(false);
  const [waitlistStats, setWaitlistStats] = useState({ total: 0, waiting: 0, invited: 0, paid: 0, expired: 0, unseen: 0 });
  const [showStore, setShowStore] = useState(false);
  
  // Invoice System state
  const [showInvoices, setShowInvoices] = useState(false);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);  // For opening specific invoice
  
  // Rental Management state
  const [showRentals, setShowRentals] = useState(false);
  
  // Link Invoice Dialog state
  const [showLinkInvoiceDialog, setShowLinkInvoiceDialog] = useState(false);
  const [linkInvoiceBooking, setLinkInvoiceBooking] = useState(null);
  const [linkInvoiceSearch, setLinkInvoiceSearch] = useState('');
  const [availableInvoices, setAvailableInvoices] = useState([]);
  const [linkInvoiceLoading, setLinkInvoiceLoading] = useState(false);

  // Distance Calculator state
  const [showDistanceCalculator, setShowDistanceCalculator] = useState(false);
  
  // Discounts/Coupons state
  const [showDiscounts, setShowDiscounts] = useState(false);
  const [discounts, setDiscounts] = useState([]);
  const [discountsLoading, setDiscountsLoading] = useState(false);
  const [showDiscountForm, setShowDiscountForm] = useState(false);
  const [editingDiscount, setEditingDiscount] = useState(null);
  const [discountForm, setDiscountForm] = useState({
    code: '',
    description: '',
    discount_type: 'percent',
    amount: '',
    minimum_amount: '',
    maximum_amount: '',
    usage_limit: '',
    usage_limit_per_user: '',
    product_ids: [],
    excluded_product_ids: [],
    individual_use: false,
    free_shipping: false,
    exclude_sale_items: false,
    date_expires: '',
    is_active: true
  });
  const [savingDiscount, setSavingDiscount] = useState(false);
  
  // AHA Inventory state
  const [showAHAInventory, setShowAHAInventory] = useState(false);
  const [ahaInventory, setAhaInventory] = useState([]);
  const [ahaInventoryLoading, setAhaInventoryLoading] = useState(false);
  const [ahaInventoryError, setAhaInventoryError] = useState(null);
  const [ahaCacheAge, setAhaCacheAge] = useState(null); // Cache age in minutes
  
  // eCard Links state
  const [showEcardLinks, setShowEcardLinks] = useState(false);
  const [ecardLinks, setEcardLinks] = useState([]);
  const [ecardLinksLoading, setEcardLinksLoading] = useState(false);
  const [newEcardLink, setNewEcardLink] = useState({ name: '', product_number: '', link: '', min_order: 1 });
  const [editingEcardLink, setEditingEcardLink] = useState(null);
  
  // Local eCard Inventory state
  const [localEcardInventory, setLocalEcardInventory] = useState([]);
  const [pendingInventoryChanges, setPendingInventoryChanges] = useState({}); // Track unsaved inventory changes
  const [savingInventory, setSavingInventory] = useState(false);
  
  // Instructor eCard Ordering state
  const [showInstructorPricing, setShowInstructorPricing] = useState(false);
  const [showInstructorOrders, setShowInstructorOrders] = useState(false);
  const [instructorPricing, setInstructorPricing] = useState([]);
  const [instructorOrders, setInstructorOrders] = useState([]);
  const [instructorOrdersLoading, setInstructorOrdersLoading] = useState(false);
  const [pendingInstructorOrdersCount, setPendingInstructorOrdersCount] = useState(0);
  const [readyToFillOrdersCount, setReadyToFillOrdersCount] = useState(0);
  const [selectedInstructorOrder, setSelectedInstructorOrder] = useState(null);
  const [selectedOrderIds, setSelectedOrderIds] = useState([]);
  const [supplierCartLoading, setSupplierCartLoading] = useState(false);
  const [showSupplierCartConfirm, setShowSupplierCartConfirm] = useState(false);
  const [supplierCartData, setSupplierCartData] = useState(null);
  const [statusChangeOrder, setStatusChangeOrder] = useState(null);
  const [statusChangeNewStatus, setStatusChangeNewStatus] = useState('');
  const [orderStatusFilter, setOrderStatusFilter] = useState('all'); // 'all', 'pending', 'ordered', 'fulfilled', 'cancelled'
  const [statusChangeReason, setStatusChangeReason] = useState('');
  const [statusChangeLoading, setStatusChangeLoading] = useState(false);
  const [autoRefreshOrders, setAutoRefreshOrders] = useState(true); // Auto-refresh instructor orders
  const [collapsedSections, setCollapsedSections] = useState({ fulfilled: true, cancelled: true }); // Closed orders collapsed by default
  
  // Manual supplier order state
  const [manualOrderDialogOpen, setManualOrderDialogOpen] = useState(false);
  const [manualOrderTarget, setManualOrderTarget] = useState(null);
  const [manualSupplierNumber, setManualSupplierNumber] = useState('');
  const [manualOrderNotes, setManualOrderNotes] = useState('');
  const [savingManualOrder, setSavingManualOrder] = useState(false);
  
  // Order notes state
  const [newNote, setNewNote] = useState('');
  const [noteType, setNoteType] = useState('admin'); // 'admin' or 'customer'
  const [addingNote, setAddingNote] = useState(false);
  
  // Order items edit state
  const [editingOrderItems, setEditingOrderItems] = useState(false);
  const [editedItems, setEditedItems] = useState([]);
  const [savingOrderItems, setSavingOrderItems] = useState(false);
  const [inventoryAvailability, setInventoryAvailability] = useState(null);
  const [checkingInventory, setCheckingInventory] = useState(false);
  const [fillingFromInventory, setFillingFromInventory] = useState(false);
  const [availableEcardProducts, setAvailableEcardProducts] = useState([]);
  const [priceChangeWarning, setPriceChangeWarning] = useState(null);
  
  // Instructor Management state
  const [showInstructorManagement, setShowInstructorManagement] = useState(false);
  const [instructors, setInstructors] = useState([]);
  const [instructorsLoading, setInstructorsLoading] = useState(false);
  const [showInstructorForm, setShowInstructorForm] = useState(false);
  const [editingInstructor, setEditingInstructor] = useState(null);
  const [instructorForm, setInstructorForm] = useState({
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    no_reminders: false
  });
  const [instructorFormLoading, setInstructorFormLoading] = useState(false);
  
  // Credential management
  const [credentialTypes, setCredentialTypes] = useState({});
  const [showAddCredential, setShowAddCredential] = useState(false);
  const [selectedInstructorForCredential, setSelectedInstructorForCredential] = useState(null);
  const [credentialForm, setCredentialForm] = useState({
    credential_type: '',
    number: '',
    instructor_number: '',
    expiration: ''
  });
  const [credentialFormLoading, setCredentialFormLoading] = useState(false);
  
  // Admin List Management
  const [showAdminList, setShowAdminList] = useState(false);
  const [adminList, setAdminList] = useState([]);
  const [adminListLoading, setAdminListLoading] = useState(false);
  const [newAdminForm, setNewAdminForm] = useState({ name: '', email: '' });
  const [adminFormLoading, setAdminFormLoading] = useState(false);
  const [showReports, setShowReports] = useState(false);
  const [showRescheduleDashboard, setShowRescheduleDashboard] = useState(false);
  const [showPriceList, setShowPriceList] = useState(false);
  const [priceListData, setPriceListData] = useState([]);
  const [priceListLoading, setPriceListLoading] = useState(false);
  const [showRecurringClients, setShowRecurringClients] = useState(false);
  const [priceRefreshLoading, setPriceRefreshLoading] = useState(false);
  
  // Stripe Balance Display state
  const [stripeBalanceData, setStripeBalanceData] = useState(null);
  const [stripeBalanceLoading, setStripeBalanceLoading] = useState(false);
  const [stripeBalanceError, setStripeBalanceError] = useState(null);
  
  // Vault/Billing Feature Toggle state
  const [vaultEnabled, setVaultEnabled] = useState(false);
  const [vaultToggleLoading, setVaultToggleLoading] = useState(false);
  
  // Bulk eCard Order state
  const [showBulkOrders, setShowBulkOrders] = useState(false);
  const [bulkOrders, setBulkOrders] = useState([]);
  const [bulkOrdersLoading, setBulkOrdersLoading] = useState(false);
  const [showCreateBulkOrder, setShowCreateBulkOrder] = useState(false);
  const [bulkOrderItems, setBulkOrderItems] = useState({}); // product_number -> quantity
  const [bulkOrderNotes, setBulkOrderNotes] = useState('');
  const [creatingBulkOrder, setCreatingBulkOrder] = useState(false);
  const [processingBulkOrder, setProcessingBulkOrder] = useState(null);
  const [selectedBulkOrder, setSelectedBulkOrder] = useState(null);
  // Bulk order editing state
  const [editingBulkOrder, setEditingBulkOrder] = useState(null);
  const [editedBulkItems, setEditedBulkItems] = useState([]);
  const [bulkOrderEditReason, setBulkOrderEditReason] = useState('');
  const [savingBulkOrderEdit, setSavingBulkOrderEdit] = useState(false);
  // Bulk order status change state
  const [bulkStatusChangeOrder, setBulkStatusChangeOrder] = useState(null);
  const [bulkStatusChangeNew, setBulkStatusChangeNew] = useState('');
  const [bulkStatusChangeReason, setBulkStatusChangeReason] = useState('');
  const [savingBulkStatusChange, setSavingBulkStatusChange] = useState(false);
  // Emergency stop state
  const [emergencyStopLoading, setEmergencyStopLoading] = useState(false);
  
  // Email Attachments state
  const [showEmailAttachments, setShowEmailAttachments] = useState(false);
  const [emailAttachmentGroups, setEmailAttachmentGroups] = useState([]);
  
  // Recurring Client Link Dialog state
  const [showLinkRecurringClientDialog, setShowLinkRecurringClientDialog] = useState(false);
  const [linkingClientToBooking, setLinkingClientToBooking] = useState(null);
  const [linkRecurringClientSearch, setLinkRecurringClientSearch] = useState('');
  const [linkRecurringClientResults, setLinkRecurringClientResults] = useState([]);
  const [linkRecurringClientNotes, setLinkRecurringClientNotes] = useState('');
  const [linkingClient, setLinkingClient] = useState(false);
  
  // View Recurring Client Details state
  const [showViewRecurringClient, setShowViewRecurringClient] = useState(false);
  const [viewingRecurringClient, setViewingRecurringClient] = useState(null);
  const [loadingRecurringClient, setLoadingRecurringClient] = useState(false);
  
  const [emailAttachmentsLoading, setEmailAttachmentsLoading] = useState(false);
  const [showCreateGroupDialog, setShowCreateGroupDialog] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupClassTypes, setNewGroupClassTypes] = useState([]);
  const [editingGroup, setEditingGroup] = useState(null);
  const [uploadingToGroup, setUploadingToGroup] = useState(null);
  const [uploadProgress, setUploadProgress] = useState(false);
  
  // Booking Charges Preview state
  const [showChargesPreview, setShowChargesPreview] = useState(false);
  const [chargesPreviewBooking, setChargesPreviewBooking] = useState(null);
  const [chargesPreviewData, setChargesPreviewData] = useState(null);
  const [chargesPreviewLoading, setChargesPreviewLoading] = useState(false);
  // Inline charge display cache (booking_id -> pricing data)
  const [bookingChargesCache, setBookingChargesCache] = useState({});
  
  // Force refresh WooCommerce price cache (background with polling)
  const forcePriceRefresh = async () => {
    setPriceRefreshLoading(true);
    try {
      const response = await axios.post(`${API}/admin/woo-price-cache/refresh`);
      if (response.data.success) {
        toast.info('Price cache refresh started. Please wait...');
        
        // If it's a background refresh, poll for completion
        if (response.data.background) {
          let attempts = 0;
          const maxAttempts = 20; // 20 * 3s = 60 seconds max wait
          
          const pollStatus = async () => {
            try {
              const statusRes = await axios.get(`${API}/admin/woo-price-cache/status`);
              
              if (!statusRes.data.refresh_in_progress) {
                // Refresh complete
                const result = statusRes.data.last_refresh_result;
                if (result?.success) {
                  toast.success(`Price cache refreshed: ${statusRes.data.product_count || 0} products updated`);
                  
                  // Refetch class types to get updated prices
                  try {
                    const classTypesRes = await axios.get(`${API}/admin/class-types-with-prices`);
                    setClassTypesWithPrices(classTypesRes.data);
                    toast.success('Class type prices updated!');
                  } catch (e) {
                    console.error('Error refetching class types:', e);
                  }
                } else {
                  toast.error(result?.message || result?.error || 'Price refresh failed');
                }
                setPriceRefreshLoading(false);
                return;
              }
              
              // Still refreshing, poll again
              attempts++;
              if (attempts < maxAttempts) {
                setTimeout(pollStatus, 3000); // Check every 3 seconds
              } else {
                toast.warning('Price refresh is taking longer than expected. Check back in a moment.');
                setPriceRefreshLoading(false);
              }
            } catch (pollError) {
              console.error('Error polling cache status:', pollError);
              attempts++;
              if (attempts < maxAttempts) {
                setTimeout(pollStatus, 3000);
              } else {
                setPriceRefreshLoading(false);
              }
            }
          };
          
          // Start polling after a short delay
          setTimeout(pollStatus, 3000);
        } else {
          // Synchronous refresh completed
          toast.success(`Price cache refreshed: ${response.data.product_count || 0} products updated`);
          setPriceRefreshLoading(false);
        }
      } else {
        toast.error(response.data.message || 'Failed to refresh prices');
        console.error('Price refresh failed:', response.data);
        setPriceRefreshLoading(false);
      }
    } catch (error) {
      console.error('Error refreshing price cache:', error);
      toast.error('Failed to refresh price cache: ' + (error.response?.data?.detail || error.message));
      setPriceRefreshLoading(false);
    }
  };
  
  // Fetch price list data for the Price List dialog
  const fetchPriceListData = async () => {
    setPriceListLoading(true);
    try {
      const response = await axios.get(`${API}/admin/class-types`);
      // Filter to only active class types and sort by category then name
      const activeTypes = (response.data || []).filter(ct => ct.is_active);
      setPriceListData(activeTypes);
    } catch (error) {
      console.error('Error fetching price list:', error);
      toast.error('Failed to load price list');
    } finally {
      setPriceListLoading(false);
    }
  };
  
  // Generate PDF for price list
  const generatePriceListPDF = () => {
    // Group by category
    const categories = {};
    priceListData.forEach(ct => {
      const cat = ct.course_category || 'Other';
      if (!categories[cat]) categories[cat] = [];
      categories[cat].push(ct);
    });
    
    // Sort categories
    const categoryOrder = ['bls', 'heartsaver', 'instructor', 'other'];
    const sortedCategories = Object.keys(categories).sort((a, b) => {
      const aIdx = categoryOrder.indexOf(a.toLowerCase());
      const bIdx = categoryOrder.indexOf(b.toLowerCase());
      if (aIdx === -1 && bIdx === -1) return a.localeCompare(b);
      if (aIdx === -1) return 1;
      if (bIdx === -1) return -1;
      return aIdx - bIdx;
    });
    
    // Build HTML content for PDF
    let html = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Biggest Little CPR - Class Price List</title>
        <style>
          @media print {
            @page { margin: 0.5in; size: letter; }
          }
          body { 
            font-family: Arial, sans-serif; 
            max-width: 800px; 
            margin: 0 auto; 
            padding: 20px;
            color: #333;
          }
          .header { 
            text-align: center; 
            margin-bottom: 30px; 
            padding-bottom: 20px;
            border-bottom: 3px solid #0d9488;
          }
          .header h1 { 
            color: #0d9488; 
            margin: 0 0 5px 0;
            font-size: 28px;
          }
          .header p { 
            color: #666; 
            margin: 5px 0;
            font-size: 14px;
          }
          .category { 
            margin-bottom: 25px; 
            page-break-inside: avoid;
          }
          .category h2 { 
            background: #0d9488; 
            color: white; 
            padding: 10px 15px; 
            margin: 0 0 0 0;
            font-size: 16px;
            text-transform: uppercase;
            letter-spacing: 1px;
          }
          table { 
            width: 100%; 
            border-collapse: collapse;
            font-size: 13px;
          }
          th { 
            background: #f1f5f9; 
            text-align: left; 
            padding: 10px 12px;
            border-bottom: 2px solid #e2e8f0;
            font-weight: 600;
            color: #475569;
          }
          td { 
            padding: 10px 12px; 
            border-bottom: 1px solid #e2e8f0;
          }
          tr:hover { background: #f8fafc; }
          .price { 
            text-align: right; 
            font-weight: 600;
            color: #059669;
          }
          .sku { 
            color: #64748b; 
            font-family: monospace;
            font-size: 12px;
          }
          .footer { 
            margin-top: 30px; 
            padding-top: 20px;
            border-top: 1px solid #e2e8f0;
            text-align: center; 
            font-size: 11px; 
            color: #94a3b8;
          }
          .no-price { color: #94a3b8; font-style: italic; }
        </style>
      </head>
      <body>
        <div class="header">
          <h1>Biggest Little CPR</h1>
          <p>Class Price List</p>
          <p>Generated: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
        </div>
    `;
    
    sortedCategories.forEach(cat => {
      const catName = cat.charAt(0).toUpperCase() + cat.slice(1);
      const displayName = catName === 'Bls' ? 'BLS (Basic Life Support)' : 
                          catName === 'Heartsaver' ? 'Heartsaver' :
                          catName === 'Instructor' ? 'Instructor Courses' : catName;
      
      html += `
        <div class="category">
          <h2>${displayName}</h2>
          <table>
            <thead>
              <tr>
                <th style="width: 50%">Class Name</th>
                <th style="width: 20%">SKU</th>
                <th style="width: 15%" class="price">Base Price</th>
                <th style="width: 15%" class="price">Add'l Student</th>
              </tr>
            </thead>
            <tbody>
      `;
      
      categories[cat].sort((a, b) => a.name.localeCompare(b.name)).forEach(ct => {
        const basePrice = ct.base_price ? `$${ct.base_price.toFixed(2)}` : '<span class="no-price">Not set</span>';
        const addlPrice = ct.additional_student_price ? `$${ct.additional_student_price.toFixed(2)}` : '<span class="no-price">—</span>';
        html += `
          <tr>
            <td>${ct.name}</td>
            <td class="sku">${ct.sku || '—'}</td>
            <td class="price">${basePrice}</td>
            <td class="price">${addlPrice}</td>
          </tr>
        `;
      });
      
      html += `
            </tbody>
          </table>
        </div>
      `;
    });
    
    html += `
        <div class="footer">
          <p>Prices subject to change. Contact us for group rates and custom training packages.</p>
          <p>© ${new Date().getFullYear()} Biggest Little CPR • (775) 722-8056 • support@biggestlittlecpr.com</p>
        </div>
      </body>
      </html>
    `;
    
    // Open print window
    const printWindow = window.open('', '_blank');
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.onload = () => {
      printWindow.print();
    };
  };

  const fetchAdminList = async () => {
    setAdminListLoading(true);
    try {
      const response = await axios.get(`${API}/admin/admin-list`);
      setAdminList(response.data.admins || []);
    } catch (error) {
      console.error('Error fetching admin list:', error);
      toast.error('Failed to load admin list');
    } finally {
      setAdminListLoading(false);
    }
  };
  
  const addAdmin = async () => {
    if (!newAdminForm.name.trim() || !newAdminForm.email.trim()) {
      toast.error('Please enter both name and email');
      return;
    }
    
    setAdminFormLoading(true);
    try {
      await axios.post(`${API}/admin/admin-list`, newAdminForm);
      toast.success(`Added ${newAdminForm.email} to admin list`);
      setNewAdminForm({ name: '', email: '' });
      fetchAdminList();
    } catch (error) {
      console.error('Error adding admin:', error);
      toast.error(error.response?.data?.detail || 'Failed to add admin');
    } finally {
      setAdminFormLoading(false);
    }
  };
  
  const removeAdmin = async (email) => {
    if (!confirm(`Remove ${email} from admin notification list?`)) return;
    
    try {
      await axios.delete(`${API}/admin/admin-list/${encodeURIComponent(email)}`);
      toast.success(`Removed ${email} from admin list`);
      fetchAdminList();
    } catch (error) {
      console.error('Error removing admin:', error);
      toast.error('Failed to remove admin');
    }
  };
  
  const toggleAdminActive = async (email) => {
    try {
      const response = await axios.put(`${API}/admin/admin-list/${encodeURIComponent(email)}/toggle`);
      toast.success(`${email} is now ${response.data.active ? 'active' : 'inactive'}`);
      fetchAdminList();
    } catch (error) {
      console.error('Error toggling admin:', error);
      toast.error('Failed to update admin status');
    }
  };
  
  // Create Public Class dialog state
  const [showCreatePublicClass, setShowCreatePublicClass] = useState(false);
  const [publicClassForm, setPublicClassForm] = useState({
    classTypeId: '',
    date: '',
    startTime: '',
    locationName: '',
    maxStudents: 10,
    street: '',
    city: '',
    state: 'NV',
    zip: ''
  });
  const [publicClassLoading, setPublicClassLoading] = useState(false);

  // Check existing token on mount OR admin app key
  useEffect(() => {
    const checkAuth = async () => {
      // Check for admin app bypass key
      const urlParams = new URLSearchParams(window.location.search);
      const adminKey = urlParams.get('adminkey');
      const tabParam = urlParams.get('tab');
      const userAgent = navigator.userAgent;
      
      // Admin app bypass - auto-authenticate if key is valid
      if (adminKey === 'BLCPR_ADMIN_2024' || userAgent.includes('BiggestLittleCPR-Admin-App')) {
        try {
          const response = await axios.post(`${API}/admin/app-login`, { 
            adminkey: adminKey || 'BLCPR_ADMIN_2024',
            user_agent: userAgent
          });
          if (response.data.success) {
            localStorage.setItem('adminToken', response.data.token);
            setIsAuthenticated(true);
            
            // Auto-open specific tab if requested
            if (tabParam === 'rentals') {
              setTimeout(() => setShowRentals(true), 500);
            } else if (tabParam === 'pos') {
              setTimeout(() => setShowPOS(true), 500);
            } else if (tabParam === 'invoices') {
              setTimeout(() => setShowInvoices(true), 500);
            }
            
            // Remove the key from URL for cleanliness
            window.history.replaceState({}, '', '/admin');
            setAuthChecking(false);
            return;
          }
        } catch (e) {
          console.log('Admin app login failed:', e);
        }
      }
      
      // Standard token check
      const token = localStorage.getItem('adminToken');
      if (token) {
        try {
          await axios.post(`${API}/admin/verify-token?token=${token}`);
          setIsAuthenticated(true);
          
          // Auto-open specific tab if requested (for normal login too)
          if (tabParam === 'rentals') {
            setTimeout(() => setShowRentals(true), 500);
          } else if (tabParam === 'pos') {
            setTimeout(() => setShowPOS(true), 500);
          } else if (tabParam === 'invoices') {
            setTimeout(() => setShowInvoices(true), 500);
          }
        } catch {
          localStorage.removeItem('adminToken');
        }
      }
      setAuthChecking(false);
    };
    checkAuth();
  }, []);

  useEffect(() => {
    if (isAuthenticated) {
      fetchBookings();
      checkCalendarStatus();
      fetchPaymentMode();
      fetchStripeBalance(); // Load Stripe balance
      fetchVaultEnabled(); // Load vault enabled setting
      fetchAdminClassTypes(); // Load class types for pending queue sidebar
      fetchCalendarEvents(); // Load calendar events for weekly view
      fetchPendingInstructorOrdersCount(); // Load pending instructor orders count for badge
      fetchEcardNeedsSummary(); // Load eCard needs for header indicator
      fetchQueueSummary(); // Load send queue summary for conditional button display
      fetchWaitlistStats(); // Load waitlist stats for button tooltip
      // Auto-refresh bookings every 15 seconds (calendar events only refresh on week change or manual refresh)
      const bookingsInterval = setInterval(fetchBookings, 15000);
      // Auto-refresh pending orders count every 60 seconds
      const ordersCountInterval = setInterval(fetchPendingInstructorOrdersCount, 60000);
      // Auto-refresh eCard needs summary every 60 seconds
      const ecardNeedsInterval = setInterval(fetchEcardNeedsSummary, 60000);
      // Auto-refresh queue summary every 30 seconds (for active send queue indicator)
      const queueSummaryInterval = setInterval(fetchQueueSummary, 30000);
      // Auto-refresh waitlist stats every 60 seconds
      const waitlistStatsInterval = setInterval(fetchWaitlistStats, 60000);
      
      const urlParams = new URLSearchParams(window.location.search);
      if (urlParams.get('calendar_connected') === 'true') {
        toast.success('Google Calendar connected successfully!');
        window.history.replaceState({}, '', '/admin');
        setCalendarConnected(true);
      }
      
      // Handle QuickBooks OAuth callback
      const qboStatus = urlParams.get('qbo_status');
      if (qboStatus === 'connected') {
        toast.success('QuickBooks connected successfully!');
        window.history.replaceState({}, '', '/admin');
        fetchQboStatus();
      } else if (qboStatus === 'error') {
        const qboError = urlParams.get('message') || 'Connection failed';
        toast.error(`QuickBooks error: ${qboError}`);
        window.history.replaceState({}, '', '/admin');
      }
      
      // Fetch QuickBooks status on load
      fetchQboStatus();
      
      // Handle quick approve/reject messages from email links
      const message = urlParams.get('message');
      const error = urlParams.get('error');
      const bookingId = urlParams.get('booking');
      
      if (message === 'approved') {
        toast.success(`Booking approved successfully!`, { duration: 5000 });
        window.history.replaceState({}, '', '/admin');
      } else if (message === 'rejected') {
        toast.success(`Booking rejected successfully!`, { duration: 5000 });
        window.history.replaceState({}, '', '/admin');
      } else if (message === 'already_processed') {
        const status = urlParams.get('status');
        toast.info(`This booking has already been ${status}`, { duration: 5000 });
        window.history.replaceState({}, '', '/admin');
      }
      
      if (error === 'booking_not_found') {
        toast.error('Booking not found', { duration: 5000 });
        window.history.replaceState({}, '', '/admin');
      } else if (error === 'invalid_token') {
        toast.error('Invalid or expired action link', { duration: 5000 });
        window.history.replaceState({}, '', '/admin');
      }
      
      return () => {
        clearInterval(bookingsInterval);
        clearInterval(ordersCountInterval);
        clearInterval(ecardNeedsInterval);
        clearInterval(queueSummaryInterval);
        clearInterval(waitlistStatsInterval);
      };
    }
  }, [isAuthenticated]);

  // Auto-refresh instructor orders when the panel is open (every 5 seconds for live updates)
  useEffect(() => {
    if (!showInstructorOrders || !autoRefreshOrders) return;
    
    // Silent refresh that doesn't show loading state
    const silentRefresh = async () => {
      try {
        const url = orderStatusFilter && orderStatusFilter !== 'all' 
          ? `${API}/admin/instructor-ecard-orders?status=${orderStatusFilter}` 
          : `${API}/admin/instructor-ecard-orders`;
        const response = await axios.get(url);
        let orders = response.data || [];
        
        // Sort orders same as fetchInstructorOrders
        // Ready to fill: either explicitly set to 'ready_to_fill' OR 'ordered' with supplier complete
        const isReadyToFill = (order) => {
          return order.order_status === 'ready_to_fill' ||
                 (order.order_status === 'ordered' && 
                  order.supplier_status && 
                  order.supplier_status.toLowerCase().includes('complete'));
        };
        
        orders.sort((a, b) => {
          const aReadyToFill = isReadyToFill(a);
          const bReadyToFill = isReadyToFill(b);
          if (aReadyToFill && !bReadyToFill) return -1;
          if (!aReadyToFill && bReadyToFill) return 1;
          
          const statusPriority = { 'pending': 0, 'ordered': 1, 'fulfilled': 2, 'cancelled': 3 };
          const statusA = statusPriority[a.order_status] ?? 4;
          const statusB = statusPriority[b.order_status] ?? 4;
          if (statusA !== statusB) return statusA - statusB;
          
          const dateA = new Date(a.created_at || 0);
          const dateB = new Date(b.created_at || 0);
          return dateB - dateA;
        });
        
        // Only update if data changed (compare admin_notes_log lengths)
        setInstructorOrders(prevOrders => {
          const hasChanges = orders.some((newOrder, idx) => {
            const oldOrder = prevOrders.find(o => o.id === newOrder.id);
            if (!oldOrder) return true;
            // Check if admin_notes or status changed
            if (oldOrder.admin_notes !== newOrder.admin_notes) return true;
            if (oldOrder.order_status !== newOrder.order_status) return true;
            if ((oldOrder.admin_notes_log?.length || 0) !== (newOrder.admin_notes_log?.length || 0)) return true;
            return false;
          });
          return hasChanges ? orders : prevOrders;
        });
      } catch (error) {
        console.error('Silent refresh failed:', error);
      }
    };
    
    const refreshInterval = setInterval(silentRefresh, 5000); // Refresh every 5 seconds
    
    return () => clearInterval(refreshInterval);
  }, [showInstructorOrders, autoRefreshOrders, orderStatusFilter]);

  const handleLogin = (token) => {
    setIsAuthenticated(true);
  };

  const handleLogout = async () => {
    try {
      await axios.post(`${API}/admin/logout`);
    } catch (e) {
      // Ignore errors
    }
    localStorage.removeItem('adminToken');
    setIsAuthenticated(false);
    toast.success('Logged out successfully');
  };

  const fetchSignInPassword = async () => {
    try {
      const response = await axios.get(`${API}/signin/settings`);
      setSignInPassword(response.data.password || '9911');
    } catch (error) {
      console.error('Failed to fetch sign-in password:', error);
      setSignInPassword('9911');
    }
  };

  const updateSignInPassword = async () => {
    if (!signInPassword.trim()) {
      toast.error('Password cannot be empty');
      return;
    }
    setSignInPasswordLoading(true);
    try {
      await axios.put(`${API}/admin/signin-settings?password=${encodeURIComponent(signInPassword)}`);
      toast.success('Sign-in password updated');
      setShowSignInPasswordDialog(false);
    } catch (error) {
      toast.error('Failed to update sign-in password');
    } finally {
      setSignInPasswordLoading(false);
    }
  };

  const checkCalendarStatus = async () => {
    try {
      const response = await axios.get(`${API}/auth/google/status`);
      setCalendarConnected(response.data.connected);
    } catch (error) {
      console.error('Failed to check calendar status');
    }
  };
  
  const fetchPaymentMode = async () => {
    try {
      const response = await axios.get(`${API}/admin/payment-mode`);
      setPaymentMode(response.data.mode);
    } catch (error) {
      console.error('Failed to fetch payment mode:', error);
    }
  };
  
  // Fetch Stripe balance data
  const fetchStripeBalance = async () => {
    setStripeBalanceLoading(true);
    setStripeBalanceError(null);
    try {
      const response = await axios.get(`${API}/admin/stripe/balance`);
      if (response.data.success) {
        setStripeBalanceData(response.data);
        setStripeBalanceError(null);
      } else {
        // Store the full response for help_steps if available
        setStripeBalanceData(response.data.setup_required ? response.data : null);
        setStripeBalanceError(response.data.error || 'Failed to fetch balance');
      }
    } catch (error) {
      console.error('Failed to fetch Stripe balance:', error);
      setStripeBalanceError(error.response?.data?.detail || error.message || 'Failed to fetch balance');
    } finally {
      setStripeBalanceLoading(false);
    }
  };
  
  // Fetch waitlist stats for tooltip
  const fetchWaitlistStats = async () => {
    try {
      const response = await axios.get(`${API}/waitlist`);
      if (response.data.stats) {
        setWaitlistStats(response.data.stats);
      }
    } catch (error) {
      console.error('Failed to fetch waitlist stats:', error);
    }
  };

  // Mark waitlist entries as seen when opening the panel
  const markWaitlistSeen = async () => {
    try {
      await axios.post(`${API}/waitlist/mark-seen`);
      // Update local state to clear unseen count
      setWaitlistStats(prev => ({ ...prev, unseen: 0 }));
    } catch (error) {
      console.error('Failed to mark waitlist as seen:', error);
    }
  };

  // Handle opening waitlist - mark as seen and refresh stats
  const handleOpenWaitlist = () => {
    setShowWaitlist(true);
    fetchWaitlistStats();
    markWaitlistSeen();
  };

  const togglePaymentMode = async () => {
    const newMode = paymentMode === 'live' ? 'sandbox' : 'live';
    const confirmMessage = newMode === 'sandbox' 
      ? 'Switch to SANDBOX mode? Bookings will be UNAVAILABLE for customers.'
      : 'Switch to LIVE mode? Customers will be able to make real payments.';
    
    if (!window.confirm(confirmMessage)) return;
    
    setPaymentModeLoading(true);
    try {
      const response = await axios.post(`${API}/admin/payment-mode?mode=${newMode}`);
      setPaymentMode(response.data.mode);
      toast.success(`Payment mode switched to ${newMode.toUpperCase()}`);
    } catch (error) {
      console.error('Payment mode toggle error:', error.response?.data || error.message);
      toast.error(`Failed to change payment mode: ${error.response?.data?.detail || error.message}`);
    } finally {
      setPaymentModeLoading(false);
    }
  };

  // Vault/Billing Feature Toggle functions
  const fetchVaultEnabled = async () => {
    try {
      const response = await axios.get(`${API}/admin/settings/vault-enabled`);
      setVaultEnabled(response.data.enabled);
    } catch (error) {
      console.error('Failed to fetch vault enabled setting:', error);
    }
  };

  const toggleVaultEnabled = async () => {
    const newValue = !vaultEnabled;
    const confirmMessage = newValue
      ? 'Enable Client Portal Billing? Clients will be able to save payment methods and use auto-pay.'
      : 'Disable Client Portal Billing? Clients will see "Coming Soon" instead of payment options.';
    
    if (!window.confirm(confirmMessage)) return;
    
    setVaultToggleLoading(true);
    try {
      const response = await axios.post(`${API}/admin/settings/vault-enabled?enabled=${newValue}`);
      setVaultEnabled(response.data.enabled);
      toast.success(`Client Portal Billing ${newValue ? 'enabled' : 'disabled'}`);
    } catch (error) {
      console.error('Failed to toggle vault enabled:', error);
      toast.error('Failed to update setting');
    } finally {
      setVaultToggleLoading(false);
    }
  };

  // Recalculate travel times for all out-of-area bookings
  const [recalculatingTravel, setRecalculatingTravel] = useState(false);
  
  const recalculateTravelTimes = async () => {
    try {
      setRecalculatingTravel(true);
      toast.loading('Recalculating travel times for all bookings...', { id: 'travel-calc' });
      
      const response = await axios.post(`${API}/admin/recalculate-travel-times`);
      
      toast.dismiss('travel-calc');
      
      if (response.data.success) {
        const { total_with_address, updated, calendar_synced } = response.data;
        toast.success(
          `Travel times updated!\n${updated} bookings recalculated\n${calendar_synced} calendars synced`,
          { duration: 5000 }
        );
        fetchBookings(); // Refresh the list
      }
    } catch (error) {
      toast.dismiss('travel-calc');
      toast.error(`Failed to recalculate: ${error.response?.data?.detail || error.message}`);
    } finally {
      setRecalculatingTravel(false);
    }
  };

  const connectGoogleCalendar = async () => {
    try {
      const response = await axios.get(`${API}/auth/google/login`);
      setAuthUrl(response.data.authorization_url);
      
      const newTab = window.open(response.data.authorization_url, '_blank', 'noopener,noreferrer');
      
      if (!newTab || newTab.closed) {
        setShowAuthUrl(true);
        toast.info('Please use the link below to authorize');
      } else {
        toast.info('Complete authorization in the new tab, then return here');
        const checkInterval = setInterval(async () => {
          try {
            const statusResponse = await axios.get(`${API}/auth/google/status`);
            if (statusResponse.data.connected) {
              clearInterval(checkInterval);
              setCalendarConnected(true);
              toast.success('Google Calendar connected successfully!');
              window.location.reload();
            }
          } catch (err) {
            // Continue checking
          }
        }, 3000);
        
        setTimeout(() => clearInterval(checkInterval), 180000);
      }
    } catch (error) {
      toast.error('Failed to initiate Google Calendar connection');
    }
  };
  
  const copyAuthUrl = () => {
    navigator.clipboard.writeText(authUrl);
    toast.success('Link copied to clipboard!');
  };
  
  // Create Public Class handler
  const handleCreatePublicClass = async () => {
    if (!publicClassForm.classTypeId || !publicClassForm.date || !publicClassForm.startTime) {
      toast.error('Please fill in class type, date, and time');
      return;
    }
    
    if (!publicClassForm.locationName) {
      toast.error('Please enter a location name for the public class');
      return;
    }
    
    setPublicClassLoading(true);
    try {
      const selectedClass = adminClassTypes.find(ct => ct.id === publicClassForm.classTypeId);
      const durationHours = selectedClass?.duration_hours || 4;
      
      const startDateTime = new Date(`${publicClassForm.date}T${publicClassForm.startTime}`);
      const endDateTime = new Date(startDateTime.getTime() + durationHours * 60 * 60 * 1000);
      
      const bookingData = {
        class_type_id: publicClassForm.classTypeId,
        class_type_name: selectedClass?.name || '',
        start_time: startDateTime.toISOString(),
        end_time: endDateTime.toISOString(),
        students: [], // No students for public class posting
        is_public: true,
        max_students: parseInt(publicClassForm.maxStudents) || 10,
        location_name: publicClassForm.locationName,
        class_address: publicClassForm.street ? {
          street: publicClassForm.street,
          city: publicClassForm.city,
          state: publicClassForm.state,
          zip: publicClassForm.zip
        } : null,
        status: 'approved', // Auto-approve public classes
        payment_status: 'not_required', // No payment for posting
        source: 'admin_public_class'
      };
      
      await axios.post(`${API}/admin/bookings/create-public`, bookingData);
      
      toast.success('Public class created successfully!');
      setShowCreatePublicClass(false);
      setPublicClassForm({
        classTypeId: '',
        date: '',
        startTime: '',
        locationName: '',
        maxStudents: 10,
        street: '',
        city: '',
        state: 'NV',
        zip: ''
      });
      fetchBookings();
    } catch (error) {
      console.error('Error creating public class:', error);
      toast.error(error.response?.data?.detail || 'Failed to create public class');
    } finally {
      setPublicClassLoading(false);
    }
  };

  const fetchCalendarEvents = async (weekStart = calendarWeekStart) => {
    setCalendarLoading(true);
    try {
      const startDate = new Date(weekStart);
      startDate.setHours(0, 0, 0, 0);
      const endDate = new Date(startDate);
      endDate.setDate(endDate.getDate() + 7);
      endDate.setHours(23, 59, 59, 999);
      
      const response = await axios.get(`${API}/admin/calendar-events`, {
        params: {
          start_date: startDate.toISOString(),
          end_date: endDate.toISOString()
        }
      });
      
      setCalendarEvents(response.data.events || []);
      setAvailableCalendars(response.data.calendars || []);
      // Default to all calendars selected
      if (selectedCalendars.length === 0 && response.data.calendars?.length > 0) {
        setSelectedCalendars(response.data.calendars.map(c => c.id));
      }
    } catch (error) {
      console.error('Failed to fetch calendar events:', error);
      // Don't show error toast - calendar might just not be connected
    } finally {
      setCalendarLoading(false);
    }
  };

  const navigateWeek = (direction) => {
    const newStart = new Date(calendarWeekStart);
    newStart.setDate(newStart.getDate() + (direction * 7));
    setCalendarWeekStart(newStart);
    fetchCalendarEvents(newStart);
  };

  const goToCurrentWeek = () => {
    // Return to today (current date on far left)
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    setCalendarWeekStart(today);
    fetchCalendarEvents(today);
  };

  // Fetch AHA eCard Inventory (force refresh from AHA portal)
  const fetchAHAInventory = async () => {
    setAhaInventoryLoading(true);
    setAhaInventoryError(null);
    setShowAHAInventory(true); // Open dialog immediately to show loading state
    try {
      // Force refresh - this triggers background refresh and returns cached data
      const response = await axios.get(`${API}/admin/aha-inventory?force_refresh=true`, { timeout: 30000 });
      setAhaInventory(response.data.inventory || []);
      setAhaCacheAge(response.data.cache_age_minutes);
      
      if (response.data.refresh_triggered) {
        toast.info('Refreshing AHA inventory in background...', { duration: 3000 });
        // Poll for updated data after a few seconds
        setTimeout(async () => {
          try {
            const updated = await axios.get(`${API}/admin/aha-inventory/cached`, { timeout: 5000 });
            if (updated.data.cache_age_minutes !== null && updated.data.cache_age_minutes < 1) {
              setAhaInventory(updated.data.inventory || []);
              setAhaCacheAge(updated.data.cache_age_minutes);
              toast.success('AHA inventory refreshed!');
            }
          } catch (e) {
            console.log('Background refresh polling failed', e);
          }
        }, 35000); // Wait 35 seconds for background refresh to complete
      }
    } catch (error) {
      console.error('Failed to fetch AHA inventory:', error);
      const errorMsg = error.code === 'ECONNABORTED' || error.response?.status === 504
        ? 'Request timed out - loading cached data instead'
        : error.response?.data?.detail || error.message || 'Failed to fetch inventory';
      
      // On timeout, try to load cached data
      if (error.code === 'ECONNABORTED' || error.response?.status === 504) {
        try {
          const cached = await axios.get(`${API}/admin/aha-inventory/cached`, { timeout: 5000 });
          if (cached.data.inventory?.length > 0) {
            setAhaInventory(cached.data.inventory);
            setAhaCacheAge(cached.data.cache_age_minutes);
            toast.info('Showing cached data. Background refresh in progress...');
          } else {
            setAhaInventoryError('No cached data available. Please try again later.');
          }
        } catch (e) {
          setAhaInventoryError(errorMsg);
        }
      } else {
        setAhaInventoryError(errorMsg);
      }
    } finally {
      setAhaInventoryLoading(false);
    }
  };

  // Fetch AHA inventory from cache (fast, no browser automation)
  const fetchAHAInventoryCached = async () => {
    try {
      const response = await axios.get(`${API}/admin/aha-inventory/cached`, { timeout: 5000 });
      setAhaInventory(response.data.inventory || []);
      setAhaCacheAge(response.data.cache_age_minutes);
      return response.data.inventory?.length > 0;
    } catch (error) {
      console.error('Failed to fetch cached AHA inventory:', error);
      return false;
    }
  };

  // Fetch AHA inventory in background (uses cache if fresh, otherwise fetches new)
  const fetchAHAInventoryBackground = async () => {
    // Don't re-fetch if already loading
    if (ahaInventoryLoading) return;
    
    // First try to load from cache (instant)
    const hasCachedData = await fetchAHAInventoryCached();
    
    // If no cached data, fetch fresh in background
    if (!hasCachedData) {
      setAhaInventoryLoading(true);
      setAhaInventoryError(null);
      try {
        const response = await axios.get(`${API}/admin/aha-inventory`, { timeout: 120000 });
        setAhaInventory(response.data.inventory || []);
        setAhaCacheAge(response.data.from_cache ? response.data.cache_age_minutes : null);
      } catch (error) {
        console.error('Failed to fetch AHA inventory (background):', error);
        const errorMsg = error.code === 'ECONNABORTED' 
          ? 'Request timed out'
          : error.response?.data?.detail || error.message || 'Failed to fetch';
        setAhaInventoryError(errorMsg);
      } finally {
        setAhaInventoryLoading(false);
      }
    }
  };

  // Fetch eCard Links
  const fetchEcardLinks = async () => {
    setEcardLinksLoading(true);
    try {
      const response = await axios.get(`${API}/admin/ecard-links`);
      setEcardLinks(response.data || []);
    } catch (error) {
      console.error('Failed to fetch eCard links:', error);
      toast.error('Failed to fetch eCard links');
    } finally {
      setEcardLinksLoading(false);
    }
  };

  // Fetch Local eCard Inventory
  const fetchLocalEcardInventory = async () => {
    try {
      const response = await axios.get(`${API}/admin/ecard-inventory`);
      setLocalEcardInventory(response.data || []);
    } catch (error) {
      console.error('Failed to fetch local eCard inventory:', error);
    }
  };

  // Cleanup eCard inventory database (removes orphaned/duplicate records)
  const [cleaningUpInventory, setCleaningUpInventory] = useState(false);
  const cleanupEcardInventory = async () => {
    setCleaningUpInventory(true);
    try {
      const response = await axios.post(`${API}/admin/ecard-inventory/cleanup`);
      if (response.data.success) {
        toast.success(`Inventory cleaned up! Removed ${response.data.deleted_records} old records, created ${response.data.created_records} fresh records.`);
        // Refresh all inventory-related data
        await fetchLocalEcardInventory();
        await fetchEcardData();
      }
    } catch (error) {
      console.error('Failed to cleanup inventory:', error);
      toast.error('Failed to cleanup inventory');
    } finally {
      setCleaningUpInventory(false);
    }
  };

  // Fetch Instructor eCard Pricing
  const fetchInstructorPricing = async () => {
    try {
      const response = await axios.get(`${API}/admin/instructor-ecard-pricing`);
      setInstructorPricing(response.data || []);
    } catch (error) {
      console.error('Failed to fetch instructor pricing:', error);
      toast.error('Failed to fetch instructor pricing');
    }
  };

  // Update Instructor eCard Pricing
  const updateInstructorPricing = async (productNumber, price, isActive, bulkQuantity = 0, bulkDiscountPercent = 0, supplierCost = null) => {
    try {
      let url = `${API}/admin/instructor-ecard-pricing/${encodeURIComponent(productNumber)}?price=${price}&is_active=${isActive}&bulk_quantity=${bulkQuantity}&bulk_discount_percent=${bulkDiscountPercent}`;
      if (supplierCost !== null && supplierCost !== undefined) {
        url += `&supplier_cost=${supplierCost}`;
      }
      await axios.put(url);
      toast.success('Pricing updated');
      fetchInstructorPricing();
    } catch (error) {
      console.error('Failed to update pricing:', error);
      toast.error('Failed to update pricing');
    }
  };

  // Refresh Supplier Prices
  const [refreshingSupplierPrices, setRefreshingSupplierPrices] = useState(false);
  const refreshSupplierPrices = async () => {
    setRefreshingSupplierPrices(true);
    try {
      await axios.post(`${API}/admin/refresh-supplier-prices`);
      toast.success('Supplier price refresh started');
      // Wait a few seconds then refresh the pricing data
      setTimeout(() => {
        fetchInstructorPricing();
        setRefreshingSupplierPrices(false);
      }, 5000);
    } catch (error) {
      console.error('Failed to refresh supplier prices:', error);
      toast.error('Failed to refresh supplier prices');
      setRefreshingSupplierPrices(false);
    }
  };

  // ========== Email Attachment Functions ==========
  
  const fetchEmailAttachmentGroups = async () => {
    setEmailAttachmentsLoading(true);
    try {
      const response = await axios.get(`${API}/admin/email-attachment-groups`);
      setEmailAttachmentGroups(response.data || []);
    } catch (error) {
      console.error('Failed to fetch email attachment groups:', error);
      toast.error('Failed to load attachment groups');
    } finally {
      setEmailAttachmentsLoading(false);
    }
  };

  const createAttachmentGroup = async () => {
    if (!newGroupName.trim()) {
      toast.error('Please enter a group name');
      return;
    }
    try {
      const response = await axios.post(`${API}/admin/email-attachment-groups`, {
        name: newGroupName.trim(),
        class_type_ids: newGroupClassTypes
      });
      setEmailAttachmentGroups([...emailAttachmentGroups, response.data]);
      setNewGroupName('');
      setNewGroupClassTypes([]);
      setShowCreateGroupDialog(false);
      toast.success('Attachment group created');
    } catch (error) {
      console.error('Failed to create group:', error);
      toast.error('Failed to create group');
    }
  };

  const updateAttachmentGroup = async (groupId, name, classTypeIds) => {
    try {
      await axios.put(`${API}/admin/email-attachment-groups/${groupId}`, {
        name,
        class_type_ids: classTypeIds
      });
      fetchEmailAttachmentGroups();
      setEditingGroup(null);
      toast.success('Group updated');
    } catch (error) {
      console.error('Failed to update group:', error);
      toast.error('Failed to update group');
    }
  };

  const deleteAttachmentGroup = async (groupId) => {
    if (!window.confirm('Delete this group and all its attachments?')) return;
    try {
      await axios.delete(`${API}/admin/email-attachment-groups/${groupId}`);
      setEmailAttachmentGroups(emailAttachmentGroups.filter(g => g.id !== groupId));
      toast.success('Group deleted');
    } catch (error) {
      console.error('Failed to delete group:', error);
      toast.error('Failed to delete group');
    }
  };

  const uploadAttachment = async (groupId, files) => {
    if (!files || files.length === 0) return;
    
    setUploadProgress(true);
    const fileArray = Array.from(files);
    let successCount = 0;
    let failCount = 0;
    
    try {
      for (const file of fileArray) {
        try {
          const formData = new FormData();
          formData.append('file', file);
          await axios.post(`${API}/admin/email-attachment-groups/${groupId}/attachments`, formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
          successCount++;
        } catch (error) {
          console.error(`Failed to upload ${file.name}:`, error);
          failCount++;
        }
      }
      
      fetchEmailAttachmentGroups();
      
      if (successCount > 0 && failCount === 0) {
        toast.success(`Uploaded ${successCount} file${successCount > 1 ? 's' : ''} successfully`);
      } else if (successCount > 0 && failCount > 0) {
        toast.warning(`Uploaded ${successCount} file${successCount > 1 ? 's' : ''}, ${failCount} failed`);
      } else {
        toast.error('Failed to upload files');
      }
    } catch (error) {
      console.error('Failed to upload attachments:', error);
      toast.error('Failed to upload files');
    } finally {
      setUploadProgress(false);
      setUploadingToGroup(null);
    }
  };

  const deleteAttachment = async (attachmentId) => {
    try {
      await axios.delete(`${API}/admin/email-attachments/${attachmentId}`);
      fetchEmailAttachmentGroups();
      toast.success('Attachment deleted');
    } catch (error) {
      console.error('Failed to delete attachment:', error);
      toast.error('Failed to delete attachment');
    }
  };

  // ========== Bulk eCard Order Functions ==========
  
  const fetchBulkOrders = async () => {
    setBulkOrdersLoading(true);
    try {
      const response = await axios.get(`${API}/admin/bulk-ecard-orders`);
      setBulkOrders(response.data || []);
    } catch (error) {
      console.error('Failed to fetch bulk orders:', error);
      toast.error('Failed to fetch bulk orders');
    } finally {
      setBulkOrdersLoading(false);
    }
  };
  
  const openBulkOrders = () => {
    setShowBulkOrders(true);
    fetchBulkOrders();
    fetchEcardLinks();
    fetchLocalEcardInventory();
  };
  
  const createBulkOrder = async () => {
    // Build items array from bulkOrderItems
    const items = [];
    for (const [productNumber, quantity] of Object.entries(bulkOrderItems)) {
      if (quantity > 0) {
        // Find the product details from ecardLinks
        const link = ecardLinks.find(l => l.product_number === productNumber);
        items.push({
          product_number: productNumber,
          product_url: link?.link || '',
          product_name: link?.name || productNumber,
          quantity: parseInt(quantity)
        });
      }
    }
    
    if (items.length === 0) {
      toast.error('Please select at least one product to order');
      return;
    }
    
    setCreatingBulkOrder(true);
    try {
      const response = await axios.post(`${API}/admin/bulk-ecard-orders`, {
        items,
        notes: bulkOrderNotes
      });
      
      if (response.data.success) {
        toast.success(`Bulk order created: ${response.data.order_id}`);
        setBulkOrderItems({});
        setBulkOrderNotes('');
        setShowCreateBulkOrder(false);
        fetchBulkOrders();
      }
    } catch (error) {
      console.error('Failed to create bulk order:', error);
      toast.error('Failed to create bulk order');
    } finally {
      setCreatingBulkOrder(false);
    }
  };
  
  // Emergency stop - kills all browser sessions
  const emergencyStopBrowser = async () => {
    if (!window.confirm('⚠️ EMERGENCY STOP\n\nThis will immediately terminate all browser sessions and cancel any in-progress orders.\n\nContinue?')) {
      return;
    }
    
    setEmergencyStopLoading(true);
    try {
      const response = await axios.post(`${API}/admin/cpr-suppliers-session/emergency-stop`);
      if (response.data.success) {
        toast.success('Emergency stop complete - all sessions terminated');
        // Clear any processing state
        setProcessingBulkOrder(null);
        setPlacingOrder(false);
        setCompletingCheckout(false);
        // Refresh order list
        fetchBulkOrders();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Emergency stop failed');
    } finally {
      setEmergencyStopLoading(false);
    }
  };
  
  const processBulkOrder = async (orderId) => {
    setProcessingBulkOrder(orderId);
    try {
      const response = await axios.post(`${API}/admin/bulk-ecard-orders/${orderId}/process`);
      if (response.data.success) {
        toast.success('Bulk order processing started');
        // Poll for updates
        const pollInterval = setInterval(async () => {
          const orderRes = await axios.get(`${API}/admin/bulk-ecard-orders/${orderId}`);
          const order = orderRes.data;
          if (order.status !== 'processing') {
            clearInterval(pollInterval);
            setProcessingBulkOrder(null);
            fetchBulkOrders();
            if (order.status === 'ordered') {
              toast.success(`Order completed! Supplier Order #${order.supplier_order_number}`);
            } else if (order.status === 'failed') {
              toast.error(`Order failed: ${order.error_message}`);
            }
          } else {
            // Update the selected order if viewing details
            if (selectedBulkOrder?.id === orderId) {
              setSelectedBulkOrder(order);
            }
          }
        }, 3000);
      }
    } catch (error) {
      console.error('Failed to process bulk order:', error);
      toast.error(error.response?.data?.detail || 'Failed to process bulk order');
      setProcessingBulkOrder(null);
    }
  };
  
  const resetCprSuppliersBrowser = async () => {
    try {
      const response = await axios.post(`${API}/admin/cpr-suppliers-session/reset`);
      if (response.data.success) {
        toast.success(response.data.message);
      }
    } catch (error) {
      console.error('Failed to reset browser session:', error);
      toast.error(error.response?.data?.detail || 'Failed to reset browser session');
    }
  };
  
  const [browserTestRunning, setBrowserTestRunning] = useState(false);
  const [browserTestResults, setBrowserTestResults] = useState(null);
  
  const testCprSuppliersBrowser = async () => {
    setBrowserTestRunning(true);
    setBrowserTestResults(null);
    try {
      const response = await axios.post(`${API}/admin/cpr-suppliers-session/test`);
      setBrowserTestResults(response.data);
      if (response.data.success) {
        toast.success('Browser test passed! All steps successful.');
      } else {
        toast.error('Browser test failed. Check results below.');
      }
    } catch (error) {
      console.error('Browser test failed:', error);
      setBrowserTestResults({ error: error.response?.data?.detail || error.message });
      toast.error(error.response?.data?.detail || 'Browser test failed');
    } finally {
      setBrowserTestRunning(false);
    }
  };
  
  const markBulkOrderReceived = async (orderId) => {
    try {
      const response = await axios.post(`${API}/admin/bulk-ecard-orders/${orderId}/mark-received`);
      if (response.data.success) {
        toast.success(response.data.message);
        fetchBulkOrders();
        fetchLocalEcardInventory();
        setSelectedBulkOrder(null);
      }
    } catch (error) {
      console.error('Failed to mark order as received:', error);
      toast.error(error.response?.data?.detail || 'Failed to mark order as received');
    }
  };
  
  const deleteBulkOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to delete this bulk order?')) return;
    
    try {
      await axios.delete(`${API}/admin/bulk-ecard-orders/${orderId}`);
      toast.success('Bulk order deleted');
      fetchBulkOrders();
      if (selectedBulkOrder?.id === orderId) {
        setSelectedBulkOrder(null);
      }
    } catch (error) {
      console.error('Failed to delete bulk order:', error);
      toast.error(error.response?.data?.detail || 'Failed to delete bulk order');
    }
  };
  
  const stopBulkOrder = async (orderId) => {
    if (!window.confirm('Stop this processing order? You can restart it later.')) return;
    
    try {
      const response = await axios.post(`${API}/admin/bulk-ecard-orders/${orderId}/stop`);
      if (response.data.success) {
        toast.success('Order stopped. You can restart it when ready.');
        setProcessingBulkOrder(null);
        fetchBulkOrders();
        if (selectedBulkOrder?.id === orderId) {
          const orderRes = await axios.get(`${API}/admin/bulk-ecard-orders/${orderId}`);
          setSelectedBulkOrder(orderRes.data);
        }
      }
    } catch (error) {
      console.error('Failed to stop bulk order:', error);
      toast.error(error.response?.data?.detail || 'Failed to stop bulk order');
    }
  };
  
  const clearBulkOrderLog = async (orderId) => {
    try {
      await axios.delete(`${API}/admin/bulk-ecard-orders/${orderId}/clear-log`);
      toast.success('Log cleared');
      fetchBulkOrders();
      if (selectedBulkOrder?.id === orderId) {
        const orderRes = await axios.get(`${API}/admin/bulk-ecard-orders/${orderId}`);
        setSelectedBulkOrder(orderRes.data);
      }
    } catch (error) {
      console.error('Failed to clear log:', error);
      toast.error('Failed to clear log');
    }
  };
  
  const retryBulkOrder = async (orderId) => {
    setProcessingBulkOrder(orderId);
    try {
      const response = await axios.post(`${API}/admin/bulk-ecard-orders/${orderId}/retry`);
      if (response.data.success) {
        toast.success('Retrying bulk order...');
        // Poll for updates (same as processBulkOrder)
        const pollInterval = setInterval(async () => {
          const orderRes = await axios.get(`${API}/admin/bulk-ecard-orders/${orderId}`);
          const order = orderRes.data;
          if (order.status !== 'processing') {
            clearInterval(pollInterval);
            setProcessingBulkOrder(null);
            fetchBulkOrders();
            if (order.status === 'ordered') {
              toast.success(`Order completed! Supplier Order #${order.supplier_order_number}`);
            } else if (order.status === 'failed') {
              toast.error(`Order failed: ${order.error_message}`);
            }
          } else if (selectedBulkOrder?.id === orderId) {
            setSelectedBulkOrder(order);
          }
        }, 3000);
      }
    } catch (error) {
      console.error('Failed to retry bulk order:', error);
      toast.error(error.response?.data?.detail || 'Failed to retry bulk order');
      setProcessingBulkOrder(null);
    }
  };

  // Start editing bulk order items
  const startEditingBulkOrder = async (order) => {
    // Fetch available products for dropdown
    try {
      const linksResponse = await axios.get(`${API}/admin/ecard-links`);
      const links = linksResponse.data || [];
      setAvailableEcardProducts(links.map(l => ({
        product_number: l.product_number,
        product_name: l.name || `Product ${l.product_number}`,
        price: l.cached_supplier_price || 15.00
      })));
    } catch (e) {
      console.error('Failed to fetch ecard links:', e);
    }
    
    // Initialize edited items
    const items = JSON.parse(JSON.stringify(order.items || []));
    setEditedBulkItems(items);
    setEditingBulkOrder(order);
    setBulkOrderEditReason('');
  };

  // Cancel editing bulk order
  const cancelEditingBulkOrder = () => {
    setEditingBulkOrder(null);
    setEditedBulkItems([]);
    setBulkOrderEditReason('');
  };

  // Save bulk order edits
  const saveBulkOrderEdits = async () => {
    if (!editingBulkOrder || !bulkOrderEditReason.trim() || bulkOrderEditReason.trim().length < 3) {
      toast.error('Please enter a reason for the edit (minimum 3 characters)');
      return;
    }
    
    if (editedBulkItems.length === 0) {
      toast.error('Order must have at least one item');
      return;
    }
    
    setSavingBulkOrderEdit(true);
    try {
      const response = await axios.put(
        `${API}/admin/bulk-ecard-orders/${editingBulkOrder.id}/items`,
        {
          items: editedBulkItems.map(item => ({
            product_number: item.product_number,
            product_name: item.product_name,
            quantity: item.quantity,
            price: item.price
          })),
          edit_reason: bulkOrderEditReason.trim()
        }
      );
      
      if (response.data.success) {
        toast.success(`Order updated - ${response.data.changes} changes, new total: ${response.data.new_total}`);
        cancelEditingBulkOrder();
        fetchBulkOrders();
      }
    } catch (error) {
      console.error('Failed to save bulk order edits:', error);
      toast.error(error.response?.data?.detail || 'Failed to save changes');
    } finally {
      setSavingBulkOrderEdit(false);
    }
  };

  // Update bulk item quantity
  const updateBulkItemQuantity = (index, newQty) => {
    const updated = [...editedBulkItems];
    const qty = parseInt(newQty);
    updated[index].quantity = isNaN(qty) ? 1 : Math.max(1, qty);
    setEditedBulkItems(updated);
  };

  // Remove bulk item
  const removeBulkItem = (index) => {
    const updated = editedBulkItems.filter((_, i) => i !== index);
    setEditedBulkItems(updated);
  };

  // Add bulk item
  const addBulkItem = (product) => {
    // Check if already exists
    const existingIndex = editedBulkItems.findIndex(i => i.product_number === product.product_number);
    if (existingIndex >= 0) {
      // Increment quantity
      const updated = [...editedBulkItems];
      updated[existingIndex].quantity += 1;
      setEditedBulkItems(updated);
    } else {
      setEditedBulkItems([...editedBulkItems, {
        product_number: product.product_number,
        product_name: product.product_name,
        quantity: 1,
        price: product.price
      }]);
    }
  };

  // Open bulk order status change dialog
  const openBulkStatusChange = (order, newStatus) => {
    setBulkStatusChangeOrder(order);
    setBulkStatusChangeNew(newStatus);
    setBulkStatusChangeReason('');
  };

  // Submit bulk order status change
  const submitBulkStatusChange = async () => {
    if (!bulkStatusChangeOrder || !bulkStatusChangeNew) return;
    if (!bulkStatusChangeReason || bulkStatusChangeReason.trim().length < 3) {
      toast.error('Please enter a reason (minimum 3 characters)');
      return;
    }
    
    setSavingBulkStatusChange(true);
    try {
      const response = await axios.put(
        `${API}/admin/bulk-ecard-orders/${bulkStatusChangeOrder.id}/status`,
        {
          new_status: bulkStatusChangeNew,
          reason: bulkStatusChangeReason.trim()
        }
      );
      
      if (response.data.success) {
        toast.success(`Status changed to ${bulkStatusChangeNew}`);
        setBulkStatusChangeOrder(null);
        setBulkStatusChangeNew('');
        setBulkStatusChangeReason('');
        fetchBulkOrders();
      }
    } catch (error) {
      console.error('Failed to change bulk order status:', error);
      toast.error(error.response?.data?.detail || 'Failed to change status');
    } finally {
      setSavingBulkStatusChange(false);
    }
  };

  // Fetch Instructor eCard Orders
  const fetchInstructorOrders = async (status = null) => {
    setInstructorOrdersLoading(true);
    setOrderStatusFilter(status || 'all');
    try {
      const url = status ? `${API}/admin/instructor-ecard-orders?status=${status}` : `${API}/admin/instructor-ecard-orders`;
      const response = await axios.get(url);
      let orders = response.data || [];
      
      // Sort orders: Ready to Fill first, then pending, then ordered, then fulfilled/cancelled
      // Within each status, sort by created_at (newest first)
      // Ready to fill: either explicitly set to 'ready_to_fill' OR 'ordered' with supplier complete
      const isReadyToFill = (order) => {
        return order.order_status === 'ready_to_fill' ||
               (order.order_status === 'ordered' && 
                order.supplier_status && 
                order.supplier_status.toLowerCase().includes('complete'));
      };
      
      orders.sort((a, b) => {
        // Ready to Fill orders always come first
        const aReadyToFill = isReadyToFill(a);
        const bReadyToFill = isReadyToFill(b);
        if (aReadyToFill && !bReadyToFill) return -1;
        if (!aReadyToFill && bReadyToFill) return 1;
        
        // Then sort by status priority: ready to fill, pending, ordered, on_hold, fulfilled, cancelled
        const statusPriority = {
          'pending': 0,
          'ordered': 1,
          'on_hold': 2,
          'fulfilled': 3,
          'cancelled': 4
        };
        const statusA = statusPriority[a.order_status] ?? 5;
        const statusB = statusPriority[b.order_status] ?? 5;
        if (statusA !== statusB) {
          return statusA - statusB;
        }
        // Then sort by date (newest first)
        const dateA = new Date(a.created_at || 0);
        const dateB = new Date(b.created_at || 0);
        return dateB - dateA;
      });
      
      setInstructorOrders(orders);
      // Update pending count and ready to fill count
      const pendingCount = orders.filter(o => o.order_status === 'pending').length;
      setPendingInstructorOrdersCount(pendingCount);
      
      // Count ready to fill orders
      const readyToFillCount = orders.filter(o => isReadyToFill(o)).length;
      setReadyToFillOrdersCount(readyToFillCount);
    } catch (error) {
      console.error('Failed to fetch instructor orders:', error);
      toast.error('Failed to fetch orders');
    } finally {
      setInstructorOrdersLoading(false);
    }
  };

  // Fetch actionable instructor orders count (for badge)
  // Only count orders that need attention: pending, failed, holding, or >14 days old (excluding cancelled/fulfilled)
  // NOTE: Ready to fill orders are shown separately, so exclude them from this count
  const fetchPendingInstructorOrdersCount = async () => {
    try {
      const response = await axios.get(`${API}/admin/instructor-ecard-orders`);
      const orders = response.data || [];
      const now = new Date();
      const fourteenDaysAgo = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);
      
      // Helper functions for status checks
      // Ready to fill: either explicitly set to 'ready_to_fill' OR 'ordered' with supplier complete
      const isReadyToFill = (o) => o.order_status === 'ready_to_fill' || (o.order_status === 'ordered' && o.supplier_status && o.supplier_status.toLowerCase().includes('complete'));
      const isOnHold = (o) => o.supplier_status && (o.supplier_status.toLowerCase().includes('hold'));
      const isFailed = (o) => o.auto_order_failed === true;
      const isPending = (o) => o.order_status === 'pending';
      const isActiveOrder = (o) => o.order_status !== 'cancelled' && o.order_status !== 'fulfilled';
      const isOlderThan14Days = (o) => {
        const createdAt = new Date(o.created_at || 0);
        return createdAt < fourteenDaysAgo;
      };
      
      // Count orders that need attention (EXCLUDING ready to fill - those get their own button):
      // All conditions require the order to be ACTIVE (not cancelled/fulfilled)
      // 1. Pending (not ordered yet)
      // 2. Failed to order (auto_order_failed) - but still active
      // 3. Holding (supplier status contains 'hold')
      // 4. Active orders older than 14 days
      const actionableCount = orders.filter(o => 
        !isReadyToFill(o) && isActiveOrder(o) && (
          isPending(o) || 
          isFailed(o) || 
          isOnHold(o) || 
          isOlderThan14Days(o)
        )
      ).length;
      
      setPendingInstructorOrdersCount(actionableCount);
      
      // Ready to fill count for the separate Ready To Fill button
      const readyToFillCount = orders.filter(o => isReadyToFill(o)).length;
      setReadyToFillOrdersCount(readyToFillCount);
    } catch (error) {
      console.error('Failed to fetch pending orders count:', error);
    }
  };

  // Fetch Send Queue summary for conditional button display
  const fetchQueueSummary = async () => {
    try {
      const response = await axios.get(`${API}/admin/ebook-queue`);
      if (response.data?.summary) {
        setQueueSummary(response.data.summary);
      }
    } catch (error) {
      console.error('Failed to fetch queue summary:', error);
    }
  };

  // Fulfill Instructor Order
  const fulfillInstructorOrder = async (orderId) => {
    try {
      await axios.put(`${API}/admin/instructor-ecard-orders/${orderId}/fulfill`);
      toast.success('Order marked as fulfilled');
      fetchInstructorOrders();
      setSelectedInstructorOrder(null);
    } catch (error) {
      console.error('Failed to fulfill order:', error);
      toast.error('Failed to fulfill order');
    }
  };

  // Bulk Fulfill Instructor Orders
  const bulkFulfillInstructorOrders = async () => {
    if (selectedOrderIds.length === 0) {
      toast.error('No orders selected');
      return;
    }
    try {
      const response = await axios.post(`${API}/admin/instructor-ecard-orders/bulk-fulfill`, {
        order_ids: selectedOrderIds
      });
      const data = response.data;
      if (data.fulfilled_count > 0) {
        toast.success(`${data.fulfilled_count} order(s) marked as fulfilled`);
      }
      if (data.failed_count > 0) {
        toast.warning(`${data.failed_count} order(s) could not be fulfilled`);
      }
      setSelectedOrderIds([]);
      fetchInstructorOrders();
    } catch (error) {
      console.error('Failed to bulk fulfill orders:', error);
      toast.error('Failed to fulfill orders');
    }
  };

  // Generate supplier cart for selected instructor orders
  const generateSupplierCart = async () => {
    if (selectedOrderIds.length === 0) {
      toast.error('No orders selected');
      return;
    }
    setSupplierCartLoading(true);
    try {
      const response = await axios.post(`${API}/admin/instructor-ecard-orders/generate-supplier-cart`, {
        order_ids: selectedOrderIds
      });
      const data = response.data;
      if (data.success) {
        setSupplierCartData(data);
        setShowSupplierCartConfirm(true);
        // Open checkout in new tab
        window.open(data.checkout_url, '_blank');
        toast.success('Cart generated! Complete checkout in the new tab.');
      }
    } catch (error) {
      console.error('Failed to generate supplier cart:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate cart');
    } finally {
      setSupplierCartLoading(false);
    }
  };

  // Update orders with new status (works for both supplier cart dialog and bulk buttons)
  const updateOrdersStatus = async (newStatus, orderIds = null) => {
    const idsToUpdate = orderIds || supplierCartData?.order_ids || selectedOrderIds;
    if (!idsToUpdate || idsToUpdate.length === 0) {
      toast.error('No orders selected');
      return;
    }
    try {
      const response = await axios.put(`${API}/admin/instructor-ecard-orders/bulk-status`, {
        order_ids: idsToUpdate,
        new_status: newStatus,
        reason: supplierCartData ? `Supplier cart generated - status set to ${newStatus}` : `Bulk status change to ${newStatus}`
      });
      if (response.data.updated_count > 0) {
        toast.success(`${response.data.updated_count} order(s) marked as ${newStatus}`);
      }
      // Close dialog if it was open
      if (supplierCartData) {
        setShowSupplierCartConfirm(false);
        setSupplierCartData(null);
      }
      setSelectedOrderIds([]);
      fetchInstructorOrders();
    } catch (error) {
      console.error(`Failed to mark orders as ${newStatus}:`, error);
      toast.error(`Failed to mark orders as ${newStatus}`);
    }
  };

  // Close supplier cart dialog without changing status
  const closeSupplierCartNoChange = () => {
    setShowSupplierCartConfirm(false);
    setSupplierCartData(null);
    setSelectedOrderIds([]);
    toast.info('Orders left unchanged');
  };

  // Open status change dialog
  const openStatusChange = (order, newStatus) => {
    setStatusChangeOrder(order);
    setStatusChangeNewStatus(newStatus);
    setStatusChangeReason('');
  };

  // Submit status change
  const submitStatusChange = async () => {
    if (!statusChangeOrder || !statusChangeNewStatus) return;
    if (!statusChangeReason || statusChangeReason.trim().length < 3) {
      toast.error('Please enter a reason (minimum 3 characters)');
      return;
    }
    
    setStatusChangeLoading(true);
    try {
      const response = await axios.put(
        `${API}/admin/instructor-ecard-orders/${statusChangeOrder.id}/status`,
        {
          new_status: statusChangeNewStatus,
          reason: statusChangeReason.trim()
        }
      );
      if (response.data.success) {
        toast.success(`Order status changed to ${statusChangeNewStatus}`);
        setStatusChangeOrder(null);
        setStatusChangeNewStatus('');
        setStatusChangeReason('');
        fetchInstructorOrders();
      }
    } catch (error) {
      console.error('Failed to change order status:', error);
      toast.error(error.response?.data?.detail || 'Failed to change status');
    } finally {
      setStatusChangeLoading(false);
    }
  };

  // Open manual supplier order dialog
  const openManualOrderDialog = (order) => {
    setManualOrderTarget(order);
    setManualSupplierNumber('');
    setManualOrderNotes('');
    setManualOrderDialogOpen(true);
  };

  // Submit manual supplier order number
  const submitManualSupplierOrder = async () => {
    if (!manualOrderTarget || !manualSupplierNumber.trim()) {
      toast.error('Please enter a TC order number');
      return;
    }
    
    setSavingManualOrder(true);
    try {
      // First refresh the orders to ensure we have the latest data
      await fetchInstructorOrders();
      
      // Small delay to ensure state is updated
      await new Promise(resolve => setTimeout(resolve, 300));
      
      const response = await axios.post(
        `${API}/admin/instructor-ecard-orders/${manualOrderTarget.id}/set-supplier-order`,
        {
          supplier_order_number: manualSupplierNumber.trim(),
          notes: manualOrderNotes.trim() || null
        }
      );
      if (response.data.success) {
        toast.success(`Linked to TC Order #${manualSupplierNumber.trim()}`);
        setManualOrderDialogOpen(false);
        setManualOrderTarget(null);
        setManualSupplierNumber('');
        setManualOrderNotes('');
        fetchInstructorOrders();
      }
    } catch (error) {
      console.error('Failed to set manual order:', error);
      toast.error(error.response?.data?.detail || 'Failed to set order number');
    } finally {
      setSavingManualOrder(false);
    }
  };

  // Toggle order selection
  const toggleOrderSelection = (orderId) => {
    setSelectedOrderIds(prev => 
      prev.includes(orderId) 
        ? prev.filter(id => id !== orderId)
        : [...prev, orderId]
    );
  };

  // Select all pending/ordered orders (selectable orders)
  const selectAllPendingOrders = () => {
    const selectableIds = instructorOrders
      .filter(o => o.order_status === 'pending' || o.order_status === 'ordered')
      .map(o => o.id);
    setSelectedOrderIds(selectableIds);
  };

  // Cancel Instructor Order
  const cancelInstructorOrder = async (orderId) => {
    try {
      await axios.put(`${API}/admin/instructor-ecard-orders/${orderId}/cancel`);
      toast.success('Order cancelled');
      fetchInstructorOrders();
      setSelectedInstructorOrder(null);
    } catch (error) {
      console.error('Failed to cancel order:', error);
      toast.error('Failed to cancel order');
    }
  };

  // Add note to order
  const addNoteToOrder = async () => {
    if (!selectedInstructorOrder || !newNote.trim()) {
      toast.error('Please enter a note');
      return;
    }
    setAddingNote(true);
    try {
      const response = await axios.post(
        `${API}/admin/instructor-ecard-orders/${selectedInstructorOrder.id}/notes`,
        {
          note: newNote.trim(),
          note_type: noteType
        }
      );
      if (response.data.success) {
        toast.success('Note added');
        // Refresh the order to show the new note
        const updatedOrder = await axios.get(`${API}/admin/instructor-ecard-orders/${selectedInstructorOrder.id}`);
        setSelectedInstructorOrder(updatedOrder.data);
        setNewNote('');
        fetchInstructorOrders();
      }
    } catch (error) {
      console.error('Failed to add note:', error);
      toast.error(error.response?.data?.detail || 'Failed to add note');
    } finally {
      setAddingNote(false);
    }
  };

  // Start editing order items
  const startEditingOrderItems = async () => {
    if (selectedInstructorOrder?.items) {
      // Fetch available products for dropdown
      try {
        const response = await axios.get(`${API}/admin/ecard-products`);
        setAvailableEcardProducts(response.data.products || []);
      } catch (error) {
        console.error('Failed to fetch eCard products:', error);
        // Fallback to ecard_links
        try {
          const linksResponse = await axios.get(`${API}/admin/ecard-links`);
          const links = linksResponse.data || [];
          setAvailableEcardProducts(links.map(l => ({
            product_number: l.product_number,
            product_name: l.name || `Product ${l.product_number}`,
            price: 15.00 // Default price
          })));
        } catch (e) {
          console.error('Failed to fetch ecard links:', e);
        }
      }
      
      // Initialize edited items with original tracking
      const items = JSON.parse(JSON.stringify(selectedInstructorOrder.items)).map(item => {
        // Normalize price field (DB uses unit_price, frontend uses price)
        const unitPrice = item.price || item.unit_price || 0;
        return {
          ...item,
          price: unitPrice,  // Ensure price is set
          unit_price: unitPrice,  // Keep both for compatibility
          subtotal: item.quantity * unitPrice,  // Recalculate subtotal
          original_product_number: item.product_number,
          original_product_name: item.product_name,
          original_price: unitPrice
        };
      });
      setEditedItems(items);
      setEditingOrderItems(true);
      setPriceChangeWarning(null);
    }
  };

  // Cancel editing order items
  const cancelEditingOrderItems = () => {
    setEditingOrderItems(false);
    setEditedItems([]);
    setPriceChangeWarning(null);
  };

  // Update item quantity
  const updateItemQuantity = (index, newQty) => {
    const updated = [...editedItems];
    // Parse quantity - allow empty during typing
    const parsed = parseInt(newQty);
    const qty = isNaN(parsed) ? 1 : Math.max(1, parsed);
    
    updated[index].quantity = qty;
    
    // Use price or unit_price (items from DB use unit_price)
    const unitPrice = updated[index].price || updated[index].unit_price || 0;
    updated[index].subtotal = qty * unitPrice;
    
    setEditedItems(updated);
    checkForPriceChanges(updated);
  };

  // Change product for an item
  const changeItemProduct = (index, newProductNumber) => {
    const newProduct = availableEcardProducts.find(p => p.product_number === newProductNumber);
    if (!newProduct) return;
    
    const updated = [...editedItems];
    const item = updated[index];
    const newPrice = newProduct.price || 0;
    
    // Update to new product, preserving original info
    updated[index] = {
      ...item,
      product_number: newProduct.product_number,
      product_name: newProduct.product_name,
      price: newPrice,
      unit_price: newPrice,  // Keep both for compatibility
      subtotal: item.quantity * newPrice,
      // Keep original tracking
      original_product_number: item.original_product_number,
      original_product_name: item.original_product_name,
      original_price: item.original_price
    };
    
    setEditedItems(updated);
    checkForPriceChanges(updated);
  };

  // Check for price changes and set warning
  const checkForPriceChanges = (items) => {
    const originalTotal = selectedInstructorOrder?.original_total || selectedInstructorOrder?.total || 0;
    const newTotal = items.reduce((sum, item) => sum + (item.subtotal || 0), 0);
    const difference = newTotal - originalTotal;
    
    if (Math.abs(difference) > 0.01) {
      setPriceChangeWarning({
        originalTotal,
        newTotal,
        difference
      });
    } else {
      setPriceChangeWarning(null);
    }
  };

  // Remove item from order
  const removeOrderItem = (index) => {
    if (editedItems.length <= 1) {
      toast.error('Order must have at least one item');
      return;
    }
    const updated = editedItems.filter((_, i) => i !== index);
    setEditedItems(updated);
    checkForPriceChanges(updated);
  };

  // Add new item to order
  const addOrderItem = () => {
    if (!availableEcardProducts || availableEcardProducts.length === 0) {
      toast.error('No products available to add');
      return;
    }
    const firstProduct = availableEcardProducts[0];
    const newItem = {
      product_number: firstProduct.product_number,
      product_name: firstProduct.product_name,
      price: firstProduct.price || 0,
      unit_price: firstProduct.price || 0,
      quantity: 1,
      subtotal: firstProduct.price || 0,
      is_new: true  // Flag to identify newly added items
    };
    const updated = [...editedItems, newItem];
    setEditedItems(updated);
    checkForPriceChanges(updated);
  };

  // Save edited order items
  const saveOrderItems = async () => {
    if (!selectedInstructorOrder || editedItems.length === 0) return;
    
    setSavingOrderItems(true);
    try {
      const response = await axios.put(
        `${API}/admin/instructor-ecard-orders/${selectedInstructorOrder.id}/items`,
        { items: editedItems }
      );
      
      if (response.data.success) {
        if (response.data.has_price_change) {
          toast.success(`Order updated. Price changed from $${response.data.original_total?.toFixed(2)} to $${response.data.total?.toFixed(2)}`);
        } else {
          toast.success('Order items updated');
        }
        // Refresh the order
        const updatedOrder = await axios.get(`${API}/admin/instructor-ecard-orders/${selectedInstructorOrder.id}`);
        setSelectedInstructorOrder(updatedOrder.data);
        setEditingOrderItems(false);
        setEditedItems([]);
        setPriceChangeWarning(null);
        fetchInstructorOrders();
      }
    } catch (error) {
      console.error('Failed to update order items:', error);
      toast.error(error.response?.data?.detail || 'Failed to update order items');
    } finally {
      setSavingOrderItems(false);
    }
  };

  // Check inventory availability for order
  const checkInventoryForOrder = async () => {
    if (!selectedInstructorOrder) return;
    
    setCheckingInventory(true);
    try {
      const response = await axios.get(
        `${API}/admin/instructor-ecard-orders/${selectedInstructorOrder.id}/check-inventory`
      );
      setInventoryAvailability(response.data);
    } catch (error) {
      console.error('Failed to check inventory:', error);
      toast.error('Failed to check inventory availability');
    } finally {
      setCheckingInventory(false);
    }
  };

  // Fill order from inventory
  const fillOrderFromInventory = async () => {
    if (!selectedInstructorOrder) return;
    
    if (!confirm('This will deduct items from your eCard inventory and mark the order as complete. Continue?')) {
      return;
    }
    
    setFillingFromInventory(true);
    try {
      const response = await axios.post(
        `${API}/admin/instructor-ecard-orders/${selectedInstructorOrder.id}/fill-from-inventory`
      );
      
      if (response.data.success) {
        toast.success('Order filled from inventory and marked as complete');
        // Refresh the order
        const updatedOrder = await axios.get(`${API}/admin/instructor-ecard-orders/${selectedInstructorOrder.id}`);
        setSelectedInstructorOrder(updatedOrder.data);
        setInventoryAvailability(null);
        fetchInstructorOrders();
      } else {
        // Show which items can't be filled
        toast.error(response.data.message || 'Cannot fill order from inventory');
        setInventoryAvailability(response.data);
      }
    } catch (error) {
      console.error('Failed to fill order from inventory:', error);
      toast.error(error.response?.data?.detail || 'Failed to fill order from inventory');
    } finally {
      setFillingFromInventory(false);
    }
  };

  // ============ Instructor Management Functions ============
  
  // Fetch all instructors
  const fetchInstructors = async () => {
    setInstructorsLoading(true);
    try {
      const [instructorsRes, typesRes] = await Promise.all([
        axios.get(`${API}/admin/instructors`),
        axios.get(`${API}/admin/credential-types`)
      ]);
      setInstructors(instructorsRes.data || []);
      setCredentialTypes(typesRes.data || {});
    } catch (error) {
      console.error('Failed to fetch instructors:', error);
      toast.error('Failed to load instructors');
    } finally {
      setInstructorsLoading(false);
    }
  };

  // Open add instructor form
  const openAddInstructor = () => {
    setEditingInstructor(null);
    setInstructorForm({
      first_name: '',
      last_name: '',
      email: '',
      phone: '',
      no_reminders: false
    });
    setShowInstructorForm(true);
  };

  // Open edit instructor form
  const openEditInstructor = (instructor) => {
    setEditingInstructor(instructor);
    setInstructorForm({
      first_name: instructor.first_name || '',
      last_name: instructor.last_name || '',
      email: instructor.email || '',
      phone: instructor.phone || '',
      no_reminders: instructor.no_reminders || false
    });
    setShowInstructorForm(true);
  };

  // Save instructor (create or update)
  const saveInstructor = async () => {
    if (!instructorForm.first_name || !instructorForm.last_name || !instructorForm.email) {
      toast.error('First name, last name, and email are required');
      return;
    }
    
    setInstructorFormLoading(true);
    try {
      if (editingInstructor) {
        await axios.put(`${API}/admin/instructors/${editingInstructor.id}`, instructorForm);
        toast.success('Instructor updated');
      } else {
        await axios.post(`${API}/admin/instructors`, instructorForm);
        toast.success('Instructor added');
      }
      
      setShowInstructorForm(false);
      fetchInstructors();
    } catch (error) {
      console.error('Failed to save instructor:', error);
      toast.error(error.response?.data?.detail || 'Failed to save instructor');
    } finally {
      setInstructorFormLoading(false);
    }
  };

  // Delete instructor
  const deleteInstructor = async (instructor) => {
    if (!confirm(`Are you sure you want to delete ${instructor.first_name} ${instructor.last_name}?`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/instructors/${instructor.id}`);
      toast.success('Instructor deleted');
      fetchInstructors();
    } catch (error) {
      console.error('Failed to delete instructor:', error);
      toast.error('Failed to delete instructor');
    }
  };

  // Open add credential dialog
  const openAddCredential = (instructor) => {
    setSelectedInstructorForCredential(instructor);
    // Find first credential type not already added
    const existingTypes = (instructor.credentials || []).map(c => c.credential_type);
    const availableType = Object.keys(credentialTypes).find(t => !existingTypes.includes(t)) || '';
    setCredentialForm({
      credential_type: availableType,
      number: '',
      instructor_number: '',
      expiration: ''
    });
    setShowAddCredential(true);
  };

  // Check if credential type is instructor type (requires instructor number)
  const isInstructorType = (credType) => {
    return credType && credType.endsWith('_instructor');
  };

  // Save credential
  const saveCredential = async () => {
    if (!credentialForm.credential_type || !credentialForm.number) {
      toast.error('Credential type and card number are required');
      return;
    }
    
    // Validate instructor number for instructor-type credentials
    if (isInstructorType(credentialForm.credential_type) && !credentialForm.instructor_number) {
      toast.error('Instructor number is required for instructor certifications');
      return;
    }
    
    setCredentialFormLoading(true);
    try {
      await axios.post(
        `${API}/admin/instructors/${selectedInstructorForCredential.id}/credentials`,
        {
          credential_type: credentialForm.credential_type,
          number: credentialForm.number,
          instructor_number: isInstructorType(credentialForm.credential_type) ? credentialForm.instructor_number : null,
          expiration: credentialForm.expiration || null
        }
      );
      toast.success('Credential added');
      setShowAddCredential(false);
      fetchInstructors();
    } catch (error) {
      console.error('Failed to add credential:', error);
      toast.error(error.response?.data?.detail || 'Failed to add credential');
    } finally {
      setCredentialFormLoading(false);
    }
  };

  // Delete credential
  const deleteCredential = async (instructorId, credentialId, credName) => {
    if (!confirm(`Are you sure you want to remove the ${credName} credential?`)) {
      return;
    }
    
    try {
      await axios.delete(`${API}/admin/instructors/${instructorId}/credentials/${credentialId}`);
      toast.success('Credential removed');
      fetchInstructors();
    } catch (error) {
      console.error('Failed to delete credential:', error);
      toast.error('Failed to remove credential');
    }
  };

  // Get days until expiration
  const getDaysUntilExpiration = (dateStr) => {
    if (!dateStr) return null;
    const expDate = new Date(dateStr);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    expDate.setHours(0, 0, 0, 0);
    const diffTime = expDate - today;
    return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
  };

  // Get expiration status color
  const getExpirationStatus = (dateStr) => {
    const days = getDaysUntilExpiration(dateStr);
    if (days === null) return { color: 'text-slate-500', bg: 'bg-slate-700', label: 'No exp' };
    if (days < 0) return { color: 'text-red-400', bg: 'bg-red-900/50', label: 'EXPIRED' };
    if (days <= 7) return { color: 'text-red-400', bg: 'bg-red-900/50', label: `${days}d left` };
    if (days <= 14) return { color: 'text-orange-400', bg: 'bg-orange-900/50', label: `${days}d left` };
    if (days <= 30) return { color: 'text-amber-400', bg: 'bg-amber-900/50', label: `${days}d left` };
    if (days <= 60) return { color: 'text-yellow-400', bg: 'bg-yellow-900/50', label: `${days}d left` };
    if (days <= 90) return { color: 'text-white', bg: 'bg-blue-900/50', label: `${days}d left` };
    return { color: 'text-white', bg: 'bg-emerald-900/50', label: `${days}d` };
  };

  // Get credential type display name
  const getCredentialTypeName = (type) => {
    return credentialTypes[type]?.name || type;
  };

  // ============ End Instructor Management Functions ============

  // Create eCard Link
  const createEcardLink = async () => {
    if (!newEcardLink.name || !newEcardLink.product_number || !newEcardLink.link) {
      toast.error('Please fill in all fields');
      return;
    }
    try {
      await axios.post(`${API}/admin/ecard-links`, {
        ...newEcardLink,
        min_order: parseInt(newEcardLink.min_order) || 1
      });
      toast.success('eCard link created');
      setNewEcardLink({ name: '', product_number: '', link: '', min_order: 1 });
      fetchEcardLinks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create eCard link');
    }
  };

  // Update eCard Link
  const updateEcardLink = async (link) => {
    try {
      await axios.put(`${API}/admin/ecard-links/${link.id}`, {
        ...link,
        min_order: parseInt(link.min_order) || 1
      });
      toast.success('eCard link updated');
      setEditingEcardLink(null);
      fetchEcardLinks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update eCard link');
    }
  };

  // Delete eCard Link
  const deleteEcardLink = async (linkId) => {
    if (!window.confirm('Are you sure you want to delete this eCard link?')) return;
    try {
      await axios.delete(`${API}/admin/ecard-links/${linkId}`);
      toast.success('eCard link deleted');
      fetchEcardLinks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete eCard link');
    }
  };

  // ========== AHA Credentials Functions ==========
  
  // ========== Discounts/Coupons Functions ==========
  
  const fetchDiscounts = async () => {
    setDiscountsLoading(true);
    try {
      const response = await axios.get(`${API}/admin/discounts`);
      setDiscounts(response.data.discounts || []);
    } catch (error) {
      console.error('Failed to fetch discounts:', error);
      toast.error('Failed to load discounts');
    } finally {
      setDiscountsLoading(false);
    }
  };
  
  const resetDiscountForm = () => {
    setDiscountForm({
      code: '',
      description: '',
      discount_type: 'percent',
      amount: '',
      minimum_amount: '',
      maximum_amount: '',
      usage_limit: '',
      usage_limit_per_user: '',
      product_ids: [],
      excluded_product_ids: [],
      individual_use: false,
      free_shipping: false,
      exclude_sale_items: false,
      date_expires: '',
      is_active: true
    });
    setEditingDiscount(null);
  };
  
  const openAddDiscountForm = () => {
    resetDiscountForm();
    setShowDiscountForm(true);
  };
  
  const openEditDiscountForm = (discount) => {
    setEditingDiscount(discount.id);
    setDiscountForm({
      code: discount.code || '',
      description: discount.description || '',
      discount_type: discount.discount_type || 'percent',
      amount: discount.amount || '',
      minimum_amount: discount.minimum_amount || '',
      maximum_amount: discount.maximum_amount || '',
      usage_limit: discount.usage_limit || '',
      usage_limit_per_user: discount.usage_limit_per_user || '',
      product_ids: discount.product_ids || [],
      excluded_product_ids: discount.excluded_product_ids || [],
      individual_use: discount.individual_use || false,
      free_shipping: discount.free_shipping || false,
      exclude_sale_items: discount.exclude_sale_items || false,
      date_expires: discount.date_expires ? discount.date_expires.split('T')[0] : '',
      is_active: discount.is_active !== false
    });
    setShowDiscountForm(true);
  };
  
  const saveDiscount = async () => {
    if (!discountForm.code.trim()) {
      toast.error('Please enter a discount code');
      return;
    }
    if (!discountForm.amount || parseFloat(discountForm.amount) <= 0) {
      toast.error('Please enter a valid discount amount');
      return;
    }
    
    setSavingDiscount(true);
    try {
      const payload = {
        code: discountForm.code.trim().toUpperCase(),
        description: discountForm.description,
        discount_type: discountForm.discount_type,
        amount: parseFloat(discountForm.amount) || 0,
        minimum_amount: parseFloat(discountForm.minimum_amount) || 0,
        maximum_amount: parseFloat(discountForm.maximum_amount) || 0,
        usage_limit: discountForm.usage_limit ? parseInt(discountForm.usage_limit) : null,
        usage_limit_per_user: discountForm.usage_limit_per_user ? parseInt(discountForm.usage_limit_per_user) : null,
        product_ids: discountForm.product_ids,
        excluded_product_ids: discountForm.excluded_product_ids,
        individual_use: discountForm.individual_use,
        free_shipping: discountForm.free_shipping,
        exclude_sale_items: discountForm.exclude_sale_items,
        date_expires: discountForm.date_expires || null,
        is_active: discountForm.is_active
      };
      
      if (editingDiscount) {
        await axios.put(`${API}/admin/discounts/${editingDiscount}`, payload);
        toast.success('Discount updated!');
      } else {
        await axios.post(`${API}/admin/discounts`, payload);
        toast.success('Discount created!');
      }
      
      setShowDiscountForm(false);
      resetDiscountForm();
      fetchDiscounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save discount');
    } finally {
      setSavingDiscount(false);
    }
  };
  
  const toggleDiscount = async (discountId) => {
    try {
      const response = await axios.post(`${API}/admin/discounts/${discountId}/toggle`);
      toast.success(response.data.message);
      fetchDiscounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to toggle discount');
    }
  };
  
  const deleteDiscount = async (discountId, code) => {
    if (!window.confirm(`Are you sure you want to delete discount "${code}"?`)) return;
    try {
      await axios.delete(`${API}/admin/discounts/${discountId}`);
      toast.success('Discount deleted');
      fetchDiscounts();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete discount');
    }
  };
  
  const fetchAhaCredentials = async () => {
    setAhaCredentialsLoading(true);
    try {
      const response = await axios.get(`${API}/admin/aha-credentials`);
      setAhaUsername(response.data.username || '');
      setAhaPassword(response.data.password_saved ? '********' : '');
      setAhaCredentialsConfigured(response.data.configured || false);
    } catch (error) {
      console.error('Failed to fetch AHA credentials:', error);
    } finally {
      setAhaCredentialsLoading(false);
    }
  };
  
  const saveAhaCredentials = async () => {
    if (!ahaUsername || !ahaPassword || ahaPassword === '********') {
      toast.error('Please enter both username and password');
      return;
    }
    
    setAhaCredentialsLoading(true);
    try {
      await axios.post(`${API}/admin/aha-credentials`, {
        username: ahaUsername,
        password: ahaPassword
      });
      toast.success('AHA credentials saved');
      setAhaCredentialsConfigured(true);
      setAhaPassword('********');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save AHA credentials');
    } finally {
      setAhaCredentialsLoading(false);
    }
  };
  
  const testAhaLogin = async () => {
    setAhaTestingLogin(true);
    try {
      const response = await axios.post(`${API}/admin/aha-credentials/test`);
      if (response.data.success) {
        toast.success(response.data.message || 'AHA login successful!');
      } else {
        toast.error(response.data.error || 'AHA login failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to test AHA login');
    } finally {
      setAhaTestingLogin(false);
    }
  };
  
  // ========== End AHA Credentials Functions ==========

  // ========== AHA Product Links Functions ==========
  const fetchAhaProductLinks = async () => {
    try {
      setAhaProductLinksLoading(true);
      const response = await axios.get(`${API}/admin/aha-product-links`);
      setAhaProductLinks(response.data || []);
    } catch (error) {
      console.error('Error fetching AHA product links:', error);
      toast.error('Failed to load AHA product links');
    } finally {
      setAhaProductLinksLoading(false);
    }
  };

  const saveAhaProductLink = async (link) => {
    try {
      if (link.id) {
        // Update existing
        await axios.put(`${API}/admin/aha-product-links/${link.id}`, link);
        toast.success('AHA product link updated');
      } else {
        // Create new
        await axios.post(`${API}/admin/aha-product-links`, link);
        toast.success('AHA product link created');
      }
      await fetchAhaProductLinks();
      setEditingAhaLink(null);
      setNewAhaLink({
        name: '',
        product_type: 'ebook',
        aha_product_name: '',
        class_type_ids: [],
        is_active: true
      });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save AHA product link');
    }
  };

  const deleteAhaProductLink = async (linkId) => {
    if (!confirm('Are you sure you want to delete this AHA product link?')) return;
    try {
      await axios.delete(`${API}/admin/aha-product-links/${linkId}`);
      toast.success('AHA product link deleted');
      await fetchAhaProductLinks();
    } catch (error) {
      toast.error('Failed to delete AHA product link');
    }
  };
  
  // Fetch BLS Test Link
  const fetchBlsTestLink = async () => {
    try {
      const response = await axios.get(`${API}/admin/bls-test-link`);
      setBlsTestLink(response.data.assign_url || '');
    } catch (error) {
      console.error('Error fetching BLS test link:', error);
    }
  };
  
  // Save BLS Test Link
  const saveBlsTestLink = async () => {
    try {
      setBlsTestLinkSaving(true);
      await axios.post(`${API}/admin/bls-test-link`, { assign_url: blsTestLink });
      toast.success('BLS Test Link saved');
    } catch (error) {
      toast.error('Failed to save BLS Test Link');
    } finally {
      setBlsTestLinkSaving(false);
    }
  };
  // ========== End AHA Product Links Functions ==========

  // Update Local Inventory - saves all pending changes
  const saveAllInventoryChanges = async () => {
    const changes = Object.entries(pendingInventoryChanges);
    if (changes.length === 0) {
      toast.info('No changes to save');
      return;
    }
    
    setSavingInventory(true);
    let totalAssigned = 0;
    let savedCount = 0;
    
    try {
      for (const [productUrl, quantity] of changes) {
        const response = await axios.post(`${API}/admin/ecard-inventory/by-product?product_url=${encodeURIComponent(productUrl)}&quantity=${quantity}`);
        totalAssigned += response.data.cards_auto_assigned || 0;
        savedCount++;
      }
      
      if (totalAssigned > 0) {
        toast.success(`Saved ${savedCount} inventory changes. Auto-assigned ${totalAssigned} cards to students!`);
      } else {
        toast.success(`Saved ${savedCount} inventory changes`);
      }
      
      setPendingInventoryChanges({});
      await fetchLocalEcardInventory();
      fetchEcardData();
      fetchEcardNeedsSummary();
    } catch (error) {
      toast.error('Failed to save some inventory changes');
      fetchLocalEcardInventory();
    } finally {
      setSavingInventory(false);
    }
  };
  
  // Track inventory change (doesn't save until button clicked)
  const trackInventoryChange = (productUrl, quantity) => {
    setPendingInventoryChanges(prev => ({
      ...prev,
      [productUrl]: quantity
    }));
  };

  const fetchBookings = async () => {
    try {
      const response = await axios.get(`${API}/bookings`);
      setBookings(response.data);
      setLastRefresh(new Date());
      setLoading(false);
    } catch (error) {
      toast.error('Failed to fetch bookings');
      setLoading(false);
    }
  };

  const handleApprove = async (bookingId) => {
    try {
      let params = {};
      if (editingBooking === bookingId && editDate && editStartTime && editEndTime) {
        // User enters times in PST - convert to UTC for the backend
        // Create a date string representing the local PST time
        const startLocalStr = `${editDate}T${editStartTime}:00`;
        const endLocalStr = `${editDate}T${editEndTime}:00`;
        
        // fromZonedTime converts a "local time in a specific timezone" to UTC
        const startDateTime = fromZonedTime(startLocalStr, 'America/Los_Angeles');
        const endDateTime = fromZonedTime(endLocalStr, 'America/Los_Angeles');
        
        params = {
          start_time: startDateTime.toISOString(),
          end_time: endDateTime.toISOString()
        };
      }
      
      toast.loading('Approving and syncing to calendar...', { id: 'approve' });
      
      const response = await axios.patch(`${API}/bookings/${bookingId}/approve`, null, { params });
      
      toast.dismiss('approve');
      
      // Check for registration actions
      if (response.data.registration_action === 'closed') {
        toast.warning('Approved - Registration was automatically closed (class now within cutoff)', { duration: 5000 });
      } else if (response.data.registration_action === 'reopen_eligible') {
        toast.success('Approved - Class moved outside cutoff. Check the booking to reopen registration.', { duration: 5000 });
      } else if (response.data.calendar_synced) {
        const message = params.start_time 
          ? '✓ Approved with new time | 📅 Calendar updated'
          : '✓ Approved | 📅 Calendar synced';
        toast.success(message, { duration: 4000 });
      } else {
        toast.warning(params.start_time ? 'Booking approved with updated time' : 'Booking approved');
      }
      
      setEditingBooking(null);
      fetchBookings();
    } catch (error) {
      toast.dismiss('approve');
      toast.error('Failed to approve booking');
    }
  };

  const handleEditBooking = (booking) => {
    setEditingBooking(booking.id);
    // Always display times in PST regardless of admin's browser timezone
    const startDate = new Date(booking.start_time);
    setEditDate(formatInTimeZone(startDate, 'America/Los_Angeles', 'yyyy-MM-dd'));
    setEditStartTime(formatInTimeZone(startDate, 'America/Los_Angeles', 'HH:mm'));
    const endDate = new Date(booking.end_time);
    setEditEndTime(formatInTimeZone(endDate, 'America/Los_Angeles', 'HH:mm'));
  };

  const cancelEdit = () => {
    setEditingBooking(null);
    setEditDate('');
    setEditStartTime('');
    setEditEndTime('');
  };

  // Change Class Type functions
  const openChangeClassType = async (booking) => {
    setChangeClassTypeBooking(booking);
    setNewClassTypeId('');
    setClassTypeChangeReason('');
    setShowChangeClassType(true);
    
    // Fetch class types with prices
    setLoadingPrices(true);
    try {
      const response = await axios.get(`${API}/admin/class-types-with-prices`);
      setClassTypesWithPrices(response.data);
    } catch (error) {
      console.error('Failed to fetch class types with prices:', error);
      // Fall back to adminClassTypes without prices
      setClassTypesWithPrices(adminClassTypes.map(ct => ({ ...ct, price: 0 })));
    } finally {
      setLoadingPrices(false);
    }
  };

  const closeChangeClassType = () => {
    setShowChangeClassType(false);
    setChangeClassTypeBooking(null);
    setNewClassTypeId('');
    setClassTypeChangeReason('');
    setClassTypesWithPrices([]);
  };

  // Fetch charges preview for a recurring client booking
  const fetchBookingCharges = async (bookingId) => {
    try {
      const response = await axios.get(`${API}/admin/bookings/${bookingId}/invoice-preview`);
      if (response.data.success) {
        return response.data.pricing;
      }
      return null;
    } catch (error) {
      console.error('Failed to fetch booking charges:', error);
      return null;
    }
  };

  // Open the charges breakdown modal
  const openChargesPreview = async (booking) => {
    setChargesPreviewBooking(booking);
    setShowChargesPreview(true);
    setChargesPreviewLoading(true);
    
    try {
      const pricing = await fetchBookingCharges(booking.id);
      setChargesPreviewData(pricing);
      // Update cache
      if (pricing) {
        setBookingChargesCache(prev => ({ ...prev, [booking.id]: pricing }));
      }
    } catch (error) {
      console.error('Error loading charges:', error);
      toast.error('Failed to load charges');
    } finally {
      setChargesPreviewLoading(false);
    }
  };

  // Close the charges breakdown modal
  const closeChargesPreview = () => {
    setShowChargesPreview(false);
    setChargesPreviewBooking(null);
    setChargesPreviewData(null);
  };

  // Fetch charges for inline display (with caching)
  const fetchInlineBookingCharges = async (bookingId) => {
    // Check cache first
    if (bookingChargesCache[bookingId]) {
      return bookingChargesCache[bookingId];
    }
    
    try {
      const pricing = await fetchBookingCharges(bookingId);
      if (pricing) {
        setBookingChargesCache(prev => ({ ...prev, [bookingId]: pricing }));
      }
      return pricing;
    } catch (error) {
      return null;
    }
  };

  // Effect to preload charges for recurring client bookings when bookings change
  useEffect(() => {
    const loadRecurringClientCharges = async () => {
      const recurringBookings = bookings.filter(b => 
        b.recurring_client_id && 
        !b.linked_invoice_id && 
        b.payment_status !== 'paid' &&
        !['rejected', 'rejected_no_refund', 'hidden'].includes(b.status)
      );
      
      // Load charges for visible bookings that aren't cached
      for (const booking of recurringBookings.slice(0, 20)) { // Limit to first 20 for performance
        if (!bookingChargesCache[booking.id]) {
          const pricing = await fetchBookingCharges(booking.id);
          if (pricing) {
            setBookingChargesCache(prev => ({ ...prev, [booking.id]: pricing }));
          }
        }
      }
    };
    
    if (bookings.length > 0) {
      loadRecurringClientCharges();
    }
  }, [bookings]);

  const handleChangeClassType = async () => {
    if (!changeClassTypeBooking || !newClassTypeId || !classTypeChangeReason.trim()) {
      toast.error('Please select a class type and enter a reason');
      return;
    }

    setChangingClassType(true);
    try {
      const response = await axios.put(
        `${API}/admin/bookings/${changeClassTypeBooking.id}/class-type`,
        {
          new_class_type_id: newClassTypeId,
          reason: classTypeChangeReason.trim()
        }
      );

      if (response.data.success) {
        // Show different message if there's a balance due
        if (response.data.balance_due && response.data.balance_due > 0) {
          toast.success(`Class type changed! Balance due: $${response.data.balance_due.toFixed(2)}. Email sent to customer.`, { duration: 6000 });
        } else {
          toast.success(response.data.message);
        }
        closeChangeClassType();
        fetchBookings();
      }
    } catch (error) {
      console.error('Failed to change class type:', error);
      toast.error(error.response?.data?.detail || 'Failed to change class type');
    } finally {
      setChangingClassType(false);
    }
  };

  const handleMarkBalancePaid = async (bookingId) => {
    try {
      toast.loading('Marking balance as paid...', { id: 'mark-balance' });
      const response = await axios.post(`${API}/admin/bookings/${bookingId}/mark-balance-paid`);
      toast.dismiss('mark-balance');
      if (response.data.success) {
        toast.success(response.data.message);
        fetchBookings();
      }
    } catch (error) {
      toast.dismiss('mark-balance');
      console.error('Failed to mark balance paid:', error);
      toast.error(error.response?.data?.detail || 'Failed to mark balance as paid');
    }
  };

  const handleReject = async (bookingId) => {
    try {
      await axios.patch(`${API}/bookings/${bookingId}/reject`);
      toast.success('Booking rejected');
      fetchBookings();
    } catch (error) {
      toast.error('Failed to reject booking');
    }
  };

  const handleStatusChange = async (bookingId, newStatus) => {
    // If changing to 'rejected', show dialog to choose refund option
    if (newStatus === 'rejected') {
      const booking = bookings.find(b => b.id === bookingId);
      setRejectBooking(booking);
      setShowRejectDialog(true);
      return; // Don't proceed until user chooses refund option
    }
    
    await executeStatusChange(bookingId, newStatus);
  };

  const executeStatusChange = async (bookingId, newStatus) => {
    try {
      // Show specific message based on status
      const loadingMsg = newStatus === 'rejected' 
        ? 'Rejecting and processing refund...' 
        : newStatus === 'rejected_no_refund'
          ? 'Rejecting booking...'
          : 'Updating status and syncing to calendar...';
      toast.loading(loadingMsg, { id: 'status-update' });
      
      const response = await axios.patch(`${API}/bookings/${bookingId}/status?status=${newStatus}`);
      
      toast.dismiss('status-update');
      
      const statusMessages = {
        pending: '✓ Status → Pending | 📅 Calendar updated',
        approved: '✓ Status → Approved | 📅 Calendar synced',
        rejected: response.data.refund_processed 
          ? `✓ Status → Rejected | 💰 Refund issued (${response.data.refund_id}) | 📅 Removed from calendar`
          : '✓ Status → Rejected | 📅 Removed from calendar',
        rejected_no_refund: '✓ Status → Rejected (No Refund) | 📅 Removed from calendar'
      };
      
      if (response.data.calendar_synced !== false) {
        toast.success(statusMessages[newStatus] || 'Status updated and synced', { duration: 5000 });
      } else {
        toast.warning(`Status updated to ${newStatus} (Calendar not connected)`, { duration: 4000 });
      }
      
      fetchBookings();
    } catch (error) {
      toast.dismiss('status-update');
      toast.error(error.response?.data?.detail || 'Failed to update status');
    }
  };

  // Handle rejection with refund
  const handleRejectWithRefund = async () => {
    if (!rejectBooking) return;
    setShowRejectDialog(false);
    await executeStatusChange(rejectBooking.id, 'rejected');
    setRejectBooking(null);
  };

  // Handle rejection without refund
  const handleRejectNoRefund = async () => {
    if (!rejectBooking) return;
    setShowRejectDialog(false);
    await executeStatusChange(rejectBooking.id, 'rejected_no_refund');
    setRejectBooking(null);
  };

  // Cancel rejection dialog
  const cancelReject = () => {
    setShowRejectDialog(false);
    setRejectBooking(null);
  };

  const handleAddToCalendar = async (bookingId) => {
    try {
      toast.loading('Adding to calendar...', { id: 'add-cal' });
      await axios.post(`${API}/bookings/${bookingId}/sync-calendar`);
      toast.dismiss('add-cal');
      toast.success('✓ Added to calendar', { duration: 3000 });
      fetchBookings();
    } catch (error) {
      toast.dismiss('add-cal');
      toast.error('Failed to add to calendar');
    }
  };

  const handleRemoveFromCalendar = async (bookingId) => {
    try {
      toast.loading('Removing from calendar...', { id: 'remove-cal' });
      await axios.delete(`${API}/bookings/${bookingId}/sync-calendar`);
      toast.dismiss('remove-cal');
      toast.success('✓ Removed from calendar (status unchanged)', { duration: 3000 });
      fetchBookings();
    } catch (error) {
      toast.dismiss('remove-cal');
      toast.error('Failed to remove from calendar');
    }
  };

  const handleRefreshCalendarEvent = async (bookingId) => {
    try {
      toast.loading('Refreshing calendar event...', { id: 'refresh-cal' });
      await axios.post(`${API}/bookings/${bookingId}/refresh-calendar`);
      toast.dismiss('refresh-cal');
      toast.success('✓ Calendar event refreshed with latest info', { duration: 3000 });
      fetchBookings();
    } catch (error) {
      toast.dismiss('refresh-cal');
      toast.error(error.response?.data?.detail || 'Failed to refresh calendar event');
    }
  };

  // Enrollware sync handler
  const handleSyncToEnrollware = async (bookingId) => {
    try {
      toast.loading('Syncing to Enrollware...', { id: 'enrollware-sync' });
      const response = await axios.post(`${API}/admin/booking/${bookingId}/sync-enrollware`);
      toast.dismiss('enrollware-sync');
      
      if (response.data.success) {
        toast.success(`✓ Synced to Enrollware! Class ID: ${response.data.enrollware_class_id}${response.data.students_added > 0 ? `, ${response.data.students_added} students added` : ''}`, { duration: 5000 });
        fetchBookings();
      } else {
        toast.error(response.data.error_message || 'Failed to sync to Enrollware');
      }
    } catch (error) {
      toast.dismiss('enrollware-sync');
      toast.error(error.response?.data?.detail || 'Failed to sync to Enrollware');
    }
  };

  // Delete from Enrollware handler
  const [deleteEnrollwareConfirm, setDeleteEnrollwareConfirm] = useState(null);
  
  const handleDeleteFromEnrollware = async (bookingId) => {
    try {
      toast.loading('Deleting from Enrollware... This may take a moment.', { id: 'enrollware-delete' });
      const response = await axios.post(`${API}/admin/booking/${bookingId}/delete-from-enrollware`);
      toast.dismiss('enrollware-delete');
      
      if (response.data.success) {
        const { students_deleted, class_deleted } = response.data;
        toast.success(
          `✓ Deleted from Enrollware! ${students_deleted} student(s) removed${class_deleted ? ', class deleted' : ''}. You can now re-sync if needed.`,
          { duration: 6000 }
        );
        fetchBookings();
      } else {
        const errors = response.data.errors?.join(', ') || 'Unknown error';
        toast.error(`Partial delete: ${errors}`);
        fetchBookings();
      }
    } catch (error) {
      toast.dismiss('enrollware-delete');
      toast.error(error.response?.data?.detail || 'Failed to delete from Enrollware');
    }
    setDeleteEnrollwareConfirm(null);
  };

  // Issue eCards handler
  const handleIssueEcards = async (bookingId) => {
    try {
      toast.loading('Issuing eCards via Enrollware...', { id: 'issue-ecards' });
      const response = await axios.post(`${API}/admin/booking/${bookingId}/issue-ecards`);
      toast.dismiss('issue-ecards');
      
      if (response.data.success) {
        toast.success(`✓ Issued ${response.data.ecards_issued} eCard(s)! Codes saved to student records.`, { duration: 5000 });
        fetchBookings();
      } else {
        toast.error(response.data.error_message || 'Failed to issue eCards');
      }
    } catch (error) {
      toast.dismiss('issue-ecards');
      const detail = error.response?.data?.detail;
      if (detail?.includes('not synced')) {
        toast.error('Please sync to Enrollware first before issuing eCards');
      } else {
        toast.error(detail || 'Failed to issue eCards');
      }
    }
  };

  // Fetch BLS Test Certificates handler
  const handleFetchBlsCertificates = async (bookingId) => {
    try {
      toast.loading('Fetching BLS test certificates from AHA...', { id: 'fetch-bls-certs' });
      const response = await axios.post(`${API}/admin/booking/${bookingId}/fetch-bls-certificates`);
      toast.dismiss('fetch-bls-certs');
      
      const { 
        certificates_found, 
        certificates_uploaded, 
        students_matched,
        scores_entered,
        students_marked_complete,
        students_not_found,
        already_retrieved,
        message,
        errors
      } = response.data;
      
      // Check if all were already retrieved
      if (message && already_retrieved?.length > 0) {
        const alreadyInfo = already_retrieved.map(s => 
          `${s.name} (${s.score !== null ? s.score + '%' : 'N/A'})`
        ).join(', ');
        toast.success(`All students already have BLS certificates retrieved: ${alreadyInfo}`, { duration: 6000 });
        return;
      }
      
      if (response.data.success || certificates_found > 0) {
        // Build detailed success message with score colors
        const matchedInfo = students_matched?.map(s => {
          if (typeof s === 'object') {
            const score = s.score;
            const scoreText = score !== null && score !== undefined ? `${score}%` : 'N/A';
            const passStatus = score !== null && score !== undefined ? (score >= 84 ? '✓' : '✗') : '';
            return `${s.name} ${passStatus}${scoreText}`;
          }
          return s;
        }).join(', ') || 'None';
        
        const notFoundInfo = students_not_found?.length > 0 
          ? ` | Not found: ${students_not_found.join(', ')}` 
          : '';
        
        toast.success(
          `✓ BLS Certificates: Found ${certificates_found}. ` +
          `Students: ${matchedInfo}${notFoundInfo}`,
          { duration: 10000 }
        );
        fetchBookings();
      } else {
        const errorMsg = errors?.join(', ') || 'No certificates found';
        const notFoundInfo = students_not_found?.length > 0 
          ? ` Students not in AHA completed list: ${students_not_found.join(', ')}` 
          : '';
        toast.error(`BLS Certificates: ${errorMsg}${notFoundInfo}`, { duration: 8000 });
      }
    } catch (error) {
      toast.dismiss('fetch-bls-certs');
      const detail = error.response?.data?.detail;
      if (detail?.includes('BLS classes')) {
        toast.error('BLS certificate retrieval is only available for BLS classes');
      } else if (detail?.includes('Enrollware')) {
        toast.error('Please sync to Enrollware first before fetching BLS certificates');
      } else {
        toast.error(detail || 'Failed to fetch BLS certificates');
      }
    }
  };

  // Start BLS Certificate Auto-Polling
  const [blsPollingStatus, setBlsPollingStatus] = useState({});
  
  const startBlsPolling = async (bookingId) => {
    try {
      toast.loading('Starting BLS certificate auto-polling...', { id: 'bls-polling' });
      const response = await axios.post(`${API}/admin/booking/${bookingId}/start-bls-polling`);
      toast.dismiss('bls-polling');
      
      if (response.data.success) {
        const { status, students_total, message } = response.data;
        
        if (status === 'complete') {
          toast.success('All students already have BLS certificates!');
        } else {
          toast.success(message, { duration: 5000 });
          
          // Start polling for status updates
          setBlsPollingStatus(prev => ({
            ...prev,
            [bookingId]: { active: true, startTime: Date.now() }
          }));
          
          // Poll status every 10 seconds
          const pollInterval = setInterval(async () => {
            try {
              const statusRes = await axios.get(`${API}/admin/booking/${bookingId}/bls-polling-status`);
              const data = statusRes.data;
              
              // Update local state
              setBlsPollingStatus(prev => ({
                ...prev,
                [bookingId]: { 
                  ...prev[bookingId],
                  ...data 
                }
              }));
              
              // Refresh bookings to show new scores
              fetchBookings();
              
              // Stop polling if complete
              if (data.status !== 'running') {
                clearInterval(pollInterval);
                setBlsPollingStatus(prev => ({
                  ...prev,
                  [bookingId]: { ...prev[bookingId], active: false }
                }));
                
                if (data.students_pending === 0) {
                  toast.success('All BLS certificates found and uploaded!', { duration: 5000 });
                } else {
                  toast.info(`BLS polling complete. ${data.students_with_certificates} of ${data.students_total} certificates uploaded.`);
                }
              }
            } catch (err) {
              console.error('Polling status error:', err);
            }
          }, 10000);
          
          // Auto-stop after 65 minutes (in case backend times out)
          setTimeout(() => {
            clearInterval(pollInterval);
            setBlsPollingStatus(prev => ({
              ...prev,
              [bookingId]: { ...prev[bookingId], active: false }
            }));
          }, 65 * 60 * 1000);
        }
      }
    } catch (error) {
      toast.dismiss('bls-polling');
      toast.error(error.response?.data?.detail || 'Failed to start BLS polling');
    }
  };
  
  const stopBlsPolling = async (bookingId) => {
    try {
      await axios.post(`${API}/admin/booking/${bookingId}/stop-bls-polling`);
      toast.success('BLS polling stopped. Uploading found certificates...');
      setBlsPollingStatus(prev => ({
        ...prev,
        [bookingId]: { ...prev[bookingId], active: false }
      }));
    } catch (error) {
      toast.error('Failed to stop polling');
    }
  };

  const handleDelete = async (bookingId, hasCalendarEvent = false) => {
    // Find the booking to store for the dialog
    const booking = bookings.find(b => b.id === bookingId);
    if (booking) {
      setDeleteBooking({ ...booking, hasCalendarEvent });
      setShowDeleteDialog(true);
    }
  };

  // Execute hide action
  const executeHide = async () => {
    if (!deleteBooking) return;
    
    try {
      const response = await axios.delete(`${API}/bookings/${deleteBooking.id}?permanent=false`);
      toast.success('Booking hidden. You can find it in the Hidden tab.');
      fetchBookings();
    } catch (error) {
      toast.error('Failed to hide booking');
    } finally {
      setShowDeleteDialog(false);
      setDeleteBooking(null);
    }
  };

  // Execute permanent delete
  const executeDelete = async () => {
    if (!deleteBooking) return;
    
    // If booking has a calendar event, ask if they want to remove it from calendar too
    let removeFromCalendar = false;
    if (deleteBooking.hasCalendarEvent) {
      removeFromCalendar = window.confirm('Do you also want to remove this class from Google Calendar?');
    }
    
    try {
      const response = await axios.delete(`${API}/bookings/${deleteBooking.id}?remove_from_calendar=${removeFromCalendar}&permanent=true`);
      if (response.data.calendar_deleted) {
        toast.success('Booking permanently deleted and removed from calendar');
      } else {
        toast.success('Booking permanently deleted');
      }
      fetchBookings();
    } catch (error) {
      toast.error('Failed to delete booking');
    } finally {
      setShowDeleteDialog(false);
      setDeleteBooking(null);
    }
  };

  // Cancel delete dialog
  const cancelDelete = () => {
    setShowDeleteDialog(false);
    setDeleteBooking(null);
  };

  // Restore a hidden booking
  const handleRestore = async (bookingId, restoreToStatus = 'pending') => {
    try {
      const response = await axios.patch(`${API}/bookings/${bookingId}/restore`, null, {
        params: { restore_to_status: restoreToStatus }
      });
      toast.success(`Booking restored to ${restoreToStatus}`);
      fetchBookings();
    } catch (error) {
      toast.error('Failed to restore booking');
    }
  };

  const handleResendEmail = async (bookingId, template, studentEmail = null) => {
    try {
      toast.loading('Sending email...', { id: 'resend-email' });
      const payload = {
        booking_id: bookingId,
        template: template
      };
      if (studentEmail) {
        payload.student_email = studentEmail;
      }
      const response = await axios.post(`${API}/admin/resend-email`, payload);
      toast.dismiss('resend-email');
      toast.success(response.data.message);
    } catch (error) {
      toast.dismiss('resend-email');
      toast.error(error.response?.data?.detail || 'Failed to send email');
    }
  };

  const handleResendPaymentLink = async (bookingId) => {
    await handleResendEmail(bookingId, 'payment_reminder');
  };

  // Resend eBook for a student
  const handleResendEbook = async (bookingId, studentIndex, studentName) => {
    try {
      toast.loading(`Resending eBook to ${studentName}...`, { id: 'resend-ebook' });
      const response = await axios.post(`${API}/admin/booking/${bookingId}/resend-ebook/${studentIndex}`);
      toast.dismiss('resend-ebook');
      toast.success(response.data.message || 'eBook queued for sending');
      // Refresh bookings to update status
      await fetchBookings();
    } catch (error) {
      toast.dismiss('resend-ebook');
      toast.error(error.response?.data?.detail || 'Failed to resend eBook');
    }
  };

  // Resend digital test for a student
  const handleResendDigitalTest = async (bookingId, studentIndex, studentName) => {
    try {
      toast.loading(`Resending digital test to ${studentName}...`, { id: 'resend-test' });
      const response = await axios.post(`${API}/admin/booking/${bookingId}/resend-digital-test/${studentIndex}`);
      toast.dismiss('resend-test');
      toast.success(response.data.message || 'Digital test queued for sending');
      // Refresh bookings to update status
      await fetchBookings();
    } catch (error) {
      toast.dismiss('resend-test');
      toast.error(error.response?.data?.detail || 'Failed to resend digital test');
    }
  };

  // Toggle sign-in status for a student (admin override)
  const handleToggleSignIn = async (bookingId, studentIndex, student) => {
    const studentName = `${student.first_name} ${student.last_name}`;
    const action = student.signed_in ? 'mark as not signed in' : 'mark as signed in';
    
    if (!window.confirm(`Are you sure you want to ${action} ${studentName}?${!student.signed_in ? '\n\nThis will mark the signature as "Signed in by Admin" and trigger eBook sending.' : ''}`)) {
      return;
    }
    
    try {
      toast.loading(`Updating sign-in status for ${studentName}...`, { id: 'toggle-signin' });
      const response = await axios.post(`${API}/admin/booking/${bookingId}/mark-signed-in/${studentIndex}`);
      toast.dismiss('toggle-signin');
      toast.success(response.data.message || 'Sign-in status updated');
      // Refresh bookings to update status
      await fetchBookings();
    } catch (error) {
      toast.dismiss('toggle-signin');
      toast.error(error.response?.data?.detail || 'Failed to update sign-in status');
    }
  };

  // Toggle AHA verification status for a student (for BLS Renewal classes)
  const handleToggleAhaVerification = async (bookingId, studentIndex, student) => {
    const studentName = `${student.first_name} ${student.last_name}`;
    const currentStatus = student.aha_verified || false;
    
    // Get the AHA name from the eCard data
    const rawCardName = student.aha_ecard?.aha_returned_name || student.aha_ecard?.verification_name || student.aha_ecard?.card_holder_name || student.aha_ecard?.found_name || '';
    const ahaName = rawCardName.replace(/^Verified:\s*/i, '').split(',')[0].trim() || 'Not available';
    
    // Show the verification modal
    setAhaVerifyModal({
      show: true,
      bookingId: bookingId,
      studentIndex: studentIndex,
      student: student,
      enteredName: studentName,
      ahaName: ahaName,
      currentStatus: currentStatus
    });
  };
  
  // Handle AHA verification modal response
  const handleAhaVerifyResponse = async (verified) => {
    const { bookingId, studentIndex, student, enteredName } = ahaVerifyModal;
    
    // Close modal first
    setAhaVerifyModal({ show: false, bookingId: null, studentIndex: null, student: null, enteredName: '', ahaName: '', currentStatus: false });
    
    try {
      toast.loading(`${verified ? 'Verifying' : 'Marking unverified'}...`, { id: 'toggle-aha' });
      
      // Call the API to set the specific status (not toggle)
      const response = await axios.post(`${API}/admin/booking/${bookingId}/set-aha-verification/${studentIndex}`, {
        verified: verified
      });
      
      toast.dismiss('toggle-aha');
      toast.success(verified ? `✅ ${enteredName} marked as VERIFIED` : `❌ ${enteredName} marked as UNVERIFIED`);
      
      // Refresh bookings to update status
      await fetchBookings();
    } catch (error) {
      toast.dismiss('toggle-aha');
      toast.error(error.response?.data?.detail || 'Failed to update AHA verification');
    }
  };

  // Helper to check if class is a BLS Renewal
  const isRenewalClass = (booking) => {
    if (!booking) return false;
    const classTypeName = (booking.class_type_name || '').toLowerCase();
    const classTypeId = (booking.class_type_id || '').toLowerCase();
    return classTypeName.includes('renewal') || classTypeId.includes('renewal');
  };

  // Email log functions
  const openEmailLog = async (booking) => {
    setEmailLogBooking(booking);
    setShowEmailLog(true);
    setEmailLogLoading(true);
    
    try {
      const response = await axios.get(`${API}/admin/bookings/${booking.id}/email-logs`);
      setEmailLogs(response.data.logs || []);
    } catch (error) {
      console.error('Error fetching email logs:', error);
      toast.error('Failed to load email history');
      setEmailLogs([]);
    } finally {
      setEmailLogLoading(false);
    }
  };

  const getEmailTemplateName = (template) => {
    const names = {
      'new_booking': 'Booking Confirmation',
      'booking_approved': 'Booking Approved',
      'booking_rejected': 'Booking Rejected',
      'booking_rejected_no_refund': 'Rejected (No Refund)',
      'booking_rejected_refunded': 'Rejected (Refunded)',
      'payment_confirmed': 'Payment Confirmed',
      'booking_updated': 'Booking Updated',
      'payment_reminder': 'Payment Reminder',
      'payment_request': 'Payment Request',
      'class_reminder': '24hr Class Reminder',
      'class_followup': 'Post-Class Thank You'
    };
    return names[template] || template;
  };

  const openNotesDialog = (booking) => {
    setNotesBookingId(booking.id);
    setNotesText(booking.admin_notes || '');
    setShowNotesDialog(true);
  };

  const saveNotes = async () => {
    if (!notesBookingId) return;
    
    setNotesSaving(true);
    try {
      const response = await axios.patch(`${API}/bookings/${notesBookingId}/notes`, {
        notes: notesText
      });
      
      if (response.data.success) {
        toast.success(response.data.woo_synced 
          ? 'Notes saved and synced to WooCommerce!' 
          : 'Notes saved successfully!');
        setShowNotesDialog(false);
        fetchBookings();
      }
    } catch (error) {
      toast.error('Failed to save notes');
    } finally {
      setNotesSaving(false);
    }
  };

  // Edit Students functions
  const openEditStudents = (booking) => {
    setEditStudentsBooking(booking);
    setEditStudentsList(booking.students || []);
    setNewStudent({ firstName: '', lastName: '', email: '', phone: '', notes: '', paymentStatus: 'paid' });
    setShowEditStudents(true);
  };

  // Digital Test functions (BLS classes only)
  const isBLSClass = (classTypeName) => {
    const lower = (classTypeName || '').toLowerCase();
    return lower.includes('bls') || lower.includes('basic life support');
  };

  const openDigitalTestDialog = async (booking) => {
    setDigitalTestBooking(booking);
    setSelectedTestStudents([]);
    setSendingDigitalTests(false);
    
    // Fetch student eBook/test status
    try {
      const response = await axios.get(`${API}/admin/booking/${booking.id}/ebook-status`);
      setDigitalTestStudents(response.data.students || []);
    } catch (error) {
      console.error('Failed to fetch eBook status:', error);
      // Use booking students as fallback
      setDigitalTestStudents((booking.students || []).map((s, idx) => ({
        student_index: idx,
        first_name: s.first_name || s.firstName,
        last_name: s.last_name || s.lastName,
        email: s.email,
        signed_in: s.signed_in,
        digital_test_status: s.digital_test_status,
        digital_test_sent_at: s.digital_test_sent_at,
        digital_test_error: s.digital_test_error
      })));
    }
    
    setShowDigitalTestDialog(true);
  };

  const toggleTestStudent = (studentIndex) => {
    setSelectedTestStudents(prev => {
      if (prev.includes(studentIndex)) {
        return prev.filter(i => i !== studentIndex);
      } else {
        return [...prev, studentIndex];
      }
    });
  };

  const selectAllSignedInStudents = () => {
    const signedInIndices = digitalTestStudents
      .filter(s => s.signed_in && s.digital_test_status !== 'sent')
      .map(s => s.student_index);
    setSelectedTestStudents(signedInIndices);
  };

  const sendDigitalTests = async () => {
    if (!digitalTestBooking || selectedTestStudents.length === 0) {
      toast.error('Please select at least one student');
      return;
    }
    
    setSendingDigitalTests(true);
    try {
      const response = await axios.post(
        `${API}/admin/booking/${digitalTestBooking.id}/send-digital-tests`,
        { student_indices: selectedTestStudents }
      );
      
      if (response.data.success) {
        toast.success(`Queued ${response.data.queued_count} digital test(s) for sending`);
        setShowDigitalTestDialog(false);
        // Refresh bookings to get updated status
        fetchBookings();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send digital tests');
    } finally {
      setSendingDigitalTests(false);
    }
  };

  const addStudentToBooking = async () => {
    if (!editStudentsBooking) return;
    if (!newStudent.firstName.trim() || !newStudent.lastName.trim() || !newStudent.email.trim() || !newStudent.phone.trim()) {
      toast.error('All student fields are required');
      return;
    }
    
    setStudentSaving(true);
    try {
      const response = await axios.post(`${API}/bookings/${editStudentsBooking.id}/admin/add-student`, {
        first_name: newStudent.firstName.trim(),
        last_name: newStudent.lastName.trim(),
        email: newStudent.email.trim(),
        phone: newStudent.phone.trim(),
        notes: newStudent.notes.trim() || null,
        payment_status: newStudent.paymentStatus
      });
      
      if (response.data.success) {
        toast.success('Student added and calendar updated!');
        setEditStudentsList(response.data.students);
        setNewStudent({ firstName: '', lastName: '', email: '', phone: '', notes: '', paymentStatus: 'paid' });
        fetchBookings();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add student');
    } finally {
      setStudentSaving(false);
    }
  };

  const removeStudentFromBooking = async (studentIndex, studentData = null) => {
    if (!editStudentsBooking) return;
    
    // Get student from the list by index
    const student = studentData || editStudentsList[studentIndex];
    if (!student) {
      toast.error('Student not found');
      return;
    }
    
    const hasEcard = student?.ecard_ordered === true;
    
    const isLastStudent = editStudentsList.length === 1;
    let confirmMsg = isLastStudent 
      ? 'This is the last student. Removing them will delete the booking and remove it from the calendar. Continue?'
      : 'Are you sure you want to remove this student?';
    
    if (!window.confirm(confirmMsg)) return;
    
    // Handle eCard logic
    let returnEcard = false;
    if (hasEcard) {
      // Student has an eCard assigned - ask about returning it
      returnEcard = window.confirm(
        '🎴 Return eCard to Inventory?\n\n' +
        'This student has an eCard assigned.\n\n' +
        'Click OK to return the card to inventory.\n' +
        'Click Cancel to keep the inventory unchanged.'
      );
    } else {
      // No eCard assigned - just show info message
      toast.info('No card assigned yet - proceeding with deletion');
    }
    
    setStudentSaving(true);
    try {
      // Use index instead of email for safe deletion
      const response = await axios.delete(
        `${API}/bookings/${editStudentsBooking.id}/admin/remove-student/${studentIndex}?return_ecard=${returnEcard}`
      );
      
      if (response.data.success) {
        if (response.data.booking_deleted) {
          // Booking was deleted because it was the last student
          let msg = 'Last student removed - booking and calendar deleted';
          if (response.data.ecard_returned) {
            if (response.data.ecard_reassigned_to) {
              msg += ` • eCard reassigned to ${response.data.ecard_reassigned_to}`;
            } else {
              msg += ' • eCard returned to inventory';
            }
          }
          toast.success(msg);
          setShowEditStudents(false);
          setEditStudentsBooking(null);
          setEditStudentsList([]);
        } else if (response.data.last_student_removed) {
          // Last student removed - ask if they want to delete the booking
          let msg = 'Student removed';
          if (response.data.ecard_returned) {
            if (response.data.ecard_reassigned_to) {
              msg += ` • eCard reassigned to ${response.data.ecard_reassigned_to}`;
            } else {
              msg += ' • eCard returned to inventory';
            }
          }
          toast.success(msg);
          setEditStudentsList([]);
          
          // Ask if they want to close/delete the booking
          const shouldDelete = window.confirm(
            'This was the last student. Would you like to delete this booking and remove it from the calendar?\n\nClick OK to delete, or Cancel to keep the booking with 0 students.'
          );
          
          if (shouldDelete) {
            try {
              await axios.delete(`${API}/bookings/${editStudentsBooking.id}?remove_from_calendar=true&permanent=true`);
              toast.success('Booking and calendar event deleted');
              setShowEditStudents(false);
              setEditStudentsBooking(null);
            } catch (delError) {
              toast.error('Failed to delete booking');
            }
          } else {
            toast.info('Booking kept with 0 students');
          }
        } else {
          let msg = 'Student removed and calendar updated!';
          if (response.data.ecard_returned) {
            if (response.data.ecard_reassigned_to) {
              msg += ` • eCard reassigned to ${response.data.ecard_reassigned_to}`;
            } else {
              msg += ' • eCard returned to inventory';
            }
          }
          toast.success(msg);
          setEditStudentsList(response.data.students);
        }
        fetchBookings();
        fetchEcardNeedsSummary(); // Refresh eCard badge
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to remove student');
    } finally {
      setStudentSaving(false);
    }
  };

  // Update existing student in the list (local state) - uses index
  const updateExistingStudent = (index, field, value) => {
    setEditStudentsList(prev => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  // Save updated student to backend
  const saveStudentUpdate = async (student) => {
    if (!editStudentsBooking || editingStudentOriginalEmail === null) return;
    
    setStudentSaving(true);
    try {
      const response = await axios.put(
        `${API}/bookings/${editStudentsBooking.id}/admin/update-student?old_email=${encodeURIComponent(editingStudentOriginalEmail)}`,
        {
          first_name: student.first_name,
          last_name: student.last_name,
          email: student.email,
          phone: student.phone
        }
      );
      
      if (response.data.success) {
        toast.success('Student updated and calendar synced!');
        setEditStudentsList(response.data.students);
        setEditingStudentIndex(null);
        setEditingStudentOriginalEmail(null);
        fetchBookings();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update student');
    } finally {
      setStudentSaving(false);
    }
  };

  // Edit DateTime functions
  const openEditDateTime = (booking) => {
    setEditDateTimeBooking(booking);
    // Parse the existing start time - it's stored in UTC, so convert to Pacific
    const startDateUTC = new Date(booking.start_time);
    // Format in Pacific timezone for display
    setEditDate(formatInTimeZone(startDateUTC, 'America/Los_Angeles', 'yyyy-MM-dd'));
    setEditTime(formatInTimeZone(startDateUTC, 'America/Los_Angeles', 'HH:mm'));
    setShowEditDateTime(true);
  };

  const saveDateTime = async () => {
    console.log('saveDateTime called', { editDateTimeBooking, editDate, editTime });
    if (!editDateTimeBooking || !editDate || !editTime) {
      console.log('saveDateTime early return - missing data');
      return;
    }
    
    setDateTimeSaving(true);
    try {
      console.log('Making API call to update datetime:', {
        url: `${API}/bookings/${editDateTimeBooking.id}/admin/update-datetime`,
        data: { date: editDate, start_time: editTime }
      });
      
      const response = await axios.patch(`${API}/bookings/${editDateTimeBooking.id}/admin/update-datetime`, {
        date: editDate,
        start_time: editTime
      });
      
      console.log('API response:', response.data);
      
      if (response.data.success) {
        // Check for registration actions
        if (response.data.registration_action === 'closed') {
          toast.warning('Date/time updated - Registration was automatically closed (class now within cutoff)', { duration: 5000 });
        } else if (response.data.registration_action === 'reopen_eligible') {
          toast.success('Date/time updated - Class moved outside cutoff. Check the booking to reopen registration.', { duration: 5000 });
        } else {
          toast.success('Date/time updated and calendar synced!');
        }
        setShowEditDateTime(false);
        fetchBookings();
      }
    } catch (error) {
      console.error('saveDateTime error:', error);
      toast.error(error.response?.data?.detail || 'Failed to update date/time');
    } finally {
      setDateTimeSaving(false);
    }
  };

  const syncFromCalendar = async (bookingId) => {
    try {
      const response = await axios.post(`${API}/bookings/${bookingId}/sync-from-calendar`);
      
      if (response.data.success) {
        toast.success('Booking synced from calendar!');
        fetchBookings();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to sync from calendar');
    }
  };

  // Toggle booking public/private status
  const toggleBookingPublic = async (booking) => {
    const newStatus = !booking.is_public;
    const action = newStatus ? 'public' : 'private';
    
    try {
      await axios.put(`${API}/admin/bookings/${booking.id}/toggle-public`);
      toast.success(`Class is now ${action}`);
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to make class ${action}`);
    }
  };

  // Toggle add-on payment waived status
  const toggleAddonPaymentWaived = async (booking) => {
    const newStatus = !booking.addon_payment_waived;
    const action = newStatus ? 'waived (prepaid)' : 'required';
    
    try {
      await axios.put(`${API}/admin/bookings/${booking.id}/addon-payment-waived?waived=${newStatus}`);
      toast.success(`Add-on payment is now ${action}`);
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update add-on payment status');
    }
  };

  // Open Fee Waivers dialog
  const openFeeWaiversDialog = (booking) => {
    setFeeWaiversBooking(booking);
    setShowFeeWaiversDialog(true);
  };

  // Update fee waivers
  const updateFeeWaivers = async (waivers) => {
    if (!feeWaiversBooking) return;
    setFeeWaiversLoading(true);
    try {
      await axios.put(`${API}/admin/bookings/${feeWaiversBooking.id}/fee-waivers`, waivers);
      toast.success('Fee waivers updated successfully');
      fetchBookings();
      setShowFeeWaiversDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update fee waivers');
    } finally {
      setFeeWaiversLoading(false);
    }
  };

  // Toggle registration open/closed status
  const toggleRegistration = async (booking) => {
    const newStatus = !booking.registration_closed;
    const action = newStatus ? 'closed' : 'open';
    
    try {
      await axios.put(`${API}/admin/bookings/${booking.id}/toggle-registration`);
      toast.success(`Registration is now ${action}`);
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || `Failed to ${newStatus ? 'close' : 'open'} registration`);
    }
  };

  // Reopen registration for a booking
  const reopenRegistration = async (bookingId) => {
    try {
      await axios.put(`${API}/admin/bookings/${bookingId}/reopen-registration`);
      toast.success('Registration has been reopened');
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reopen registration');
    }
  };

  // Dismiss reopen eligibility - keep registration closed
  const dismissReopenEligible = async (bookingId) => {
    try {
      await axios.put(`${API}/admin/bookings/${bookingId}/dismiss-reopen-eligible`);
      toast.info('Registration will remain closed');
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to dismiss');
    }
  };

  // Open link invoice dialog
  const openLinkInvoiceDialog = async (booking) => {
    setLinkInvoiceBooking(booking);
    setLinkInvoiceSearch('');
    setShowLinkInvoiceDialog(true);
    
    // Fetch available invoices
    try {
      setLinkInvoiceLoading(true);
      const response = await axios.get(`${API}/invoices`);
      setAvailableInvoices(response.data.invoices || []);
    } catch (error) {
      toast.error('Failed to load invoices');
    } finally {
      setLinkInvoiceLoading(false);
    }
  };

  // Link invoice to booking
  const linkInvoiceToBooking = async (invoiceId) => {
    if (!linkInvoiceBooking) return;
    try {
      await axios.put(`${API}/admin/bookings/${linkInvoiceBooking.id}/link-invoice`, {
        invoice_id: invoiceId
      });
      toast.success(invoiceId ? 'Invoice linked to class' : 'Invoice unlinked from class');
      setShowLinkInvoiceDialog(false);
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to link invoice');
    }
  };

  // Open link recurring client dialog
  const openLinkRecurringClientDialog = async (booking) => {
    setLinkingClientToBooking(booking);
    setLinkRecurringClientSearch('');
    setLinkRecurringClientNotes('');
    setShowLinkRecurringClientDialog(true);
    
    // Fetch recurring clients
    try {
      const response = await axios.get(`${API}/admin/recurring-clients/search?q=`);
      setLinkRecurringClientResults(response.data || []);
    } catch (error) {
      toast.error('Failed to load recurring clients');
    }
  };

  // Search recurring clients for linking
  const searchRecurringClientsForLink = async (query) => {
    setLinkRecurringClientSearch(query);
    try {
      const response = await axios.get(`${API}/admin/recurring-clients/search?q=${encodeURIComponent(query)}`);
      setLinkRecurringClientResults(response.data || []);
    } catch (error) {
      console.error('Error searching clients:', error);
    }
  };

  // Link recurring client to booking (with duplicate click protection)
  const [linkClientCooldown, setLinkClientCooldown] = useState(false);
  
  const handleLinkRecurringClient = async (clientId) => {
    if (!linkingClientToBooking) return;
    
    // Prevent multiple clicks
    if (linkingClient || linkClientCooldown) {
      toast.warning('Please wait - request already in progress. Multiple clicks may send duplicate emails.');
      return;
    }
    
    setLinkingClient(true);
    setLinkClientCooldown(true);
    
    try {
      await axios.post(`${API}/admin/bookings/${linkingClientToBooking.id}/link-recurring-client`, {
        recurring_client_id: clientId,
        notes: linkRecurringClientNotes.trim() || null
      });
      toast.success('Recurring client linked to booking');
      setShowLinkRecurringClientDialog(false);
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to link client');
    } finally {
      setLinkingClient(false);
      // Keep cooldown for 5 seconds to prevent rapid re-clicking
      setTimeout(() => setLinkClientCooldown(false), 5000);
    }
  };

  // Unlink recurring client from booking
  const handleUnlinkRecurringClient = async (bookingId) => {
    if (!window.confirm('Remove recurring client link from this booking?')) return;
    try {
      await axios.delete(`${API}/admin/bookings/${bookingId}/unlink-recurring-client`);
      toast.success('Recurring client unlinked');
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unlink client');
    }
  };

  // Open recurring client details dialog
  const openRecurringClientDetails = async (clientId) => {
    setLoadingRecurringClient(true);
    setShowViewRecurringClient(true);
    try {
      const response = await axios.get(`${API}/admin/recurring-clients/${clientId}`);
      setViewingRecurringClient(response.data);
    } catch (error) {
      toast.error('Failed to load client details');
      setShowViewRecurringClient(false);
    } finally {
      setLoadingRecurringClient(false);
    }
  };

  // View linked invoice - open invoice system with that invoice selected
  const viewLinkedInvoice = (invoiceId) => {
    setSelectedInvoiceId(invoiceId);
    setShowInvoices(true);
  };

  // Update registration close hours for a booking
  const [editingCloseHours, setEditingCloseHours] = useState(null);
  const [newCloseHours, setNewCloseHours] = useState(24);
  
  const updateRegistrationCloseHours = async (bookingId) => {
    try {
      const response = await axios.put(`${API}/admin/bookings/${bookingId}/registration-close-hours?hours=${newCloseHours}`);
      
      // Check if registration was immediately closed
      if (response.data.registration_closed) {
        toast.success(`Registration closed immediately - class starts within ${newCloseHours} hours`, { duration: 5000 });
      } else {
        toast.success(`Registration will auto-close ${newCloseHours} hours before class`);
      }
      
      setEditingCloseHours(null);
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update close hours');
    }
  };

  // Generate AHA Paperwork for a booking
  const [paperworkMappedClasses, setPaperworkMappedClasses] = useState([]);
  const [generatingPaperwork, setGeneratingPaperwork] = useState(null);
  const [sendingPaperworkEmail, setSendingPaperworkEmail] = useState(null);
  
  // Fetch paperwork mapping on component mount
  useEffect(() => {
    const fetchPaperworkMapping = async () => {
      try {
        const response = await axios.get(`${API}/paperwork-mapping`);
        setPaperworkMappedClasses(response.data.supported_class_types || []);
      } catch (error) {
        console.error('Could not fetch paperwork mapping:', error);
      }
    };
    fetchPaperworkMapping();
  }, []);
  
  const generatePaperwork = async (booking, exportType = 'combined') => {
    try {
      setGeneratingPaperwork(booking.id);
      
      // Download the file
      const response = await axios.get(`${API}/generate-paperwork/${booking.id}?export_type=${exportType}`, {
        responseType: 'blob'
      });
      
      // Extract filename from content-disposition header or generate one
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'AHA_Paperwork.pdf';
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) {
          filename = match[1].replace(/"/g, '');
        }
      }
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('AHA Paperwork downloaded successfully');
    } catch (error) {
      console.error('Error generating paperwork:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate AHA paperwork');
    } finally {
      setGeneratingPaperwork(null);
    }
  };

  // Send Paperwork Email manually (sends AHA paperwork to all admins)
  const sendPaperworkEmail = async (bookingId) => {
    try {
      setSendingPaperworkEmail(bookingId);
      
      const response = await axios.post(`${API}/admin/bookings/${bookingId}/send-paperwork-email`);
      
      if (response.data.success) {
        toast.success('Paperwork email sent to all admins');
      } else {
        toast.error(response.data.message || 'Failed to send email');
      }
    } catch (error) {
      console.error('Error sending paperwork email:', error);
      toast.error(error.response?.data?.detail || 'Failed to send paperwork email');
    } finally {
      setSendingPaperworkEmail(null);
    }
  };

  // Send Class Roster Email manually
  const [sendingRosterEmail, setSendingRosterEmail] = useState(null);
  
  const sendClassRosterEmail = async (booking) => {
    try {
      setSendingRosterEmail(booking.id);
      
      const response = await axios.post(`${API}/send-class-roster-email/${booking.id}`);
      
      if (response.data.success) {
        toast.success('Class roster email sent successfully');
      } else {
        toast.error(response.data.message || 'Failed to send email');
      }
    } catch (error) {
      console.error('Error sending class roster email:', error);
      toast.error(error.response?.data?.detail || 'Failed to send class roster email');
    } finally {
      setSendingRosterEmail(null);
    }
  };

  // Update max students for a booking
  const updateMaxStudents = async (bookingId) => {
    try {
      await axios.put(`${API}/admin/bookings/${bookingId}/max-students?max_students=${newMaxStudents}`);
      toast.success(`Max students updated to ${newMaxStudents}`);
      setEditingMaxStudents(null);
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update max students');
    }
  };
  
  // Update location name for a public class
  const updateLocationName = async (bookingId) => {
    try {
      await axios.put(`${API}/admin/bookings/${bookingId}/location-name?location_name=${encodeURIComponent(newLocationName)}`);
      toast.success('Location name updated');
      setEditingLocationName(null);
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update location name');
    }
  };
  
  // Update reference code for a booking
  const updateReferenceCode = async (bookingId) => {
    try {
      const code = newReferenceCode.trim().toLowerCase().replace(/\s/g, '');
      await axios.put(`${API}/admin/bookings/${bookingId}/reference-code?reference_code=${encodeURIComponent(code || '')}`);
      toast.success('Booking code updated');
      setEditingReferenceCode(null);
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update booking code');
    }
  };

  // Update address for a booking
  const updateBookingAddress = async (bookingId) => {
    try {
      await axios.put(`${API}/admin/bookings/${bookingId}/address`, addressForm);
      toast.success('Address updated');
      setEditingAddress(null);
      setAddressForm({ location_name: '', street: '', city: '', state: '', zip: '' });
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update address');
    }
  };

  // Start editing address for a booking
  const startEditingAddress = async (booking) => {
    setAddressForm({
      location_name: booking.location_name || '',
      street: booking.class_address?.street || '',
      city: booking.class_address?.city || '',
      state: booking.class_address?.state || '',
      zip: booking.class_address?.zip || ''
    });
    setEditingAddress(booking.id);
    
    // Fetch recurring client locations if attached
    if (booking.recurring_client_id) {
      try {
        const response = await axios.get(`${API}/admin/recurring-clients/${booking.recurring_client_id}`);
        setRecurringClientLocations(response.data.locations || []);
      } catch (error) {
        console.error('Failed to fetch recurring client locations:', error);
        setRecurringClientLocations([]);
      }
    } else {
      setRecurringClientLocations([]);
    }
  };

  // Select a recurring client location to fill the address form
  const selectRecurringClientLocation = (location) => {
    const addr = location.address || {};
    setAddressForm({
      location_name: location.name || '',
      street: addr.street || '',
      city: addr.city || '',
      state: addr.state || '',
      zip: addr.zip || ''
    });
    toast.success(`Location "${location.name}" selected`);
  };

  // Toggle student payment status for public classes
  const toggleStudentPaymentStatus = async (bookingId, studentIndex, currentStatus) => {
    const newStatus = currentStatus === 'paid' ? 'unpaid' : 'paid';
    try {
      await axios.patch(`${API}/admin/bookings/${bookingId}/student/${studentIndex}/payment-status`, {
        payment_status: newStatus
      });
      toast.success(`Student marked as ${newStatus}`);
      fetchBookings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update student payment status');
    }
  };

  // Quick move all students from a booking to queue
  const moveBookingToQueue = async (booking) => {
    if (!window.confirm(`Move all ${booking.students?.length || 0} student(s) from this booking to the pending queue?`)) {
      return;
    }
    
    try {
      const response = await axios.post(`${API}/admin/pending-queue/from-booking`, {
        booking_id: booking.id,
        student_emails: null // null means all students
      });
      
      toast.success(`Moved ${booking.students?.length || 0} student(s) to pending queue`);
      
      // If all students were moved, ask if they want to delete the booking
      if (response.data.all_students_moved) {
        const shouldDelete = window.confirm(
          'All students have been moved to the queue. Would you like to delete this booking and remove it from the calendar?\n\nClick OK to delete, or Cancel to keep the booking.'
        );
        
        if (shouldDelete) {
          try {
            await axios.delete(`${API}/bookings/${booking.id}?remove_from_calendar=true&permanent=true`);
            toast.success('Booking and calendar event deleted');
          } catch (delError) {
            toast.error('Failed to delete booking');
          }
        } else {
          toast.info('Booking kept (status changed to "In Queue")');
        }
      }
      
      fetchBookings();
      setShowPendingQueue(true); // Open the queue sidebar
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to move to queue');
    }
  };

  // Move a single student from a booking to queue
  const moveStudentToQueue = async (booking, studentEmail) => {
    const student = booking.students?.find(s => s.email === studentEmail);
    if (!student) return;
    
    const isLastStudent = booking.students?.length === 1;
    const confirmMessage = `Move ${student.first_name} ${student.last_name} to the pending queue?`;
    
    if (!window.confirm(confirmMessage)) {
      return;
    }
    
    try {
      const response = await axios.post(`${API}/admin/pending-queue/from-booking`, {
        booking_id: booking.id,
        student_emails: [studentEmail] // Just this one student
      });
      
      toast.success(`Moved ${student.first_name} ${student.last_name} to pending queue`);
      
      // If this was the last student, ask if they want to delete the booking
      if (response.data.all_students_moved || isLastStudent) {
        const shouldDelete = window.confirm(
          'This was the last student. Would you like to delete this booking and remove it from the calendar?\n\nClick OK to delete, or Cancel to keep the booking.'
        );
        
        if (shouldDelete) {
          try {
            await axios.delete(`${API}/bookings/${booking.id}?remove_from_calendar=true&permanent=true`);
            toast.success('Booking and calendar event deleted');
          } catch (delError) {
            toast.error('Failed to delete booking');
          }
        } else {
          toast.info('Booking kept (status changed to "In Queue")');
        }
        
        setShowEditStudents(false);
        setEditStudentsBooking(null);
        setEditStudentsList([]);
      } else if (editStudentsBooking && editStudentsBooking.id === booking.id) {
        // Refresh the edit students list if dialog is open
        try {
          const updatedBooking = await axios.get(`${API}/bookings/${booking.id}`);
          setEditStudentsBooking(updatedBooking.data);
          setEditStudentsList(updatedBooking.data.students || []);
        } catch (e) {
          // Booking might have been deleted
          setShowEditStudents(false);
          setEditStudentsBooking(null);
          setEditStudentsList([]);
        }
      }
      
      fetchBookings();
      setShowPendingQueue(true); // Open the queue sidebar
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to move to queue');
    }
  };

  // Class Types Management Functions
  const fetchAdminClassTypes = async () => {
    setClassTypesLoading(true);
    try {
      const response = await axios.get(`${API}/admin/class-types`);
      setAdminClassTypes(response.data);
    } catch (error) {
      toast.error('Failed to fetch class types');
    } finally {
      setClassTypesLoading(false);
    }
  };

  const fetchCourseCategories = async () => {
    try {
      const response = await axios.get(`${API}/course-categories`);
      setCourseCategories(response.data);
    } catch (error) {
      console.error('Failed to fetch course categories:', error);
    }
  };

  const fetchPaperworkTemplateOptions = async () => {
    try {
      const response = await axios.get(`${API}/admin/paperwork-template-options`);
      setPaperworkTemplateOptions(response.data);
    } catch (error) {
      console.error('Failed to fetch paperwork template options:', error);
    }
  };

  const fetchEbookProductOptions = async () => {
    try {
      const response = await axios.get(`${API}/admin/ebook-product-options`);
      setEbookProductOptions(response.data);
    } catch (error) {
      console.error('Failed to fetch eBook product options:', error);
    }
  };

  const openClassTypesDialog = () => {
    fetchAdminClassTypes();
    fetchEcardLinks(); // Load eCard links for the dropdown
    fetchCourseCategories(); // Load course categories
    fetchPaperworkTemplateOptions(); // Load paperwork template options
    fetchEbookProductOptions(); // Load eBook product options
    setShowClassTypes(true);
    setEditingClassType(null);
    resetClassTypeForm();
    // Reset scroll position when opening dialog fresh
    classTypesScrollPositionRef.current = 0;
  };

  const resetClassTypeForm = () => {
    // Calculate next sort order as max existing + 1
    const maxSortOrder = adminClassTypes.length > 0 
      ? Math.max(...adminClassTypes.map(ct => ct.sort_order || 0)) 
      : 0;
    
    setClassTypeForm({
      id: '',
      name: '',
      duration_hours: 4,
      sku: '',
      description: '',
      is_active: true,
      sort_order: maxSortOrder + 1,
      expiration_months: '',
      ecard_product_number: '',
      ecard_product_url: '',
      ecard_min_order: 1,
      course_category: '',
      paperwork_template: '',
      ebook_product_id: '',
      base_price: '',
      additional_student_price: ''
    });
    setShowAddClassForm(false);
  };

  // Category Management Functions
  const openCategoryManager = async () => {
    await fetchCourseCategories();
    setShowCategoryManager(true);
    resetCategoryForm();
  };

  const resetCategoryForm = () => {
    // Set sort_order to the next number after the highest existing one
    const maxOrder = courseCategories.length > 0 
      ? Math.max(...courseCategories.map(c => c.sort_order || 0)) + 1 
      : 0;
    setCategoryFormData({ name: '', slug: '', sort_order: maxOrder });
    setEditingCategory(null);
  };

  const startEditCategory = (category) => {
    setEditingCategory(category.id);
    setCategoryFormData({
      name: category.name,
      slug: category.slug,
      sort_order: category.sort_order || 0
    });
  };

  const saveCategory = async () => {
    if (!categoryFormData.name.trim()) {
      toast.error('Category name is required');
      return;
    }
    
    const slug = categoryFormData.slug.trim() || categoryFormData.name.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    
    setCategorySaving(true);
    try {
      if (editingCategory) {
        await axios.put(`${API}/admin/course-categories/${editingCategory}`, {
          name: categoryFormData.name.trim(),
          slug: slug,
          sort_order: parseInt(categoryFormData.sort_order) || 0
        });
        toast.success('Category updated!');
      } else {
        await axios.post(`${API}/admin/course-categories`, {
          name: categoryFormData.name.trim(),
          slug: slug,
          sort_order: parseInt(categoryFormData.sort_order) || courseCategories.length
        });
        toast.success('Category created!');
      }
      await fetchCourseCategories();
      resetCategoryForm();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save category');
    } finally {
      setCategorySaving(false);
    }
  };

  const deleteCategory = async (categoryId) => {
    if (!confirm('Are you sure you want to delete this category? Classes using it will become uncategorized.')) {
      return;
    }
    try {
      await axios.delete(`${API}/admin/course-categories/${categoryId}`);
      toast.success('Category deleted');
      await fetchCourseCategories();
    } catch (error) {
      toast.error('Failed to delete category');
    }
  };

  // Drag-and-drop sensors for category reordering
  const categorySensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag end for category reordering
  const handleCategoryDragEnd = async (event) => {
    const { active, over } = event;
    
    if (active.id !== over?.id) {
      const oldIndex = courseCategories.findIndex(c => c.id === active.id);
      const newIndex = courseCategories.findIndex(c => c.id === over.id);
      
      const newOrder = arrayMove(courseCategories, oldIndex, newIndex);
      setCourseCategories(newOrder);
      
      // Save new order to backend
      try {
        await axios.put(`${API}/admin/course-categories/reorder`, { order: newOrder.map(c => c.id) });
        toast.success('Category order saved');
      } catch (error) {
        toast.error('Failed to save order');
        // Revert on error
        await fetchCourseCategories();
      }
    }
  };

  const startEditClassType = (classType) => {
    // Save scroll position of the list before switching to edit form
    if (classTypesListRef.current) {
      classTypesScrollPositionRef.current = classTypesListRef.current.scrollTop;
    }
    setEditingClassType(classType.id);
    setShowAddClassForm(true);
    setClassTypeForm({
      id: classType.id,
      name: classType.name,
      duration_hours: classType.duration_hours,
      sku: classType.sku || '',
      description: classType.description || '',
      is_active: classType.is_active !== false,
      sort_order: classType.sort_order || 0,
      expiration_months: classType.expiration_months || '',
      ecard_product_number: classType.ecard_product_number || '',
      ecard_product_url: classType.ecard_product_url || '',
      ecard_min_order: classType.ecard_min_order || 1,
      course_category: classType.course_category || '',
      paperwork_template: classType.paperwork_template || '',
      ebook_product_id: classType.ebook_product_id || '',
      base_price: classType.base_price || '',
      additional_student_price: classType.additional_student_price || ''
    });
  };

  const cancelEditClassType = () => {
    setEditingClassType(null);
    resetClassTypeForm();
    // Restore scroll position when going back to list
    setTimeout(() => {
      if (classTypesListRef.current) {
        classTypesListRef.current.scrollTop = classTypesScrollPositionRef.current;
      }
    }, 100);
  };

  const saveClassType = async () => {
    if (!classTypeForm.name.trim()) {
      toast.error('Class name is required');
      return;
    }
    if (!classTypeForm.sku.trim()) {
      toast.error('SKU is required');
      return;
    }

    setClassTypeSaving(true);
    try {
      if (editingClassType) {
        // Update existing
        await axios.put(`${API}/admin/class-types/${editingClassType}`, {
          name: classTypeForm.name.trim(),
          duration_hours: parseFloat(classTypeForm.duration_hours) || 4,
          sku: classTypeForm.sku.trim(),
          description: classTypeForm.description.trim() || null,
          is_active: classTypeForm.is_active,
          sort_order: parseInt(classTypeForm.sort_order) || 0,
          expiration_months: classTypeForm.expiration_months ? parseInt(classTypeForm.expiration_months) : null,
          ecard_product_number: classTypeForm.ecard_product_number?.trim() || null,
          ecard_product_url: classTypeForm.ecard_product_url?.trim() || null,
          ecard_min_order: parseInt(classTypeForm.ecard_min_order) || 1,
          course_category: classTypeForm.course_category?.trim() || null,
          paperwork_template: classTypeForm.paperwork_template || null,
          ebook_product_id: classTypeForm.ebook_product_id || null,
          base_price: classTypeForm.base_price ? parseFloat(classTypeForm.base_price) : null,
          additional_student_price: classTypeForm.additional_student_price ? parseFloat(classTypeForm.additional_student_price) : null
        });
        toast.success('Class type updated!');
      } else {
        // Create new
        const newId = classTypeForm.id.trim() || classTypeForm.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '');
        await axios.post(`${API}/admin/class-types`, {
          id: newId,
          name: classTypeForm.name.trim(),
          duration_hours: parseFloat(classTypeForm.duration_hours) || 4,
          sku: classTypeForm.sku.trim(),
          description: classTypeForm.description.trim() || null,
          is_active: classTypeForm.is_active,
          sort_order: parseInt(classTypeForm.sort_order) || adminClassTypes.length,
          expiration_months: classTypeForm.expiration_months ? parseInt(classTypeForm.expiration_months) : null,
          ecard_product_number: classTypeForm.ecard_product_number?.trim() || null,
          ecard_product_url: classTypeForm.ecard_product_url?.trim() || null,
          ecard_min_order: parseInt(classTypeForm.ecard_min_order) || 1,
          course_category: classTypeForm.course_category?.trim() || null,
          paperwork_template: classTypeForm.paperwork_template || null,
          ebook_product_id: classTypeForm.ebook_product_id || null,
          base_price: classTypeForm.base_price ? parseFloat(classTypeForm.base_price) : null,
          additional_student_price: classTypeForm.additional_student_price ? parseFloat(classTypeForm.additional_student_price) : null
        });
        toast.success('Class type created!');
      }
      
      // Store the scroll position we want to restore (saved when editing started)
      const savedScrollPosition = classTypesScrollPositionRef.current;
      
      await fetchAdminClassTypes();
      
      // Reset form state to show the list again
      setEditingClassType(null);
      setShowAddClassForm(false);
      resetClassTypeForm();
      
      // Restore scroll position after list re-renders
      setTimeout(() => {
        if (classTypesListRef.current) {
          classTypesListRef.current.scrollTop = savedScrollPosition;
        }
      }, 150);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save class type');
    } finally {
      setClassTypeSaving(false);
    }
  };

  const deleteClassType = async (classTypeId) => {
    if (!window.confirm('Are you sure you want to delete this class type? This cannot be undone.')) return;

    try {
      // Always attempt permanent deletion - backend will reject if bookings exist
      await axios.delete(`${API}/admin/class-types/${classTypeId}?permanent=true`);
      toast.success('Class type deleted!');
      fetchAdminClassTypes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete class type');
    }
  };

  const toggleClassTypeActive = async (classType) => {
    try {
      await axios.put(`${API}/admin/class-types/${classType.id}`, {
        is_active: !classType.is_active
      });
      toast.success(`Class type ${classType.is_active ? 'deactivated' : 'activated'}!`);
      fetchAdminClassTypes();
    } catch (error) {
      toast.error('Failed to update class type');
    }
  };

  // ========== eCard Ordering Functions ==========
  
  const [allEcardInventory, setAllEcardInventory] = useState([]); // All class types with inventory
  
  const fetchEcardData = async () => {
    setEcardLoading(true);
    setEcardCartResult(null);
    setSupplierLaunched(false);
    try {
      const [needsRes, settingsRes, pricesRes, inventoryRes, allInventoryRes, cachedPricesRes] = await Promise.all([
        axios.get(`${API}/admin/ecard-needs`),
        axios.get(`${API}/admin/ecard-supplier/settings`),
        axios.get(`${API}/admin/ecard-product-prices`),
        axios.get(`${API}/admin/ecard-inventory`),
        axios.get(`${API}/admin/ecard-all-inventory`),
        axios.get(`${API}/admin/supplier-price-cache`)
      ]);
      
      // Filter out products with "NONE" in the URL (case-insensitive)
      const filteredNeeds = needsRes.data.filter(item => 
        item.product_url && !item.product_url.toUpperCase().includes('NONE')
      );
      
      setEcardNeeds(filteredNeeds);
      setEcardSupplierSettings(settingsRes.data);
      setAllEcardInventory(allInventoryRes.data);
      setCachedPrices(cachedPricesRes.data || { by_url: {}, by_product_number: {}, last_sync: null });
      
      // Build prices map by product_url
      const pricesMap = {};
      pricesRes.data.forEach(item => {
        if (item.product_url && !item.product_url.toUpperCase().includes('NONE')) {
          pricesMap[item.product_url] = item.unit_price || 0;
        }
      });
      setEcardPrices(pricesMap);
      
      // Build inventory map by product_url
      const inventoryMap = {};
      inventoryRes.data.forEach(item => {
        const key = item.product_url || item.class_type_id;
        if (key && !key.toUpperCase().includes('NONE')) {
          inventoryMap[key] = item.available || 0;
        }
      });
      // Also include inventory from needs response (now keyed by product_url)
      filteredNeeds.forEach(item => {
        if (item.raw_inventory > 0) {
          inventoryMap[item.product_url] = item.raw_inventory;
        }
      });
      setEcardInventory(inventoryMap);
      
      // Initialize order quantities by product_url
      // Order = max(cards_needed, min_order) for products with needs
      const quantities = {};
      filteredNeeds.forEach(item => {
        if (item.cards_needed > 0) {
          // Fill with needed amount, respecting minimum order
          quantities[item.product_url] = Math.max(item.cards_needed, item.min_order || 1);
        } else {
          quantities[item.product_url] = 0;
        }
      });
      setEcardOrderQuantities(quantities);
    } catch (error) {
      toast.error('Failed to fetch eCard data');
    } finally {
      setEcardLoading(false);
    }
  };

  const openEcardOrdering = () => {
    setShowEcardOrdering(true);
    setShowEcardSettings(false);
    setEditingInventory(null);
    setShowStudentsNeedingEcards(false);
    setShowStudentsWithEcards(false);
    setEcardCartResult(null);
    setCheckoutResult(null);
    setCompletingCheckout(false);
    setSupplierLaunched(false);
    fetchEcardData();
    fetchStudentsNeedingEcards();
    fetchStudentsWithEcards();
    fetchEcardLinks();
    fetchLocalEcardInventory();
    // Auto-fetch AHA inventory when opening Order eCards
    fetchAHAInventoryBackground();
  };

  // Fetch just the CPR Supplier settings (for settings dialog)
  const fetchEcardSupplierSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/ecard-supplier/settings`);
      setEcardSupplierSettings(response.data);
      setEcardSettingsForm(prev => ({
        ...prev,
        username: response.data.username || '',
        password: '' // Don't pre-fill password
      }));
    } catch (error) {
      console.error('Failed to fetch supplier settings:', error);
    }
  };

  const saveEcardSupplierSettings = async () => {
    if (!ecardSettingsForm.username || !ecardSettingsForm.password) {
      toast.error('Please enter both username and password');
      return;
    }
    
    try {
      toast.loading('Saving credentials...', { id: 'save-creds' });
      console.log('Saving credentials:', ecardSettingsForm.username);
      
      const response = await axios.post(`${API}/admin/ecard-supplier/settings`, {
        username: ecardSettingsForm.username,
        password: ecardSettingsForm.password,
        site_url: 'https://cprsuppliers.com'
      });
      
      console.log('Save response:', response.data);
      toast.dismiss('save-creds');
      toast.success(`Credentials saved for ${response.data.saved_username}`);
      
      // Refresh settings from server to confirm save
      const settingsRes = await axios.get(`${API}/admin/ecard-supplier/settings`);
      console.log('Fetched settings after save:', settingsRes.data);
      setEcardSupplierSettings(settingsRes.data);
      
      // Update form with saved values
      setEcardSettingsForm({ 
        ...ecardSettingsForm, 
        username: settingsRes.data.username || '', 
        password: '' // Clear password field for security
      });
    } catch (error) {
      toast.dismiss('save-creds');
      toast.error('Failed to save settings: ' + (error.response?.data?.detail || error.message));
      console.error('Save settings error:', error);
    }
  };

  const savePaymentCard = async () => {
    try {
      await axios.post(`${API}/admin/ecard-supplier/payment-card`, {
        card_number: cardSettingsForm.card_number.replace(/\s/g, ''),
        card_expiry: cardSettingsForm.card_expiry,
        card_cvv: cardSettingsForm.card_cvv,
        tax_exempt: cardSettingsForm.tax_exempt
      });
      toast.success('Payment card saved securely');
      setShowCardSettings(false);
      setCardSettingsForm({ card_number: '', card_expiry: '', card_cvv: '', tax_exempt: true });
      // Refresh settings to show card is saved
      const settingsRes = await axios.get(`${API}/admin/ecard-supplier/settings`);
      setEcardSupplierSettings(settingsRes.data);
    } catch (error) {
      toast.error('Failed to save payment card');
    }
  };

  const deletePaymentCard = async () => {
    if (!window.confirm('Remove saved payment card?')) return;
    try {
      await axios.delete(`${API}/admin/ecard-supplier/payment-card`);
      toast.success('Payment card removed');
      const settingsRes = await axios.get(`${API}/admin/ecard-supplier/settings`);
      setEcardSupplierSettings(settingsRes.data);
    } catch (error) {
      toast.error('Failed to remove card');
    }
  };

  // Enrollware Settings Functions
  const fetchEnrollwareSettings = async () => {
    try {
      const response = await axios.get(`${API}/admin/enrollware/settings`);
      setEnrollwareSettings(response.data);
      setEnrollwareSettingsForm({
        username: response.data.username || '',
        password: '' // Don't pre-fill password
      });
    } catch (error) {
      console.error('Failed to fetch Enrollware settings:', error);
    }
  };

  const saveEnrollwareSettings = async () => {
    if (!enrollwareSettingsForm.username || !enrollwareSettingsForm.password) {
      toast.error('Please enter both username and password');
      return;
    }
    
    try {
      setEnrollwareLoading(true);
      toast.loading('Saving Enrollware credentials...', { id: 'save-enrollware' });
      
      await axios.post(`${API}/admin/enrollware/settings`, {
        username: enrollwareSettingsForm.username,
        password: enrollwareSettingsForm.password
      });
      
      toast.dismiss('save-enrollware');
      toast.success('Enrollware credentials saved');
      
      // Refresh settings
      const settingsRes = await axios.get(`${API}/admin/enrollware/settings`);
      setEnrollwareSettings(settingsRes.data);
      setEnrollwareSettingsForm({ username: settingsRes.data.username || '', password: '' });
      setEnrollwareTestResult(null);
    } catch (error) {
      toast.dismiss('save-enrollware');
      toast.error('Failed to save: ' + (error.response?.data?.detail || error.message));
    } finally {
      setEnrollwareLoading(false);
    }
  };

  const testEnrollwareConnection = async () => {
    try {
      setEnrollwareLoading(true);
      setEnrollwareTestResult(null);
      toast.loading('Testing Enrollware connection...', { id: 'test-enrollware' });
      
      const response = await axios.post(`${API}/admin/enrollware/test-connection`);
      
      toast.dismiss('test-enrollware');
      setEnrollwareTestResult(response.data);
      
      if (response.data.success) {
        toast.success('Successfully connected to Enrollware!');
      } else {
        toast.error(response.data.error || 'Connection failed');
      }
    } catch (error) {
      toast.dismiss('test-enrollware');
      setEnrollwareTestResult({ success: false, error: error.response?.data?.detail || error.message });
      toast.error('Connection test failed');
    } finally {
      setEnrollwareLoading(false);
    }
  };

  const [testingLogin, setTestingLogin] = useState(false);
  const [testLoginResult, setTestLoginResult] = useState(null);
  const [clearingCart, setClearingCart] = useState(false);
  
  const toggleEcardTestMode = async (enable) => {
    try {
      await axios.post(`${API}/admin/ecard-supplier/toggle-test-mode?enable=${enable}`);
      setEcardSupplierSettings({ ...ecardSupplierSettings, checkout_test_mode: enable });
      toast.success(enable ? 'Test mode enabled - orders will be simulated' : 'Live mode enabled');
    } catch (error) {
      toast.error('Failed to toggle test mode');
    }
  };

  const toggleAutoOrderMode = async (mode) => {
    try {
      await axios.post(`${API}/admin/ecard-supplier/auto-order-mode`, { mode });
      setEcardSupplierSettings({ ...ecardSupplierSettings, auto_order_mode: mode });
      toast.success(mode === 'auto' 
        ? 'Auto mode enabled - eCards will be auto-ordered when inventory is low' 
        : 'Manual mode enabled - eCards will be added to needed list for manual ordering');
    } catch (error) {
      toast.error('Failed to change auto-order mode');
    }
  };
  
  const testSupplierLogin = async () => {
    setTestingLogin(true);
    setTestLoginResult(null);
    try {
      toast.loading('Testing supplier login...', { id: 'test-login' });
      const response = await axios.post(`${API}/admin/ecard-supplier/test-login`, {}, { timeout: 90000 });
      toast.dismiss('test-login');
      
      if (response.data.success) {
        toast.success('Login test successful! Credentials are working.');
        setTestLoginResult({ success: true, message: 'Login successful' });
      } else {
        const errorMsg = response.data.error || 'Unknown error';
        const steps = response.data.results?.steps || [];
        toast.error(`Login test failed: ${errorMsg}`);
        setTestLoginResult({ 
          success: false, 
          error: errorMsg, 
          steps: steps,
          details: response.data.results 
        });
        console.error('Login test details:', response.data);
      }
    } catch (error) {
      toast.dismiss('test-login');
      const errorMsg = error.response?.data?.detail || error.response?.data?.error || error.message || 'Connection failed';
      toast.error(`Login test failed: ${errorMsg}`);
      setTestLoginResult({ 
        success: false, 
        error: errorMsg,
        timeout: error.code === 'ECONNABORTED' || error.message?.includes('timeout')
      });
      console.error('Login test error:', error);
    } finally {
      setTestingLogin(false);
    }
  };

  const runPlaywrightDiagnostics = async () => {
    setTestingLogin(true);
    setTestLoginResult(null);
    try {
      toast.loading('Running Playwright diagnostics...', { id: 'diagnose' });
      const response = await axios.get(`${API}/admin/ecard-supplier/diagnose`, { timeout: 90000 });
      toast.dismiss('diagnose');
      
      const checks = response.data.diagnostics?.checks || [];
      const failedChecks = checks.filter(c => c.status === 'failed');
      
      if (response.data.success) {
        toast.success('All Playwright checks passed!');
        setTestLoginResult({ 
          success: true, 
          message: 'Diagnostics passed',
          diagnostics: response.data.diagnostics
        });
      } else {
        const errorMsg = response.data.error || 'Some checks failed';
        toast.error(`Diagnostics failed: ${errorMsg}`);
        setTestLoginResult({ 
          success: false, 
          error: errorMsg,
          diagnostics: response.data.diagnostics,
          failedChecks: failedChecks
        });
      }
      console.log('Diagnostics result:', response.data);
    } catch (error) {
      toast.dismiss('diagnose');
      const errorMsg = error.response?.data?.detail || error.message || 'Diagnostics failed';
      toast.error(`Diagnostics error: ${errorMsg}`);
      setTestLoginResult({ 
        success: false, 
        error: errorMsg,
        timeout: error.code === 'ECONNABORTED'
      });
    } finally {
      setTestingLogin(false);
    }
  };

  const clearSupplierCart = async () => {
    setClearingCart(true);
    try {
      const response = await axios.post(`${API}/admin/ecard-order/clear-cart`);
      if (response.data.success) {
        toast.success(response.data.message);
      } else {
        toast.error('Failed to clear cart');
      }
    } catch (error) {
      toast.error('Failed to clear cart');
      console.error('Clear cart error:', error);
    } finally {
      setClearingCart(false);
    }
  };

  // QuickBooks Integration functions
  const fetchQboStatus = async () => {
    try {
      const response = await axios.get(`${API}/qbo/status`);
      setQboStatus(response.data);
    } catch (error) {
      console.error('Failed to fetch QBO status:', error);
    }
  };

  const connectToQuickBooks = async () => {
    setQboLoading(true);
    try {
      const response = await axios.get(`${API}/qbo/auth-url`);
      // Redirect to QuickBooks OAuth
      window.location.href = response.data.url;
    } catch (error) {
      toast.error('Failed to initiate QuickBooks connection');
      console.error('QBO connect error:', error);
      setQboLoading(false);
    }
  };

  const disconnectQuickBooks = async () => {
    if (!window.confirm('Disconnect from QuickBooks? This will switch reporting back to WooCommerce.')) return;
    setQboLoading(true);
    try {
      await axios.post(`${API}/qbo/disconnect`);
      setQboStatus({ connected: false, realm_id: null, reporting_mode: 'woocommerce' });
      toast.success('QuickBooks disconnected');
    } catch (error) {
      toast.error('Failed to disconnect QuickBooks');
    } finally {
      setQboLoading(false);
    }
  };

  const setReportingMode = async (mode) => {
    setQboLoading(true);
    try {
      await axios.post(`${API}/qbo/set-reporting-mode`, { mode });
      setQboStatus(prev => ({ ...prev, reporting_mode: mode }));
      toast.success(mode === 'quickbooks' 
        ? 'Reporting switched to QuickBooks - WooCommerce sync disabled' 
        : 'Reporting switched to WooCommerce');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change reporting mode');
    } finally {
      setQboLoading(false);
    }
  };

  const testQboSync = async () => {
    setQboLoading(true);
    try {
      const response = await axios.post(`${API}/qbo/test-sync`);
      if (response.data.success) {
        toast.success('QuickBooks sync test passed!');
      } else {
        toast.error(response.data.error || 'Test failed');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Test failed');
    } finally {
      setQboLoading(false);
    }
  };

  const [wooQboSyncStatus, setWooQboSyncStatus] = useState(null);
  const [wooQboSyncing, setWooQboSyncing] = useState(false);

  const syncWooProductsToQbo = async () => {
    setWooQboSyncing(true);
    try {
      const response = await axios.post(`${API}/qbo/sync-woo-products`);
      toast.success('Product sync started! This may take a few minutes.');
      // Poll for status
      const pollStatus = setInterval(async () => {
        try {
          const statusRes = await axios.get(`${API}/qbo/sync-woo-products/status`);
          if (statusRes.data.has_synced) {
            setWooQboSyncStatus(statusRes.data);
            clearInterval(pollStatus);
            setWooQboSyncing(false);
            const results = statusRes.data.results;
            toast.success(`Sync complete: ${results.created} items created, ${results.already_existed} already existed`);
          }
        } catch (e) {
          // Ignore polling errors
        }
      }, 5000);
      // Stop polling after 5 minutes max
      setTimeout(() => {
        clearInterval(pollStatus);
        setWooQboSyncing(false);
      }, 300000);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to start sync');
      setWooQboSyncing(false);
    }
  };

  const checkWooQboSyncStatus = async () => {
    try {
      const response = await axios.get(`${API}/qbo/sync-woo-products/status`);
      setWooQboSyncStatus(response.data);
    } catch (error) {
      // Ignore errors
    }
  };

  // Check sync status on mount when QBO is connected
  useEffect(() => {
    if (qboStatus.connected) {
      checkWooQboSyncStatus();
    }
  }, [qboStatus.connected]);

  // Production Sync function
  const syncFromProduction = async () => {
    if (!productionSyncForm.production_mongo_url) {
      toast.error('Production MongoDB URL is required');
      return;
    }
    if (!productionSyncForm.password) {
      toast.error('Admin password is required');
      return;
    }
    
    setProductionSyncing(true);
    setProductionSyncResult(null);
    
    try {
      const response = await axios.post(`${API}/admin/sync-from-production`, {
        password: productionSyncForm.password,
        production_mongo_url: productionSyncForm.production_mongo_url,
        production_db_name: productionSyncForm.production_db_name || 'biggestlittleclassscheduler'
      });
      
      setProductionSyncResult(response.data);
      toast.success(response.data.message);
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.message || 'Sync failed';
      toast.error(errorMsg);
      setProductionSyncResult({ success: false, error: errorMsg });
    } finally {
      setProductionSyncing(false);
    }
  };

  const adjustEcardQuantity = (productUrl, delta) => {
    // Find the minimum order for this product
    const need = ecardNeeds.find(n => n.product_url === productUrl);
    const minOrder = need?.min_order || 1;
    
    setEcardOrderQuantities(prev => {
      const currentQty = prev[productUrl] || 0;
      
      if (delta > 0) {
        // Increasing: if at 0, jump to minimum
        if (currentQty === 0) {
          if (minOrder > 1) {
            toast.info(`Minimum order for ${need?.product_name || 'this item'} is ${minOrder}`);
          }
          return { ...prev, [productUrl]: minOrder };
        }
        return { ...prev, [productUrl]: currentQty + delta };
      } else {
        // Decreasing: if at or below minimum, go to 0
        if (currentQty <= minOrder) {
          return { ...prev, [productUrl]: 0 };
        }
        return { ...prev, [productUrl]: currentQty + delta };
      }
    });
  };

  const setEcardQuantity = (productUrl, value) => {
    // Allow empty string for typing
    if (value === '' || value === null || value === undefined) {
      setEcardOrderQuantities(prev => ({ ...prev, [productUrl]: '' }));
      return;
    }
    
    const qty = parseInt(value) || 0;
    const need = ecardNeeds.find(n => n.product_url === productUrl);
    const minOrder = need?.min_order || 1;
    
    // If entering a value greater than 0 but less than minimum, enforce minimum
    if (qty > 0 && qty < minOrder) {
      toast.info(`Minimum order for ${need?.product_name || 'this item'} is ${minOrder}`);
      setEcardOrderQuantities(prev => ({ ...prev, [productUrl]: minOrder }));
    } else {
      setEcardOrderQuantities(prev => ({ ...prev, [productUrl]: Math.max(0, qty) }));
    }
  };
  
  // Handler for onBlur to ensure valid value
  const handleEcardQuantityBlur = (productUrl, value) => {
    const qty = parseInt(value) || 0;
    const need = ecardNeeds.find(n => n.product_url === productUrl);
    const minOrder = need?.min_order || 1;
    
    if (qty > 0 && qty < minOrder) {
      setEcardOrderQuantities(prev => ({ ...prev, [productUrl]: minOrder }));
    } else {
      setEcardOrderQuantities(prev => ({ ...prev, [productUrl]: Math.max(0, qty) }));
    }
  };

  // Get cached price for a product URL
  const getCachedPrice = (productUrl) => {
    return cachedPrices.by_url?.[productUrl]?.price || 0;
  };

  // Calculate estimated total using cached prices
  const getEstimatedTotal = () => {
    let total = 0;
    Object.entries(ecardOrderQuantities).forEach(([productUrl, qty]) => {
      const price = getCachedPrice(productUrl);
      total += price * qty;
    });
    return total;
  };

  const getTotalCardsToOrder = () => {
    return Object.values(ecardOrderQuantities).reduce((sum, qty) => sum + qty, 0);
  };

  // Refresh cached prices manually
  const refreshCachedPrices = async () => {
    setRefreshingPrices(true);
    try {
      toast.loading('Syncing prices from CPR Suppliers...', { id: 'price-sync' });
      const response = await axios.post(`${API}/admin/refresh-supplier-prices-playwright`);
      
      if (response.data.success) {
        toast.dismiss('price-sync');
        toast.success('Price sync started! Prices will update shortly.');
        
        // Wait a moment then refetch cached prices
        setTimeout(async () => {
          try {
            const cachedRes = await axios.get(`${API}/admin/supplier-price-cache`);
            setCachedPrices(cachedRes.data || { by_url: {}, by_product_number: {}, last_sync: null });
            toast.success('Prices updated!');
          } catch (e) {
            console.error('Failed to fetch updated prices:', e);
          }
          setRefreshingPrices(false);
        }, 30000); // Wait 30 seconds for prices to sync
      }
    } catch (error) {
      toast.dismiss('price-sync');
      toast.error('Failed to start price sync');
      setRefreshingPrices(false);
    }
  };

  // Direct checkout using cached prices (skip generate-cart step)
  const placeDirectOrder = async () => {
    // Filter items with quantity > 0
    const items = Object.entries(ecardOrderQuantities)
      .filter(([_, qty]) => qty > 0)
      .map(([productUrl, quantity]) => {
        const need = ecardNeeds.find(n => n.product_url === productUrl);
        // Find product_number from ecard_links via the need or ecardLinks
        const link = ecardLinks.find(l => l.link === productUrl);
        const productNumber = link?.product_number || need?.product_number || '';
        return { 
          product_url: productUrl, 
          product_number: productNumber,
          quantity: parseInt(quantity),
          product_name: need?.product_name || link?.name || productNumber
        };
      });
    
    if (items.length === 0) {
      toast.error('No items to order');
      return;
    }
    
    if (!ecardSupplierSettings.has_payment_card && !ecardSupplierSettings.checkout_test_mode) {
      toast.error('Please add a payment card or enable Test Mode');
      setShowCardSettings(true);
      return;
    }
    
    setPlacingOrder(true);
    setOrderResult(null);
    
    const isTestMode = ecardSupplierSettings.checkout_test_mode;
    const totalQty = items.reduce((sum, i) => sum + i.quantity, 0);
    
    try {
      // Create a bulk order first (for tracking and retry capability)
      const createRes = await axios.post(`${API}/admin/bulk-ecard-orders`, {
        items,
        notes: `Class eCard order - ${totalQty} total cards${isTestMode ? ' (TEST MODE)' : ''}`
      });
      
      if (!createRes.data.success) {
        toast.error('Failed to create order');
        setPlacingOrder(false);
        return;
      }
      
      const orderId = createRes.data.order_id;
      toast.success(`Order #${orderId} created`);
      toast.loading(isTestMode ? 'Simulating order (TEST MODE)...' : 'Processing automated checkout...', { id: 'direct-order' });
      
      // Start processing the order
      await axios.post(`${API}/admin/bulk-ecard-orders/${orderId}/process`);
      
      // Poll for completion
      let completed = false;
      let finalOrder = null;
      
      for (let i = 0; i < 60; i++) { // Poll for up to 3 minutes
        await new Promise(r => setTimeout(r, 3000));
        const orderRes = await axios.get(`${API}/admin/bulk-ecard-orders/${orderId}`);
        const order = orderRes.data;
        
        if (order.status !== 'processing') {
          completed = true;
          finalOrder = order;
          break;
        }
      }
      
      toast.dismiss('direct-order');
      
      if (completed && finalOrder) {
        if (finalOrder.status === 'ordered') {
          const toastMsg = isTestMode 
            ? `TEST: Simulated order - Supplier #${finalOrder.supplier_order_number}` 
            : `Order placed! Supplier #${finalOrder.supplier_order_number}`;
          toast.success(toastMsg);
          
          // Set order result for display and inventory adjustment prompt
          setOrderResult({
            success: true,
            order_number: finalOrder.supplier_order_number,
            test_mode: isTestMode
          });
          
          // Build cart result for display
          setEcardCartResult({
            success: true,
            items_added: items.map(i => ({
              class_type_name: i.product_name,
              quantity: i.quantity,
              subtotal: 0 // We don't have the exact subtotal
            })),
            cart_total: finalOrder.cart_total || 0
          });
          
          // Refresh data
          fetchEcardData();
          fetchStudentsNeedingEcards();
          fetchStudentsWithEcards();
          fetchEcardNeedsSummary();
          fetchBulkOrders();
        } else if (finalOrder.status === 'failed') {
          toast.error(`Order failed: ${finalOrder.error_message}. You can retry from Bulk Inventory Orders.`, { duration: 8000 });
          setShowBulkOrders(true);
          fetchBulkOrders();
        }
      } else {
        toast.info('Order is still processing. Check Bulk Inventory Orders for status.');
        setShowBulkOrders(true);
        fetchBulkOrders();
      }
    } catch (error) {
      toast.dismiss('direct-order');
      const errorMsg = error.response?.data?.detail || error.response?.data?.message || 'Order failed';
      toast.error('Order failed: ' + errorMsg + '. Check Bulk Inventory Orders to retry.');
      console.error('Direct order error:', error);
      setShowBulkOrders(true);
      fetchBulkOrders();
    } finally {
      setPlacingOrder(false);
    }
  };

  // Legacy function - still available for generate-cart flow if needed
  const placeEcardOrder = async () => {
    // Filter items with quantity > 0 - now using product_url
    const items = Object.entries(ecardOrderQuantities)
      .filter(([_, qty]) => qty > 0)
      .map(([productUrl, quantity]) => ({ product_url: productUrl, quantity }));
    
    if (items.length === 0) {
      toast.error('No items to order');
      return;
    }
    
    setEcardOrdering(true);
    try {
      // Convert to the format expected by generate-cart (still uses class_type_id internally)
      // Find class_type_id for each product_url from ecardNeeds
      const cartItems = items.map(item => {
        const need = ecardNeeds.find(n => n.product_url === item.product_url);
        return {
          class_type_id: need?.class_type_ids?.[0] || item.product_url,
          quantity: item.quantity
        };
      });
      
      const response = await axios.post(`${API}/admin/ecard-order/generate-cart`, { items: cartItems }, {
        timeout: 180000  // 3 minute timeout for Playwright-based cart generation
      });
      
      if (response.data.success) {
        setEcardCartResult(response.data);
        toast.success(`Cart created! Total: $${response.data.cart_total?.toFixed(2) || '0.00'}`);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to generate cart');
    } finally {
      setEcardOrdering(false);
    }
  };

  const [completingCheckout, setCompletingCheckout] = useState(false);
  const [checkoutResult, setCheckoutResult] = useState(null);
  const [supplierLaunched, setSupplierLaunched] = useState(false); // Track if supplier site was launched

  // Create bulk order for class eCards and optionally process immediately
  const createClassEcardBulkOrder = async (autoProcess = true) => {
    // Build items from ecardOrderQuantities and ecardNeeds
    const items = [];
    for (const [productUrl, quantity] of Object.entries(ecardOrderQuantities)) {
      if (quantity > 0) {
        // Find the product details from ecardNeeds
        const need = ecardNeeds.find(n => n.product_url === productUrl);
        // Find product_number from ecardLinks
        const link = ecardLinks.find(l => l.link === productUrl);
        
        items.push({
          product_number: link?.product_number || need?.product_number || '',
          product_url: productUrl,
          product_name: need?.product_name || link?.name || productUrl,
          quantity: parseInt(quantity)
        });
      }
    }
    
    if (items.length === 0) {
      toast.error('Please select at least one product to order');
      return;
    }
    
    // Check if payment card is configured
    if (!ecardSupplierSettings.has_payment_card) {
      toast.error('Please add a payment card before ordering');
      setShowCardSettings(true);
      return;
    }
    
    setCompletingCheckout(true);
    try {
      // Create the bulk order
      const response = await axios.post(`${API}/admin/bulk-ecard-orders`, {
        items,
        notes: `Class eCard order - ${items.reduce((sum, i) => sum + i.quantity, 0)} total cards`
      });
      
      if (response.data.success) {
        const orderId = response.data.order_id;
        toast.success(`Bulk order #${orderId} created`);
        
        if (autoProcess) {
          // Automatically start processing
          toast.loading('Starting automated checkout...', { id: 'auto-checkout' });
          
          try {
            await axios.post(`${API}/admin/bulk-ecard-orders/${orderId}/process`);
            toast.dismiss('auto-checkout');
            toast.success('Order processing started! Check Bulk Inventory Orders for progress.');
            
            // Close the eCard ordering dialog and open bulk orders
            setShowEcardOrdering(false);
            setShowBulkOrders(true);
            fetchBulkOrders();
            
            // Poll for completion
            const pollInterval = setInterval(async () => {
              try {
                const orderRes = await axios.get(`${API}/admin/bulk-ecard-orders/${orderId}`);
                const order = orderRes.data;
                
                if (order.status !== 'processing') {
                  clearInterval(pollInterval);
                  fetchBulkOrders();
                  
                  if (order.status === 'ordered') {
                    toast.success(`Order completed! Supplier Order #${order.supplier_order_number}`);
                    
                    // Mark students as having eCards ordered
                    const productUrls = items.map(i => i.product_url);
                    await axios.post(`${API}/admin/ecard-order/mark-ordered`, {
                      product_urls: productUrls,
                      quantities: ecardOrderQuantities,
                      skip_inventory: ecardDontAdjustInventory
                    });
                    
                    fetchEcardData();
                    fetchStudentsNeedingEcards();
                    fetchEcardNeedsSummary();
                  } else if (order.status === 'failed') {
                    toast.error(`Order failed: ${order.error_message}. You can retry from Bulk Inventory Orders.`);
                  }
                }
              } catch (pollErr) {
                console.error('Polling error:', pollErr);
              }
            }, 3000);
            
          } catch (processErr) {
            toast.dismiss('auto-checkout');
            toast.error('Failed to start processing. You can retry from Bulk Inventory Orders.');
            setShowBulkOrders(true);
            fetchBulkOrders();
          }
        } else {
          toast.info('Order created. Go to Bulk Inventory Orders to process it.');
          setShowEcardOrdering(false);
          setShowBulkOrders(true);
          fetchBulkOrders();
        }
      }
    } catch (error) {
      console.error('Failed to create bulk order:', error);
      toast.error(error.response?.data?.detail || 'Failed to create bulk order');
    } finally {
      setCompletingCheckout(false);
    }
  };

  const completeEcardCheckout = async () => {
    if (!ecardCartResult?.items_added || ecardCartResult.items_added.length === 0) {
      toast.error('No items in cart. Please generate a cart first.');
      return;
    }
    
    // Check if payment card is configured
    if (!ecardSupplierSettings.has_payment_card) {
      toast.error('Please add a payment card before completing checkout');
      setShowCardSettings(true);
      return;
    }
    
    setCompletingCheckout(true);
    try {
      // Call backend to complete full checkout
      const items = ecardCartResult.items_added.map(item => ({
        class_type_id: item.class_type_id,
        quantity: item.quantity
      }));
      
      // Try auto-checkout first (uses Browserless if configured)
      let response;
      try {
        response = await axios.post(`${API}/admin/ecard-order/auto-checkout`, {
          items: items,
          confirm_total: ecardCartResult.cart_total
        });
      } catch (autoErr) {
        // If auto-checkout fails (no Browserless token), fall back to manual
        if (autoErr.response?.status === 400 && autoErr.response?.data?.detail?.includes('Browserless')) {
          console.log('Auto-checkout not available, using manual checkout');
          response = await axios.post(`${API}/admin/ecard-order/complete-checkout`, {
            items: items,
            confirm_total: ecardCartResult.cart_total
          });
        } else {
          throw autoErr;
        }
      }
      
      if (response.data.success) {
        setCheckoutResult(response.data);
        
        if (response.data.requires_manual_payment) {
          // Cart is ready but payment needs to be done in browser
          toast.success('Cart prepared! Complete payment in browser.');
          // Open checkout page for manual payment
          window.open(response.data.checkout_url, '_blank');
        } else if (response.data.order_number) {
          toast.success(`Order #${response.data.order_number} placed successfully!`);
        } else {
          toast.success('Cards ordered successfully!');
        }
        
        // Auto-mark as ordered
        const productUrls = Object.entries(ecardOrderQuantities)
          .filter(([_, qty]) => qty > 0)
          .map(([productUrl]) => productUrl);
        
        if (productUrls.length > 0) {
          await axios.post(`${API}/admin/ecard-order/mark-ordered`, {
            product_urls: productUrls,
            quantities: ecardOrderQuantities,
            skip_inventory: ecardDontAdjustInventory
          });
        }
        
        fetchEcardData();
        fetchStudentsNeedingEcards();
        fetchStudentsWithEcards();
        fetchEcardNeedsSummary(); // Update header indicator
      } else {
        toast.error('Payment failed: ' + (response.data.message || 'Unknown error'));
      }
    } catch (error) {
      const errorMsg = error.response?.data?.detail || error.response?.data?.message || 'Payment failed';
      toast.error('Payment failed: ' + errorMsg);
      console.error('Checkout error:', error);
    } finally {
      setCompletingCheckout(false);
    }
  };

  const confirmOrderCompleted = async (skipInventory = false) => {
    // Get product_urls with quantity > 0
    const productUrls = Object.entries(ecardOrderQuantities)
      .filter(([_, qty]) => qty > 0)
      .map(([productUrl]) => productUrl);
    
    try {
      await axios.post(`${API}/admin/ecard-order/mark-ordered`, {
        product_urls: productUrls,
        quantities: ecardOrderQuantities,
        skip_inventory: skipInventory
      });
      
      const message = skipInventory 
        ? 'Order recorded (inventory not adjusted)' 
        : 'eCards marked as ordered. Bookings updated!';
      toast.success(message);
      setEcardCartResult(null);
      setCheckoutResult(null);
      setSupplierLaunched(false);
      setShowEcardOrdering(false);
      // Refresh both eCard data AND main bookings to ensure UI is in sync
      fetchEcardData();
      fetchBookings();
    } catch (error) {
      toast.error('Failed to mark order as complete');
    }
  };

  const cancelEcardOrder = async () => {
    // Clear cart on CPR Suppliers and close without inventory changes
    try {
      toast.loading('Clearing cart on CPR Suppliers...', { id: 'clear-cart' });
      await axios.post(`${API}/admin/ecard-order/clear-cart`);
      toast.dismiss('clear-cart');
      toast.success('Cart cleared');
    } catch (error) {
      toast.dismiss('clear-cart');
      // Don't show error - cart may already be empty
      console.log('Cart clear attempt:', error);
    }
    setEcardCartResult(null);
    setCheckoutResult(null);
    setSupplierLaunched(false);
  };

  const launchCprSuppliers = () => {
    // Simply open CPR Suppliers checkout page in a new tab
    const checkoutUrl = ecardCartResult?.checkout_url || 'https://cprsuppliers.com/checkout/';
    window.open(checkoutUrl, '_blank');
    setSupplierLaunched(true);
  };

  // Automated Playwright checkout
  const placeAutomatedOrder = async () => {
    // Build items from ecardOrderQuantities
    const items = [];
    for (const [productUrl, quantity] of Object.entries(ecardOrderQuantities)) {
      if (quantity > 0) {
        // Find the product details from ecardNeeds
        const need = ecardNeeds.find(n => n.product_url === productUrl);
        // Find product_number from ecardLinks
        const link = ecardLinks.find(l => l.link === productUrl);
        
        items.push({
          product_number: link?.product_number || need?.product_number || '',
          product_url: productUrl,
          product_name: need?.product_name || link?.name || productUrl,
          quantity: parseInt(quantity)
        });
      }
    }
    
    if (items.length === 0) {
      toast.error('No items to order');
      return;
    }
    
    if (!ecardSupplierSettings.has_payment_card) {
      toast.error('Please add a payment card before ordering');
      setShowCardSettings(true);
      return;
    }
    
    setPlacingOrder(true);
    setOrderResult(null);
    
    const isTestMode = ecardSupplierSettings.checkout_test_mode;
    
    try {
      // Create a bulk order first (for tracking and retry capability)
      const totalQty = items.reduce((sum, i) => sum + i.quantity, 0);
      const createRes = await axios.post(`${API}/admin/bulk-ecard-orders`, {
        items,
        notes: `Class eCard order - ${totalQty} total cards${isTestMode ? ' (TEST MODE)' : ''}`
      });
      
      if (!createRes.data.success) {
        toast.error('Failed to create order');
        return;
      }
      
      const orderId = createRes.data.order_id;
      toast.success(`Order #${orderId} created`);
      toast.loading(isTestMode ? 'Simulating order (TEST MODE)...' : 'Processing automated checkout...', { id: 'auto-order' });
      
      // Start processing the order
      await axios.post(`${API}/admin/bulk-ecard-orders/${orderId}/process`);
      
      // Poll for completion
      let completed = false;
      let finalOrder = null;
      
      for (let i = 0; i < 60; i++) { // Poll for up to 3 minutes
        await new Promise(r => setTimeout(r, 3000));
        const orderRes = await axios.get(`${API}/admin/bulk-ecard-orders/${orderId}`);
        const order = orderRes.data;
        
        if (order.status !== 'processing') {
          completed = true;
          finalOrder = order;
          break;
        }
      }
      
      toast.dismiss('auto-order');
      
      if (completed && finalOrder) {
        if (finalOrder.status === 'ordered') {
          const toastMsg = isTestMode 
            ? `TEST: Simulated order - Supplier #${finalOrder.supplier_order_number}` 
            : `Order placed! Supplier #${finalOrder.supplier_order_number}`;
          toast.success(toastMsg);
          
          // Set order result to show inventory adjustment prompt
          setOrderResult({
            success: true,
            order_number: finalOrder.supplier_order_number,
            test_mode: isTestMode
          });
          
          // Refresh data
          fetchEcardData();
          fetchStudentsNeedingEcards();
          fetchStudentsWithEcards();
          fetchEcardNeedsSummary();
          fetchBulkOrders();
        } else if (finalOrder.status === 'failed') {
          toast.error(`Order failed: ${finalOrder.error_message}. You can retry from Bulk Inventory Orders.`);
          setShowBulkOrders(true);
          fetchBulkOrders();
        }
      } else {
        toast.info('Order is still processing. Check Bulk Inventory Orders for status.');
        setShowBulkOrders(true);
        fetchBulkOrders();
      }
    } catch (error) {
      toast.dismiss('auto-order');
      const errorMsg = error.response?.data?.detail || error.response?.data?.message || 'Order failed';
      toast.error('Order failed: ' + errorMsg + '. Check Bulk Inventory Orders to retry.');
      console.error('Automated order error:', error);
      setShowBulkOrders(true);
      fetchBulkOrders();
    } finally {
      setPlacingOrder(false);
    }
  };
  
  // Handle inventory adjustment after successful order
  const handleOrderInventoryAdjustment = async (adjustInventory) => {
    if (adjustInventory) {
      // Adjust inventory and auto-assign to students
      const productUrls = Object.entries(ecardOrderQuantities)
        .filter(([_, qty]) => qty > 0)
        .map(([productUrl]) => productUrl);
      
      if (productUrls.length > 0) {
        try {
          toast.loading('Adjusting inventory...', { id: 'adjust-inv' });
          await axios.post(`${API}/admin/ecard-order/mark-ordered`, {
            product_urls: productUrls,
            quantities: ecardOrderQuantities,
            skip_inventory: false
          });
          toast.dismiss('adjust-inv');
          toast.success('Inventory adjusted and students assigned!');
          
          // Refresh data
          fetchEcardData();
          fetchStudentsNeedingEcards();
          fetchStudentsWithEcards();
          fetchEcardNeedsSummary();
          fetchBookings();
        } catch (error) {
          toast.dismiss('adjust-inv');
          toast.error('Failed to adjust inventory');
        }
      }
    } else {
      toast.success('Order recorded without inventory adjustment');
    }
    
    // Close the modal
    setEcardCartResult(null);
    setOrderResult(null);
    setShowEcardOrdering(false);
  };

  // Fetch order history
  const fetchOrderHistory = async (search = '') => {
    setOrderHistoryLoading(true);
    try {
      const params = search ? `?search=${encodeURIComponent(search)}` : '';
      const response = await axios.get(`${API}/admin/ecard-order-history${params}`);
      setOrderHistory(response.data.orders || []);
    } catch (error) {
      console.error('Failed to fetch order history:', error);
      toast.error('Failed to load order history');
    } finally {
      setOrderHistoryLoading(false);
    }
  };

  const updateEcardInventory = async (productUrl, newQuantity) => {
    try {
      await axios.post(`${API}/admin/ecard-inventory/by-product?product_url=${encodeURIComponent(productUrl)}&quantity=${newQuantity}`);
      toast.success('Inventory updated');
      setEditingInventory(null);
      fetchEcardData();
    } catch (error) {
      toast.error('Failed to update inventory');
    }
  };

  const startEditInventory = (productUrl, currentValue) => {
    setEditingInventory(productUrl);
    setEditInventoryValue(currentValue);
  };

  const saveEditInventory = (productUrl) => {
    updateEcardInventory(productUrl, editInventoryValue);
  };

  const cancelEditInventory = () => {
    setEditingInventory(null);
    setEditInventoryValue(0);
  };

  // Fetch eCard needs summary for header indicator (lightweight)
  const fetchEcardNeedsSummary = async () => {
    try {
      const response = await axios.get(`${API}/admin/ecard-needs-summary`);
      setEcardNeedsSummary(response.data);
    } catch (error) {
      console.error('Failed to fetch eCard needs summary:', error);
    }
  };

  // Fetch individual STUDENTS that need eCards
  const fetchStudentsNeedingEcards = async () => {
    try {
      const response = await axios.get(`${API}/admin/students-needing-ecards`);
      setStudentsNeedingEcards(response.data);
    } catch (error) {
      console.error('Failed to fetch students needing eCards:', error);
    }
  };

  // Fetch students who already have eCards marked
  const fetchStudentsWithEcards = async () => {
    try {
      const response = await axios.get(`${API}/admin/students-with-ecards`);
      setStudentsWithEcards(response.data);
    } catch (error) {
      console.error('Failed to fetch students with eCards:', error);
    }
  };

  // Mark a specific STUDENT's eCard as already purchased (or unmark)
  const markStudentEcardPurchased = async (bookingId, studentIndex, studentName, markAsOrdered = true) => {
    setMarkingEcardStudent({ booking_id: bookingId, student_index: studentIndex });
    try {
      await axios.post(`${API}/admin/student/mark-ecard`, {
        booking_id: bookingId,
        student_index: studentIndex,
        mark_as_ordered: markAsOrdered
      });
      toast.success(markAsOrdered ? `eCard marked for ${studentName}` : `eCard unmarked for ${studentName}`);
      await fetchStudentsNeedingEcards();
      await fetchStudentsWithEcards();
      await fetchEcardData();
      await fetchEcardNeedsSummary(); // Update header indicator
    } catch (error) {
      toast.error('Failed to update eCard status');
    } finally {
      setMarkingEcardStudent(null);
    }
  };

  // Drag-and-drop sensors for class type reordering
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 5,
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle drag end for class type reordering
  const handleClassTypeDragEnd = async (event) => {
    const { active, over } = event;
    
    if (active.id !== over?.id) {
      const oldIndex = adminClassTypes.findIndex((ct) => ct.id === active.id);
      const newIndex = adminClassTypes.findIndex((ct) => ct.id === over.id);
      
      // Update local state immediately for responsive UI
      const newOrder = arrayMove(adminClassTypes, oldIndex, newIndex);
      setAdminClassTypes(newOrder);
      
      // Send new order to backend
      try {
        await axios.put(`${API}/admin/class-types/reorder`, { order: newOrder.map(ct => ct.id) });
        toast.success('Class order updated!');
      } catch (error) {
        // Revert on error
        fetchAdminClassTypes();
        toast.error('Failed to update order');
      }
    }
  };

  // Calculate cutoff time for past classes
  // Classes are considered "current" until 6 hours after they END
  // Use PST timezone for all comparisons since that's the business timezone
  const isPastClass = (booking) => {
    // Parse the end time - it's stored in UTC/ISO format
    const endTime = new Date(booking.end_time);
    
    // Get current time
    const now = new Date();
    
    // A class is "past" if it ended more than 6 hours ago
    const cutoffTime = new Date(now.getTime() - 6 * 60 * 60 * 1000);
    
    return endTime < cutoffTime;
  };
  
  // Check if booking matches the search date
  const matchesSearchDate = (booking) => {
    if (!searchDate) return true;
    const bookingDate = format(new Date(booking.start_time), 'yyyy-MM-dd');
    return bookingDate === searchDate;
  };
  
  
  // Helper to check if a booking is within the current calendar week
  const isWithinCalendarWeek = (booking) => {
    if (!booking.start_time) return false;
    const bookingDate = new Date(booking.start_time);
    const weekStart = new Date(calendarWeekStart);
    weekStart.setHours(0, 0, 0, 0);
    const weekEnd = new Date(weekStart);
    weekEnd.setDate(weekEnd.getDate() + 7);
    weekEnd.setHours(23, 59, 59, 999);
    return bookingDate >= weekStart && bookingDate < weekEnd;
  };

  const filteredBookings = bookings.filter((booking) => {
    // Apply status filter
    // Group 'rejected' and 'rejected_no_refund' together when filtering by 'rejected'
    let matchesStatus = filter === 'all' || booking.status === filter;
    if (filter === 'rejected' && booking.status === 'rejected_no_refund') {
      matchesStatus = true;
    }
    // Special filter for public classes only
    if (filter === 'public') {
      if (!booking.is_public) return false;
      matchesStatus = true;
    }
    // Exclude hidden bookings from 'all' filter - they should only show in 'hidden' filter
    if (filter === 'all' && booking.status === 'hidden') {
      return false;
    }
    if (!matchesStatus) return false;
    
    // Apply date search filter
    if (!matchesSearchDate(booking)) return false;
    
    // Apply Calendar View filter (takes priority when enabled)
    if (calendarViewMode && !searchDate) {
      return isWithinCalendarWeek(booking);
    }
    
    // Apply past/current filter (only if no date search is active and not in calendar view)
    // Note: Hidden bookings show all (no past/current filter) since they're hidden anyway
    if (!searchDate && filter !== 'hidden') {
      if (showPastClasses) {
        return isPastClass(booking);
      } else {
        return !isPastClass(booking);
      }
    }
    
    return true;
  }).sort((a, b) => {
    // Sort by start_time - next class first (ascending for upcoming, descending for past)
    const dateA = new Date(a.start_time);
    const dateB = new Date(b.start_time);
    if (showPastClasses && !calendarViewMode) {
      return dateB - dateA; // Most recent past class first
    }
    return dateA - dateB; // Nearest upcoming class first
  });
  
  // Pagination calculations
  const totalPages = Math.ceil(filteredBookings.length / BOOKINGS_PER_PAGE);
  const startIndex = (currentPage - 1) * BOOKINGS_PER_PAGE;
  const endIndex = startIndex + BOOKINGS_PER_PAGE;
  const paginatedBookings = filteredBookings.slice(startIndex, endIndex);
  
  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [filter, searchDate, showPastClasses, calendarViewMode, calendarWeekStart]);
  
  // Generate page numbers for pagination
  const getPageNumbers = () => {
    const pages = [];
    const maxVisiblePages = 5;
    
    if (totalPages <= maxVisiblePages) {
      for (let i = 1; i <= totalPages; i++) {
        pages.push(i);
      }
    } else {
      // Always show first page
      pages.push(1);
      
      // Calculate range around current page
      let start = Math.max(2, currentPage - 1);
      let end = Math.min(totalPages - 1, currentPage + 1);
      
      // Adjust if at the beginning
      if (currentPage <= 3) {
        end = Math.min(totalPages - 1, 4);
      }
      
      // Adjust if at the end
      if (currentPage >= totalPages - 2) {
        start = Math.max(2, totalPages - 3);
      }
      
      // Add ellipsis if needed
      if (start > 2) {
        pages.push('...');
      }
      
      // Add middle pages
      for (let i = start; i <= end; i++) {
        pages.push(i);
      }
      
      // Add ellipsis if needed
      if (end < totalPages - 1) {
        pages.push('...');
      }
      
      // Always show last page
      if (totalPages > 1) {
        pages.push(totalPages);
      }
    }
    
    return pages;
  };
  
  // Count past classes for the button
  const pastClassesCount = bookings.filter(isPastClass).length;
  const currentClassesCount = bookings.filter(b => !isPastClass(b)).length;

  const getStatusBadge = (status) => {
    const variants = {
      pending: { color: 'bg-amber-900/50 text-amber-400 border-amber-700', icon: Clock, label: 'Pending' },
      approved: { color: 'bg-teal-900/50 text-teal-400 border-teal-700', icon: CheckCircle, label: 'Approved' },
      rejected: { color: 'bg-red-900/50 text-red-400 border-red-700', icon: XCircle, label: 'Rejected' },
      rejected_no_refund: { color: 'bg-rose-900/50 text-rose-400 border-rose-700', icon: XCircle, label: 'Rejected (No Refund)' },
      moved_to_queue: { color: 'bg-purple-900/50 text-white border-purple-700', icon: ArrowRightLeft, label: 'In Queue' },
      hidden: { color: 'bg-slate-700/50 text-white border-teal-600', icon: EyeOff, label: 'Hidden' },
      partially_paid: { color: 'bg-orange-900/50 text-orange-400 border-orange-700', icon: AlertCircle, label: 'Partially Paid' }
    };
    const variant = variants[status] || variants.pending;
    const Icon = variant.icon;
    return (
      <Badge className={`${variant.color} border flex items-center gap-1`} data-testid={`status-badge-${status}`}>
        <Icon className="h-3 w-3" />
        {variant.label}
      </Badge>
    );
  };

  const getPaymentBadge = (paymentStatus, wooOrderId) => {
    const variants = {
      unpaid: { color: 'bg-orange-900/50 text-orange-400 border-orange-700', icon: CreditCard, label: 'Unpaid' },
      paid: { color: 'bg-emerald-900/50 text-white border-emerald-700', icon: DollarSign, label: 'Paid' },
      refunded: { color: 'bg-purple-900/50 text-white border-purple-700', icon: RefreshCw, label: 'Refunded' },
      expired: { color: 'bg-red-900/50 text-red-400 border-red-700', icon: XCircle, label: 'Canceled - No Pmt' }
    };
    const variant = variants[paymentStatus] || variants.unpaid;
    const Icon = variant.icon;
    return (
      <Badge className={`${variant.color} border flex items-center gap-1`} data-testid={`payment-badge-${paymentStatus}`}>
        <Icon className="h-3 w-3" />
        {variant.label}
        {wooOrderId && <span className="text-xs opacity-70">#{wooOrderId}</span>}
      </Badge>
    );
  };

  const syncPaymentStatus = async (bookingId) => {
    try {
      toast.loading('Checking payment status...', { id: 'sync-payment' });
      const response = await axios.post(`${API}/bookings/${bookingId}/sync-payment`);
      toast.dismiss('sync-payment');
      if (response.data.success) {
        toast.success(`Payment status: ${response.data.payment_status}`);
        fetchBookings();
      } else {
        toast.error('Could not check payment status');
      }
    } catch (error) {
      toast.dismiss('sync-payment');
      toast.error('Failed to sync payment status');
    }
  };

  const markAsPaid = async (bookingId) => {
    try {
      toast.loading('Marking as paid...', { id: 'mark-paid' });
      const response = await axios.patch(`${API}/bookings/${bookingId}/payment-status?payment_status=paid`);
      toast.dismiss('mark-paid');
      if (response.data.success) {
        toast.success('Booking marked as paid! Calendar updated.');
        fetchBookings();
      } else {
        toast.error('Failed to mark as paid');
      }
    } catch (error) {
      toast.dismiss('mark-paid');
      toast.error('Failed to mark as paid');
    }
  };
  
  // Create invoice for a recurring client booking
  const createInvoiceForBooking = async (bookingId) => {
    const sendEmail = window.confirm('Send invoice email to client now?');
    
    try {
      toast.loading('Creating invoice...', { id: 'create-invoice' });
      const response = await axios.post(`${API}/admin/bookings/${bookingId}/create-invoice?send_email=${sendEmail}`);
      toast.dismiss('create-invoice');
      
      if (response.data.success) {
        const pricing = response.data.pricing;
        toast.success(
          <div>
            <p className="font-bold">Invoice {response.data.invoice_id} created!</p>
            <p className="text-sm">Total: ${response.data.total?.toFixed(2)}</p>
            {response.data.email_sent && <p className="text-sm text-green-300">Email sent to client</p>}
          </div>
        );
        fetchBookings();
      } else {
        toast.error('Failed to create invoice');
      }
    } catch (error) {
      toast.dismiss('create-invoice');
      toast.error(error.response?.data?.detail || 'Failed to create invoice');
    }
  };
  
  // Toggle auto-invoice setting for a booking
  const toggleAutoInvoice = async (bookingId, enabled) => {
    try {
      const response = await axios.put(`${API}/admin/bookings/${bookingId}/auto-invoice`, {
        auto_invoice: enabled
      });
      
      if (response.data.success) {
        toast.success(`Auto-invoice ${enabled ? 'enabled' : 'disabled'}`);
        fetchBookings();
      } else {
        toast.error('Failed to toggle auto-invoice');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to toggle auto-invoice');
    }
  };
  
  const markAsUnpaid = async (bookingId) => {
    const reason = window.prompt('Reason for marking as unpaid (optional):\n\nThis will update the WooCommerce order status to "pending".');
    if (reason === null) return; // User cancelled
    
    try {
      toast.loading('Marking as unpaid...', { id: 'mark-unpaid' });
      const response = await axios.post(`${API}/admin/bookings/${bookingId}/mark-unpaid`, {
        reason: reason || 'Marked as unpaid by admin'
      });
      toast.dismiss('mark-unpaid');
      if (response.data.success) {
        let message = 'Booking marked as unpaid!';
        if (response.data.woo_result) {
          message += ` ${response.data.woo_result}`;
        }
        toast.success(message);
        fetchBookings();
      } else {
        toast.error('Failed to mark as unpaid');
      }
    } catch (error) {
      toast.dismiss('mark-unpaid');
      toast.error(error.response?.data?.detail || 'Failed to mark as unpaid');
    }
  };

  // Show loading state while checking auth
  if (authChecking) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-pulse text-slate-500">Loading...</div>
      </div>
    );
  }

  // Show login screen if not authenticated
  if (!isAuthenticated) {
    return <LoginScreen onLogin={handleLogin} />;
  }

  return (
    <div className="min-h-screen bg-slate-900 overflow-x-hidden">
      {/* Sandbox Mode Warning Banner - hides on hover */}
      {paymentMode === 'sandbox' && (
        <div 
          className="bg-amber-500 text-amber-950 py-2 px-4 text-center font-semibold sticky top-0 z-[60] transition-opacity duration-200 hover:opacity-0 hover:pointer-events-none cursor-default" 
          data-testid="admin-sandbox-banner"
          title="Hover to hide"
        >
          ⚠️ BOOKINGS CURRENTLY NOT BEING ACCEPTED - System in Sandbox Mode
        </div>
      )}
      
      <nav className="bg-slate-800 border-b border-slate-700 sticky top-0 z-50">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl py-4">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
            <div className="flex items-center gap-3 flex-shrink-0">
              <div className="h-10 w-10 rounded-full bg-gradient-to-br from-teal-500 to-teal-600 flex items-center justify-center flex-shrink-0">
                <Heart className="h-5 w-5 text-white" data-testid="admin-logo-icon" />
              </div>
              <div className="min-w-0">
                <h1 className="text-xl lg:text-2xl font-bold font-heading text-slate-100 truncate" data-testid="admin-title">Admin Dashboard</h1>
                <div className="flex items-center gap-2 text-xs text-slate-500">
                  <span className="inline-flex items-center gap-1">
                    <span className="h-2 w-2 rounded-full bg-green-500 animate-pulse"></span>
                    Auto-refresh: 15s
                  </span>
                  {lastRefresh && (
                    <span className="hidden sm:inline">• Last: {lastRefresh.toLocaleTimeString()}</span>
                  )}
                </div>
              </div>
            </div>
            <div className="flex flex-col gap-2">
              {/* Main Navigation Row: POS, Clients, Rentals, Invoices, eCards, Tools, Settings */}
              <div className="flex items-center gap-2 flex-wrap">
                {/* POS */}
                <Button
                  onClick={() => setShowPOS(true)}
                  variant="outline"
                  size="sm"
                  className="border-teal-600 text-white hover:bg-emerald-900/30"
                  data-testid="pos-button"
                >
                  <ShoppingCart className="h-4 w-4 mr-2" />
                  POS
                </Button>
                
                {/* Recurring Clients - Now a main button */}
                <Button
                  onClick={() => setShowRecurringClients(true)}
                  variant="outline"
                  size="sm"
                  className="border-blue-600 text-white hover:bg-blue-900/30"
                  data-testid="recurring-clients-button"
                >
                  <Building2 className="h-4 w-4 mr-2" />
                  Clients
                </Button>
                
                {/* Rentals */}
                <Button
                  onClick={() => setShowRentals(true)}
                  variant="outline"
                  size="sm"
                  className="border-orange-600 text-white hover:bg-orange-900/30"
                  data-testid="rentals-button"
                >
                  <Package className="h-4 w-4 mr-2" />
                  Rentals
                </Button>
                
                {/* Invoices */}
                <Button
                  onClick={() => setShowInvoices(true)}
                  variant="outline"
                  size="sm"
                  className="border-purple-600 text-white hover:bg-purple-900/30"
                  data-testid="invoices-button"
                >
                  <FileText className="h-4 w-4 mr-2" />
                  Invoices
                </Button>
                
                {/* Waitlist */}
                <Button
                  onClick={handleOpenWaitlist}
                  variant="outline"
                  size="sm"
                  className="border-pink-600 text-white hover:bg-pink-900/30 relative group"
                  data-testid="waitlist-button"
                  title={`Waitlist: ${waitlistStats.total} total (${waitlistStats.waiting} waiting, ${waitlistStats.invited} invited, ${waitlistStats.paid} paid)`}
                >
                  <Clock className="h-4 w-4 mr-2" />
                  Waitlist
                  {/* Show unseen badge (pulsing) if there are new entries, otherwise show total */}
                  {waitlistStats.unseen > 0 ? (
                    <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium animate-pulse">
                      {waitlistStats.unseen}
                    </span>
                  ) : waitlistStats.total > 0 ? (
                    <span className="absolute -top-1 -right-1 bg-pink-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center font-medium">
                      {waitlistStats.total}
                    </span>
                  ) : null}
                  {/* Hover tooltip */}
                  <div className="absolute bottom-full left-1/2 transform -translate-x-1/2 mb-2 px-3 py-2 bg-slate-900 border border-pink-600 rounded-lg text-xs whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                    <div className="text-pink-300 font-semibold mb-1">Waitlist Summary</div>
                    <div className="grid grid-cols-2 gap-x-4 gap-y-1">
                      <span className="text-yellow-400">Waiting:</span><span className="text-white">{waitlistStats.waiting}</span>
                      <span className="text-blue-400">Invited:</span><span className="text-white">{waitlistStats.invited}</span>
                      <span className="text-green-400">Paid:</span><span className="text-white">{waitlistStats.paid}</span>
                      <span className="text-red-400">Expired:</span><span className="text-white">{waitlistStats.expired}</span>
                      {waitlistStats.unseen > 0 && (
                        <>
                          <span className="text-red-400 font-bold">New:</span>
                          <span className="text-red-400 font-bold">{waitlistStats.unseen}</span>
                        </>
                      )}
                    </div>
                  </div>
                </Button>
                
                {/* Store Manager */}
                <Button
                  onClick={() => setShowStore(true)}
                  variant="outline"
                  size="sm"
                  className="border-orange-600 text-white hover:bg-orange-900/30"
                  data-testid="store-manager-button"
                  title="Manage products and categories"
                >
                  <ShoppingBag className="h-4 w-4 mr-2" />
                  Store
                </Button>
                
                {/* eCards Dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="border-teal-600 text-white hover:bg-purple-900/30" data-testid="ecards-dropdown">
                      <CreditCard className="h-4 w-4 mr-2" />
                      eCards
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-slate-800 border-slate-700 w-56">
                    <DropdownMenuItem onClick={openEcardOrdering} className="text-purple-300 focus:bg-slate-700 focus:text-purple-200" data-testid="order-ecards-menu">
                      <ShoppingCart className="h-4 w-4 mr-2" />
                      Order eCards (For Classes)
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={openBulkOrders} className="text-green-300 focus:bg-slate-700 focus:text-green-200" data-testid="bulk-inventory-order-menu">
                      <Package className="h-4 w-4 mr-2" />
                      Bulk Inventory Order
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => { setShowStudentEcardManager(true); fetchStudentsNeedingEcards(); fetchStudentsWithEcards(); }} className="text-teal-300 focus:bg-slate-700 focus:text-teal-200" data-testid="student-ecard-manager-menu">
                      <Users className="h-4 w-4 mr-2" />
                      Students Needing eCards
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={fetchAHAInventory} 
                      disabled={ahaInventoryLoading}
                      className="text-rose-300 focus:bg-slate-700 focus:text-rose-200" 
                      data-testid="aha-inventory-menu"
                    >
                      {ahaInventoryLoading ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <Heart className="h-4 w-4 mr-2" />
                      )}
                      AHA Inv
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-slate-700" />
                    <DropdownMenuSub>
                      <DropdownMenuSubTrigger className="text-yellow-300 focus:bg-slate-700 focus:text-yellow-200">
                        <Store className="h-4 w-4 mr-2" />
                        Instructor eCard Store
                      </DropdownMenuSubTrigger>
                      <DropdownMenuSubContent className="bg-slate-800 border-slate-700">
                        <DropdownMenuItem 
                          onClick={() => { setShowInstructorPricing(true); fetchInstructorPricing(); }}
                          className="text-yellow-300 focus:bg-slate-700 focus:text-yellow-200"
                        >
                          <DollarSign className="h-4 w-4 mr-2" />
                          Instructor eCard Pricing
                        </DropdownMenuItem>
                        <DropdownMenuItem 
                          onClick={() => { setShowInstructorOrders(true); fetchInstructorOrders(); }}
                          className="text-yellow-300 focus:bg-slate-700 focus:text-yellow-200"
                        >
                          <ShoppingBag className="h-4 w-4 mr-2" />
                          Order Fulfillment
                        </DropdownMenuItem>
                      </DropdownMenuSubContent>
                    </DropdownMenuSub>
                    <DropdownMenuSeparator className="bg-slate-700" />
                    <DropdownMenuItem 
                      onClick={() => { setShowOrderHistory(true); fetchOrderHistory(); }}
                      className="text-blue-300 focus:bg-slate-700 focus:text-blue-200" 
                      data-testid="order-history-menu"
                    >
                      <History className="h-4 w-4 mr-2" />
                      Order History
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-slate-700" />
                    <DropdownMenuItem 
                      onClick={() => { setShowEcardLinks(true); fetchEcardLinks(); fetchLocalEcardInventory(); }}
                      className="text-orange-300 focus:bg-slate-700 focus:text-orange-200" 
                      data-testid="ecard-links-menu"
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      eCard Manager/Editor
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                
                {/* Tools Dropdown */}
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="outline" size="sm" className="border-teal-600 text-white hover:bg-slate-700" data-testid="tools-dropdown">
                      <Wrench className="h-4 w-4 mr-2" />
                      Tools
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="bg-slate-800 border-slate-700 w-56">
                    <DropdownMenuItem 
                      onClick={() => setShowReports(true)} 
                      className="text-emerald-300 focus:bg-slate-700 focus:text-emerald-200" 
                      data-testid="reports-menu"
                    >
                      <BarChart3 className="h-4 w-4 mr-2" />
                      Reports
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => setShowRescheduleDashboard(true)} 
                      className="text-amber-300 focus:bg-slate-700 focus:text-amber-200" 
                      data-testid="reschedule-dashboard-menu"
                    >
                      <RefreshCw className="h-4 w-4 mr-2" />
                      Reschedule Management
                    </DropdownMenuItem>
                    <DropdownMenuItem onClick={() => setShowAdminTasks(true)} className="text-amber-300 focus:bg-slate-700 focus:text-amber-200" data-testid="admin-tasks-menu">
                      <CheckCircle className="h-4 w-4 mr-2" />
                      System Issues
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => setShowDistanceCalculator(true)} 
                      className="text-cyan-300 focus:bg-slate-700 focus:text-cyan-200" 
                      data-testid="distance-calculator-menu"
                    >
                      <MapPin className="h-4 w-4 mr-2" />
                      Distance Calculator
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => { setShowDiscounts(true); fetchDiscounts(); }} 
                      className="text-green-300 focus:bg-slate-700 focus:text-green-200" 
                      data-testid="discounts-menu"
                    >
                      <Tag className="h-4 w-4 mr-2" />
                      Discount Codes
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => { setShowInstructorManagement(true); fetchInstructors(); }} 
                      className="text-teal-300 focus:bg-slate-700 focus:text-teal-200" 
                      data-testid="instructor-management-menu"
                    >
                      <Users className="h-4 w-4 mr-2" />
                      Instructor Management
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-slate-700" />
                    <DropdownMenuItem onClick={openClassTypesDialog} className="text-blue-300 focus:bg-slate-700 focus:text-blue-200" data-testid="manage-classes-menu">
                      <BookOpen className="h-4 w-4 mr-2" />
                      Manage Class Types
                    </DropdownMenuItem>
                    <DropdownMenuItem 
                      onClick={() => { setShowPriceList(true); fetchPriceListData(); }} 
                      className="text-yellow-300 focus:bg-slate-700 focus:text-yellow-200" 
                      data-testid="price-list-menu"
                    >
                      <DollarSign className="h-4 w-4 mr-2" />
                      Price List
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-slate-700" />
                    <DropdownMenuItem 
                      onClick={() => setShowSendQueue(true)} 
                      className="text-purple-300 focus:bg-slate-700 focus:text-purple-200" 
                      data-testid="send-queue-menu"
                    >
                      <Clock className="h-4 w-4 mr-2" />
                      Send Queue
                    </DropdownMenuItem>
                    <DropdownMenuSeparator className="bg-slate-700" />
                    <DropdownMenuItem 
                      onClick={forcePriceRefresh} 
                      disabled={priceRefreshLoading}
                      className="text-green-300 focus:bg-slate-700 focus:text-green-200" 
                      data-testid="force-price-refresh-menu"
                    >
                      {priceRefreshLoading ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <RefreshCw className="h-4 w-4 mr-2" />
                      )}
                      {priceRefreshLoading ? 'Refreshing Prices...' : 'Force Price Update'}
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
                
                {/* Settings Dropdown */}
                <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="outline" size="sm" className="border-teal-600 text-white hover:bg-slate-700" data-testid="settings-dropdown">
                    <Settings className="h-4 w-4 mr-2" />
                    Settings
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-slate-800 border-slate-700 w-56">
                  {/* Payment Mode Toggle */}
                  <div className="px-2 py-2">
                    <p className="text-xs text-slate-500 mb-2">Payment Mode</p>
                    <div className="flex gap-1">
                      <Button
                        onClick={() => togglePaymentMode('sandbox')}
                        size="sm"
                        disabled={paymentModeLoading}
                        className={`flex-1 text-xs ${paymentMode === 'sandbox' 
                          ? 'bg-teal-600 hover:bg-teal-500 text-white' 
                          : 'bg-slate-700 text-white hover:bg-slate-600'}`}
                      >
                        Sandbox
                      </Button>
                      <Button
                        onClick={() => togglePaymentMode('live')}
                        size="sm"
                        disabled={paymentModeLoading}
                        className={`flex-1 text-xs ${paymentMode === 'live' 
                          ? 'bg-teal-600 hover:bg-teal-500 text-white' 
                          : 'bg-slate-700 text-white hover:bg-slate-600'}`}
                      >
                        Live
                      </Button>
                    </div>
                    {paymentMode === 'sandbox' && (
                      <p className="text-xs text-amber-400 mt-1">Test card: 4111 1111 1111 1111</p>
                    )}
                  </div>
                  <DropdownMenuSeparator className="bg-slate-700" />
                  {/* eCard Checkout Test Mode Toggle */}
                  <div className="px-2 py-2">
                    <p className="text-xs text-slate-500 mb-2">eCard Checkout Mode</p>
                    <div className="flex gap-1">
                      <Button
                        onClick={() => toggleEcardTestMode(false)}
                        size="sm"
                        className={`flex-1 text-xs ${!ecardSupplierSettings.checkout_test_mode 
                          ? 'bg-green-600 hover:bg-green-500 text-white' 
                          : 'bg-slate-700 text-white hover:bg-slate-600'}`}
                        data-testid="ecard-live-mode-btn"
                      >
                        Live
                      </Button>
                      <Button
                        onClick={() => toggleEcardTestMode(true)}
                        size="sm"
                        className={`flex-1 text-xs ${ecardSupplierSettings.checkout_test_mode 
                          ? 'bg-amber-600 hover:bg-amber-500 text-white' 
                          : 'bg-slate-700 text-white hover:bg-slate-600'}`}
                        data-testid="ecard-test-mode-btn"
                      >
                        Test
                      </Button>
                    </div>
                    {ecardSupplierSettings.checkout_test_mode && (
                      <p className="text-xs text-amber-400 mt-1">Orders will be simulated</p>
                    )}
                  </div>
                  <DropdownMenuSeparator className="bg-slate-700" />
                  <DropdownMenuItem onClick={() => setShowSignInPasswordDialog(true)} className="text-emerald-300 focus:bg-slate-700 focus:text-emerald-200" data-testid="signin-password-menu">
                    <UserPlus className="h-4 w-4 mr-2" />
                    Class Sign-In Password
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => { setShowAhaCredentialsDialog(true); fetchAhaCredentials(); }} className="text-red-300 focus:bg-slate-700 focus:text-red-200" data-testid="aha-credentials-menu">
                    <Key className="h-4 w-4 mr-2" />
                    AHA Credentials
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => { setShowAhaProductLinksDialog(true); fetchAhaProductLinks(); fetchBlsTestLink(); }} className="text-purple-300 focus:bg-slate-700 focus:text-purple-200" data-testid="aha-product-links-menu">
                    <BookOpen className="h-4 w-4 mr-2" />
                    AHA Product Names
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => { setShowSupplierSettings(true); fetchEcardSupplierSettings(); }} className="text-cyan-300 focus:bg-slate-700 focus:text-cyan-200" data-testid="cpr-supplier-settings-menu">
                    <Store className="h-4 w-4 mr-2" />
                    CPR Suppliers Credentials
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => { setShowEnrollwareSettings(true); fetchEnrollwareSettings(); }} className="text-violet-300 focus:bg-slate-700 focus:text-violet-200" data-testid="enrollware-settings-menu">
                    <Upload className="h-4 w-4 mr-2" />
                    Enrollware Credentials
                    {enrollwareSettings.configured && <span className="ml-auto text-xs text-violet-400">●</span>}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => { setShowQboSettings(true); fetchQboStatus(); }} className="text-green-300 focus:bg-slate-700 focus:text-green-200" data-testid="quickbooks-settings-menu">
                    <TrendingUp className="h-4 w-4 mr-2" />
                    QuickBooks Integration
                    {qboStatus.connected && <span className="ml-auto text-xs text-green-400">●</span>}
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setShowProductionSync(true)} className="text-orange-300 focus:bg-slate-700 focus:text-orange-200" data-testid="production-sync-menu">
                    <Download className="h-4 w-4 mr-2" />
                    Sync from Production
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => { setShowEmailAttachments(true); fetchEmailAttachmentGroups(); }} className="text-amber-300 focus:bg-slate-700 focus:text-amber-200" data-testid="email-attachments-menu">
                    <Paperclip className="h-4 w-4 mr-2" />
                    Email Attachments
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => setShowEmailTemplateEditor(true)} className="text-orange-300 focus:bg-slate-700 focus:text-orange-200" data-testid="email-templates-menu">
                    <Mail className="h-4 w-4 mr-2" />
                    Email Templates
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => { setShowAdminList(true); fetchAdminList(); }} 
                    className="text-purple-300 focus:bg-slate-700 focus:text-purple-200" 
                    data-testid="admin-list-menu"
                  >
                    <UserPlus className="h-4 w-4 mr-2" />
                    Admin Notification List
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={refreshCachedPrices}
                    disabled={refreshingPrices}
                    className="text-green-300 focus:bg-slate-700 focus:text-green-200" 
                    data-testid="sync-prices-menu"
                  >
                    {refreshingPrices ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <RefreshCw className="h-4 w-4 mr-2" />
                    )}
                    {refreshingPrices ? 'Syncing Prices...' : 'Sync CPR Supplier Prices'}
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-slate-700" />
                  <DropdownMenuItem onClick={() => setShowPasswordChange(true)} className="text-white focus:bg-slate-700 focus:text-slate-100" data-testid="change-password-menu">
                    <Lock className="h-4 w-4 mr-2" />
                    Change Password
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-slate-700" />
                  <DropdownMenuItem onClick={handleLogout} className="text-red-400 focus:bg-slate-700 focus:text-red-300" data-testid="logout-menu">
                    <LogOut className="h-4 w-4 mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="text-white hover:text-teal-400 hover:bg-slate-700 flex items-center gap-2" data-testid="quick-jump-to-dropdown">
                    <Globe className="h-4 w-4" />
                    Quick Jump To
                    <ChevronDown className="h-4 w-4" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="bg-slate-800 border-slate-700">
                  <DropdownMenuItem 
                    onClick={() => window.location.href = '/'} 
                    className="text-white focus:bg-slate-700 focus:text-teal-300"
                    data-testid="jump-to-booking"
                  >
                    <Calendar className="h-4 w-4 mr-2" />
                    Booking Page
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => window.location.href = '/rentals'} 
                    className="text-orange-400 focus:bg-slate-700 focus:text-orange-300"
                    data-testid="jump-to-rentals"
                  >
                    <Package className="h-4 w-4 mr-2" />
                    Instructor Rentals
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => window.location.href = '/instructor-ecards'} 
                    className="text-purple-400 focus:bg-slate-700 focus:text-purple-300"
                    data-testid="jump-to-instructor-ecards"
                  >
                    <CreditCard className="h-4 w-4 mr-2" />
                    Instructor eCard Store
                  </DropdownMenuItem>
                  <DropdownMenuSeparator className="bg-slate-700" />
                  <DropdownMenuItem 
                    onClick={() => window.location.href = '/signin'} 
                    className="text-emerald-400 focus:bg-slate-700 focus:text-emerald-300"
                    data-testid="jump-to-signin"
                  >
                    <UserPlus className="h-4 w-4 mr-2" />
                    Sign In Page
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => window.location.href = '/clients'} 
                    className="text-teal-400 focus:bg-slate-700 focus:text-teal-300"
                    data-testid="jump-to-client-portal"
                  >
                    <Building2 className="h-4 w-4 mr-2" />
                    Client Portal
                  </DropdownMenuItem>
                  <DropdownMenuItem 
                    onClick={() => window.location.href = '/shop'} 
                    className="text-white focus:bg-slate-700 focus:text-orange-300"
                    data-testid="jump-to-store-page"
                  >
                    <Tag className="h-4 w-4 mr-2" />
                    Product Store
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
              </div>
              
              {/* Conditional Trigger Buttons Row - Only shows when there are items needing attention */}
              {(ecardNeedsSummary.total_needed > 0 || pendingInstructorOrdersCount > 0 || readyToFillOrdersCount > 0 || queueSummary.processing > 0 || queueSummary.failed > 0) && (
                <div className="flex items-center gap-2 flex-wrap w-full border-t border-slate-700 pt-2 mt-1">
                  {/* eCard Needs Indicator - Only shows when cards needed */}
                  {ecardNeedsSummary.total_needed > 0 && (
                    <TooltipProvider>
                      <Tooltip delayDuration={200}>
                        <TooltipTrigger asChild>
                          <Button
                            onClick={openEcardOrdering}
                            variant="outline"
                            size="sm"
                            className="border-orange-600 text-orange-400 hover:bg-orange-900/30 relative"
                            data-testid="ecard-needs-indicator"
                          >
                            <CreditCard className="h-4 w-4 mr-2" />
                            My eCards Needed
                            <span className="absolute -top-2 -right-2 bg-orange-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center animate-pulse">
                              {ecardNeedsSummary.total_needed > 99 ? '99+' : ecardNeedsSummary.total_needed}
                            </span>
                          </Button>
                        </TooltipTrigger>
                        <TooltipContent side="bottom" className="bg-slate-900 border-slate-700 p-3 max-w-xs">
                          <div className="space-y-2">
                            <p className="font-semibold text-orange-400 text-sm">eCards to Order:</p>
                            {ecardNeedsSummary.by_product.map((item, idx) => (
                              <div key={idx} className="flex justify-between gap-4 text-xs">
                                <span className="text-slate-300 truncate">{item.product}</span>
                                <span className="text-orange-300 font-medium">{item.count}</span>
                              </div>
                            ))}
                            <p className="text-xs text-slate-500 pt-1 border-t border-slate-700">Click to order</p>
                          </div>
                        </TooltipContent>
                      </Tooltip>
                    </TooltipProvider>
                  )}
                  
                  {/* Quick Access: Instructor eCard Orders - shows when there are actionable orders */}
                  {pendingInstructorOrdersCount > 0 && (
                    <Button
                      onClick={() => { setShowInstructorOrders(true); fetchInstructorOrders(); }}
                      variant="outline"
                      size="sm"
                      className="border-amber-600 text-amber-400 hover:bg-amber-900/30 relative"
                      data-testid="order-fulfillment-quick-btn"
                    >
                      <Package className="h-4 w-4 mr-2" />
                      Inst. eCard Orders
                      <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center animate-pulse">
                        {pendingInstructorOrdersCount > 9 ? '9+' : pendingInstructorOrdersCount}
                      </span>
                    </Button>
                  )}
                  
                  {/* Ready To Fill Orders - Prominent Blue Button */}
                  {readyToFillOrdersCount > 0 && (
                    <Button
                      onClick={() => { 
                        setShowInstructorOrders(true); 
                        fetchInstructorOrders(); 
                      }}
                      size="sm"
                      className="bg-blue-600 hover:bg-blue-500 text-white relative animate-pulse"
                      data-testid="ready-to-fill-orders-btn"
                    >
                      <Package className="h-4 w-4 mr-2" />
                      Ready To Fill Orders
                      <span className="ml-2 bg-white text-blue-600 text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                        {readyToFillOrdersCount > 9 ? '9+' : readyToFillOrdersCount}
                      </span>
                    </Button>
                  )}
                  
                  {/* Send Queue Button - Only shows when actively processing or has failed items */}
                  {(queueSummary.processing > 0 || queueSummary.failed > 0) && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setShowSendQueue(true)}
                      className={`relative ${queueSummary.failed > 0 ? 'border-red-600 text-red-400 hover:bg-red-900/30' : 'border-purple-600 text-purple-400 hover:bg-purple-900/30'}`}
                      data-testid="send-queue-btn"
                    >
                      {queueSummary.processing > 0 ? (
                        <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      ) : (
                        <AlertCircle className="h-4 w-4 mr-2" />
                      )}
                      {queueSummary.failed > 0 ? 'Send Queue (Failed)' : 'Sending...'}
                      <span className={`absolute -top-2 -right-2 ${queueSummary.failed > 0 ? 'bg-red-500' : 'bg-purple-500'} text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center`}>
                        {queueSummary.failed > 0 ? queueSummary.failed : queueSummary.processing}
                      </span>
                    </Button>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      </nav>

      <main className="container mx-auto px-4 md:px-6 max-w-7xl py-6 lg:py-12 overflow-x-hidden">
        {/* Stripe Balance Display Widget */}
        <div className="mb-6 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="stripe-balance-widget">
          {/* Card 1: Stripe Balance (Raw) */}
          <Card className="bg-gradient-to-br from-slate-800 to-slate-900 border-blue-700/50 overflow-hidden">
            <div className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="h-8 w-8 rounded-lg bg-blue-600/20 flex items-center justify-center">
                    <DollarSign className="h-5 w-5 text-blue-400" />
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-slate-400">Stripe Balance</h3>
                    {stripeBalanceData?.mode === 'sandbox' && (
                      <span className="text-xs text-amber-400 font-medium">SANDBOX</span>
                    )}
                  </div>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={fetchStripeBalance}
                  disabled={stripeBalanceLoading}
                  className="h-8 w-8 p-0 text-slate-400 hover:text-blue-400 hover:bg-slate-700/50"
                  data-testid="refresh-stripe-balance"
                >
                  <RefreshCw className={`h-4 w-4 ${stripeBalanceLoading ? 'animate-spin' : ''}`} />
                </Button>
              </div>
              {stripeBalanceLoading && !stripeBalanceData ? (
                <div className="flex items-center gap-2 text-slate-400">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-sm">Loading...</span>
                </div>
              ) : stripeBalanceError ? (
                <div>
                  <div className="text-sm text-amber-400 mb-2">{stripeBalanceError}</div>
                  {stripeBalanceData?.help_steps && (
                    <div className="text-xs text-slate-500 space-y-0.5">
                      {stripeBalanceData.help_steps.map((step, idx) => (
                        <div key={idx}>{step}</div>
                      ))}
                    </div>
                  )}
                </div>
              ) : stripeBalanceData?.success ? (
                <div>
                  <div className="text-3xl font-bold text-blue-400" data-testid="stripe-raw-balance">
                    ${stripeBalanceData.stripe_available?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
                  </div>
                  <p className="text-xs text-amber-500/80 mt-1">
                    ⚠️ Can be up to 3 hours delayed
                  </p>
                </div>
              ) : (
                <div className="text-slate-500 text-sm">Click refresh to load</div>
              )}
            </div>
          </Card>

          {/* Card 2: Available Total (After Deductions) */}
          <Card className="bg-gradient-to-br from-slate-800 to-slate-900 border-emerald-700/50 overflow-hidden">
            <div className="p-4">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <div className="h-8 w-8 rounded-lg bg-emerald-600/20 flex items-center justify-center">
                    <TrendingUp className="h-5 w-5 text-emerald-400" />
                  </div>
                  <h3 className="text-sm font-medium text-slate-400">Available Total</h3>
                </div>
                <TooltipProvider>
                  <Tooltip delayDuration={200}>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0 text-slate-400 hover:text-emerald-400 hover:bg-slate-700/50"
                      >
                        <Info className="h-4 w-4" />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="bg-slate-900 border-slate-700 p-3 max-w-sm">
                      <div className="space-y-2 text-xs">
                        <p className="font-semibold text-emerald-400">Calculation:</p>
                        <div className="space-y-1">
                          <div className="flex justify-between">
                            <span className="text-slate-400">Stripe Balance:</span>
                            <span className="text-blue-400">${stripeBalanceData?.stripe_available?.toFixed(2) || '0.00'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-400">- Deposits Held:</span>
                            <span className="text-amber-400">${stripeBalanceData?.deposits_held?.toFixed(2) || '0.00'}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-400">- eCards Pending ({stripeBalanceData?.ecards_pending_count || 0}):</span>
                            <span className="text-purple-400">${stripeBalanceData?.ecards_pending_cost?.toFixed(2) || '0.00'}</span>
                          </div>
                          <div className="border-t border-slate-700 pt-1 flex justify-between font-semibold">
                            <span className="text-slate-300">= Available:</span>
                            <span className="text-emerald-400">${stripeBalanceData?.net_available?.toFixed(2) || '0.00'}</span>
                          </div>
                        </div>
                        <p className="text-slate-500 pt-1 text-[10px]">{stripeBalanceData?.note || ''}</p>
                      </div>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
              {stripeBalanceLoading && !stripeBalanceData ? (
                <div className="flex items-center gap-2 text-slate-400">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  <span className="text-sm">Loading...</span>
                </div>
              ) : stripeBalanceError ? (
                <div className="text-sm text-red-400">--</div>
              ) : stripeBalanceData ? (
                <div>
                  <div className={`text-3xl font-bold ${stripeBalanceData.net_available >= 0 ? 'text-emerald-400' : 'text-red-400'}`} data-testid="stripe-net-available">
                    ${stripeBalanceData.net_available?.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || '0.00'}
                  </div>
                  <div className="flex items-center gap-3 mt-1 text-xs">
                    <span className="text-amber-400">
                      Deposits: ${stripeBalanceData.deposits_held?.toFixed(2) || '0.00'}
                    </span>
                    <span className="text-purple-400">
                      eCards: ${stripeBalanceData.ecards_pending_cost?.toFixed(2) || '0.00'}
                    </span>
                  </div>
                </div>
              ) : (
                <div className="text-slate-500 text-sm">--</div>
              )}
            </div>
          </Card>

          {/* Card 3: Client Portal Billing Toggle */}
          <Card className="bg-gradient-to-br from-slate-800 to-slate-900 border-purple-700/50 overflow-hidden md:col-span-2 lg:col-span-1">
            <div className="p-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <div className="h-8 w-8 rounded-lg bg-purple-600/20 flex items-center justify-center">
                    <CreditCard className="h-5 w-5 text-purple-400" />
                  </div>
                  <div>
                    <h3 className="text-sm font-medium text-slate-400">Client Billing</h3>
                    <p className="text-xs text-slate-500">Saved Cards & Auto-Pay</p>
                  </div>
                </div>
                <Button
                  variant={vaultEnabled ? "default" : "outline"}
                  size="sm"
                  onClick={toggleVaultEnabled}
                  disabled={vaultToggleLoading}
                  className={vaultEnabled 
                    ? "bg-purple-600 hover:bg-purple-700 text-white"
                    : "border-slate-600 text-slate-400 hover:bg-slate-700"}
                  data-testid="toggle-vault-enabled"
                >
                  {vaultToggleLoading ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-1" />
                  ) : null}
                  {vaultEnabled ? 'Enabled' : 'Disabled'}
                </Button>
              </div>
              <p className="text-xs text-slate-500 mt-3">
                {vaultEnabled 
                  ? 'Clients can save payment methods in the Client Portal.'
                  : 'Clients see "Coming Soon" in the Billing tab.'}
              </p>
            </div>
          </Card>
        </div>

        <div className="mb-8">
          <h2 className="text-2xl lg:text-3xl font-bold text-slate-100 mb-2 font-heading" data-testid="dashboard-heading">Booking Management</h2>
          <p className="text-white text-sm lg:text-base">Review and manage CPR class booking requests</p>
        </div>

        <div className="mb-6">
          {/* Weekly Calendar View */}
          <Card className="bg-slate-800 border-slate-700" data-testid="weekly-calendar">
            <div className="p-4 border-b border-slate-700">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <h3 className="text-lg font-semibold text-slate-100 flex items-center gap-2">
                    <Calendar className="h-5 w-5 text-teal-400" />
                    Weekly Calendar
                  </h3>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigateWeek(-1)}
                      className="border-teal-600 text-white hover:bg-slate-700 h-8 w-8 p-0"
                    >
                      <ArrowLeft className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={goToCurrentWeek}
                      className="border-teal-600 text-white hover:bg-slate-700 text-xs px-2"
                    >
                      Today
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => navigateWeek(1)}
                      className="border-teal-600 text-white hover:bg-slate-700 h-8 w-8 p-0"
                    >
                      <ArrowRight className="h-4 w-4" />
                    </Button>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-white text-sm">
                      {(() => {
                        const startDate = new Date(calendarWeekStart);
                        const endDate = new Date(calendarWeekStart);
                        endDate.setDate(endDate.getDate() + 6);
                        const formatOptions = { month: 'short', day: 'numeric' };
                        const formatOptionsYear = { month: 'short', day: 'numeric', year: 'numeric' };
                        return `${startDate.toLocaleDateString('en-US', formatOptions)} - ${endDate.toLocaleDateString('en-US', formatOptionsYear)}`;
                      })()}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => fetchCalendarEvents(calendarWeekStart)}
                      className="h-7 w-7 p-0 text-white hover:text-teal-400 hover:bg-slate-700"
                      title="Refresh calendar"
                      data-testid="refresh-calendar-btn"
                    >
                      <RefreshCw className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                
                {/* Calendar Filter */}
                {availableCalendars.length > 0 && (
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="outline" size="sm" className="border-teal-600 text-white text-xs">
                        <Globe className="h-3 w-3 mr-1" />
                        Calendars ({selectedCalendars.length === availableCalendars.length ? 'All' : selectedCalendars.length})
                        <ChevronDown className="h-3 w-3 ml-1" />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent className="bg-slate-800 border-slate-700">
                      <DropdownMenuItem
                        onClick={() => setSelectedCalendars(availableCalendars.map(c => c.id))}
                        className="text-white focus:bg-slate-700"
                      >
                        <CheckCircle className={`h-3 w-3 mr-2 ${selectedCalendars.length === availableCalendars.length ? 'text-teal-400' : 'text-transparent'}`} />
                        Show All
                      </DropdownMenuItem>
                      <div className="border-t border-teal-600 my-1"></div>
                      {availableCalendars.map(cal => (
                        <DropdownMenuItem
                          key={cal.id}
                          onClick={() => {
                            if (selectedCalendars.includes(cal.id)) {
                              setSelectedCalendars(selectedCalendars.filter(id => id !== cal.id));
                            } else {
                              setSelectedCalendars([...selectedCalendars, cal.id]);
                            }
                          }}
                          className="text-white focus:bg-slate-700"
                        >
                          <div 
                            className="w-3 h-3 rounded mr-2" 
                            style={{ backgroundColor: cal.color || '#4285f4' }}
                          ></div>
                          <span className="flex-1 truncate">{cal.name}</span>
                          {selectedCalendars.includes(cal.id) && <CheckCircle className="h-3 w-3 text-teal-400 ml-2" />}
                        </DropdownMenuItem>
                      ))}
                    </DropdownMenuContent>
                  </DropdownMenu>
                )}
                
                {/* Show Only Classes & Rentals Filter */}
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setShowOnlyClassesAndRentals(!showOnlyClassesAndRentals)}
                  className={`text-xs ${showOnlyClassesAndRentals 
                    ? 'bg-teal-600 border-teal-500 text-white hover:bg-teal-500' 
                    : 'border-slate-600 text-slate-300 hover:bg-slate-700'}`}
                  data-testid="toggle-classes-rentals-filter"
                >
                  <Filter className="h-3 w-3 mr-1" />
                  {showOnlyClassesAndRentals ? 'Classes & Rentals Only' : 'Show All Events'}
                </Button>
              </div>
            </div>
            
            {/* Calendar Grid */}
            <div className="overflow-x-auto">
              {calendarLoading ? (
                <div className="flex items-center justify-center py-12">
                  <Loader2 className="h-6 w-6 animate-spin text-teal-400" />
                  <span className="ml-2 text-white">Loading calendar...</span>
                </div>
              ) : !calendarConnected ? (
                <div className="flex flex-col items-center justify-center py-12 text-white">
                  <Calendar className="h-12 w-12 mb-4 text-slate-600" />
                  <p>Connect Google Calendar to see your schedule</p>
                  <Button 
                    onClick={connectGoogleCalendar}
                    className="mt-4 bg-teal-600 hover:bg-teal-500"
                    size="sm"
                  >
                    Connect Calendar
                  </Button>
                </div>
              ) : (
                <div className="grid grid-cols-7 min-w-[800px]">
                  {/* Day Headers - Dynamic based on start date */}
                  {[0, 1, 2, 3, 4, 5, 6].map((idx) => {
                    const dayDate = new Date(calendarWeekStart);
                    dayDate.setDate(dayDate.getDate() + idx);
                    const isToday = dayDate.toDateString() === new Date().toDateString();
                    const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
                    const dayName = dayNames[dayDate.getDay()];
                    return (
                      <div 
                        key={idx} 
                        className={`p-2 text-center border-b border-r border-slate-700 ${isToday ? 'bg-teal-900/30' : ''}`}
                      >
                        <div className={`text-xs font-medium ${isToday ? 'text-teal-400' : 'text-white'}`}>{dayName}</div>
                        <div className={`text-lg font-semibold ${isToday ? 'text-teal-300' : 'text-slate-200'}`}>
                          {dayDate.getDate()}
                        </div>
                      </div>
                    );
                  })}
                  
                  {/* Event Cells */}
                  {[0, 1, 2, 3, 4, 5, 6].map(dayOffset => {
                    const dayDate = new Date(calendarWeekStart);
                    dayDate.setDate(dayDate.getDate() + dayOffset);
                    const dayStart = new Date(dayDate);
                    dayStart.setHours(0, 0, 0, 0);
                    const dayEnd = new Date(dayDate);
                    dayEnd.setHours(23, 59, 59, 999);
                    
                    // Format current day as YYYY-MM-DD for all-day event comparison
                    const dayStr = dayDate.toISOString().split('T')[0];
                    
                    // Filter events for this day
                    // Show all events if no calendar filter is set, otherwise filter by selected calendars
                    const dayEvents = calendarEvents.filter(event => {
                      // If we have calendar filters and this event's calendar isn't selected, skip it
                      if (selectedCalendars.length > 0 && availableCalendars.length > 0 && !selectedCalendars.includes(event.calendar_id)) {
                        return false;
                      }
                      
                      // Apply "Classes & Rentals Only" filter
                      if (showOnlyClassesAndRentals) {
                        const title = (event.title || '').toLowerCase();
                        const isCprClass = title.includes('cpr class') || 
                                          title.includes('basic life support') ||
                                          title.includes('heartsaver') ||
                                          title.includes('bls') ||
                                          title.includes('acls') ||
                                          title.includes('pals') ||
                                          title.includes('first aid');
                        const isRentalPickupDropoff = title.includes('pickup') || 
                                                       title.includes('drop off') || 
                                                       title.includes('drop-off') ||
                                                       title.includes('dropoff') ||
                                                       title.includes('rental');
                        if (!isCprClass && !isRentalPickupDropoff) {
                          return false;
                        }
                      }
                      
                      // Check if this is an all-day event
                      const isAllDayEvent = event.is_all_day || (event.start && !event.start.includes('T'));
                      
                      if (isAllDayEvent) {
                        // For all-day events, compare the date string directly
                        // All-day event start is just a date like "2026-05-07"
                        const eventDateStr = event.start?.split('T')[0] || event.start;
                        return eventDateStr === dayStr;
                      } else {
                        // For timed events, use the normal time-based comparison
                        const eventStart = new Date(event.start);
                        return eventStart >= dayStart && eventStart <= dayEnd;
                      }
                    }).sort((a, b) => new Date(a.start) - new Date(b.start));
                    
                    const isToday = dayDate.toDateString() === new Date().toDateString();
                    
                    // Check if this is a FULL DAY blackout (only all-day blackout events)
                    const isFullDayBlackout = dayEvents.some(event => {
                      if (!event.title?.toLowerCase().includes('blackout')) return false;
                      // Only count as full-day blackout if it's an all-day event
                      const isAllDayEvent = event.is_all_day || (event.start && !event.start.includes('T'));
                      if (isAllDayEvent) {
                        const eventDateStr = event.start?.split('T')[0] || event.start;
                        return eventDateStr === dayStr;
                      }
                      return false; // Timed blackout events don't make it a full-day blackout
                    });
                    
                    return (
                      <div 
                        key={dayOffset} 
                        className={`min-h-[120px] p-1 border-r border-slate-700 ${isToday ? 'bg-teal-900/10' : ''} ${isFullDayBlackout ? 'bg-slate-900' : ''}`}
                      >
                        {isFullDayBlackout ? (
                          <div className="h-full flex items-center justify-center">
                            <div className="text-center">
                              <div className="text-2xl mb-1">🚫</div>
                              <div className="text-sm font-bold text-white uppercase tracking-wider">Blackout</div>
                              <div className="text-sm font-bold text-white uppercase tracking-wider">Day</div>
                            </div>
                          </div>
                        ) : dayEvents.length === 0 ? (
                          <div className="text-xs text-slate-600 text-center mt-4">No events</div>
                        ) : (
                          <div className="space-y-1">
                            {dayEvents.slice(0, 4).map((event, idx) => {
                              // Determine event type and color based on title
                              const title = event.title || '';
                              const isCprClass = title.toLowerCase().includes('cpr class') || 
                                                 title.toLowerCase().includes('basic life support') ||
                                                 title.toLowerCase().includes('heartsaver') ||
                                                 title.toLowerCase().includes('bls') ||
                                                 title.toLowerCase().includes('acls') ||
                                                 title.toLowerCase().includes('pals');
                              
                              let bgColor, borderColor, textColor, timeColor;
                              
                              if (isCprClass) {
                                // Check status from title
                                if (title.includes('*Approved*') || title.includes('Approved')) {
                                  // Green for approved
                                  bgColor = 'rgba(34, 197, 94, 0.25)';
                                  borderColor = '#22c55e';
                                  textColor = '#bbf7d0';
                                  timeColor = '#86efac';
                                } else if (title.includes('*Pending*') || title.includes('Pending')) {
                                  // Yellow for pending
                                  bgColor = 'rgba(234, 179, 8, 0.25)';
                                  borderColor = '#eab308';
                                  textColor = '#fef08a';
                                  timeColor = '#fde047';
                                } else if (title.includes('*Rejected*') || title.includes('Rejected') || title.includes('Reschedule')) {
                                  // Red for rejected/reschedule
                                  bgColor = 'rgba(239, 68, 68, 0.25)';
                                  borderColor = '#ef4444';
                                  textColor = '#fecaca';
                                  timeColor = '#fca5a5';
                                } else {
                                  // Default green for CPR classes without explicit status
                                  bgColor = 'rgba(34, 197, 94, 0.25)';
                                  borderColor = '#22c55e';
                                  textColor = '#bbf7d0';
                                  timeColor = '#86efac';
                                }
                              } else {
                                // Light blue for non-booking events
                                bgColor = 'rgba(56, 189, 248, 0.2)';
                                borderColor = '#38bdf8';
                                textColor = '#e0f2fe';
                                timeColor = '#bae6fd';
                              }
                              
                              // Check if this is a timed blackout event
                              const isTimedBlackout = title.toLowerCase().includes('blackout') && !event.is_all_day && event.start?.includes('T');
                              if (isTimedBlackout) {
                                // Dark red/black for timed blackout events
                                bgColor = 'rgba(127, 29, 29, 0.6)';
                                borderColor = '#991b1b';
                                textColor = '#fecaca';
                                timeColor = '#fca5a5';
                              }
                              
                              // Light red for events containing "shift" in title
                              const isShiftEvent = title.toLowerCase().includes('shift');
                              if (isShiftEvent) {
                                bgColor = 'rgba(239, 68, 68, 0.4)';  // More visible red background
                                borderColor = '#dc2626';  // Red-600 border for more contrast
                                textColor = '#fecaca';  // Light red text
                                timeColor = '#fca5a5';  // Light red time
                              }
                              
                              // Extract booking ID from description if it's a CPR class
                              const bookingIdMatch = event.description?.match(/Booking ID:\s*([a-zA-Z0-9-]+)/);
                              const bookingId = isCprClass && bookingIdMatch ? bookingIdMatch[1] : null;
                              
                              const handleEventClick = () => {
                                if (bookingId) {
                                  // Find the booking card and scroll to it
                                  const bookingElement = document.querySelector(`[data-booking-id="${bookingId}"]`);
                                  if (bookingElement) {
                                    bookingElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
                                    // Flash highlight effect
                                    bookingElement.classList.add('ring-2', 'ring-teal-400', 'ring-offset-2', 'ring-offset-slate-900');
                                    setTimeout(() => {
                                      bookingElement.classList.remove('ring-2', 'ring-teal-400', 'ring-offset-2', 'ring-offset-slate-900');
                                    }, 2000);
                                  }
                                }
                              };
                              
                              return (
                                <div 
                                  key={event.id || idx}
                                  className={`text-xs p-1.5 rounded ${isCprClass ? 'cursor-pointer hover:opacity-80' : 'cursor-default'}`}
                                  style={{ 
                                    backgroundColor: bgColor,
                                    borderLeft: `3px solid ${borderColor}`
                                  }}
                                  title={`${event.title}\n${formatInTimeZone(new Date(event.start), 'America/Los_Angeles', 'h:mm a')} - ${formatInTimeZone(new Date(event.end), 'America/Los_Angeles', 'h:mm a')}`}
                                  onClick={isCprClass ? handleEventClick : undefined}
                                >
                                  <div 
                                    className="font-medium truncate" 
                                    style={{ color: textColor }}
                                  >
                                    {event.title}
                                  </div>
                                  <div style={{ color: timeColor }}>
                                    {event.is_all_day ? 'All day' : `${formatInTimeZone(new Date(event.start), 'America/Los_Angeles', 'h:mm a')} - ${formatInTimeZone(new Date(event.end), 'America/Los_Angeles', 'h:mm a')}`}
                                  </div>
                                </div>
                              );
                            })}
                            {dayEvents.length > 4 && (
                              <div className="text-xs text-slate-500 text-center">
                                +{dayEvents.length - 4} more
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          </Card>
        </div>

        <Card className="bg-slate-800 rounded-xl border border-slate-700 shadow-sm mb-6 p-6" data-testid="filter-card">
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div className="flex flex-wrap gap-3">
              {['all', 'public', 'pending', 'approved', 'partially_paid', 'rejected', 'hidden'].map((status) => (
                <Button
                  key={status}
                  onClick={() => setFilter(status)}
                  variant={filter === status ? 'default' : 'outline'}
                  className={filter === status 
                    ? status === 'hidden' 
                      ? 'bg-amber-500 hover:bg-amber-400 text-slate-900' 
                      : status === 'public'
                        ? 'bg-purple-500 hover:bg-purple-400 text-white'
                        : status === 'partially_paid'
                          ? 'bg-orange-500 hover:bg-orange-400 text-slate-900'
                          : 'bg-teal-500 hover:bg-teal-400 text-slate-900' 
                    : status === 'public'
                      ? 'border-teal-600 text-white hover:bg-purple-900/30'
                      : status === 'partially_paid'
                        ? 'border-orange-600 text-orange-400 hover:bg-orange-900/30'
                        : 'border-teal-600 text-white hover:bg-slate-700'}
                  data-testid={`filter-${status}`}
                >
                  {status === 'partially_paid' ? 'Partially Paid' : status.charAt(0).toUpperCase() + status.slice(1)}
                  <Badge className={`ml-2 ${status === 'hidden' && filter === status ? 'bg-amber-700/50' : status === 'public' && filter === status ? 'bg-purple-700/50' : status === 'partially_paid' && filter === status ? 'bg-orange-700/50' : 'bg-white/20'}`}>
                    {status === 'all' 
                      ? (showPastClasses ? pastClassesCount : currentClassesCount)
                      : status === 'public'
                        ? bookings.filter(b => b.is_public && (showPastClasses ? isPastClass(b) : !isPastClass(b))).length
                        : status === 'rejected'
                          ? bookings.filter(b => (b.status === 'rejected' || b.status === 'rejected_no_refund') && (showPastClasses ? isPastClass(b) : !isPastClass(b))).length
                          : bookings.filter(b => b.status === status && (showPastClasses ? isPastClass(b) : !isPastClass(b))).length}
                  </Badge>
                </Button>
              ))}
              
              {/* Add Public Class button - only shown when on public filter */}
              {filter === 'public' && (
                <Button
                  onClick={() => setShowCreatePublicClass(true)}
                  size="sm"
                  className="bg-teal-600 hover:bg-teal-500 text-white ml-2"
                  data-testid="add-public-class-btn"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Public Class
                </Button>
              )}
            </div>
            
            {/* Date Search */}
            <div className="flex items-center gap-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-white" />
                <Input
                  type="date"
                  value={searchDate}
                  onChange={(e) => setSearchDate(e.target.value)}
                  className="pl-10 pr-10 bg-slate-700 border-teal-600 text-slate-200 rounded-full w-48"
                  data-testid="search-date-input"
                />
                {searchDate && (
                  <button
                    onClick={() => setSearchDate('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-white hover:text-slate-200"
                    data-testid="clear-search-date"
                  >
                    <X className="h-4 w-4" />
                  </button>
                )}
              </div>
            </div>
            
            <Button
              onClick={() => setCalendarViewMode(!calendarViewMode)}
              variant="outline"
              className={calendarViewMode 
                ? 'bg-blue-900/50 border-blue-600 text-blue-300 hover:bg-blue-900/70' 
                : 'border-slate-600 text-slate-400 hover:bg-slate-700'}
              data-testid="toggle-calendar-view"
            >
              <Calendar className="h-4 w-4 mr-2" />
              Calendar View
            </Button>
            
            <Button
              onClick={() => setShowPastClasses(!showPastClasses)}
              variant="outline"
              disabled={calendarViewMode}
              className={showPastClasses 
                ? 'bg-amber-900/50 border-amber-600 text-amber-300 hover:bg-amber-900/70' 
                : calendarViewMode 
                  ? 'border-slate-700 text-slate-500 cursor-not-allowed'
                  : 'border-teal-600 text-white hover:bg-slate-700'}
              data-testid="toggle-past-classes"
            >
              <Clock className="h-4 w-4 mr-2" />
              {showPastClasses ? 'View Current Classes' : 'View Past Classes'}
              <Badge className="ml-2 bg-white/20">
                {showPastClasses ? currentClassesCount : pastClassesCount}
              </Badge>
            </Button>
            
            <Button
              onClick={() => setShowCreateBooking(true)}
              className="bg-teal-500 hover:bg-teal-400 text-slate-900"
              data-testid="create-booking-button"
            >
              <Plus className="h-4 w-4 mr-2" />
              Create Booking
            </Button>
          </div>
          
          {searchDate && (
            <div className="mt-4 p-3 bg-cyan-900/30 border border-cyan-700 rounded-lg">
              <p className="text-sm text-cyan-300 flex items-center gap-2">
                <Search className="h-4 w-4" />
                Showing classes on {format(new Date(searchDate + 'T00:00:00'), 'MMMM d, yyyy')}
                <span className="text-cyan-400">({filteredBookings.length} found)</span>
              </p>
            </div>
          )}
          
          {calendarViewMode && !searchDate && (
            <div className="mt-4 p-3 bg-blue-900/30 border border-blue-700 rounded-lg">
              <p className="text-sm text-blue-300 flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Showing classes for the week of {format(new Date(calendarWeekStart), 'MMMM d, yyyy')}
                <span className="text-blue-400">({filteredBookings.length} found)</span>
                <span className="text-blue-400/70 text-xs ml-2">• Use calendar arrows to navigate weeks</span>
              </p>
            </div>
          )}
          
          {showPastClasses && !searchDate && !calendarViewMode && (
            <div className="mt-4 p-3 bg-amber-900/30 border border-amber-700 rounded-lg">
              <p className="text-sm text-amber-300 flex items-center gap-2">
                <Clock className="h-4 w-4" />
                Showing past classes (ended more than 6 hours ago)
              </p>
            </div>
          )}
        </Card>

        {!calendarConnected && (
          <Card className="bg-blue-900/30 rounded-xl border border-teal-600 shadow-sm p-6 mb-6" data-testid="calendar-connect-card">
            <div className="flex flex-col gap-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Calendar className="h-8 w-8 text-white" />
                  <div>
                    <h3 className="font-semibold text-blue-300">Connect Google Calendar</h3>
                    <p className="text-sm text-white">Enable two-way sync to automatically add approved bookings to your calendar</p>
                  </div>
                </div>
                <Button
                  onClick={connectGoogleCalendar}
                  className="bg-teal-600 hover:bg-teal-500 text-white rounded-full"
                  data-testid="connect-calendar-button"
                >
                  Connect Now
                </Button>
              </div>
              
              {showAuthUrl && (
                <div className="bg-slate-800 rounded-lg p-4 border border-teal-600">
                  <p className="text-sm text-blue-300 mb-2 font-medium">
                    Copy this link and open it in a new browser tab:
                  </p>
                  <div className="flex gap-2">
                    <input
                      type="text"
                      value={authUrl}
                      readOnly
                      className="flex-1 px-3 py-2 text-xs border border-teal-600 rounded bg-slate-700 text-slate-200 font-mono"
                    />
                    <Button
                      onClick={copyAuthUrl}
                      variant="outline"
                      className="border-teal-600 text-white"
                    >
                      Copy Link
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </Card>
        )}

        {loading ? (
          <div className="text-center py-16" data-testid="loading-indicator">
            <div className="animate-pulse text-slate-500">Loading bookings...</div>
          </div>
        ) : filteredBookings.length === 0 ? (
          <Card className="bg-slate-800 rounded-xl border border-slate-700 shadow-sm p-12 text-center" data-testid="no-bookings-card">
            <Calendar className="h-12 w-12 text-slate-600 mx-auto mb-4" />
            <p className="text-white">No bookings found</p>
          </Card>
        ) : (
          <div className="space-y-4">
            {/* Bookings info bar */}
            <div className="flex items-center justify-between text-sm text-white">
              <span>
                Showing {startIndex + 1}-{Math.min(endIndex, filteredBookings.length)} of {filteredBookings.length} bookings
              </span>
              <span>Sorted by class date (next class first)</span>
            </div>
            
            <div className="grid gap-4" data-testid="bookings-list">
            {paginatedBookings.map((booking) => {
              const isExpanded = expandedBookingId === booking.id;
              
              return (
              <Card 
                key={booking.id} 
                className={`bg-slate-800 rounded-xl border shadow-sm overflow-hidden transition-all duration-300 ${isExpanded ? 'border-cyan-600' : 'border-slate-700 hover:border-teal-600'}`}
                data-testid={`booking-card-${booking.id}`} 
                data-booking-id={booking.id}
              >
                {/* Collapsed Header - Always Visible */}
                <div 
                  className={`px-4 py-3 cursor-pointer transition-colors ${isExpanded ? 'bg-slate-700/50' : 'hover:bg-slate-700/30'}`}
                  onClick={() => setExpandedBookingId(isExpanded ? null : booking.id)}
                  data-testid={`booking-header-${booking.id}`}
                >
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center gap-3 flex-wrap flex-1 min-w-0">
                      {/* Expand/Collapse Icon */}
                      <ChevronDown className={`h-5 w-5 text-white transition-transform flex-shrink-0 ${isExpanded ? 'rotate-180' : ''}`} />
                      
                      {/* Class Type */}
                      <div className="flex items-center gap-2 min-w-0">
                        {booking.original_class_type_name && booking.original_class_type_name !== booking.class_type_name && (
                          <span className="text-sm text-red-400/60 line-through truncate hidden sm:inline">
                            {booking.original_class_type_name}
                          </span>
                        )}
                        <span className={`font-semibold truncate ${booking.original_class_type_name && booking.original_class_type_name !== booking.class_type_name ? 'text-white' : 'text-slate-100'}`}>
                          {booking.class_type_name}
                        </span>
                      </div>
                      
                      {/* Date & Time */}
                      <div className="flex items-center gap-1 text-sm text-white flex-shrink-0">
                        <Calendar className="h-4 w-4 text-slate-500" />
                        <span className="hidden sm:inline">{formatInTimeZone(new Date(booking.start_time), 'America/Los_Angeles', 'MMM d, yyyy')}</span>
                        <span className="sm:hidden">{formatInTimeZone(new Date(booking.start_time), 'America/Los_Angeles', 'M/d')}</span>
                        <span className="text-slate-500">@</span>
                        <span>{formatInTimeZone(new Date(booking.start_time), 'America/Los_Angeles', 'h:mm a')}</span>
                      </div>
                    </div>
                    
                    <div className="flex items-center gap-2 flex-shrink-0 flex-wrap justify-end">
                      {/* Status Badge */}
                      {getStatusBadge(booking.status)}
                      
                      {/* Payment Status - compact */}
                      {!booking.is_public && (
                        <Badge className={`text-xs ${booking.payment_status === 'paid' ? 'bg-emerald-900/50 text-white border-emerald-700' : 'bg-red-900/50 text-red-400 border-red-700'} border`}>
                          {booking.payment_status === 'paid' ? <DollarSign className="h-3 w-3" /> : <AlertCircle className="h-3 w-3" />}
                        </Badge>
                      )}
                      
                      {/* Calendar Status - compact */}
                      {booking.calendar_event_id ? (
                        <Badge className="bg-blue-900/50 text-white border-teal-600 border text-xs">
                          <Calendar className="h-3 w-3" />
                        </Badge>
                      ) : (
                        <Badge className="bg-slate-700/50 text-slate-500 border-teal-600 border text-xs">
                          <Calendar className="h-3 w-3" />
                        </Badge>
                      )}
                      
                      {/* Student Count */}
                      <Badge className="bg-slate-700/50 text-white border-teal-600 border text-xs">
                        <Users className="h-3 w-3 mr-1" />
                        {booking.is_public ? `${booking.students?.length || 0}/${booking.max_students || 10}` : (booking.students?.length || 0)}
                      </Badge>
                      
                      {/* Public indicator */}
                      {booking.is_public && (
                        <Badge className="bg-purple-900/50 text-white border-purple-700 border text-xs">
                          <Globe className="h-3 w-3" />
                        </Badge>
                      )}
                      
                      {/* Registration Closed indicator */}
                      {booking.registration_closed && (
                        <Badge className="bg-red-900/50 text-red-400 border-red-700 border text-xs" title="Registration is closed for this class">
                          <EyeOff className="h-3 w-3" />
                        </Badge>
                      )}
                      
                      {/* Recurring Client indicator */}
                      {booking.recurring_client_id && (
                        <Badge 
                          className="bg-blue-700/50 text-blue-200 border-blue-500 border text-xs cursor-pointer hover:bg-blue-600/50 transition-colors" 
                          title={`Click to view: ${booking.recurring_client_name || 'Unknown'}${booking.recurring_client_company ? ` (${booking.recurring_client_company})` : ''}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            openRecurringClientDetails(booking.recurring_client_id);
                          }}
                          data-testid={`rc-badge-${booking.id}`}
                        >
                          <Users className="h-3 w-3" />
                          <span className="ml-1">
                            {booking.recurring_client_company 
                              ? booking.recurring_client_company 
                              : (booking.recurring_client_name || 'RC')}
                          </span>
                        </Badge>
                      )}
                      
                      {/* Auto-Invoice indicator */}
                      {booking.recurring_client_id && booking.auto_invoice && !booking.linked_invoice_id && booking.payment_status !== 'paid' && (
                        <Badge 
                          className="bg-amber-700/50 text-amber-200 border-amber-500 border text-xs"
                          title="Auto-invoice will be sent 48hrs before class"
                        >
                          <Clock className="h-3 w-3" />
                          <span className="ml-1">Auto-Inv</span>
                        </Badge>
                      )}
                    </div>
                  </div>
                </div>
                
                {/* Expanded Content */}
                {isExpanded && (
                <div className="p-4 sm:p-6 border-t border-slate-700">
                  <div className="flex flex-col lg:flex-row lg:items-start justify-between gap-4">
                    <div className="flex-1 space-y-3">
                      <div className="flex flex-wrap items-center gap-2">
                        {getStatusBadge(booking.status)}
                        {/* Hide booking-level payment badge for public classes (they track per-student) */}
                        {!booking.is_public && getPaymentBadge(booking.payment_status, booking.woo_order_id)}
                        {booking.calendar_event_id && (
                          <Badge className="bg-blue-900/50 text-white border-teal-600 border flex items-center gap-1">
                            <Calendar className="h-3 w-3" />
                            On Calendar
                          </Badge>
                        )}
                        {booking.enrollware_class_id && (
                          <Badge className="bg-purple-900/50 text-purple-300 border-purple-600 border flex items-center gap-1" title={`Enrollware Class ID: ${booking.enrollware_class_id}`}>
                            <Upload className="h-3 w-3" />
                            Enrollware
                          </Badge>
                        )}
                        {booking.reminder_sent && (
                          <Badge className="bg-amber-900/50 text-amber-400 border-amber-700 border flex items-center gap-1">
                            <Mail className="h-3 w-3" />
                            Reminder Sent
                          </Badge>
                        )}
                        {/* Add-on Class Fee Prepaid Badge */}
                        {booking.addon_payment_waived && (
                          <Badge className="bg-emerald-900/50 text-emerald-400 border-emerald-700 border flex items-center gap-1" title="Class fee is prepaid for add-on students (add-ons still require payment)">
                            <DollarSign className="h-3 w-3" />
                            Class Fee Prepaid
                          </Badge>
                        )}
                        {/* Linked Invoice Badge */}
                        {booking.linked_invoice_id && (
                          <Badge 
                            className={`cursor-pointer border flex items-center gap-1 hover:opacity-80 transition-opacity ${
                              booking.linked_invoice_status === 'paid' 
                                ? 'bg-green-900/50 text-green-400 border-green-700' 
                                : booking.linked_invoice_status === 'partial'
                                ? 'bg-yellow-900/50 text-yellow-400 border-yellow-700'
                                : 'bg-blue-900/50 text-blue-400 border-blue-700'
                            }`}
                            title={`Invoice ${booking.linked_invoice_id} - Click to view`}
                            onClick={(e) => {
                              e.stopPropagation();
                              viewLinkedInvoice(booking.linked_invoice_id);
                            }}
                            data-testid={`linked-invoice-badge-${booking.id}`}
                          >
                            <FileText className="h-3 w-3" />
                            {booking.linked_invoice_id}
                            {booking.linked_invoice_status === 'paid' && <CheckCircle2 className="h-3 w-3 ml-1" />}
                            {booking.linked_invoice_status === 'partial' && <Clock className="h-3 w-3 ml-1" />}
                          </Badge>
                        )}
                        {/* Hidden reason badge */}
                        {booking.status === 'hidden' && booking.hidden_reason && (
                          <Badge className="bg-slate-600/50 text-white border-slate-500 border flex items-center gap-1">
                            <AlertCircle className="h-3 w-3" />
                            {booking.hidden_reason}
                          </Badge>
                        )}
                        {/* Registration Closed badge */}
                        {booking.registration_closed && (
                          <Badge className="bg-red-900/50 text-red-400 border-red-700 border flex items-center gap-1">
                            <EyeOff className="h-3 w-3" />
                            Registration Closed
                            {booking.registration_closed_reason?.startsWith('auto_') && (
                              <span className="text-red-500 text-[10px] ml-1">(Auto)</span>
                            )}
                          </Badge>
                        )}
                        {/* Registration Reopen Eligible - Prompt to reopen */}
                        {booking.registration_reopen_eligible && booking.registration_closed && (
                          <div className="flex items-center gap-1 bg-green-900/30 rounded-full px-2 py-1 border border-green-700/50">
                            <span className="text-xs text-green-400">Class rescheduled - Reopen registration?</span>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => reopenRegistration(booking.id)}
                              className="h-6 px-2 text-green-400 hover:text-green-300 hover:bg-green-900/50"
                              title="Reopen registration"
                            >
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Yes
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => dismissReopenEligible(booking.id)}
                              className="h-6 px-2 text-slate-400 hover:text-slate-300 hover:bg-slate-700"
                              title="Keep registration closed"
                            >
                              <X className="h-3 w-3 mr-1" />
                              No
                            </Button>
                          </div>
                        )}
                        {/* Registration Close Hours - Editable for approved bookings */}
                        {booking.status === 'approved' && !booking.registration_closed && (
                          <div className="flex items-center gap-1">
                            {editingCloseHours === booking.id ? (
                              <div className="flex items-center gap-1 bg-amber-900/50 rounded-full px-2 py-1">
                                <Input
                                  type="number"
                                  min="0"
                                  max="168"
                                  value={newCloseHours}
                                  onChange={(e) => setNewCloseHours(e.target.value === '' ? '' : parseInt(e.target.value))}
                                  onBlur={(e) => {
                                    const val = parseInt(e.target.value);
                                    if (isNaN(val) || val < 0) setNewCloseHours(0);
                                    else if (val > 168) setNewCloseHours(168);
                                  }}
                                  className="w-14 h-6 text-xs bg-slate-700 border-amber-600 text-amber-200 text-center p-1"
                                  autoFocus
                                />
                                <span className="text-xs text-amber-400">hrs</span>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => updateRegistrationCloseHours(booking.id)}
                                  className="h-6 w-6 p-0 text-green-400 hover:text-green-300"
                                >
                                  <CheckCircle className="h-3 w-3" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => setEditingCloseHours(null)}
                                  className="h-6 w-6 p-0 text-red-400 hover:text-red-300"
                                >
                                  <X className="h-3 w-3" />
                                </Button>
                              </div>
                            ) : (
                              <Badge 
                                className="bg-amber-900/30 text-amber-400 border-amber-700/50 border flex items-center gap-1 cursor-pointer hover:bg-amber-800/40"
                                onClick={() => {
                                  setEditingCloseHours(booking.id);
                                  setNewCloseHours(booking.registration_close_hours || 24);
                                }}
                                title="Click to edit auto-close hours"
                              >
                                <Clock className="h-3 w-3" />
                                Close {booking.registration_close_hours || 24}hr before
                              </Badge>
                            )}
                          </div>
                        )}
                        {booking.status === 'hidden' && booking.hidden_at && (
                          <span className="text-xs text-slate-500">
                            Hidden: {new Date(booking.hidden_at).toLocaleDateString()}
                          </span>
                        )}
                        {/* Max Students - Editable for all approved bookings */}
                        {booking.status === 'approved' && (
                          <div className="flex items-center gap-1">
                            {editingMaxStudents === booking.id ? (
                              <div className="flex items-center gap-1 bg-purple-900/50 rounded-full px-2 py-1">
                                <Input
                                  type="number"
                                  min="1"
                                  max="50"
                                  value={newMaxStudents}
                                  onChange={(e) => setNewMaxStudents(e.target.value === '' ? '' : parseInt(e.target.value))}
                                  onBlur={(e) => {
                                    const val = parseInt(e.target.value);
                                    if (isNaN(val) || val < 1) setNewMaxStudents(1);
                                    else if (val > 50) setNewMaxStudents(50);
                                  }}
                                  className="w-14 h-6 text-xs bg-slate-700 border-teal-600 text-purple-200 text-center p-1"
                                  autoFocus
                                />
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => updateMaxStudents(booking.id)}
                                  className="h-6 w-6 p-0 text-green-400 hover:text-green-300"
                                >
                                  <CheckCircle className="h-3 w-3" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => setEditingMaxStudents(null)}
                                  className="h-6 w-6 p-0 text-red-400 hover:text-red-300"
                                >
                                  <X className="h-3 w-3" />
                                </Button>
                              </div>
                            ) : (
                              <Badge 
                                className={`${booking.is_public ? 'bg-purple-900/50 text-white border-purple-700' : 'bg-slate-700/50 text-white border-teal-600'} border flex items-center gap-1 cursor-pointer hover:bg-opacity-70`}
                                onClick={() => {
                                  setEditingMaxStudents(booking.id);
                                  setNewMaxStudents(booking.max_students || 10);
                                }}
                                title="Click to edit max students"
                              >
                                {booking.is_public && <Globe className="h-3 w-3" />}
                                <Users className="h-3 w-3" />
                                {booking.is_public ? 'Public ' : ''}({booking.students?.length || 0}/{booking.max_students || 10})
                              </Badge>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {/* Class Type with Change History */}
                      <div className="mb-2">
                        <div className="flex items-center gap-2 flex-wrap">
                          {/* Show original class type with strikethrough if changed */}
                          {booking.original_class_type_name && booking.original_class_type_name !== booking.class_type_name && (
                            <span className="text-lg text-red-400/60 line-through">
                              {booking.original_class_type_name}
                            </span>
                          )}
                          <h3 className={`text-lg font-semibold ${booking.original_class_type_name && booking.original_class_type_name !== booking.class_type_name ? 'text-white' : 'text-slate-100'}`} data-testid={`booking-class-${booking.id}`}>
                            {booking.original_class_type_name && booking.original_class_type_name !== booking.class_type_name ? '→ ' : ''}{booking.class_type_name}
                          </h3>
                          {/* Change Class Type button */}
                          {editingBooking !== booking.id && !['rejected', 'rejected_no_refund', 'cancelled'].includes(booking.status) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openChangeClassType(booking)}
                              className="h-6 px-2 text-cyan-400 hover:text-cyan-300 hover:bg-cyan-900/30"
                              data-testid={`change-class-type-${booking.id}`}
                            >
                              <ArrowRightLeft className="h-3 w-3 mr-1" />
                              Change
                            </Button>
                          )}
                        </div>
                        
                        {/* Class Type Change History */}
                        {booking.class_type_history && booking.class_type_history.length > 0 && (
                          <div className="mt-2 p-2 bg-slate-700/50 rounded-lg border border-teal-600">
                            <p className="text-xs font-medium text-white mb-1 flex items-center gap-1">
                              <Clock className="h-3 w-3" /> Class Type Change History:
                            </p>
                            <div className="space-y-1 max-h-24 overflow-y-auto">
                              {booking.class_type_history.map((change, idx) => (
                                <div key={idx} className="text-xs border-l-2 border-cyan-500/50 pl-2">
                                  <p className="text-white">
                                    <span className="text-red-400/70 line-through">{change.from_class_type_name}</span>
                                    <span className="text-slate-500"> → </span>
                                    <span className="text-white">{change.to_class_type_name}</span>
                                    {change.price_difference !== undefined && change.price_difference !== 0 && (
                                      <span className={`ml-1 ${change.price_difference > 0 ? 'text-red-400' : 'text-white'}`}>
                                        ({change.price_difference > 0 ? '+' : ''}${change.price_difference.toFixed(2)})
                                      </span>
                                    )}
                                  </p>
                                  <p className="text-slate-500 text-[10px]">
                                    {change.changed_at && formatInTimeZone(new Date(change.changed_at), 'America/Los_Angeles', 'MMM d, yyyy h:mm a')} PST
                                    {change.reason && <span className="text-amber-400/70"> • {change.reason}</span>}
                                  </p>
                                </div>
                              ))}
                            </div>
                          </div>
                        )}
                        
                        {/* Balance Due Alert */}
                        {booking.balance_due > 0 && booking.status === 'partially_paid' && (
                          <div className="mt-2 p-3 bg-orange-900/30 border border-orange-700/50 rounded-lg">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="text-sm font-medium text-orange-400 flex items-center gap-1">
                                  <AlertCircle className="h-4 w-4" />
                                  Balance Due: ${booking.balance_due.toFixed(2)}
                                </p>
                                <p className="text-xs text-orange-300/70 mt-1">
                                  Previous status: {booking.previous_status || 'pending'}
                                  {booking.balance_woo_order_id && (
                                    <span className="ml-2">• WooCommerce Order #{booking.balance_woo_order_id}</span>
                                  )}
                                </p>
                              </div>
                              <div className="flex items-center gap-2">
                                {booking.balance_payment_url && (
                                  <Button
                                    variant="outline"
                                    size="sm"
                                    onClick={() => window.open(booking.balance_payment_url, '_blank')}
                                    className="h-7 text-xs border-orange-600 text-orange-400 hover:bg-orange-900/30"
                                  >
                                    <ExternalLink className="h-3 w-3 mr-1" />
                                    Payment Link
                                  </Button>
                                )}
                                <Button
                                  size="sm"
                                  onClick={() => handleMarkBalancePaid(booking.id)}
                                  className="h-7 text-xs bg-teal-600 hover:bg-teal-500"
                                  data-testid={`mark-balance-paid-${booking.id}`}
                                >
                                  <DollarSign className="h-3 w-3 mr-1" />
                                  Mark Paid
                                </Button>
                              </div>
                            </div>
                          </div>
                        )}
                      </div>
                      
                      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 text-sm text-white">
                        <div>
                          <p className="font-medium text-white mb-1">Class Time</p>
                          <p data-testid={`booking-date-${booking.id}`}>{formatInTimeZone(new Date(booking.start_time), 'America/Los_Angeles', 'MMMM d, yyyy')}</p>
                          <p data-testid={`booking-time-${booking.id}`}>
                            {formatInTimeZone(new Date(booking.start_time), 'America/Los_Angeles', 'h:mm a')} - {formatInTimeZone(new Date(booking.end_time), 'America/Los_Angeles', 'h:mm a')} PST
                          </p>
                          
                          {/* Show travel buffer info if applicable */}
                          {booking.travel_time_minutes > 0 && (
                            <div className="mt-2 p-2 bg-amber-900/30 border border-amber-700/50 rounded-lg">
                              <p className="text-xs text-amber-400 font-medium">
                                🚗 Travel Buffer: {booking.travel_time_minutes >= 60 
                                  ? `${Math.floor(booking.travel_time_minutes / 60)}h ${booking.travel_time_minutes % 60 > 0 ? (booking.travel_time_minutes % 60) + 'm' : ''}`
                                  : `${booking.travel_time_minutes}m`
                                } each way
                              </p>
                              <p className="text-xs text-amber-300 mt-1">
                                Calendar blocks: {formatInTimeZone(new Date(new Date(booking.start_time).getTime() - booking.travel_time_minutes * 60000), 'America/Los_Angeles', 'h:mm a')} - {formatInTimeZone(new Date(new Date(booking.end_time).getTime() + booking.travel_time_minutes * 60000), 'America/Los_Angeles', 'h:mm a')}
                              </p>
                              {booking.mileage > 0 && (
                                <p className="text-xs text-amber-300/70">
                                  Distance: {booking.mileage} miles
                                </p>
                              )}
                            </div>
                          )}
                          
                          {/* Booking Code / Reference Code */}
                          <div className="mt-3 p-2 bg-teal-900/20 border border-teal-700/50 rounded-lg">
                            <p className="text-xs font-medium text-teal-400 mb-1 flex items-center gap-1">
                              <Tag className="h-3 w-3" /> Booking Code:
                            </p>
                            {editingReferenceCode === booking.id ? (
                              <div className="flex items-center gap-2">
                                <Input
                                  value={newReferenceCode}
                                  onChange={(e) => setNewReferenceCode(e.target.value.toLowerCase().replace(/\s/g, ''))}
                                  placeholder="Enter code"
                                  className="h-7 text-xs bg-slate-700 border-teal-600 text-white lowercase"
                                  maxLength={20}
                                  data-testid={`edit-reference-code-${booking.id}`}
                                />
                                <Button
                                  size="sm"
                                  onClick={() => updateReferenceCode(booking.id)}
                                  className="h-7 w-7 p-0 bg-teal-600 hover:bg-teal-500"
                                >
                                  <CheckCircle2 className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => setEditingReferenceCode(null)}
                                  className="h-7 w-7 p-0 border-teal-600"
                                >
                                  <X className="h-4 w-4" />
                                </Button>
                              </div>
                            ) : (
                              <div className="flex items-center justify-between">
                                <span className="text-sm font-mono text-teal-300 bg-slate-700/50 px-2 py-0.5 rounded">
                                  {booking.reference_code || '—'}
                                </span>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => {
                                    setNewReferenceCode(booking.reference_code || '');
                                    setEditingReferenceCode(booking.id);
                                  }}
                                  className="h-6 px-2 text-xs text-teal-400 hover:text-teal-300"
                                  data-testid={`edit-reference-code-btn-${booking.id}`}
                                >
                                  <Edit2 className="h-3 w-3" />
                                </Button>
                              </div>
                            )}
                          </div>
                        </div>
                        <div>
                          <p className="font-medium text-white mb-1 flex items-center gap-2">
                            <Users className="h-4 w-4" />
                            Students ({booking.students?.length || 0})
                            {/* Copy All Students Button */}
                            {booking.students && booking.students.length > 0 && (
                              <Button
                                size="sm"
                                variant="ghost"
                                className="h-6 px-2 text-xs text-teal-400 hover:bg-teal-900/30"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  const allStudentsText = booking.students.map(s => 
                                    `${s.first_name}\t${s.last_name}\t${s.email}\t${s.phone || ''}`
                                  ).join('\n');
                                  copyToClipboard(allStudentsText, `Copied ${booking.students.length} student(s) to clipboard`);
                                }}
                                title="Copy all students (tab-separated)"
                                data-testid={`copy-all-students-${booking.id}`}
                              >
                                <Copy className="h-3 w-3 mr-1" />
                                Copy All
                              </Button>
                            )}
                          </p>
                          {booking.students && booking.students.length > 0 ? (
                            <div className="space-y-2">
                              {booking.students.map((student, idx) => (
                                <div key={idx} className="p-2 bg-slate-700/50 rounded-lg border border-teal-600">
                                  <div className="flex items-start justify-between">
                                    <div className="flex-1">
                                      <div className="flex items-center gap-1 mb-1">
                                        <p className="font-medium text-slate-200 flex items-center gap-2">
                                          {/* Sign-in status indicator */}
                                          {student.signed_in ? (
                                            <span 
                                              className="text-emerald-400 cursor-pointer hover:text-emerald-300" 
                                              title={`Signed in${student.signed_in_by_admin ? ' by Admin' : ''}${student.signed_in_at ? ` at ${new Date(student.signed_in_at).toLocaleString()}` : ''} - Click to ${student.signature && student.signature !== 'SIGNED_IN_BY_ADMIN' ? 'view signature' : 'toggle'}`}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                if (student.signature && student.signature !== 'SIGNED_IN_BY_ADMIN') {
                                                  setViewSignatureData({ 
                                                    name: `${student.first_name} ${student.last_name}`,
                                                    signature: student.signature,
                                                    signed_at: student.signed_in_at,
                                                    signed_in_by_admin: student.signed_in_by_admin
                                                  });
                                                } else {
                                                  handleToggleSignIn(booking.id, idx, student);
                                                }
                                              }}
                                            >
                                              {student.signed_in_by_admin ? (
                                                <UserCheck className="h-4 w-4" />
                                              ) : (
                                                <CheckCircle className="h-4 w-4" />
                                              )}
                                            </span>
                                          ) : (
                                            <span 
                                              className="text-red-400 cursor-pointer hover:text-amber-400" 
                                              title="Not signed in - Click to mark as signed in by admin"
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleToggleSignIn(booking.id, idx, student);
                                              }}
                                            >
                                              <XCircle className="h-4 w-4" />
                                            </span>
                                          )}
                                          {/* eBook status indicator - only show for signed-in students */}
                                          {student.signed_in && student.ebook_status && student.ebook_status !== 'not_applicable' && (
                                            student.ebook_status === 'sent' ? (
                                              <span className="text-green-400" title="eBook sent successfully">
                                                <BookOpen className="h-4 w-4" />
                                              </span>
                                            ) : student.ebook_status === 'queued' || student.ebook_status === 'processing' ? (
                                              <span className="text-amber-400" title={`eBook ${student.ebook_status}`}>
                                                <BookOpen className="h-4 w-4 animate-pulse" />
                                              </span>
                                            ) : student.ebook_status === 'failed' ? (
                                              <span 
                                                className="text-red-400 cursor-pointer hover:text-red-300" 
                                                title={`eBook failed${student.ebook_error ? `: ${student.ebook_error}` : ''} - Click to resend`}
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleResendEbook(booking.id, idx, `${student.first_name} ${student.last_name}`);
                                                }}
                                              >
                                                <BookOpen className="h-4 w-4" />
                                              </span>
                                            ) : student.ebook_status === 'canceled_by_admin' ? (
                                              <span 
                                                className="text-slate-400 cursor-pointer hover:text-slate-300" 
                                                title="eBook canceled by admin - Click to resend"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleResendEbook(booking.id, idx, `${student.first_name} ${student.last_name}`);
                                                }}
                                              >
                                                <BookOpen className="h-4 w-4" />
                                              </span>
                                            ) : null
                                          )}
                                          {/* Digital test status indicator - only show for signed-in students in BLS classes */}
                                          {student.signed_in && student.digital_test_status && (
                                            student.digital_test_status === 'sent' ? (
                                              <span className="text-green-400" title="Digital test sent successfully">
                                                <FileText className="h-4 w-4" />
                                              </span>
                                            ) : student.digital_test_status === 'queued' || student.digital_test_status === 'processing' ? (
                                              <span className="text-cyan-400" title={`Digital test ${student.digital_test_status}`}>
                                                <FileText className="h-4 w-4 animate-pulse" />
                                              </span>
                                            ) : student.digital_test_status === 'failed' ? (
                                              <span 
                                                className="text-red-400 cursor-pointer hover:text-red-300" 
                                                title={`Digital test failed${student.digital_test_error ? `: ${student.digital_test_error}` : ''} - Click to resend`}
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleResendDigitalTest(booking.id, idx, `${student.first_name} ${student.last_name}`);
                                                }}
                                              >
                                                <FileText className="h-4 w-4" />
                                              </span>
                                            ) : student.digital_test_status === 'canceled_by_admin' ? (
                                              <span 
                                                className="text-slate-400 cursor-pointer hover:text-slate-300" 
                                                title="Digital test canceled by admin - Click to resend"
                                                onClick={(e) => {
                                                  e.stopPropagation();
                                                  handleResendDigitalTest(booking.id, idx, `${student.first_name} ${student.last_name}`);
                                                }}
                                              >
                                                <FileText className="h-4 w-4" />
                                              </span>
                                            ) : null
                                          )}
                                          {/* Show name with yellow highlight if changed during sign-in */}
                                          {student.signin_changes?.fields?.first_name || student.signin_changes?.fields?.last_name ? (
                                            <span 
                                              className="bg-amber-500/30 px-1 rounded" 
                                              title={`Name changed during sign-in. Original: ${student.signin_changes?.fields?.first_name?.old || student.first_name} ${student.signin_changes?.fields?.last_name?.old || student.last_name}`}
                                            >
                                              {student.first_name} {student.last_name}
                                            </span>
                                          ) : (
                                            <>{student.first_name} {student.last_name}</>
                                          )}
                                          {/* Info Changed Badge */}
                                          {student.signin_changes && (
                                            <Badge 
                                              className="ml-2 bg-amber-500/80 text-amber-950 text-xs px-1.5 py-0 cursor-help"
                                              title={`Changed at sign-in: ${Object.keys(student.signin_changes.fields || {}).join(', ')}`}
                                            >
                                              ✏️ Info Changed
                                            </Badge>
                                          )}
                                          {/* Rescheduled Student Badge */}
                                          {student.is_rescheduled && (
                                            <Badge className="ml-2 bg-amber-600 text-amber-100 text-xs px-1.5 py-0">
                                              <RefreshCw className="h-2.5 w-2.5 mr-1" />
                                              Rescheduled
                                            </Badge>
                                          )}
                                          {/* BLS Test Score Badge */}
                                          {student.bls_score !== undefined && student.bls_score !== null && (
                                            <Badge 
                                              className={`ml-2 text-xs px-1.5 py-0 ${
                                                student.bls_score >= 84 
                                                  ? 'bg-emerald-600 text-emerald-100' 
                                                  : 'bg-red-600 text-red-100'
                                              }`}
                                              title={`BLS Test Score: ${student.bls_score}%${student.bls_completion_date ? ` - Completed: ${student.bls_completion_date}` : ''}`}
                                            >
                                              📝 Tested {student.bls_score}%
                                            </Badge>
                                          )}
                                          {/* AHA Verification Badge - only for BLS Renewal classes */}
                                          {isRenewalClass(booking) && (() => {
                                            // Get the name from the verified AHA card - check all possible fields
                                            const rawCardName = student.aha_ecard?.aha_returned_name || student.aha_ecard?.verification_name || student.aha_ecard?.card_holder_name || student.aha_ecard?.found_name || '';
                                            // Clean up the name - remove "Verified: " prefix and anything after comma
                                            const cardName = rawCardName.replace(/^Verified:\s*/i, '').split(',')[0].trim();
                                            const cardNameLower = cardName.toLowerCase();
                                            const studentName = `${student.first_name} ${student.last_name}`.trim();
                                            const studentNameLower = studentName.toLowerCase();
                                            const isExactMatch = cardNameLower && studentNameLower && cardNameLower === studentNameLower;
                                            const hasCardName = !!cardName;
                                            const hasEcardData = !!student.aha_ecard;
                                            const wasManuallyVerified = student.aha_ecard?.manually_verified;
                                            const needsReview = student.aha_ecard?.needs_review;
                                            
                                            // Determine badge color and text:
                                            // - Green "Verified": explicitly marked as verified by admin, OR verified + names match exactly
                                            // - Yellow "Review": not yet reviewed (no aha_verified set), needs_review flag, or legacy/bypassed
                                            // - Red "Unverified": explicitly marked as not verified
                                            const getBadgeColor = () => {
                                              if (student.aha_verified === false) return 'bg-red-600 hover:bg-red-500 text-red-100'; // Explicitly unverified
                                              if (student.aha_verified === true) return 'bg-emerald-600 hover:bg-emerald-500 text-emerald-100'; // Admin verified (or exact match)
                                              // Not yet reviewed - yellow
                                              return 'bg-amber-600 hover:bg-amber-500 text-amber-100';
                                            };
                                            
                                            const getBadgeText = () => {
                                              if (student.aha_verified === false) return 'Unverified';
                                              if (student.aha_verified === true) {
                                                if (isExactMatch) return 'Exact Match';
                                                return 'Verified'; // Admin explicitly verified
                                              }
                                              return 'Review'; // Needs review
                                            };
                                            
                                            return (
                                            <TooltipProvider>
                                              <Tooltip delayDuration={200}>
                                                <TooltipTrigger asChild>
                                                  <Badge 
                                                    className={`ml-2 text-xs px-1.5 py-0 cursor-pointer transition-colors ${getBadgeColor()}`}
                                                    onClick={(e) => {
                                                      e.stopPropagation();
                                                      handleToggleAhaVerification(booking.id, idx, student);
                                                    }}
                                                  >
                                                    {student.aha_verified === true ? (
                                                      <>
                                                        <CheckCircle className="h-2.5 w-2.5 mr-1" />
                                                        {getBadgeText()}
                                                      </>
                                                    ) : student.aha_verified === false ? (
                                                      <>
                                                        <XCircle className="h-2.5 w-2.5 mr-1" />
                                                        {getBadgeText()}
                                                      </>
                                                    ) : (
                                                      <>
                                                        <AlertTriangle className="h-2.5 w-2.5 mr-1" />
                                                        {getBadgeText()}
                                                      </>
                                                    )}
                                                  </Badge>
                                                </TooltipTrigger>
                                                <TooltipContent side="top" className="bg-slate-900 border-slate-700 p-3 max-w-xs">
                                                  {student.aha_ecard ? (
                                                    <div className="space-y-1.5">
                                                      <p className="text-xs font-medium text-slate-200">AHA eCard Verification</p>
                                                      
                                                      {/* Name on Card - Primary display */}
                                                      {hasCardName ? (
                                                        <div className={`p-2 rounded ${isExactMatch ? 'bg-emerald-900/40 border border-emerald-700/50' : 'bg-amber-900/40 border border-amber-700/50'}`}>
                                                          <p className="text-xs text-slate-500 mb-0.5">Name on Card:</p>
                                                          <p className={`text-sm font-medium ${isExactMatch ? 'text-emerald-300' : 'text-amber-300'}`}>
                                                            {cardName}
                                                          </p>
                                                          {!isExactMatch && (
                                                            <p className="text-xs text-amber-400 mt-1">
                                                              ⚠ Does not match student: {studentName}
                                                            </p>
                                                          )}
                                                        </div>
                                                      ) : (
                                                        <p className="text-xs text-slate-400">No card name available</p>
                                                      )}
                                                      
                                                      {student.aha_ecard.ecard_code && (
                                                        <p className="text-xs text-slate-400">
                                                          <span className="text-slate-500">eCard:</span>{' '}
                                                          <span className="text-slate-200 font-mono">{student.aha_ecard.ecard_code}</span>
                                                        </p>
                                                      )}
                                                      {student.aha_ecard.expiration_date && (
                                                        <p className="text-xs text-slate-400">
                                                          <span className="text-slate-500">Expires:</span>{' '}
                                                          <span className="text-slate-200">{student.aha_ecard.expiration_date}</span>
                                                        </p>
                                                      )}
                                                      {student.aha_ecard.manually_verified && (
                                                        <p className="text-xs text-amber-400 mt-1">⚠ Manually verified by client</p>
                                                      )}
                                                      <p className="text-xs text-slate-500 mt-2 pt-2 border-t border-slate-700">
                                                        Click to review verification status
                                                      </p>
                                                    </div>
                                                  ) : (
                                                    <div className="space-y-1">
                                                      <p className="text-xs text-slate-400">No eCard verification data</p>
                                                      <p className="text-xs text-slate-500">Click to review verification status</p>
                                                    </div>
                                                  )}
                                                </TooltipContent>
                                              </Tooltip>
                                            </TooltipProvider>
                                            );
                                          })()}
                                          {/* Per-student email button */}
                                          <StudentEmailButton
                                            bookingId={booking.id}
                                            student={student}
                                            onResend={handleResendEmail}
                                          />
                                        </p>
                                        {/* Copy Row Button - copies all student info */}
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          className="h-5 w-5 p-0 text-teal-400 hover:bg-teal-900/30"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            const text = `${student.first_name}\t${student.last_name}\t${student.email}\t${student.phone || ''}`;
                                            copyToClipboard(text, 'Copied student info');
                                          }}
                                          title="Copy row (tab-separated)"
                                        >
                                          <Copy className="h-3 w-3" />
                                        </Button>
                                      </div>
                                      <div className="flex items-center gap-1 group">
                                        <p className={`text-xs flex items-center gap-1 ${student.signin_changes?.fields?.email ? 'bg-amber-500/30 px-1 rounded text-amber-200' : 'text-white'}`}
                                           title={student.signin_changes?.fields?.email ? `Email changed during sign-in. Original: ${student.signin_changes.fields.email.old || '(empty)'}` : ''}>
                                          <Mail className="h-3 w-3" />
                                          {student.email}
                                        </p>
                                        <Button
                                          size="sm"
                                          variant="ghost"
                                          className="h-4 w-4 p-0 text-slate-500 hover:text-teal-400 opacity-0 group-hover:opacity-100 transition-opacity"
                                          onClick={(e) => {
                                            e.stopPropagation();
                                            copyToClipboard(student.email, 'Email copied');
                                          }}
                                          title="Copy email"
                                        >
                                          <Copy className="h-2.5 w-2.5" />
                                        </Button>
                                      </div>
                                      <div className="flex items-center gap-1 group">
                                        <p className={`text-xs ${student.signin_changes?.fields?.phone ? 'bg-amber-500/30 px-1 rounded text-amber-200' : 'text-white'}`}
                                           title={student.signin_changes?.fields?.phone ? `Phone changed during sign-in. Original: ${student.signin_changes.fields.phone.old || '(empty)'}` : ''}>
                                          📞 {student.phone}
                                        </p>
                                        {student.phone && (
                                          <Button
                                            size="sm"
                                            variant="ghost"
                                            className="h-4 w-4 p-0 text-slate-500 hover:text-teal-400 opacity-0 group-hover:opacity-100 transition-opacity"
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              copyToClipboard(student.phone, 'Phone copied');
                                            }}
                                            title="Copy phone"
                                          >
                                            <Copy className="h-2.5 w-2.5" />
                                          </Button>
                                        )}
                                      </div>
                                    </div>
                                    {/* Per-student payment status toggle - for all students */}
                                    <Button
                                      size="sm"
                                      variant="ghost"
                                      onClick={() => toggleStudentPaymentStatus(booking.id, idx, student.payment_status)}
                                      className={`h-7 px-2 text-xs ${
                                        student.payment_status === 'paid'
                                          ? 'bg-emerald-900/50 text-white hover:bg-emerald-900/70'
                                          : 'bg-red-900/50 text-red-400 hover:bg-red-900/70'
                                      }`}
                                      data-testid={`student-payment-${booking.id}-${idx}`}
                                    >
                                      <DollarSign className="h-3 w-3 mr-1" />
                                      {student.payment_status === 'paid' ? 'Paid' : 'Unpaid'}
                                    </Button>
                                  </div>
                                  {/* Display per-student add-ons */}
                                  {student.addons && student.addons.length > 0 && (
                                    <div className="mt-2 pt-2 border-t border-teal-600">
                                      <p className="text-xs font-medium text-white flex items-center gap-1">
                                        🛒 Add-ons:
                                      </p>
                                      {student.addons.map((addon, addonIdx) => (
                                        <p key={addonIdx} className="text-xs text-purple-300 ml-4">
                                          • {addon.name} {addon.quantity > 1 ? `x${addon.quantity}` : ''} {addon.price ? `($${addon.price.toFixed(2)})` : ''}
                                        </p>
                                      ))}
                                    </div>
                                  )}
                                  {/* Display per-student notes */}
                                  {student.notes && (
                                    <div className="mt-2 pt-2 border-t border-teal-600">
                                      <p className="text-xs font-medium text-amber-400 flex items-center gap-1">
                                        📝 Notes:
                                      </p>
                                      <p className="text-xs text-amber-200 ml-4 italic">
                                        {student.notes}
                                      </p>
                                    </div>
                                  )}
                                  {/* Display AHA eCard verification (new per-student format) */}
                                  {student.aha_ecard && (
                                    <div className="mt-2 pt-2 border-t border-teal-600">
                                      <p className={`text-xs font-medium flex items-center gap-1 ${
                                        student.aha_ecard.verification_status?.includes('MATCH') && !student.aha_ecard.verification_status?.includes('NO MATCH')
                                          ? 'text-green-400' 
                                          : student.aha_ecard.verification_status?.includes('MANUAL')
                                            ? 'text-amber-400'
                                            : 'text-red-400'
                                      }`}>
                                        🎓 AHA: {student.aha_ecard.verification_status}
                                      </p>
                                      {student.aha_ecard.card_holder_name && (
                                        <p className="text-xs text-white ml-4">
                                          Card Holder: {student.aha_ecard.card_holder_name.replace(/^Verified:\s*/i, '').split(',')[0].trim()}
                                        </p>
                                      )}
                                      {student.aha_ecard.aha_returned_name && student.aha_ecard.aha_returned_name !== student.aha_ecard.card_holder_name && (
                                        <p className="text-xs text-amber-300 ml-4">
                                          AHA Name: {student.aha_ecard.aha_returned_name}
                                        </p>
                                      )}
                                      <p className="text-xs text-white ml-4">
                                        eCard: {student.aha_ecard.ecard_code}
                                      </p>
                                      {student.aha_ecard.expiration_date && (
                                        <p className="text-xs text-white ml-4">
                                          Expires: {student.aha_ecard.expiration_date}
                                        </p>
                                      )}
                                      {/* Show screenshot link when verification failed */}
                                      {student.aha_ecard.verification_screenshot_url && (
                                        <a 
                                          href={`${API}${student.aha_ecard.verification_screenshot_url}`}
                                          target="_blank"
                                          rel="noopener noreferrer"
                                          className="inline-flex items-center gap-1 text-xs text-cyan-400 hover:text-cyan-300 ml-4 mt-1"
                                        >
                                          📸 View AHA Screenshot
                                        </a>
                                      )}
                                    </div>
                                  )}
                                  {/* Display Enrollware-issued eCard code */}
                                  {student.ecard_code && !student.aha_ecard && (
                                    <div className="mt-2 pt-2 border-t border-emerald-600">
                                      <p className="text-xs font-medium text-emerald-400 flex items-center gap-1">
                                        <CreditCard className="h-3 w-3" /> Enrollware eCard
                                      </p>
                                      <p className="text-xs text-white ml-4 font-mono">
                                        Code: {student.ecard_code}
                                      </p>
                                      {student.ecard_issued_at && (
                                        <p className="text-xs text-slate-400 ml-4">
                                          Issued: {new Date(student.ecard_issued_at).toLocaleDateString()}
                                        </p>
                                      )}
                                    </div>
                                  )}
                                  {/* Display BLS Certificate verification (legacy format) */}
                                  {student.bls_certificate && !student.aha_ecard && (
                                    <div className="mt-2 pt-2 border-t border-teal-600">
                                      <p className={`text-xs font-medium flex items-center gap-1 ${
                                        student.bls_certificate.aha_verification_status?.includes('MATCH') 
                                          ? 'text-green-400' 
                                          : 'text-red-400'
                                      }`}>
                                        🎓 {student.bls_certificate.aha_verification_status}
                                      </p>
                                      <p className="text-xs text-white ml-4">
                                        eCard: {student.bls_certificate.ecard_code}
                                      </p>
                                      <p className="text-xs text-white ml-4">
                                        Expires: {student.bls_certificate.expiration_date}
                                      </p>
                                    </div>
                                  )}
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="text-slate-400 italic">No students added yet</p>
                          )}
                        </div>
                      </div>
                      
                      {/* Display legacy booking-level add-ons (for backwards compatibility) */}
                      {booking.addons && booking.addons.length > 0 && !booking.students?.some(s => s.addons?.length > 0) && (
                        <div className="mt-2 p-2 bg-purple-900/30 rounded-lg border border-purple-700">
                          <p className="text-xs font-medium text-white flex items-center gap-1 mb-1">
                            🛒 Add-ons (Booking Level):
                          </p>
                          {booking.addons.map((addon, idx) => (
                            <p key={idx} className="text-xs text-purple-300 ml-2">
                              • {addon.name} {addon.quantity > 1 ? `x${addon.quantity}` : ''} {addon.price ? `($${addon.price.toFixed(2)})` : ''}
                            </p>
                          ))}
                        </div>
                      )}
                      
                      {booking.woo_cart_url && booking.payment_status === 'unpaid' && (
                        <div className="mt-2">
                          <a 
                            href={booking.woo_cart_url} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="text-sm text-white hover:underline"
                          >
                            View Payment Link
                          </a>
                        </div>
                      )}
                      
                      {/* Edit Address Section */}
                      <div className="mt-3 p-3 bg-slate-700/30 rounded-lg border border-teal-600">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs font-medium text-white flex items-center gap-1">
                            <MapPin className="h-3 w-3" />
                            Location Details
                          </p>
                          {editingAddress !== booking.id && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => startEditingAddress(booking)}
                              className="h-6 px-2 text-xs text-white hover:text-slate-200"
                              data-testid={`edit-address-btn-${booking.id}`}
                            >
                              <Edit2 className="h-3 w-3 mr-1" />
                              Edit
                            </Button>
                          )}
                        </div>
                        
                        {editingAddress === booking.id ? (
                          <div className="space-y-2">
                            {/* Recurring Client Saved Locations */}
                            {recurringClientLocations.length > 0 && (
                              <div className="mb-3 pb-3 border-b border-slate-600">
                                <label className="text-xs text-teal-400 mb-2 block">
                                  Saved Client Locations (click to use)
                                </label>
                                <div className="flex flex-wrap gap-2">
                                  {recurringClientLocations.map((loc, locIdx) => (
                                    <button
                                      key={locIdx}
                                      onClick={() => selectRecurringClientLocation(loc)}
                                      className="px-2 py-1 text-xs bg-teal-700/50 hover:bg-teal-600 text-teal-100 rounded border border-teal-600 transition-colors"
                                      title={`${loc.address?.street || ''}, ${loc.address?.city || ''}, ${loc.address?.state || ''} ${loc.address?.zip || ''}`}
                                    >
                                      <MapPin className="h-3 w-3 inline mr-1" />
                                      {loc.name || `Location ${locIdx + 1}`}
                                    </button>
                                  ))}
                                </div>
                              </div>
                            )}
                            <div>
                              <label className="text-xs text-slate-500">Location Name (optional)</label>
                              <Input
                                value={addressForm.location_name}
                                onChange={(e) => setAddressForm({...addressForm, location_name: e.target.value})}
                                placeholder="e.g., Reno Fire Station #1"
                                className="h-8 text-xs bg-slate-800 border-teal-600 text-white"
                              />
                            </div>
                            <div>
                              <label className="text-xs text-slate-500">Street Address</label>
                              <Input
                                value={addressForm.street}
                                onChange={(e) => setAddressForm({...addressForm, street: e.target.value})}
                                placeholder="123 Main Street"
                                className="h-8 text-xs bg-slate-800 border-teal-600 text-white"
                              />
                            </div>
                            <div className="grid grid-cols-3 gap-2">
                              <div>
                                <label className="text-xs text-slate-500">City</label>
                                <Input
                                  value={addressForm.city}
                                  onChange={(e) => setAddressForm({...addressForm, city: e.target.value})}
                                  placeholder="Reno"
                                  className="h-8 text-xs bg-slate-800 border-teal-600 text-white"
                                />
                              </div>
                              <div>
                                <label className="text-xs text-slate-500">State</label>
                                <Input
                                  value={addressForm.state}
                                  onChange={(e) => setAddressForm({...addressForm, state: e.target.value})}
                                  placeholder="NV"
                                  className="h-8 text-xs bg-slate-800 border-teal-600 text-white"
                                />
                              </div>
                              <div>
                                <label className="text-xs text-slate-500">ZIP</label>
                                <Input
                                  value={addressForm.zip}
                                  onChange={(e) => setAddressForm({...addressForm, zip: e.target.value})}
                                  placeholder="89501"
                                  className="h-8 text-xs bg-slate-800 border-teal-600 text-white"
                                />
                              </div>
                            </div>
                            <div className="flex gap-2 mt-2">
                              <Button
                                size="sm"
                                onClick={() => updateBookingAddress(booking.id)}
                                className="h-7 bg-teal-600 hover:bg-teal-500 text-white text-xs"
                              >
                                <CheckCircle2 className="h-3 w-3 mr-1" />
                                Save
                              </Button>
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => {
                                  setEditingAddress(null);
                                  setAddressForm({ location_name: '', street: '', city: '', state: '', zip: '' });
                                  setRecurringClientLocations([]);
                                }}
                                className="h-7 border-teal-600 text-white text-xs"
                              >
                                Cancel
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <div className="text-sm">
                            {booking.location_name && (
                              <p className="text-slate-200 font-medium">{booking.location_name}</p>
                            )}
                            {booking.class_address ? (
                              <div>
                                <p className="text-white">
                                  {booking.class_address.street}, {booking.class_address.city}, {booking.class_address.state} {booking.class_address.zip}
                                </p>
                                {booking.class_address.is_outside_service_area && (
                                  <p className="text-xs text-amber-400 mt-1 font-medium">⚠️ Outside Reno/Sparks - Travel fee may apply</p>
                                )}
                              </div>
                            ) : (
                              <p className="text-slate-500 italic">No address set - click Edit to add</p>
                            )}
                          </div>
                        )}
                      </div>
                      
                      {booking.payment_status === 'expired' && booking.expiry_reason && (
                        <div className="mt-2 p-2 bg-red-900/30 border border-red-700 rounded-lg">
                          <p className="text-sm text-red-400 font-medium flex items-center gap-1">
                            <XCircle className="h-4 w-4" />
                            {booking.expiry_reason}
                          </p>
                        </div>
                      )}
                      
                      {/* Display admin notes if present */}
                      {booking.admin_notes && (
                        <div className="mt-2 p-2 bg-purple-900/30 border border-purple-700 rounded-lg">
                          <p className="text-xs text-white font-medium flex items-center gap-1 mb-1">
                            <FileText className="h-3 w-3" />
                            Admin Notes:
                          </p>
                          <p className="text-sm text-purple-200 whitespace-pre-wrap">{booking.admin_notes}</p>
                        </div>
                      )}
                      
                      {/* Display purchased add-ons if present */}
                      {booking.addons && booking.addons.length > 0 && (
                        <div className="mt-2 p-2 bg-teal-900/30 border border-teal-700 rounded-lg" data-testid={`booking-addons-${booking.id}`}>
                          <p className="text-xs text-teal-400 font-medium flex items-center gap-1 mb-1">
                            <Plus className="h-3 w-3" />
                            Add-Ons Purchased:
                          </p>
                          <ul className="text-sm text-teal-200 space-y-1">
                            {booking.addons.map((addon, idx) => (
                              <li key={idx} className="flex items-center gap-2">
                                <span>•</span>
                                <span>{addon.name || 'Add-on'} x {addon.quantity || 1}</span>
                                {addon.price && (
                                  <span className="text-teal-400 text-xs">(${(addon.price * (addon.quantity || 1)).toFixed(2)})</span>
                                )}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      
                      {/* Display coupon code if applied */}
                      {booking.coupon_code && (
                        <div className="mt-2 p-2 bg-green-900/30 border border-green-700 rounded-lg" data-testid={`booking-coupon-${booking.id}`}>
                          <p className="text-sm text-green-300 flex items-center gap-2">
                            <DollarSign className="h-4 w-4" />
                            <span>Coupon Applied: <strong>{booking.coupon_code.toUpperCase()}</strong></span>
                          </p>
                        </div>
                      )}
                      
                      {/* Display instructor notes if present */}
                      {booking.instructor_notes && (
                        <div className="mt-2 p-2 bg-amber-900/30 border border-amber-700 rounded-lg" data-testid={`booking-instructor-notes-${booking.id}`}>
                          <p className="text-xs text-amber-400 font-medium flex items-center gap-1 mb-1">
                            <Edit2 className="h-3 w-3" />
                            Notes for Instructor:
                          </p>
                          <p className="text-sm text-amber-200 whitespace-pre-wrap">{booking.instructor_notes}</p>
                        </div>
                      )}
                      
                      {editingBooking === booking.id && (
                        <div className="mt-4 p-4 bg-slate-700 rounded-lg border border-teal-600" data-testid={`edit-form-${booking.id}`}>
                          <h4 className="font-medium text-slate-200 mb-3 flex items-center gap-2">
                            <Edit2 className="h-4 w-4" />
                            Edit Date/Time
                          </h4>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                            <div>
                              <Label htmlFor="edit-date">Date</Label>
                              <Input
                                id="edit-date"
                                type="date"
                                value={editDate}
                                onChange={(e) => setEditDate(e.target.value)}
                                data-testid={`edit-date-input-${booking.id}`}
                              />
                            </div>
                            <div>
                              <Label htmlFor="edit-start">Start Time</Label>
                              <Input
                                id="edit-start"
                                type="time"
                                value={editStartTime}
                                onChange={(e) => setEditStartTime(e.target.value)}
                                data-testid={`edit-start-input-${booking.id}`}
                              />
                            </div>
                            <div>
                              <Label htmlFor="edit-end">End Time</Label>
                              <Input
                                id="edit-end"
                                type="time"
                                value={editEndTime}
                                onChange={(e) => setEditEndTime(e.target.value)}
                                data-testid={`edit-end-input-${booking.id}`}
                              />
                            </div>
                          </div>
                          <div className="flex gap-2 mt-3">
                            <Button
                              onClick={() => handleApprove(booking.id)}
                              className="bg-teal-600 hover:bg-teal-500"
                              data-testid={`save-edit-${booking.id}`}
                            >
                              Save & Approve
                            </Button>
                            <Button
                              onClick={cancelEdit}
                              variant="outline"
                              data-testid={`cancel-edit-${booking.id}`}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                    
                    {/* Action Buttons - Right Side - Organized into Categories */}
                    <div className="flex flex-wrap gap-2 w-full lg:w-auto lg:flex-col lg:min-w-[200px]">
                      {/* Row 1: Status & Primary Edit */}
                      {editingBooking !== booking.id && (
                        <div className="flex gap-2 w-full">
                          {/* Change Status Dropdown */}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button 
                                variant="outline" 
                                size="sm"
                                className="flex-1 border-teal-600 text-slate-200 hover:bg-slate-700 rounded-full"
                                data-testid={`change-status-${booking.id}`}
                              >
                                <Settings className="h-4 w-4 mr-1" />
                                Status
                                <ChevronDown className="h-3 w-3 ml-1" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent className="bg-slate-800 border-slate-700 w-56">
                              <DropdownMenuItem 
                                onClick={() => handleApprove(booking.id)}
                                className="text-white focus:bg-emerald-900/30 focus:text-emerald-300"
                                data-testid={`status-accept-${booking.id}`}
                              >
                                <CheckCircle className="h-4 w-4 mr-2" />
                                Accept
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => executeStatusChange(booking.id, 'pending')}
                                className="text-amber-400 focus:bg-amber-900/30 focus:text-amber-300"
                                data-testid={`status-pending-${booking.id}`}
                              >
                                <Clock className="h-4 w-4 mr-2" />
                                Pending
                              </DropdownMenuItem>
                              <DropdownMenuSeparator className="bg-slate-700" />
                              <DropdownMenuItem 
                                onClick={() => executeStatusChange(booking.id, 'rejected_no_refund')}
                                className="text-white focus:bg-slate-700 focus:text-slate-200"
                                data-testid={`status-rejected-${booking.id}`}
                              >
                                <XCircle className="h-4 w-4 mr-2" />
                                Rejected
                              </DropdownMenuItem>
                              {booking.payment_status === 'paid' && (booking.stripe_transaction_id || booking.stripe_order_id) && (
                                <DropdownMenuItem 
                                  onClick={() => executeStatusChange(booking.id, 'rejected')}
                                  className="text-red-400 focus:bg-red-900/30 focus:text-red-300"
                                  data-testid={`status-refund-${booking.id}`}
                                >
                                  <DollarSign className="h-4 w-4 mr-2" />
                                  Refund {booking.amount_paid ? `$${booking.amount_paid.toFixed(2)}` : ''}
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                          
                          {/* Edit Booking Button */}
                          <Button
                            onClick={() => handleEditBooking(booking)}
                            variant="outline"
                            size="sm"
                            className="border-teal-600 text-white hover:bg-slate-700 rounded-full"
                            data-testid={`edit-${booking.id}`}
                          >
                            <Edit2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                      
                      {/* Row 2: Booking Actions Dropdown */}
                      {editingBooking !== booking.id && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="w-full border-cyan-700 text-cyan-300 hover:bg-cyan-900/30 rounded-full"
                            >
                              <Users className="h-4 w-4 mr-2" />
                              Booking Actions
                              <ChevronDown className="h-3 w-3 ml-auto" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent className="bg-slate-800 border-slate-700 w-56">
                            <DropdownMenuItem 
                              onClick={() => openEditStudents(booking)}
                              className="text-white focus:bg-slate-700"
                              data-testid={`edit-students-${booking.id}`}
                            >
                              <Users className="h-4 w-4 mr-2" />
                              Edit Students
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={() => openEditDateTime(booking)}
                              className="text-white focus:bg-slate-700"
                              data-testid={`edit-datetime-${booking.id}`}
                            >
                              <Calendar className="h-4 w-4 mr-2" />
                              Edit Date/Time
                            </DropdownMenuItem>
                            <DropdownMenuSeparator className="bg-slate-700" />
                            {!['rejected', 'rejected_no_refund', 'moved_to_queue'].includes(booking.status) && (
                              <DropdownMenuItem 
                                onClick={() => toggleBookingPublic(booking)}
                                className={booking.is_public ? "text-purple-300 focus:bg-purple-900/30" : "text-white focus:bg-slate-700"}
                                data-testid={`toggle-public-${booking.id}`}
                              >
                                {booking.is_public ? (
                                  <>
                                    <LockKeyhole className="h-4 w-4 mr-2" />
                                    Make Private
                                  </>
                                ) : (
                                  <>
                                    <Globe className="h-4 w-4 mr-2" />
                                    Make Public
                                  </>
                                )}
                              </DropdownMenuItem>
                            )}
                            {booking.status === 'approved' && (
                              <DropdownMenuItem 
                                onClick={() => toggleRegistration(booking)}
                                className={booking.registration_closed ? "text-red-400 focus:bg-red-900/30" : "text-green-400 focus:bg-green-900/30"}
                                data-testid={`toggle-registration-${booking.id}`}
                              >
                                {booking.registration_closed ? (
                                  <>
                                    <UserPlus className="h-4 w-4 mr-2" />
                                    Open Registration
                                  </>
                                ) : (
                                  <>
                                    <EyeOff className="h-4 w-4 mr-2" />
                                    Close Registration
                                  </>
                                )}
                              </DropdownMenuItem>
                            )}
                            {/* Add-on Fee Waivers - for approved classes */}
                            {booking.status === 'approved' && (
                              <DropdownMenuItem 
                                onClick={() => openFeeWaiversDialog(booking)}
                                className={
                                  (booking.addon_payment_waived || booking.addon_rush_fee_waived || booking.addon_afterhours_fee_waived)
                                    ? "text-emerald-400 focus:bg-emerald-900/30" 
                                    : "text-slate-300 focus:bg-slate-700"
                                }
                                data-testid={`fee-waivers-${booking.id}`}
                              >
                                <DollarSign className="h-4 w-4 mr-2" />
                                Fee Waivers
                                {(booking.addon_payment_waived || booking.addon_rush_fee_waived || booking.addon_afterhours_fee_waived) && (
                                  <span className="ml-2 text-emerald-400">✓</span>
                                )}
                              </DropdownMenuItem>
                            )}
                            {/* Link Invoice Option */}
                            <DropdownMenuItem 
                              onClick={() => openLinkInvoiceDialog(booking)}
                              className={
                                booking.linked_invoice_id
                                  ? "text-blue-400 focus:bg-blue-900/30" 
                                  : "text-slate-300 focus:bg-slate-700"
                              }
                              data-testid={`link-invoice-${booking.id}`}
                            >
                              <FileText className="h-4 w-4 mr-2" />
                              {booking.linked_invoice_id ? `Invoice: ${booking.linked_invoice_id}` : 'Link Invoice'}
                            </DropdownMenuItem>
                            {/* Create Invoice for Recurring Client Booking */}
                            {booking.recurring_client_id && !booking.linked_invoice_id && (
                              <DropdownMenuItem 
                                onClick={() => createInvoiceForBooking(booking.id)}
                                className="text-teal-400 focus:bg-teal-900/30"
                                data-testid={`create-invoice-${booking.id}`}
                              >
                                <FileText className="h-4 w-4 mr-2" />
                                Create Invoice
                              </DropdownMenuItem>
                            )}
                            {/* Auto-Invoice Toggle for Recurring Client Booking */}
                            {booking.recurring_client_id && !booking.linked_invoice_id && booking.payment_status !== 'paid' && (
                              <DropdownMenuItem 
                                onClick={() => toggleAutoInvoice(booking.id, !booking.auto_invoice)}
                                className={booking.auto_invoice ? "text-amber-400 focus:bg-amber-900/30" : "text-slate-400 focus:bg-slate-700"}
                                data-testid={`auto-invoice-toggle-${booking.id}`}
                              >
                                <Clock className="h-4 w-4 mr-2" />
                                Auto-Invoice: {booking.auto_invoice ? 'ON' : 'OFF'}
                              </DropdownMenuItem>
                            )}
                            {/* Link/Unlink Recurring Client */}
                            <DropdownMenuItem 
                              onClick={() => {
                                if (booking.recurring_client_id) {
                                  handleUnlinkRecurringClient(booking.id);
                                } else {
                                  openLinkRecurringClientDialog(booking);
                                }
                              }}
                              className={
                                booking.recurring_client_id
                                  ? "text-blue-400 focus:bg-blue-900/30" 
                                  : "text-slate-300 focus:bg-slate-700"
                              }
                              data-testid={`link-recurring-client-${booking.id}`}
                            >
                              <Users className="h-4 w-4 mr-2" />
                              {booking.recurring_client_id 
                                ? `RC: ${booking.recurring_client_name || 'Linked'}` 
                                : 'Link Recurring Client'}
                              {booking.recurring_client_id && (
                                <X className="h-3 w-3 ml-auto text-red-400" />
                              )}
                            </DropdownMenuItem>
                            {booking.students?.length > 0 && !['rejected', 'rejected_no_refund', 'moved_to_queue'].includes(booking.status) && (
                              <>
                                <DropdownMenuSeparator className="bg-slate-700" />
                                <DropdownMenuItem 
                                  onClick={() => moveBookingToQueue(booking)}
                                  className="text-amber-400 focus:bg-amber-900/30"
                                  data-testid={`move-to-queue-${booking.id}`}
                                >
                                  <ArrowRightLeft className="h-4 w-4 mr-2" />
                                  Move to Queue
                                </DropdownMenuItem>
                              </>
                            )}
                            {booking.payment_status === 'unpaid' && !['rejected', 'rejected_no_refund'].includes(booking.status) && (
                              <>
                                <DropdownMenuSeparator className="bg-slate-700" />
                                <DropdownMenuItem 
                                  onClick={() => markAsPaid(booking.id)}
                                  className="text-emerald-400 focus:bg-emerald-900/30"
                                  data-testid={`mark-paid-${booking.id}`}
                                >
                                  <DollarSign className="h-4 w-4 mr-2" />
                                  Mark as Paid
                                </DropdownMenuItem>
                              </>
                            )}
                            {booking.payment_status === 'paid' && !['rejected', 'rejected_no_refund'].includes(booking.status) && (
                              <>
                                <DropdownMenuSeparator className="bg-slate-700" />
                                <DropdownMenuItem 
                                  onClick={() => markAsUnpaid(booking.id)}
                                  className="text-orange-400 focus:bg-orange-900/30"
                                  data-testid={`mark-unpaid-${booking.id}`}
                                >
                                  <XCircle className="h-4 w-4 mr-2" />
                                  Mark as Not Paid
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                      
                      {/* Send Digital Tests Button - BLS New/Renewal classes only */}
                      {editingBooking !== booking.id && isBLSClass(booking.class_type_name) && (
                        <Button
                          onClick={() => openDigitalTestDialog(booking)}
                          variant="outline"
                          size="sm"
                          className="w-full border-cyan-600 bg-cyan-900/20 text-cyan-300 hover:bg-cyan-900/40 rounded-full"
                          data-testid={`send-digital-tests-${booking.id}`}
                        >
                          <FileText className="h-4 w-4 mr-2" />
                          Send Digital Tests
                        </Button>
                      )}
                      
                      {/* Row 3: Communications Dropdown */}
                      {editingBooking !== booking.id && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="w-full border-blue-700 text-blue-300 hover:bg-blue-900/30 rounded-full"
                            >
                              <Mail className="h-4 w-4 mr-2" />
                              Communications
                              <ChevronDown className="h-3 w-3 ml-auto" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent className="bg-slate-800 border-slate-700 w-56">
                            <DropdownMenuItem 
                              onClick={() => openEmailLog(booking)}
                              className="text-white focus:bg-slate-700"
                              data-testid={`email-history-${booking.id}`}
                            >
                              <Mail className="h-4 w-4 mr-2" />
                              Email History
                            </DropdownMenuItem>
                            {booking.students?.length > 0 && (
                              <>
                                <DropdownMenuSeparator className="bg-slate-700" />
                                <DropdownMenuLabel className="text-xs text-slate-400 px-2">
                                  Send to {booking.students.length > 1 ? `All ${booking.students.length} Students` : 'Student'}
                                </DropdownMenuLabel>
                                <DropdownMenuItem 
                                  onClick={() => handleResendEmail(booking.id, 'new_booking', null)}
                                  className="text-white focus:bg-slate-700"
                                >
                                  <Mail className="h-4 w-4 mr-2" />
                                  Booking Confirmation
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => handleResendEmail(booking.id, 'payment_reminder', null)}
                                  className="text-white focus:bg-slate-700"
                                >
                                  <CreditCard className="h-4 w-4 mr-2" />
                                  Payment Reminder
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => handleResendEmail(booking.id, 'booking_approved', null)}
                                  className="text-white focus:bg-slate-700"
                                >
                                  <CheckCircle className="h-4 w-4 mr-2" />
                                  Approval Email
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => handleResendEmail(booking.id, 'student_class_details', null)}
                                  className="text-white focus:bg-slate-700"
                                >
                                  <FileText className="h-4 w-4 mr-2" />
                                  Class Details
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => handleResendEmail(booking.id, 'class_reminder', null)}
                                  className="text-white focus:bg-slate-700"
                                >
                                  <Clock className="h-4 w-4 mr-2" />
                                  24hr Class Reminder
                                </DropdownMenuItem>
                                <DropdownMenuItem 
                                  onClick={() => handleResendEmail(booking.id, 'class_followup', null)}
                                  className="text-white focus:bg-slate-700"
                                >
                                  <Heart className="h-4 w-4 mr-2" />
                                  Post-Class Thank You
                                </DropdownMenuItem>
                              </>
                            )}
                            {booking.status === 'approved' && booking.students?.length > 0 && (
                              <>
                                <DropdownMenuSeparator className="bg-slate-700" />
                                <DropdownMenuItem 
                                  onClick={() => sendClassRosterEmail(booking)}
                                  disabled={sendingRosterEmail === booking.id}
                                  className="text-blue-400 focus:bg-blue-900/30"
                                  data-testid={`send-roster-email-${booking.id}`}
                                >
                                  {sendingRosterEmail === booking.id ? (
                                    <>
                                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                      Sending...
                                    </>
                                  ) : (
                                    <>
                                      <FileText className="h-4 w-4 mr-2" />
                                      Class Roster Email
                                    </>
                                  )}
                                </DropdownMenuItem>
                              </>
                            )}
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                      
                      {/* Row 4: Paperwork & Export Dropdown */}
                      {editingBooking !== booking.id && booking.students?.length > 0 && (
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button 
                              variant="outline" 
                              size="sm"
                              className="w-full border-purple-700 text-purple-300 hover:bg-purple-900/30 rounded-full"
                            >
                              <FileText className="h-4 w-4 mr-2" />
                              Paperwork
                              <ChevronDown className="h-3 w-3 ml-auto" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent className="bg-slate-800 border-slate-700 w-56">
                            {booking.status === 'approved' && 
                             booking.registration_closed &&
                             paperworkMappedClasses.includes(booking.class_type_name) && (
                              <DropdownMenuItem 
                                onClick={() => generatePaperwork(booking)}
                                disabled={generatingPaperwork === booking.id}
                                className="text-purple-400 focus:bg-purple-900/30"
                                data-testid={`generate-paperwork-${booking.id}`}
                              >
                                {generatingPaperwork === booking.id ? (
                                  <>
                                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                    Generating...
                                  </>
                                ) : (
                                  <>
                                    <FileText className="h-4 w-4 mr-2" />
                                    AHA Paperwork
                                  </>
                                )}
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem 
                              onClick={() => window.open(`${API}/bookings/${booking.id}/export-students`, '_blank')}
                              className="text-emerald-400 focus:bg-emerald-900/30"
                              data-testid={`export-students-${booking.id}`}
                            >
                              <Download className="h-4 w-4 mr-2" />
                              Export Students (Excel)
                            </DropdownMenuItem>
                            <DropdownMenuSeparator className="bg-slate-700" />
                            <DropdownMenuItem 
                              onClick={() => sendPaperworkEmail(booking.id)}
                              disabled={sendingPaperworkEmail === booking.id}
                              className="text-amber-400 focus:bg-amber-900/30"
                              data-testid={`send-paperwork-email-${booking.id}`}
                            >
                              {sendingPaperworkEmail === booking.id ? (
                                <>
                                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                                  Sending...
                                </>
                              ) : (
                                <>
                                  <Mail className="h-4 w-4 mr-2" />
                                  Send Paperwork Email
                                </>
                              )}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      )}
                      
                      {/* Row 5: Calendar & More Actions */}
                      {editingBooking !== booking.id && (
                        <div className="flex gap-2 w-full">
                          {/* Calendar Dropdown */}
                          {calendarConnected && (
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button 
                                  variant="outline" 
                                  size="sm"
                                  className={`flex-1 rounded-full ${
                                    booking.calendar_event_id 
                                      ? 'border-teal-600 text-teal-300 hover:bg-teal-900/30' 
                                      : 'border-slate-600 text-slate-300 hover:bg-slate-700'
                                  }`}
                                >
                                  <Calendar className="h-4 w-4 mr-1" />
                                  Cal
                                  <ChevronDown className="h-3 w-3 ml-1" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent className="bg-slate-800 border-slate-700 w-48">
                                {!booking.calendar_event_id ? (
                                  <DropdownMenuItem 
                                    onClick={() => handleAddToCalendar(booking.id)}
                                    className="text-teal-400 focus:bg-teal-900/30"
                                    data-testid={`add-calendar-${booking.id}`}
                                  >
                                    <CalendarPlus className="h-4 w-4 mr-2" />
                                    Add to Calendar
                                  </DropdownMenuItem>
                                ) : (
                                  <>
                                    <DropdownMenuItem 
                                      onClick={() => handleRefreshCalendarEvent(booking.id)}
                                      className="text-blue-400 focus:bg-blue-900/30"
                                      data-testid={`refresh-calendar-${booking.id}`}
                                    >
                                      <RefreshCw className="h-4 w-4 mr-2" />
                                      Refresh Calendar Event
                                    </DropdownMenuItem>
                                    <DropdownMenuSeparator className="bg-slate-700" />
                                    <DropdownMenuItem 
                                      onClick={() => handleRemoveFromCalendar(booking.id)}
                                      className="text-orange-400 focus:bg-orange-900/30"
                                      data-testid={`remove-calendar-${booking.id}`}
                                    >
                                      <CalendarX className="h-4 w-4 mr-2" />
                                      Remove from Calendar
                                    </DropdownMenuItem>
                                  </>
                                )}
                              </DropdownMenuContent>
                            </DropdownMenu>
                          )}
                          
                          {/* More Actions Dropdown */}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button 
                                variant="outline" 
                                size="sm"
                                className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700 rounded-full"
                              >
                                <MoreHorizontal className="h-4 w-4 mr-1" />
                                More
                                <ChevronDown className="h-3 w-3 ml-1" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent className="bg-slate-800 border-slate-700 w-48">
                              <DropdownMenuItem 
                                onClick={() => openNotesDialog(booking)}
                                className="text-purple-300 focus:bg-purple-900/30"
                                data-testid={`notes-${booking.id}`}
                              >
                                <FileText className="h-4 w-4 mr-2" />
                                {booking.admin_notes ? 'View Notes' : 'Add Notes'}
                              </DropdownMenuItem>
                              <DropdownMenuSeparator className="bg-slate-700" />
                              {/* Enrollware Sync Button */}
                              <DropdownMenuItem 
                                onClick={() => handleSyncToEnrollware(booking.id)}
                                className={`${booking.enrollware_class_id ? 'text-green-400 focus:bg-green-900/30' : 'text-orange-400 focus:bg-orange-900/30'}`}
                                data-testid={`enrollware-sync-${booking.id}`}
                              >
                                <Upload className="h-4 w-4 mr-2" />
                                {booking.enrollware_class_id ? 'Update Enrollware' : 'Sync to Enrollware'}
                              </DropdownMenuItem>
                              {/* Issue eCards Button - only show if synced to Enrollware */}
                              {booking.enrollware_class_id && (
                                <DropdownMenuItem 
                                  onClick={() => handleIssueEcards(booking.id)}
                                  className={`${booking.ecards_issued ? 'text-emerald-400 focus:bg-emerald-900/30' : 'text-cyan-400 focus:bg-cyan-900/30'}`}
                                  data-testid={`issue-ecards-${booking.id}`}
                                >
                                  <CreditCard className="h-4 w-4 mr-2" />
                                  {booking.ecards_issued ? `eCards Issued (${booking.ecards_issued_count || '✓'})` : 'Issue eCards'}
                                </DropdownMenuItem>
                              )}
                              {/* Delete from Enrollware - only show if synced */}
                              {booking.enrollware_class_id && (
                                <DropdownMenuItem 
                                  onClick={() => setDeleteEnrollwareConfirm(booking.id)}
                                  className="text-red-400 focus:bg-red-900/30"
                                  data-testid={`delete-enrollware-${booking.id}`}
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Delete from Enrollware
                                </DropdownMenuItem>
                              )}
                              {/* Fetch BLS Test Certificates - only for BLS classes synced to Enrollware */}
                              {booking.enrollware_class_id && 
                               (booking.class_type_name?.toLowerCase().includes('bls') || 
                                booking.class_type_name?.toLowerCase().includes('basic life support')) && (
                                <>
                                  <DropdownMenuItem 
                                    onClick={() => handleFetchBlsCertificates(booking.id)}
                                    className="text-purple-400 focus:bg-purple-900/30"
                                    data-testid={`fetch-bls-certs-${booking.id}`}
                                  >
                                    <FileCheck className="h-4 w-4 mr-2" />
                                    Fetch BLS Test Certs
                                  </DropdownMenuItem>
                                  {blsPollingStatus[booking.id]?.active ? (
                                    <DropdownMenuItem 
                                      onClick={() => stopBlsPolling(booking.id)}
                                      className="text-amber-400 focus:bg-amber-900/30"
                                      data-testid={`stop-bls-polling-${booking.id}`}
                                    >
                                      <StopCircle className="h-4 w-4 mr-2" />
                                      Stop Auto-Poll ({Math.round((Date.now() - (blsPollingStatus[booking.id]?.startTime || Date.now())) / 60000)}m)
                                    </DropdownMenuItem>
                                  ) : (
                                    <DropdownMenuItem 
                                      onClick={() => startBlsPolling(booking.id)}
                                      className="text-cyan-400 focus:bg-cyan-900/30"
                                      data-testid={`start-bls-polling-${booking.id}`}
                                    >
                                      <RefreshCw className="h-4 w-4 mr-2" />
                                      Auto-Poll BLS Certs (1hr)
                                    </DropdownMenuItem>
                                  )}
                                </>
                              )}
                              <DropdownMenuSeparator className="bg-slate-700" />
                              {booking.status === 'hidden' ? (
                                <>
                                  <DropdownMenuItem 
                                    onClick={() => handleRestore(booking.id, 'pending')}
                                    className="text-teal-400 focus:bg-teal-900/30"
                                    data-testid={`restore-${booking.id}`}
                                  >
                                    <RotateCcw className="h-4 w-4 mr-2" />
                                    Restore Booking
                                  </DropdownMenuItem>
                                  <DropdownMenuItem 
                                    onClick={async () => {
                                      if (window.confirm('Permanently delete this booking? This cannot be undone.')) {
                                        try {
                                          await axios.delete(`${API}/bookings/${booking.id}?permanent=true`);
                                          toast.success('Booking permanently deleted');
                                          fetchBookings();
                                        } catch (error) {
                                          toast.error('Failed to delete booking');
                                        }
                                      }
                                    }}
                                    className="text-red-400 focus:bg-red-900/30"
                                    data-testid={`delete-permanently-${booking.id}`}
                                  >
                                    <Trash2 className="h-4 w-4 mr-2" />
                                    Delete Permanently
                                  </DropdownMenuItem>
                                </>
                              ) : (
                                <DropdownMenuItem 
                                  onClick={() => handleDelete(booking.id, !!booking.calendar_event_id)}
                                  className="text-red-400 focus:bg-red-900/30"
                                  data-testid={`delete-${booking.id}`}
                                >
                                  <Trash2 className="h-4 w-4 mr-2" />
                                  Delete Booking
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      )}
                      
                      {/* Recurring Client Charges Display */}
                      {booking.recurring_client_id && 
                       !booking.linked_invoice_id && 
                       booking.payment_status !== 'paid' &&
                       !['rejected', 'rejected_no_refund', 'hidden'].includes(booking.status) && (
                        <div 
                          onClick={() => openChargesPreview(booking)}
                          className="mt-3 p-3 bg-slate-700/50 rounded-lg border border-emerald-600/30 cursor-pointer hover:bg-slate-700/70 hover:border-emerald-500/50 transition-all"
                          data-testid={`charges-display-${booking.id}`}
                        >
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              <DollarSign className="h-4 w-4 text-emerald-400" />
                              <span className="text-sm font-medium text-emerald-300">Estimated Charges</span>
                            </div>
                            <div className="flex items-center gap-2">
                              {bookingChargesCache[booking.id] ? (
                                <span className="text-lg font-bold text-emerald-400">
                                  ${bookingChargesCache[booking.id].total?.toFixed(2) || '0.00'}
                                </span>
                              ) : (
                                <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                              )}
                              <ChevronDown className="h-4 w-4 text-slate-400" />
                            </div>
                          </div>
                          {bookingChargesCache[booking.id] && (
                            <div className="mt-2 text-xs text-slate-400">
                              {bookingChargesCache[booking.id].student_count} student(s) × ${bookingChargesCache[booking.id].base_price?.toFixed(2) || '0.00'}
                              {bookingChargesCache[booking.id].custom_price !== null && (
                                <span className="ml-1 text-amber-400">(custom price)</span>
                              )}
                              {(bookingChargesCache[booking.id].travel_fee > 0 || 
                                bookingChargesCache[booking.id].rush_fee > 0 || 
                                bookingChargesCache[booking.id].late_add_fee > 0) && (
                                <span className="ml-1 text-blue-400">+ fees</span>
                              )}
                            </div>
                          )}
                          <p className="text-[10px] text-slate-500 mt-1">Click for detailed breakdown</p>
                        </div>
                      )}
                      
                    </div>
                  </div>
                </div>
                )}
              </Card>
              );
            })}
          </div>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-center gap-2 mt-6 pb-4" data-testid="pagination">
              {/* Previous Button */}
              <Button
                onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                disabled={currentPage === 1}
                variant="outline"
                size="sm"
                className="border-teal-600 text-white hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="pagination-prev"
              >
                <ArrowLeft className="h-4 w-4 mr-1" />
                Back
              </Button>
              
              {/* Page Numbers */}
              <div className="flex items-center gap-1">
                {getPageNumbers().map((page, idx) => (
                  page === '...' ? (
                    <span key={`ellipsis-${idx}`} className="px-2 text-slate-500">...</span>
                  ) : (
                    <Button
                      key={page}
                      onClick={() => setCurrentPage(page)}
                      variant={currentPage === page ? "default" : "outline"}
                      size="sm"
                      className={`min-w-[36px] ${
                        currentPage === page 
                          ? 'bg-teal-600 hover:bg-teal-500 text-white' 
                          : 'border-teal-600 text-white hover:bg-slate-700'
                      }`}
                      data-testid={`pagination-page-${page}`}
                    >
                      {page}
                    </Button>
                  )
                ))}
              </div>
              
              {/* Next Button */}
              <Button
                onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                disabled={currentPage === totalPages}
                variant="outline"
                size="sm"
                className="border-teal-600 text-white hover:bg-slate-700 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="pagination-next"
              >
                Next
                <ArrowRight className="h-4 w-4 ml-1" />
              </Button>
            </div>
          )}
          </div>
        )}
      </main>
      
      <PasswordChangeDialog 
        open={showPasswordChange} 
        onClose={() => setShowPasswordChange(false)} 
      />
      
      <CreateBookingDialog
        open={showCreateBooking}
        onClose={() => setShowCreateBooking(false)}
        onSuccess={() => {
          fetchBookings();
        }}
      />
      
      {/* Notes Dialog */}
      <Dialog open={showNotesDialog} onOpenChange={setShowNotesDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 text-slate-100">
          <DialogHeader>
            <DialogTitle className="text-slate-100 flex items-center gap-2">
              <FileText className="h-5 w-5 text-white" />
              Admin Notes
            </DialogTitle>
            <DialogDescription className="text-white">
              Internal notes for this booking. Will be synced to WooCommerce order.
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Textarea
              value={notesText}
              onChange={(e) => setNotesText(e.target.value)}
              placeholder="Enter internal notes about this booking..."
              className="min-h-[150px] bg-slate-700 border-teal-600 text-slate-100 placeholder:text-slate-500"
              data-testid="notes-textarea"
            />
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowNotesDialog(false)}
              className="border-teal-600 text-white hover:bg-slate-700"
            >
              Cancel
            </Button>
            <Button
              onClick={saveNotes}
              disabled={notesSaving}
              className="bg-teal-600 hover:bg-teal-500 text-white"
              data-testid="save-notes-button"
            >
              {notesSaving ? 'Saving...' : 'Save Notes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Change Class Type Dialog */}
      <Dialog open={showChangeClassType} onOpenChange={(open) => { if (!open) closeChangeClassType(); }}>
        <DialogContent className="sm:max-w-md bg-slate-800 border-slate-700 text-slate-100">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-cyan-400">
              <ArrowRightLeft className="h-5 w-5" />
              Change Class Type
            </DialogTitle>
            <DialogDescription className="text-white">
              Change the class type for this booking. All changes are tracked with date/time and reason.
            </DialogDescription>
          </DialogHeader>
          
          {changeClassTypeBooking && (
            <div className="space-y-4 py-4">
              {/* Current Class Type */}
              <div className="bg-slate-700/50 p-3 rounded-lg">
                <p className="text-xs text-white mb-1">Current Class Type:</p>
                <div className="flex items-center justify-between">
                  <p className="text-slate-200 font-medium">{changeClassTypeBooking.class_type_name}</p>
                  {!loadingPrices && classTypesWithPrices.find(ct => ct.id === changeClassTypeBooking.class_type_id)?.price > 0 && (
                    <span className="text-white font-medium">
                      ${classTypesWithPrices.find(ct => ct.id === changeClassTypeBooking.class_type_id)?.price.toFixed(2)}/student
                    </span>
                  )}
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  Booking: {changeClassTypeBooking.students?.[0]?.first_name} {changeClassTypeBooking.students?.[0]?.last_name}
                  {changeClassTypeBooking.students?.length > 1 && ` +${changeClassTypeBooking.students.length - 1} more`}
                  <span className="ml-2">({changeClassTypeBooking.students?.length || 0} student{(changeClassTypeBooking.students?.length || 0) !== 1 ? 's' : ''})</span>
                </p>
              </div>
              
              {/* Show history if exists */}
              {changeClassTypeBooking.class_type_history && changeClassTypeBooking.class_type_history.length > 0 && (
                <div className="bg-amber-900/20 border border-amber-700/50 p-3 rounded-lg">
                  <p className="text-xs text-amber-400 font-medium mb-2">Previous Changes:</p>
                  <div className="space-y-1 max-h-20 overflow-y-auto">
                    {changeClassTypeBooking.class_type_history.map((change, idx) => (
                      <div key={idx} className="text-xs text-amber-300/80">
                        {change.from_class_type_name} → {change.to_class_type_name}
                        {change.price_difference !== undefined && change.price_difference !== 0 && (
                          <span className={change.price_difference > 0 ? 'text-red-400 ml-1' : 'text-white ml-1'}>
                            ({change.price_difference > 0 ? '+' : ''}${change.price_difference.toFixed(2)})
                          </span>
                        )}
                        <span className="text-amber-400/60 ml-1">
                          ({change.changed_at && formatInTimeZone(new Date(change.changed_at), 'America/Los_Angeles', 'M/d/yy')})
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* New Class Type Selection */}
              <div>
                <label className="text-sm text-white mb-2 block">New Class Type:</label>
                {loadingPrices ? (
                  <div className="flex items-center justify-center h-10 bg-slate-700 rounded-md">
                    <Loader2 className="h-4 w-4 animate-spin text-white" />
                    <span className="text-sm text-white ml-2">Loading prices...</span>
                  </div>
                ) : (
                  <select
                    value={newClassTypeId}
                    onChange={(e) => setNewClassTypeId(e.target.value)}
                    className="w-full h-10 bg-slate-700 border border-teal-600 text-slate-100 rounded-md px-3"
                    data-testid="new-class-type-select"
                  >
                    <option value="">Select a class type...</option>
                    {classTypesWithPrices
                      .filter(ct => ct.id !== changeClassTypeBooking.class_type_id)
                      .map(ct => (
                        <option key={ct.id} value={ct.id}>
                          {ct.name} {ct.price > 0 ? `($${ct.price.toFixed(2)}/student)` : ''}
                        </option>
                      ))
                    }
                  </select>
                )}
              </div>
              
              {/* Price Comparison - show when new class type selected */}
              {newClassTypeId && !loadingPrices && (() => {
                const currentPrice = classTypesWithPrices.find(ct => ct.id === changeClassTypeBooking.class_type_id)?.price || 0;
                const newPrice = classTypesWithPrices.find(ct => ct.id === newClassTypeId)?.price || 0;
                const studentCount = changeClassTypeBooking.students?.length || 0;
                const priceDiff = (newPrice - currentPrice) * studentCount;
                
                if (priceDiff === 0) return null;
                
                return (
                  <div className={`p-3 rounded-lg border ${priceDiff > 0 ? 'bg-red-900/20 border-red-700/50' : 'bg-emerald-900/20 border-emerald-700/50'}`}>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-xs text-white">Price Difference ({studentCount} student{studentCount !== 1 ? 's' : ''}):</p>
                        <p className={`text-lg font-bold ${priceDiff > 0 ? 'text-red-400' : 'text-white'}`}>
                          {priceDiff > 0 ? '+' : ''}${priceDiff.toFixed(2)}
                        </p>
                      </div>
                      <div className="text-right text-xs text-white">
                        <p>Current: ${(currentPrice * studentCount).toFixed(2)}</p>
                        <p>New: ${(newPrice * studentCount).toFixed(2)}</p>
                      </div>
                    </div>
                    {priceDiff > 0 && (
                      <div className="mt-2 pt-2 border-t border-red-700/30">
                        <p className="text-xs text-red-300 flex items-center gap-1">
                          <AlertCircle className="h-3 w-3" />
                          Customer will be emailed to pay the ${priceDiff.toFixed(2)} balance. Booking status will change to "Partially Paid".
                        </p>
                      </div>
                    )}
                    {priceDiff < 0 && (
                      <p className="text-xs text-emerald-300 mt-2">
                        No refund will be issued for the lower price.
                      </p>
                    )}
                  </div>
                );
              })()}
              
              {/* Reason for Change */}
              <div>
                <label className="text-sm text-white mb-2 block">Reason for Change: <span className="text-red-400">*</span></label>
                <Input
                  value={classTypeChangeReason}
                  onChange={(e) => setClassTypeChangeReason(e.target.value)}
                  placeholder="e.g., Customer requested upgrade, Schedule conflict..."
                  className="bg-slate-700 border-teal-600 text-slate-100"
                  data-testid="class-type-change-reason"
                />
              </div>
              
              {/* Warning */}
              <div className="bg-slate-900/50 border border-slate-700 p-3 rounded-lg">
                <p className="text-xs text-white">
                  <AlertCircle className="h-3 w-3 inline mr-1 text-amber-400" />
                  This change will be recorded in the booking history. The calendar event will be updated automatically.
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={closeChangeClassType}
              className="border-teal-600 text-white"
              disabled={changingClassType}
            >
              Cancel
            </Button>
            <Button
              onClick={handleChangeClassType}
              disabled={changingClassType || !newClassTypeId || !classTypeChangeReason.trim() || loadingPrices}
              className="bg-cyan-600 hover:bg-cyan-500"
              data-testid="confirm-class-type-change"
            >
              {changingClassType ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Changing...
                </>
              ) : (
                <>
                  <ArrowRightLeft className="h-4 w-4 mr-2" />
                  Change Class Type
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Charges Preview Dialog */}
      <Dialog open={showChargesPreview} onOpenChange={(open) => { if (!open) closeChargesPreview(); }}>
        <DialogContent className="sm:max-w-md bg-slate-800 border-slate-700 text-slate-100">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-400">
              <DollarSign className="h-5 w-5" />
              Estimated Charges Breakdown
            </DialogTitle>
            <DialogDescription className="text-white">
              {chargesPreviewBooking?.class_type_name} - {chargesPreviewBooking?.students?.length || 0} student(s)
            </DialogDescription>
          </DialogHeader>
          
          {chargesPreviewLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-emerald-400" />
              <span className="ml-2 text-slate-400">Loading charges...</span>
            </div>
          ) : chargesPreviewData ? (
            <div className="space-y-4 py-4">
              {/* Breakdown Items */}
              <div className="space-y-2">
                {chargesPreviewData.breakdown?.filter(item => item !== null).map((item, idx) => (
                  <div key={idx} className="flex justify-between items-center py-2 border-b border-slate-700">
                    <span className="text-slate-300">{item.description}</span>
                    <span className="text-slate-200 font-medium">${item.amount?.toFixed(2) || '0.00'}</span>
                  </div>
                ))}
              </div>
              
              {/* Custom Price Indicator */}
              {chargesPreviewData.custom_price !== null && (
                <div className="bg-amber-900/30 border border-amber-700/50 p-3 rounded-lg">
                  <div className="flex items-center gap-2 text-amber-400">
                    <Tag className="h-4 w-4" />
                    <span className="text-sm font-medium">Custom pricing applied</span>
                  </div>
                  <p className="text-xs text-amber-300/70 mt-1">
                    This recurring client has a custom rate of ${chargesPreviewData.custom_price?.toFixed(2)}/student
                  </p>
                </div>
              )}
              
              {/* Additional Fee Details */}
              {(chargesPreviewData.travel_fee > 0 || chargesPreviewData.rush_fee > 0 || chargesPreviewData.late_add_fee > 0) && (
                <div className="bg-blue-900/30 border border-blue-700/50 p-3 rounded-lg">
                  <div className="flex items-center gap-2 text-blue-400 mb-2">
                    <Info className="h-4 w-4" />
                    <span className="text-sm font-medium">Additional Fees</span>
                  </div>
                  <div className="space-y-1 text-xs text-blue-300/80">
                    {chargesPreviewData.travel_fee > 0 && (
                      <p>Travel fee: Outside service area mileage charge</p>
                    )}
                    {chargesPreviewData.rush_fee > 0 && (
                      <p>Rush fee: Booking made within rush window</p>
                    )}
                    {chargesPreviewData.late_add_fee > 0 && (
                      <p>Late-add fee: {chargesPreviewData.late_add_count} student(s) added within late-add window</p>
                    )}
                  </div>
                </div>
              )}
              
              {/* Total */}
              <div className="bg-emerald-900/30 border border-emerald-600/50 p-4 rounded-lg">
                <div className="flex justify-between items-center">
                  <span className="text-emerald-300 font-medium">Total Estimated Charge</span>
                  <span className="text-2xl font-bold text-emerald-400">
                    ${chargesPreviewData.total?.toFixed(2) || '0.00'}
                  </span>
                </div>
                <p className="text-xs text-emerald-400/60 mt-2">
                  This amount will be charged at 48 hours before class (auto-invoice) or when invoice is created manually.
                </p>
              </div>
              
              {/* Linked Invoice Warning */}
              {chargesPreviewBooking?.linked_invoice_id && (
                <div className="bg-purple-900/30 border border-purple-700/50 p-3 rounded-lg">
                  <div className="flex items-center gap-2 text-purple-400">
                    <FileText className="h-4 w-4" />
                    <span className="text-sm">Invoice already created: {chargesPreviewBooking.linked_invoice_id}</span>
                  </div>
                </div>
              )}
            </div>
          ) : (
            <div className="py-8 text-center text-slate-400">
              <AlertCircle className="h-8 w-8 mx-auto mb-2 text-slate-500" />
              <p>Unable to calculate charges</p>
              <p className="text-xs mt-1">Please try again or check booking details</p>
            </div>
          )}
          
          <DialogFooter>
            <Button
              onClick={closeChargesPreview}
              className="bg-slate-700 hover:bg-slate-600"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Students Dialog */}
      <Dialog open={showEditStudents} onOpenChange={setShowEditStudents}>
        <DialogContent className="sm:max-w-2xl max-h-[90vh] overflow-y-auto bg-slate-800 border-slate-700 text-slate-100" data-testid="edit-students-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-indigo-400">
              <Users className="h-5 w-5" />
              Edit Students
            </DialogTitle>
            <DialogDescription className="text-white">
              {editStudentsBooking?.class_type_name} - {editStudentsBooking?.start_time && formatInTimeZone(new Date(editStudentsBooking.start_time), 'America/Los_Angeles', 'MMM d, yyyy h:mm a')} PST
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Current Students */}
            <div className="space-y-2">
              <Label className="text-slate-200">Current Students ({editStudentsList.length})</Label>
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {editStudentsList.map((student, index) => {
                  const isEditing = editingStudentIndex === index;
                  
                  return (
                    <div key={`student-${index}`} className="p-3 bg-slate-700 rounded-lg border border-teal-600">
                      {isEditing ? (
                        // Edit mode
                        <div className="space-y-2">
                          <div className="grid grid-cols-2 gap-2">
                            <Input
                              placeholder="First Name"
                              value={student.first_name}
                              onChange={(e) => updateExistingStudent(index, 'first_name', e.target.value)}
                              className="bg-slate-800 border-teal-600 text-slate-100 placeholder:text-slate-500"
                              data-testid={`edit-student-firstname-${index}`}
                            />
                            <Input
                              placeholder="Last Name"
                              value={student.last_name}
                              onChange={(e) => updateExistingStudent(index, 'last_name', e.target.value)}
                              className="bg-slate-800 border-teal-600 text-slate-100 placeholder:text-slate-500"
                              data-testid={`edit-student-lastname-${index}`}
                            />
                            <Input
                              placeholder="Email"
                              type="email"
                              value={student.email}
                              onChange={(e) => updateExistingStudent(index, 'email', e.target.value)}
                              className="bg-slate-800 border-teal-600 text-slate-100 placeholder:text-slate-500"
                              data-testid={`edit-student-email-${index}`}
                            />
                            <Input
                              placeholder="Phone"
                              type="tel"
                              value={student.phone}
                              onChange={(e) => updateExistingStudent(index, 'phone', formatPhoneNumber(e.target.value))}
                              className="bg-slate-800 border-teal-600 text-slate-100 placeholder:text-slate-500"
                              data-testid={`edit-student-phone-${index}`}
                            />
                          </div>
                          <div className="flex gap-2 justify-end">
                            <Button
                              onClick={() => {
                                // Cancel edit - reload original data
                                setEditStudentsList([...editStudentsBooking.students]);
                                setEditingStudentIndex(null);
                                setEditingStudentOriginalEmail(null);
                              }}
                              variant="ghost"
                              size="sm"
                              disabled={studentSaving}
                              className="text-white hover:text-white"
                            >
                              Cancel
                            </Button>
                            <Button
                              onClick={() => saveStudentUpdate(student)}
                              size="sm"
                              disabled={studentSaving || !student.first_name || !student.last_name || !student.email || !student.phone}
                              className="bg-teal-600 hover:bg-teal-500 text-white"
                              data-testid={`save-student-${index}`}
                            >
                              {studentSaving ? 'Saving...' : 'Save'}
                            </Button>
                          </div>
                        </div>
                      ) : (
                        // View mode
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <p className={`font-medium ${student.signin_changes?.fields?.first_name || student.signin_changes?.fields?.last_name ? 'bg-amber-500/30 px-1 rounded' : ''} text-slate-200`}
                               title={student.signin_changes?.fields?.first_name || student.signin_changes?.fields?.last_name ? `Name changed during sign-in` : ''}>
                              {student.first_name} {student.last_name}
                              {student.signin_changes && (
                                <span className="ml-2 text-xs text-amber-400">✏️ Changed at sign-in</span>
                              )}
                            </p>
                            <p className={`text-sm ${student.signin_changes?.fields?.email ? 'bg-amber-500/30 px-1 rounded text-amber-200' : 'text-white'}`}
                               title={student.signin_changes?.fields?.email ? `Original: ${student.signin_changes.fields.email.old || '(empty)'}` : ''}>
                              {student.email}
                            </p>
                            <p className={`text-sm ${student.signin_changes?.fields?.phone ? 'bg-amber-500/30 px-1 rounded text-amber-200' : 'text-slate-500'}`}
                               title={student.signin_changes?.fields?.phone ? `Original: ${student.signin_changes.fields.phone.old || '(empty)'}` : ''}>
                              {student.phone}
                            </p>
                            {/* Show per-student add-ons in edit dialog */}
                            {student.addons && student.addons.length > 0 && (
                              <div className="mt-1 pt-1 border-t border-teal-600">
                                <p className="text-xs text-white">Add-ons:</p>
                                {student.addons.map((addon, addonIdx) => (
                                  <p key={addonIdx} className="text-xs text-purple-300 ml-2">
                                    • {addon.name} {addon.quantity > 1 ? `x${addon.quantity}` : ''}
                                  </p>
                                ))}
                              </div>
                            )}
                            {/* AHA eCard Verification Status (new format) */}
                            {student.aha_ecard && (
                              <div className="mt-1 pt-1 border-t border-teal-600">
                                <p className={`text-xs font-semibold ${
                                  student.aha_ecard.verification_status?.includes('MATCH') && !student.aha_ecard.verification_status?.includes('NO MATCH')
                                    ? 'text-green-400' 
                                    : student.aha_ecard.verification_status?.includes('MANUAL')
                                      ? 'text-amber-400'
                                      : 'text-red-400'
                                }`}>
                                  🎓 AHA: {student.aha_ecard.verification_status}
                                </p>
                                {student.aha_ecard.card_holder_name && (
                                  <p className="text-xs text-white ml-2">Card Holder: {student.aha_ecard.card_holder_name.replace(/^Verified:\s*/i, '').split(',')[0].trim()}</p>
                                )}
                                {student.aha_ecard.aha_returned_name && student.aha_ecard.aha_returned_name !== student.aha_ecard.card_holder_name && (
                                  <p className="text-xs text-amber-300 ml-2">AHA Name: {student.aha_ecard.aha_returned_name}</p>
                                )}
                                <p className="text-xs text-white ml-2">eCard: {student.aha_ecard.ecard_code}</p>
                                {student.aha_ecard.expiration_date && (
                                  <p className="text-xs text-white ml-2">Expires: {student.aha_ecard.expiration_date}</p>
                                )}
                                {/* Show screenshot link when verification failed */}
                                {student.aha_ecard.verification_screenshot_url && (
                                  <a 
                                    href={`${API}${student.aha_ecard.verification_screenshot_url}`}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center gap-1 text-xs text-cyan-400 hover:text-cyan-300 ml-2 mt-1"
                                  >
                                    📸 View AHA Screenshot
                                  </a>
                                )}
                              </div>
                            )}
                            {/* BLS Certificate Verification Status (legacy format) */}
                            {student.bls_certificate && !student.aha_ecard && (
                              <div className="mt-1 pt-1 border-t border-teal-600">
                                <p className="text-xs text-amber-400">BLS Certificate:</p>
                                <p className="text-xs text-white ml-2">eCard: {student.bls_certificate.ecard_code}</p>
                                <p className="text-xs text-white ml-2">Exp: {student.bls_certificate.expiration_date}</p>
                                <p className={`text-xs ml-2 font-semibold ${
                                  student.bls_certificate.aha_verification_status?.includes('MATCH') 
                                    ? 'text-green-400' 
                                    : 'text-red-400'
                                }`}>
                                  {student.bls_certificate.aha_verification_status}
                                </p>
                              </div>
                            )}
                            {/* eBook Status */}
                            {student.ebook_status && (
                              <div className="mt-1 pt-1 border-t border-teal-600">
                                <p className={`text-xs font-semibold ${
                                  student.ebook_status === 'sent' ? 'text-green-400' :
                                  student.ebook_status === 'queued' || student.ebook_status === 'processing' ? 'text-amber-400' :
                                  student.ebook_status === 'failed' ? 'text-red-400' :
                                  'text-slate-400'
                                }`}>
                                  📚 eBook: {student.ebook_status === 'sent' ? 'Sent' : 
                                            student.ebook_status === 'queued' ? 'Queued' :
                                            student.ebook_status === 'processing' ? 'Sending...' :
                                            student.ebook_status === 'failed' ? 'Failed' :
                                            student.ebook_status === 'not_applicable' ? 'N/A' :
                                            student.ebook_status}
                                </p>
                                {student.ebook_error && (
                                  <p className="text-xs text-red-400 ml-2">{student.ebook_error}</p>
                                )}
                              </div>
                            )}
                            {/* Digital Test Status (BLS only) */}
                            {student.digital_test_status && (
                              <div className="mt-1 pt-1 border-t border-cyan-600">
                                <p className={`text-xs font-semibold ${
                                  student.digital_test_status === 'sent' ? 'text-green-400' :
                                  student.digital_test_status === 'queued' || student.digital_test_status === 'processing' ? 'text-cyan-400' :
                                  student.digital_test_status === 'failed' ? 'text-red-400' :
                                  'text-slate-400'
                                }`}>
                                  📝 Digital Test: {student.digital_test_status === 'sent' ? 'Sent' : 
                                            student.digital_test_status === 'queued' ? 'Queued' :
                                            student.digital_test_status === 'processing' ? 'Sending...' :
                                            student.digital_test_status === 'failed' ? 'Failed' :
                                            student.digital_test_status}
                                </p>
                                {student.digital_test_error && (
                                  <p className="text-xs text-red-400 ml-2">{student.digital_test_error}</p>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="flex gap-1">
                            <Button
                              onClick={() => {
                                setEditingStudentIndex(index);
                                setEditingStudentOriginalEmail(student.email);
                              }}
                              variant="ghost"
                              size="sm"
                              disabled={studentSaving || editingStudentIndex !== null}
                              className="text-teal-400 hover:text-teal-300 hover:bg-teal-900/30"
                              title="Edit student"
                              data-testid={`edit-student-btn-${index}`}
                            >
                              <Edit2 className="h-4 w-4" />
                            </Button>
                            <Button
                              onClick={() => moveStudentToQueue(editStudentsBooking, student.email)}
                              variant="ghost"
                              size="sm"
                              disabled={studentSaving || editingStudentIndex !== null}
                              className="text-amber-400 hover:text-amber-300 hover:bg-amber-900/30"
                              title="Move to pending queue"
                              data-testid={`move-to-queue-student-${index}`}
                            >
                              <ArrowRightLeft className="h-4 w-4" />
                            </Button>
                            <Button
                              onClick={() => removeStudentFromBooking(index, student)}
                              variant="ghost"
                              size="sm"
                              disabled={studentSaving || editingStudentIndex !== null}
                              className="text-red-400 hover:text-red-300 hover:bg-red-900/30"
                              title="Remove student"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
            
            {/* Add New Student */}
            <div className="space-y-3 pt-4 border-t border-slate-700">
              <Label className="text-slate-200 flex items-center gap-2">
                <Plus className="h-4 w-4" />
                Add New Student
              </Label>
              <div className="p-4 bg-slate-700 rounded-lg border border-teal-600 space-y-3">
                <div className="grid grid-cols-2 gap-2">
                  <Input
                    placeholder="First Name *"
                    value={newStudent.firstName}
                    onChange={(e) => setNewStudent({...newStudent, firstName: e.target.value})}
                    className="bg-slate-800 border-teal-600 text-slate-100 placeholder:text-slate-500"
                    data-testid="new-student-firstname"
                  />
                  <Input
                    placeholder="Last Name *"
                    value={newStudent.lastName}
                    onChange={(e) => setNewStudent({...newStudent, lastName: e.target.value})}
                    className="bg-slate-800 border-teal-600 text-slate-100 placeholder:text-slate-500"
                    data-testid="new-student-lastname"
                  />
                  <Input
                    placeholder="Email *"
                    type="email"
                    value={newStudent.email}
                    onChange={(e) => setNewStudent({...newStudent, email: e.target.value})}
                    className="bg-slate-800 border-teal-600 text-slate-100 placeholder:text-slate-500"
                    data-testid="new-student-email"
                  />
                  <Input
                    placeholder="Phone *"
                    type="tel"
                    value={newStudent.phone}
                    onChange={(e) => setNewStudent({...newStudent, phone: formatPhoneNumber(e.target.value)})}
                    className="bg-slate-800 border-teal-600 text-slate-100 placeholder:text-slate-500"
                    data-testid="new-student-phone"
                  />
                </div>
                <Textarea
                  placeholder="Notes for Instructor (optional) - Entry instructions, special needs, etc."
                  value={newStudent.notes}
                  onChange={(e) => setNewStudent({...newStudent, notes: e.target.value})}
                  className="bg-slate-800 border-teal-600 text-slate-100 placeholder:text-slate-500 min-h-[60px]"
                  data-testid="new-student-notes"
                />
                <div className="flex items-center gap-3 p-2 rounded-lg bg-slate-800 border border-slate-600">
                  <label className="text-sm text-slate-300 font-medium">Payment Status:</label>
                  <div className="flex gap-2">
                    <Button
                      type="button"
                      size="sm"
                      variant={newStudent.paymentStatus === 'paid' ? 'default' : 'outline'}
                      className={newStudent.paymentStatus === 'paid' ? 'bg-emerald-600 hover:bg-emerald-500 text-white' : 'border-slate-600 text-slate-400'}
                      onClick={() => setNewStudent({...newStudent, paymentStatus: 'paid'})}
                    >
                      <DollarSign className="h-3 w-3 mr-1" />
                      Paid
                    </Button>
                    <Button
                      type="button"
                      size="sm"
                      variant={newStudent.paymentStatus === 'unpaid' ? 'default' : 'outline'}
                      className={newStudent.paymentStatus === 'unpaid' ? 'bg-red-600 hover:bg-red-500 text-white' : 'border-slate-600 text-slate-400'}
                      onClick={() => setNewStudent({...newStudent, paymentStatus: 'unpaid'})}
                    >
                      <AlertCircle className="h-3 w-3 mr-1" />
                      Unpaid
                    </Button>
                  </div>
                </div>
                <Button
                  onClick={addStudentToBooking}
                  disabled={studentSaving || !newStudent.firstName || !newStudent.lastName || !newStudent.email || !newStudent.phone}
                  className="w-full bg-teal-600 hover:bg-teal-500 text-white"
                  data-testid="add-student-to-booking"
                >
                  {studentSaving ? 'Adding...' : 'Add Student'}
                </Button>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              onClick={() => setShowEditStudents(false)}
              className="bg-slate-700 hover:bg-slate-600 text-slate-100"
            >
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Digital Tests Dialog (BLS classes only) */}
      <Dialog open={showDigitalTestDialog} onOpenChange={setShowDigitalTestDialog}>
        <DialogContent className="sm:max-w-lg bg-slate-800 border-slate-700 text-slate-100" data-testid="digital-test-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-cyan-400">
              <FileText className="h-5 w-5" />
              Send BLS Digital Tests
            </DialogTitle>
            <DialogDescription className="text-white">
              {digitalTestBooking?.class_type_name} - Select students to send the BLS Online Exam
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {/* Quick select options */}
            <div className="flex gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={selectAllSignedInStudents}
                className="border-cyan-600 text-cyan-400 hover:bg-cyan-900/30"
              >
                Select All Signed In
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={() => setSelectedTestStudents([])}
                className="border-slate-600 text-slate-400 hover:bg-slate-700"
              >
                Clear Selection
              </Button>
            </div>
            
            {/* Student list with checkboxes */}
            <div className="space-y-2 max-h-64 overflow-y-auto">
              {digitalTestStudents.map((student) => (
                <div
                  key={student.student_index}
                  className={`flex items-center justify-between p-3 rounded-lg border ${
                    selectedTestStudents.includes(student.student_index)
                      ? 'border-cyan-500 bg-cyan-900/20'
                      : student.digital_test_status === 'sent'
                      ? 'border-green-600/50 bg-green-900/10'
                      : student.digital_test_status === 'failed'
                      ? 'border-red-600/50 bg-red-900/10'
                      : 'border-slate-600 bg-slate-900/50'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <input
                      type="checkbox"
                      checked={selectedTestStudents.includes(student.student_index)}
                      onChange={() => toggleTestStudent(student.student_index)}
                      disabled={student.digital_test_status === 'sent' || student.digital_test_status === 'processing'}
                      className="h-4 w-4 rounded border-slate-500 text-cyan-500 focus:ring-cyan-500"
                    />
                    <div>
                      <div className="font-medium text-slate-200">
                        {student.first_name} {student.last_name}
                      </div>
                      <div className="text-xs text-slate-400">
                        {student.email}
                        {!student.signed_in && <span className="ml-2 text-amber-400">(Not signed in)</span>}
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    {student.digital_test_status === 'sent' && (
                      <span className="text-xs px-2 py-1 bg-green-900/50 text-green-400 rounded">Sent</span>
                    )}
                    {student.digital_test_status === 'queued' && (
                      <span className="text-xs px-2 py-1 bg-amber-900/50 text-amber-400 rounded">Queued</span>
                    )}
                    {student.digital_test_status === 'processing' && (
                      <span className="text-xs px-2 py-1 bg-blue-900/50 text-blue-400 rounded flex items-center gap-1">
                        <Loader2 className="h-3 w-3 animate-spin" />
                        Sending
                      </span>
                    )}
                    {student.digital_test_status === 'failed' && (
                      <div className="text-right">
                        <span className="text-xs px-2 py-1 bg-red-900/50 text-red-400 rounded">Failed</span>
                        {student.digital_test_error && (
                          <p className="text-xs text-red-400 mt-1 max-w-[150px] truncate" title={student.digital_test_error}>
                            {student.digital_test_error}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                </div>
              ))}
              {digitalTestStudents.length === 0 && (
                <div className="text-center py-8 text-slate-500">
                  No students in this class
                </div>
              )}
            </div>
            
            {/* Selected count */}
            <div className="text-sm text-slate-400">
              {selectedTestStudents.length} student{selectedTestStudents.length !== 1 ? 's' : ''} selected
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowDigitalTestDialog(false)}
              className="border-slate-600 text-white hover:bg-slate-700"
            >
              Cancel
            </Button>
            <Button
              onClick={sendDigitalTests}
              disabled={sendingDigitalTests || selectedTestStudents.length === 0}
              className="bg-cyan-600 hover:bg-cyan-500 text-white"
            >
              {sendingDigitalTests ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4 mr-2" />
                  Send Tests ({selectedTestStudents.length})
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Date/Time Dialog */}
      <Dialog open={showEditDateTime} onOpenChange={setShowEditDateTime}>
        <DialogContent className="sm:max-w-md bg-slate-800 border-slate-700 text-slate-100" data-testid="edit-datetime-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-400">
              <Clock className="h-5 w-5" />
              Edit Date & Time
            </DialogTitle>
            <DialogDescription className="text-white">
              {editDateTimeBooking?.class_type_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label className="text-slate-200">Date</Label>
              <Input
                type="date"
                value={editDate}
                onChange={(e) => setEditDate(e.target.value)}
                className="bg-slate-700 border-teal-600 text-slate-100"
                data-testid="edit-date-input"
              />
            </div>
            
            <div className="space-y-2">
              <Label className="text-slate-200">Start Time (Pacific Time)</Label>
              <Input
                type="time"
                value={editTime}
                onChange={(e) => setEditTime(e.target.value)}
                className="bg-slate-700 border-teal-600 text-slate-100"
                data-testid="edit-time-input"
              />
              <p className="text-xs text-slate-400">
                Current: {editDateTimeBooking?.start_time && formatInTimeZone(new Date(editDateTimeBooking.start_time), 'America/Los_Angeles', 'h:mm a')} PST
              </p>
            </div>
            
            {/* Preview of new time */}
            {editDate && editTime && (
              <div className="p-3 bg-teal-900/30 rounded-lg border border-teal-600">
                <p className="text-sm text-teal-300">
                  <strong>New Time:</strong> {format(new Date(`${editDate}T${editTime}`), 'MMMM d, yyyy')} at {editTime.split(':').map((v, i) => i === 0 ? (parseInt(v) > 12 ? parseInt(v) - 12 : parseInt(v) || 12) : v).join(':')} {parseInt(editTime.split(':')[0]) >= 12 ? 'PM' : 'AM'} PST
                </p>
              </div>
            )}
            
            <div className="p-3 bg-slate-700 rounded-lg border border-teal-600">
              <p className="text-sm text-white">
                <strong className="text-slate-200">Note:</strong> Changing the date/time will automatically update the linked Google Calendar event.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              onClick={() => setShowEditDateTime(false)}
              variant="outline"
              className="border-teal-600 text-white hover:bg-slate-700"
            >
              Cancel
            </Button>
            <Button
              onClick={saveDateTime}
              disabled={dateTimeSaving || !editDate || !editTime}
              className="bg-orange-600 hover:bg-orange-500 text-white"
              data-testid="save-datetime-button"
            >
              {dateTimeSaving ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Class Types Management Dialog */}
      <Dialog open={showClassTypes} onOpenChange={setShowClassTypes}>
        <DialogContent 
          ref={classTypesScrollRef}
          className="sm:max-w-4xl max-h-[90vh] overflow-y-auto bg-slate-800 border-slate-700 text-slate-100" 
          data-testid="class-types-dialog"
        >
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-teal-400">
              <BookOpen className="h-5 w-5" />
              Manage Class Types
            </DialogTitle>
            <DialogDescription className="text-white flex items-center justify-between">
              <span>Add, edit, or remove class types available for booking</span>
              <Button
                variant="outline"
                size="sm"
                onClick={openCategoryManager}
                className="border-teal-600 text-teal-400 hover:bg-teal-900/30"
              >
                <Layers className="h-4 w-4 mr-2" />
                Manage Categories
              </Button>
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Add/Edit Form - Shows as overlay instead of class list */}
            {(showAddClassForm || editingClassType) ? (
            <div className="p-4 bg-slate-700/50 rounded-lg border border-teal-600">
              <h3 className="text-sm font-semibold text-slate-200 mb-4">
                {editingClassType ? 'Edit Class Type' : 'Add New Class Type'}
              </h3>
              
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-white">Class Name <span className="text-red-400">*</span></Label>
                  <Input
                    placeholder="e.g., BLS New Certification"
                    value={classTypeForm.name}
                    onChange={(e) => setClassTypeForm({...classTypeForm, name: e.target.value})}
                    className="bg-slate-800 border-teal-600 text-slate-100 placeholder:text-slate-500"
                    data-testid="class-type-name-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-white">Duration (hours) <span className="text-red-400">*</span></Label>
                  <Input
                    type="number"
                    step="0.5"
                    min="0.5"
                    placeholder="4"
                    value={classTypeForm.duration_hours}
                    onChange={(e) => setClassTypeForm({...classTypeForm, duration_hours: e.target.value})}
                    className="bg-slate-800 border-teal-600 text-slate-100 placeholder:text-slate-500"
                    data-testid="class-type-duration-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-white">SKU <span className="text-red-400">*</span></Label>
                  <Input
                    placeholder="e.g., BLS-NEW"
                    value={classTypeForm.sku}
                    onChange={(e) => setClassTypeForm({...classTypeForm, sku: e.target.value})}
                    className="bg-slate-800 border-teal-600 text-slate-100 placeholder:text-slate-500"
                    data-testid="class-type-sku-input"
                  />
                </div>
                
                {!editingClassType && (
                  <div className="space-y-2">
                    <Label className="text-white">ID (auto-generated if empty)</Label>
                    <Input
                      placeholder="e.g., bls-new"
                      value={classTypeForm.id}
                      onChange={(e) => setClassTypeForm({...classTypeForm, id: e.target.value})}
                      className="bg-slate-800 border-teal-600 text-slate-100 placeholder:text-slate-500"
                      data-testid="class-type-id-input"
                    />
                  </div>
                )}
                
                <div className="space-y-2">
                  <Label className="text-white">Sort Order</Label>
                  <Input
                    type="number"
                    placeholder="0"
                    value={classTypeForm.sort_order}
                    onChange={(e) => setClassTypeForm({...classTypeForm, sort_order: e.target.value})}
                    className="bg-slate-800 border-teal-600 text-slate-100 placeholder:text-slate-500"
                    data-testid="class-type-sort-input"
                  />
                </div>
                
                <div className="space-y-2">
                  <Label className="text-white">Cert Expiration (months)</Label>
                  <Input
                    type="number"
                    placeholder="Leave empty for no expiration"
                    min="1"
                    max="120"
                    value={classTypeForm.expiration_months}
                    onChange={(e) => setClassTypeForm({...classTypeForm, expiration_months: e.target.value})}
                    className="bg-slate-800 border-teal-600 text-slate-100 placeholder:text-slate-500"
                    data-testid="class-type-expiration-input"
                  />
                  <p className="text-xs text-slate-500">Leave empty if certification doesn't expire</p>
                </div>
                
                <div className="col-span-2 space-y-2">
                  <Label className="text-white">Description</Label>
                  <Input
                    placeholder="Optional description..."
                    value={classTypeForm.description}
                    onChange={(e) => setClassTypeForm({...classTypeForm, description: e.target.value})}
                    className="bg-slate-800 border-teal-600 text-slate-100 placeholder:text-slate-500"
                    data-testid="class-type-description-input"
                  />
                </div>
                
                {/* eCard Product Selection */}
                <div className="col-span-2">
                  <Label className="text-sm text-white mb-1.5 flex items-center gap-2">
                    <CreditCard className="h-4 w-4 text-white" />
                    eCard Used (Product Number)
                  </Label>
                  <Select
                    value={classTypeForm.ecard_product_number || "__none__"}
                    onValueChange={(value) => {
                      if (value === "__none__") {
                        setClassTypeForm({...classTypeForm, ecard_product_number: "", ecard_product_url: ""});
                      } else {
                        // Find the link to get its URL
                        const selectedLink = (ecardLinks || []).find(l => l.product_number === value);
                        setClassTypeForm({
                          ...classTypeForm, 
                          ecard_product_number: value,
                          ecard_product_url: selectedLink?.link || ""
                        });
                      }
                    }}
                  >
                    <SelectTrigger className="bg-slate-800 border-teal-600 text-slate-100" data-testid="class-type-ecard-select">
                      <SelectValue placeholder="Select eCard product..." />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      <SelectItem value="__none__" className="text-white">None</SelectItem>
                      {(ecardLinks || []).map(link => (
                        <SelectItem key={link.id} value={link.product_number} className="text-slate-200">
                          {link.name} ({link.product_number})
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {classTypeForm.ecard_product_url && (
                    <p className="text-xs text-teal-400 mt-1 truncate">URL: {classTypeForm.ecard_product_url}</p>
                  )}
                  {(!ecardLinks || ecardLinks.length === 0) && (
                    <p className="text-xs text-amber-400 mt-1">No eCard links configured. Add them in eCards → eCard Management</p>
                  )}
                </div>
                
                {/* eCard Minimum Order */}
                <div>
                  <Label className="text-sm text-white mb-1.5">Min Order Qty</Label>
                  <Input
                    type="number"
                    min="1"
                    value={classTypeForm.ecard_min_order}
                    onChange={(e) => setClassTypeForm({...classTypeForm, ecard_min_order: e.target.value})}
                    className="bg-slate-800 border-teal-600 text-slate-100 w-20"
                    data-testid="class-type-ecard-min-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">BLS requires min 3</p>
                </div>
                
                {/* Course Category */}
                <div className="col-span-2">
                  <Label className="text-sm text-white mb-1.5 flex items-center gap-2">
                    <Layers className="h-4 w-4 text-white" />
                    Course Category
                  </Label>
                  <Select
                    value={classTypeForm.course_category || "__none__"}
                    onValueChange={(value) => {
                      setClassTypeForm({...classTypeForm, course_category: value === "__none__" ? "" : value});
                    }}
                  >
                    <SelectTrigger className="bg-slate-800 border-teal-600 text-slate-100" data-testid="class-type-category-select">
                      <SelectValue placeholder="Select a category..." />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      <SelectItem value="__none__" className="text-white">No Category</SelectItem>
                      {courseCategories.map(cat => (
                        <SelectItem key={cat.id} value={cat.slug} className="text-slate-200">
                          {cat.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-slate-500 mt-1">Used to filter classes in the booking dropdown</p>
                </div>
                
                {/* Paperwork Template */}
                <div className="col-span-2">
                  <Label className="text-sm text-white mb-1.5 flex items-center gap-2">
                    <FileText className="h-4 w-4 text-amber-400" />
                    Paperwork Template <span className="text-red-400">*</span>
                  </Label>
                  <Select
                    value={classTypeForm.paperwork_template || "__none__"}
                    onValueChange={(value) => {
                      setClassTypeForm({...classTypeForm, paperwork_template: value === "__none__" ? "" : value});
                    }}
                  >
                    <SelectTrigger className="bg-slate-800 border-amber-600 text-slate-100" data-testid="class-type-paperwork-select">
                      <SelectValue placeholder="Select paperwork template..." />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700 max-h-60">
                      <SelectItem value="__none__" className="text-slate-400">-- Select Template --</SelectItem>
                      {paperworkTemplateOptions.map(opt => (
                        <SelectItem key={opt.value} value={opt.value} className="text-slate-200">
                          {opt.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-amber-500 mt-1">Determines which AHA roster/testing sheet is generated when class closes</p>
                </div>
                
                {/* eBook Product */}
                <div className="col-span-2">
                  <Label className="text-sm text-white mb-1.5 flex items-center gap-2">
                    <BookOpen className="h-4 w-4 text-blue-400" />
                    eBook Product
                  </Label>
                  <Select
                    value={classTypeForm.ebook_product_id || "__none__"}
                    onValueChange={(value) => {
                      setClassTypeForm({...classTypeForm, ebook_product_id: value === "__none__" ? "" : value});
                    }}
                  >
                    <SelectTrigger className="bg-slate-800 border-blue-600 text-slate-100" data-testid="class-type-ebook-select">
                      <SelectValue placeholder="Select eBook product..." />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700 max-h-60">
                      <SelectItem value="__none__" className="text-slate-400">No eBook (none will be sent)</SelectItem>
                      {ebookProductOptions.filter(opt => opt.id !== 'none').map(opt => (
                        <SelectItem key={opt.id} value={opt.id} className="text-slate-200">
                          <div className="flex flex-col">
                            <span>{opt.name}</span>
                            {opt.aha_product_name && (
                              <span className="text-xs text-purple-300">{opt.aha_product_name}</span>
                            )}
                          </div>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <p className="text-xs text-blue-400 mt-1">Which eBook to send to students when signing in</p>
                </div>
                
                {/* Pricing Section */}
                <div className="col-span-2 border-t border-slate-600 pt-4 mt-2">
                  <h4 className="text-sm font-medium text-green-400 mb-3 flex items-center gap-2">
                    <DollarSign className="h-4 w-4" />
                    Class Pricing
                  </h4>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-white">Base Price (1st Student)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="e.g., 85.00"
                        value={classTypeForm.base_price || ''}
                        onChange={(e) => setClassTypeForm({...classTypeForm, base_price: e.target.value})}
                        className="bg-slate-800 border-teal-600 text-slate-100 placeholder:text-slate-500"
                        data-testid="class-type-base-price-input"
                      />
                      <p className="text-xs text-slate-500">Price shown for single student booking</p>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-white">Additional Student Price</Label>
                      <Input
                        type="number"
                        step="0.01"
                        min="0"
                        placeholder="e.g., 75.00"
                        value={classTypeForm.additional_student_price || ''}
                        onChange={(e) => setClassTypeForm({...classTypeForm, additional_student_price: e.target.value})}
                        className="bg-slate-800 border-teal-600 text-slate-100 placeholder:text-slate-500"
                        data-testid="class-type-additional-price-input"
                      />
                      <p className="text-xs text-slate-500">Price per student after the first</p>
                    </div>
                  </div>
                </div>
                
                <div className="col-span-2 flex items-center gap-3">
                  <Checkbox
                    id="class-type-active"
                    checked={classTypeForm.is_active}
                    onCheckedChange={(checked) => setClassTypeForm({...classTypeForm, is_active: checked})}
                    className="border-slate-500 data-[state=checked]:bg-teal-600"
                    data-testid="class-type-active-checkbox"
                  />
                  <label htmlFor="class-type-active" className="text-sm text-white cursor-pointer">
                    Active (available for booking)
                  </label>
                </div>
              </div>
              
              <div className="flex justify-end gap-2 mt-4">
                <Button
                  onClick={cancelEditClassType}
                  variant="outline"
                  size="sm"
                  className="border-teal-600 text-white hover:bg-slate-700"
                >
                  Cancel
                </Button>
                <Button
                  onClick={saveClassType}
                  disabled={classTypeSaving}
                  size="sm"
                  className="bg-teal-600 hover:bg-teal-500 text-white"
                  data-testid="save-class-type-button"
                >
                  {classTypeSaving ? 'Saving...' : (editingClassType ? 'Update' : 'Add Class Type')}
                </Button>
              </div>
            </div>
            ) : (
            <>
            {/* Add Class Type Button */}
            <Button
              onClick={() => setShowAddClassForm(true)}
              className="bg-teal-600 hover:bg-teal-500 text-white"
              data-testid="add-class-type-button"
            >
              <Plus className="h-4 w-4 mr-2" />
              Add New Class Type
            </Button>
            
            {/* Class Types Table */}
            <div className="border border-teal-600 rounded-lg overflow-hidden">
              <div className="bg-slate-700/50 px-4 py-3 border-b border-teal-600">
                <h3 className="text-sm font-semibold text-slate-200 flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  Existing Class Types ({adminClassTypes.length})
                </h3>
              </div>
              
              {classTypesLoading ? (
                <div className="p-8 text-center">
                  <Loader2 className="h-8 w-8 animate-spin text-teal-400 mx-auto" />
                  <p className="text-white mt-2">Loading class types...</p>
                </div>
              ) : adminClassTypes.length === 0 ? (
                <div className="p-8 text-center text-white">
                  No class types found. Add one above to get started.
                </div>
              ) : (
                <DndContext 
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleClassTypeDragEnd}
                >
                  <SortableContext 
                    items={adminClassTypes.map(ct => ct.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div 
                      ref={classTypesListRef}
                      className="divide-y divide-slate-700 max-h-96 overflow-y-auto"
                    >
                      {adminClassTypes.map((ct) => (
                        <SortableClassTypeItem
                          key={ct.id}
                          ct={ct}
                          onEdit={startEditClassType}
                          onToggleActive={toggleClassTypeActive}
                          onDelete={deleteClassType}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              )}
            </div>
            
            <div className="p-3 bg-slate-700/30 rounded-lg border border-teal-600">
              <p className="text-xs text-white">
                <strong className="text-white">Tip:</strong> Drag the grip handle to reorder class types. This order will be shown on the booking page.
              </p>
            </div>
            </>
            )}
          </div>
          
          <DialogFooter>
            <Button
              onClick={() => setShowClassTypes(false)}
              className="bg-slate-600 hover:bg-slate-500 text-white"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Course Category Management Dialog */}
      <Dialog open={showCategoryManager} onOpenChange={setShowCategoryManager}>
        <DialogContent className="sm:max-w-lg bg-slate-800 border-slate-700 text-slate-100">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-teal-400">
              <Layers className="h-5 w-5" />
              Manage Course Categories
            </DialogTitle>
            <DialogDescription className="text-white">
              Add, edit, or remove categories used to filter classes in the booking dropdown
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Add/Edit Category Form */}
            <div className="p-4 bg-slate-700/50 rounded-lg border border-teal-600">
              <h4 className="text-sm font-semibold text-slate-200 mb-3">
                {editingCategory ? 'Edit Category' : 'Add New Category'}
              </h4>
              <div className="space-y-3">
                <div>
                  <Label className="text-white text-sm">Name *</Label>
                  <Input
                    placeholder="e.g., Heartsaver"
                    value={categoryFormData.name}
                    onChange={(e) => setCategoryFormData({...categoryFormData, name: e.target.value})}
                    className="bg-slate-800 border-teal-600 text-slate-100 mt-1"
                  />
                </div>
                <div>
                  <Label className="text-white text-sm">Slug (auto-generated if empty)</Label>
                  <Input
                    placeholder="e.g., heartsaver"
                    value={categoryFormData.slug}
                    onChange={(e) => setCategoryFormData({...categoryFormData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')})}
                    className="bg-slate-800 border-teal-600 text-slate-100 mt-1"
                  />
                </div>
                <div>
                  <Label className="text-white text-sm">Sort Order</Label>
                  <Input
                    type="number"
                    min="0"
                    value={categoryFormData.sort_order}
                    onChange={(e) => setCategoryFormData({...categoryFormData, sort_order: e.target.value})}
                    className="bg-slate-800 border-teal-600 text-slate-100 mt-1 w-24"
                  />
                </div>
                <div className="flex gap-2 pt-2">
                  <Button
                    onClick={saveCategory}
                    disabled={categorySaving || !categoryFormData.name.trim()}
                    className="bg-teal-600 hover:bg-teal-500"
                  >
                    {categorySaving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Save className="h-4 w-4 mr-2" />}
                    {editingCategory ? 'Update' : 'Add'}
                  </Button>
                  {editingCategory && (
                    <Button
                      variant="outline"
                      onClick={resetCategoryForm}
                      className="border-slate-600 text-white hover:bg-slate-700"
                    >
                      Cancel
                    </Button>
                  )}
                </div>
              </div>
            </div>
            
            {/* Category List */}
            <div className="space-y-2">
              <h4 className="text-sm font-semibold text-slate-300 flex items-center gap-2">
                Existing Categories
                <span className="text-xs text-slate-500 font-normal">(drag to reorder)</span>
              </h4>
              {courseCategories.length === 0 ? (
                <p className="text-slate-400 text-sm">No categories yet. Add one above.</p>
              ) : (
                <DndContext 
                  sensors={categorySensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleCategoryDragEnd}
                >
                  <SortableContext 
                    items={courseCategories.map(c => c.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-2">
                      {courseCategories.map((cat) => (
                        <SortableCategoryItem
                          key={cat.id}
                          category={cat}
                          onEdit={startEditCategory}
                          onDelete={deleteCategory}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              )}
            </div>
          </div>
          
          <DialogFooter>
            <Button
              onClick={() => setShowCategoryManager(false)}
              className="bg-slate-600 hover:bg-slate-500 text-white"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Email History Dialog */}
      <Dialog open={showEmailLog} onOpenChange={setShowEmailLog}>
        <DialogContent className="bg-slate-800 text-white border-slate-700 max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold text-slate-100 flex items-center gap-2">
              <Mail className="h-5 w-5 text-white" />
              Email History
            </DialogTitle>
            <DialogDescription className="text-white">
              {emailLogBooking && (
                <span>Emails sent for: {emailLogBooking.class_type_name} - {emailLogBooking.students?.[0]?.first_name} {emailLogBooking.students?.[0]?.last_name}</span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-3 mt-4">
            {emailLogLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-teal-400" />
                <span className="ml-2 text-white">Loading email history...</span>
              </div>
            ) : emailLogs.length === 0 ? (
              <div className="text-center py-8 text-white">
                <Mail className="h-12 w-12 mx-auto mb-3 opacity-30" />
                <p>No emails have been logged for this booking yet.</p>
              </div>
            ) : (
              emailLogs.map((log) => (
                <div 
                  key={log.id} 
                  className={`p-3 rounded-lg border ${log.success ? 'bg-slate-700/50 border-teal-600' : 'bg-red-900/20 border-red-700/50'}`}
                  data-testid={`email-log-${log.id}`}
                >
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-2">
                      {log.success ? (
                        <CheckCircle className="h-4 w-4 text-green-400 flex-shrink-0" />
                      ) : (
                        <XCircle className="h-4 w-4 text-red-400 flex-shrink-0" />
                      )}
                      <div>
                        <span className="font-medium text-slate-200">{getEmailTemplateName(log.template)}</span>
                        <p className="text-sm text-white">
                          To: {log.recipient_name} ({log.recipient_email})
                        </p>
                      </div>
                    </div>
                    <span className="text-xs text-slate-500">
                      {new Date(log.sent_at).toLocaleString()}
                    </span>
                  </div>
                  {!log.success && log.error_message && (
                    <div className="mt-2 p-2 bg-red-900/30 rounded text-sm text-red-300">
                      Error: {log.error_message}
                    </div>
                  )}
                </div>
              ))
            )}
          </div>
          
          <DialogFooter className="mt-4">
            <Button
              onClick={() => setShowEmailLog(false)}
              className="bg-slate-600 hover:bg-slate-500 text-white"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete/Hide Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent className="sm:max-w-md bg-slate-800 border-slate-700 text-slate-100" data-testid="delete-hide-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Trash2 className="h-5 w-5 text-red-400" />
              Remove Booking
            </DialogTitle>
            <DialogDescription className="text-white">
              Choose how you want to remove this booking:
            </DialogDescription>
          </DialogHeader>
          
          {deleteBooking && (
            <div className="py-4">
              <div className="bg-slate-700/50 rounded-lg p-4 mb-4">
                <p className="text-sm text-white">
                  <span className="font-semibold">{deleteBooking.class_name}</span>
                </p>
                <p className="text-xs text-white mt-1">
                  {deleteBooking.students?.length || 0} student(s) • {deleteBooking.status}
                </p>
              </div>
              
              <div className="space-y-3">
                <Button
                  onClick={executeHide}
                  className="w-full justify-start bg-teal-600 hover:bg-teal-500 text-white"
                  data-testid="hide-booking-btn"
                >
                  <EyeOff className="h-4 w-4 mr-3" />
                  <div className="text-left">
                    <p className="font-medium">Hide</p>
                    <p className="text-xs opacity-80">Move to Hidden tab. Can be restored later.</p>
                  </div>
                </Button>
                
                <Button
                  onClick={executeDelete}
                  variant="destructive"
                  className="w-full justify-start bg-red-600 hover:bg-red-500"
                  data-testid="delete-permanently-btn"
                >
                  <Trash2 className="h-4 w-4 mr-3" />
                  <div className="text-left">
                    <p className="font-medium">Delete Permanently</p>
                    <p className="text-xs opacity-80">Cannot be undone.</p>
                  </div>
                </Button>
              </div>
            </div>
          )}
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={cancelDelete}
              className="border-teal-600 text-white hover:bg-slate-700"
              data-testid="cancel-delete-btn"
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete from Enrollware Confirmation Dialog */}
      <Dialog open={!!deleteEnrollwareConfirm} onOpenChange={(open) => !open && setDeleteEnrollwareConfirm(null)}>
        <DialogContent className="sm:max-w-md bg-slate-800 border-slate-700 text-slate-100" data-testid="delete-enrollware-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Trash2 className="h-5 w-5 text-red-400" />
              Delete from Enrollware
            </DialogTitle>
            <DialogDescription className="text-white">
              This will permanently remove this class from Enrollware.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <div className="bg-red-900/20 border border-red-700/50 rounded-lg p-4 mb-4">
              <p className="text-sm text-red-300">
                <strong>Warning:</strong> This action will:
              </p>
              <ul className="text-sm text-red-300 mt-2 list-disc list-inside space-y-1">
                <li>Remove all student registrations from Enrollware</li>
                <li>Delete the class from Enrollware</li>
                <li>Allow you to re-sync this booking if needed</li>
              </ul>
            </div>
            
            <p className="text-sm text-slate-300">
              Type <strong className="text-red-400">DELETE</strong> to confirm:
            </p>
            <input 
              type="text"
              id="delete-enrollware-confirm-input"
              className="mt-2 w-full bg-slate-700 border border-slate-600 rounded px-3 py-2 text-white"
              placeholder="Type DELETE to confirm"
              data-testid="delete-enrollware-confirm-input"
            />
          </div>
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setDeleteEnrollwareConfirm(null)}
              className="border-slate-600 text-white hover:bg-slate-700"
            >
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={() => {
                const input = document.getElementById('delete-enrollware-confirm-input');
                if (input?.value?.toUpperCase() === 'DELETE') {
                  handleDeleteFromEnrollware(deleteEnrollwareConfirm);
                } else {
                  toast.error('Please type DELETE to confirm');
                }
              }}
              className="bg-red-600 hover:bg-red-500"
              data-testid="confirm-delete-enrollware-btn"
            >
              Delete from Enrollware
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Fee Waivers Dialog */}
      <Dialog open={showFeeWaiversDialog} onOpenChange={setShowFeeWaiversDialog}>
        <DialogContent className="sm:max-w-md bg-slate-800 border-slate-700 text-slate-100" data-testid="fee-waivers-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <DollarSign className="h-5 w-5 text-emerald-400" />
              Fee Waivers for Add-on Students
            </DialogTitle>
            <DialogDescription className="text-slate-300">
              Toggle which fees should be waived when students are added to this class. Waivers are OFF by default (fees apply).
            </DialogDescription>
          </DialogHeader>
          
          {feeWaiversBooking && (
            <div className="py-4 space-y-4">
              <div className="bg-slate-700/50 rounded-lg p-4">
                <p className="text-sm text-white font-semibold">{feeWaiversBooking.class_type_name}</p>
                <p className="text-xs text-slate-400 mt-1">
                  {new Date(feeWaiversBooking.start_time).toLocaleDateString('en-US', { 
                    weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
                  })}
                </p>
              </div>
              
              <div className="space-y-3">
                {/* Class Fee Waiver */}
                <div className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-white">Class Fee</p>
                    <p className="text-xs text-slate-400">Waive the per-student course fee</p>
                  </div>
                  <button
                    onClick={() => {
                      const newValue = !feeWaiversBooking.addon_payment_waived;
                      setFeeWaiversBooking({...feeWaiversBooking, addon_payment_waived: newValue});
                    }}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      feeWaiversBooking.addon_payment_waived ? 'bg-emerald-600' : 'bg-slate-600'
                    }`}
                    data-testid="toggle-class-fee-waiver"
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      feeWaiversBooking.addon_payment_waived ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
                
                {/* Rush Fee Waiver */}
                <div className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-white">Rush Fee</p>
                    <p className="text-xs text-slate-400">Waive the fee for classes within 48 hours</p>
                  </div>
                  <button
                    onClick={() => {
                      const newValue = !feeWaiversBooking.addon_rush_fee_waived;
                      setFeeWaiversBooking({...feeWaiversBooking, addon_rush_fee_waived: newValue});
                    }}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      feeWaiversBooking.addon_rush_fee_waived ? 'bg-emerald-600' : 'bg-slate-600'
                    }`}
                    data-testid="toggle-rush-fee-waiver"
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      feeWaiversBooking.addon_rush_fee_waived ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
                
                {/* After-Hours Fee Waiver */}
                <div className="flex items-center justify-between p-3 bg-slate-700/30 rounded-lg">
                  <div className="flex-1">
                    <p className="text-sm font-medium text-white">After-Hours Fee</p>
                    <p className="text-xs text-slate-400">Waive the fee for classes outside 8am-10pm</p>
                  </div>
                  <button
                    onClick={() => {
                      const newValue = !feeWaiversBooking.addon_afterhours_fee_waived;
                      setFeeWaiversBooking({...feeWaiversBooking, addon_afterhours_fee_waived: newValue});
                    }}
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                      feeWaiversBooking.addon_afterhours_fee_waived ? 'bg-emerald-600' : 'bg-slate-600'
                    }`}
                    data-testid="toggle-afterhours-fee-waiver"
                  >
                    <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                      feeWaiversBooking.addon_afterhours_fee_waived ? 'translate-x-6' : 'translate-x-1'
                    }`} />
                  </button>
                </div>
              </div>
              
              {/* Status Summary */}
              <div className="pt-2 border-t border-slate-700">
                <p className="text-xs text-slate-400">
                  {(feeWaiversBooking.addon_payment_waived || feeWaiversBooking.addon_rush_fee_waived || feeWaiversBooking.addon_afterhours_fee_waived) ? (
                    <span className="text-emerald-400">✓ Some fees will be waived for add-on students</span>
                  ) : (
                    <span>All fees will apply to add-on students</span>
                  )}
                </p>
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => setShowFeeWaiversDialog(false)}
              className="border-slate-600 text-white hover:bg-slate-700"
              data-testid="cancel-fee-waivers-btn"
            >
              Cancel
            </Button>
            <Button
              onClick={() => updateFeeWaivers({
                addon_payment_waived: feeWaiversBooking?.addon_payment_waived || false,
                addon_rush_fee_waived: feeWaiversBooking?.addon_rush_fee_waived || false,
                addon_afterhours_fee_waived: feeWaiversBooking?.addon_afterhours_fee_waived || false
              })}
              disabled={feeWaiversLoading}
              className="bg-emerald-600 hover:bg-emerald-500 text-white"
              data-testid="save-fee-waivers-btn"
            >
              {feeWaiversLoading ? 'Saving...' : 'Save Waivers'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link Invoice Dialog */}
      <Dialog open={showLinkInvoiceDialog} onOpenChange={setShowLinkInvoiceDialog}>
        <DialogContent className="sm:max-w-lg bg-slate-800 border-slate-700 text-slate-100" data-testid="link-invoice-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <FileText className="h-5 w-5 text-blue-400" />
              Link Invoice to Class
            </DialogTitle>
            <DialogDescription className="text-slate-300">
              Select an invoice to link to this class, or unlink the current one.
            </DialogDescription>
          </DialogHeader>
          
          {linkInvoiceBooking && (
            <div className="py-4 space-y-4">
              <div className="bg-slate-700/50 rounded-lg p-4">
                <p className="text-sm text-white font-semibold">{linkInvoiceBooking.class_type_name}</p>
                <p className="text-xs text-slate-400 mt-1">
                  {new Date(linkInvoiceBooking.start_time).toLocaleDateString('en-US', { 
                    weekday: 'short', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit'
                  })}
                </p>
                {linkInvoiceBooking.linked_invoice_id && (
                  <p className="text-xs text-blue-400 mt-2">
                    Currently linked: {linkInvoiceBooking.linked_invoice_id}
                  </p>
                )}
              </div>
              
              {/* Search Input */}
              <div>
                <Input
                  placeholder="Search invoices by ID or customer..."
                  value={linkInvoiceSearch}
                  onChange={(e) => setLinkInvoiceSearch(e.target.value)}
                  className="bg-slate-700 border-slate-600 text-white"
                  data-testid="link-invoice-search"
                />
              </div>
              
              {/* Invoice List */}
              <div className="max-h-64 overflow-y-auto space-y-2">
                {linkInvoiceLoading ? (
                  <p className="text-center text-slate-400 py-4">Loading invoices...</p>
                ) : availableInvoices
                    .filter(inv => {
                      if (!linkInvoiceSearch) return true;
                      const search = linkInvoiceSearch.toLowerCase();
                      return inv.invoice_id?.toLowerCase().includes(search) ||
                             inv.customer?.first_name?.toLowerCase().includes(search) ||
                             inv.customer?.last_name?.toLowerCase().includes(search) ||
                             inv.customer?.email?.toLowerCase().includes(search);
                    })
                    .slice(0, 20)
                    .map(inv => (
                      <div 
                        key={inv.invoice_id}
                        onClick={() => linkInvoiceToBooking(inv.invoice_id)}
                        className={`p-3 rounded-lg cursor-pointer transition-colors ${
                          linkInvoiceBooking.linked_invoice_id === inv.invoice_id
                            ? 'bg-blue-900/50 border border-blue-600'
                            : 'bg-slate-700/50 hover:bg-slate-700 border border-transparent'
                        }`}
                        data-testid={`select-invoice-${inv.invoice_id}`}
                      >
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="text-sm font-medium text-white">{inv.invoice_id}</p>
                            <p className="text-xs text-slate-400">
                              {inv.customer?.first_name} {inv.customer?.last_name}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-medium text-white">${(inv.total || 0).toFixed(2)}</p>
                            <Badge className={`text-xs ${
                              inv.status === 'paid' ? 'bg-green-900/50 text-green-400' :
                              inv.status === 'partial' ? 'bg-yellow-900/50 text-yellow-400' :
                              inv.status === 'sent' ? 'bg-blue-900/50 text-blue-400' :
                              'bg-slate-600 text-slate-300'
                            }`}>
                              {inv.status || 'draft'}
                            </Badge>
                          </div>
                        </div>
                      </div>
                    ))
                }
                {!linkInvoiceLoading && availableInvoices.length === 0 && (
                  <p className="text-center text-slate-400 py-4">No invoices found</p>
                )}
              </div>
            </div>
          )}
          
          <DialogFooter className="gap-2">
            {linkInvoiceBooking?.linked_invoice_id && (
              <Button
                variant="outline"
                onClick={() => linkInvoiceToBooking(null)}
                className="border-red-600 text-red-400 hover:bg-red-900/30"
                data-testid="unlink-invoice-btn"
              >
                Unlink Invoice
              </Button>
            )}
            <Button
              variant="outline"
              onClick={() => setShowLinkInvoiceDialog(false)}
              className="border-slate-600 text-white hover:bg-slate-700"
              data-testid="cancel-link-invoice-btn"
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Link Recurring Client Dialog */}
      <Dialog open={showLinkRecurringClientDialog} onOpenChange={setShowLinkRecurringClientDialog}>
        <DialogContent className="sm:max-w-md bg-slate-800 border-slate-700 text-slate-100" data-testid="link-recurring-client-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl text-white">
              <Users className="h-5 w-5 text-blue-500" />
              Link Recurring Client
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {linkingClientToBooking && (
                <>Link a recurring client to <strong className="text-white">{linkingClientToBooking.class_type_name}</strong></>
              )}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
              <Input
                placeholder="Search by name, email, or company..."
                value={linkRecurringClientSearch}
                onChange={(e) => searchRecurringClientsForLink(e.target.value)}
                className="pl-9 bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
                data-testid="search-recurring-clients"
              />
            </div>
            
            {/* Notes for linking */}
            <Input
              placeholder="Internal note (optional)"
              value={linkRecurringClientNotes}
              onChange={(e) => setLinkRecurringClientNotes(e.target.value)}
              className="bg-slate-700 border-slate-600 text-white placeholder:text-slate-500"
              data-testid="link-client-notes"
            />
            
            {/* Client List */}
            <ScrollArea className="h-64 rounded-md border border-slate-700 p-2">
              {linkingClient && (
                <div className="absolute inset-0 bg-slate-900/80 z-10 flex items-center justify-center rounded-md">
                  <div className="text-center">
                    <Loader2 className="h-8 w-8 animate-spin text-blue-500 mx-auto mb-2" />
                    <p className="text-sm text-white">Linking client...</p>
                    <p className="text-xs text-slate-400 mt-1">Please wait, this may take a moment</p>
                  </div>
                </div>
              )}
              {linkRecurringClientResults.length === 0 ? (
                <p className="text-center text-slate-400 py-4">
                  {linkRecurringClientSearch ? 'No clients found' : 'No recurring clients'}
                </p>
              ) : (
                <div className="space-y-2">
                  {linkRecurringClientResults.map((client) => (
                    <div
                      key={client.id}
                      onClick={() => {
                        if (linkingClient) {
                          toast.warning('Please wait - already linking a client. Multiple clicks will send duplicate emails.');
                          return;
                        }
                        handleLinkRecurringClient(client.id);
                      }}
                      className={`p-3 rounded-lg transition-colors border border-transparent ${
                        linkingClient 
                          ? 'cursor-not-allowed opacity-50 bg-slate-700/30' 
                          : 'cursor-pointer bg-slate-700/50 hover:bg-slate-700 hover:border-blue-500'
                      }`}
                      data-testid={`select-client-${client.id}`}
                    >
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 bg-blue-900/50 rounded-full flex items-center justify-center">
                          {client.company ? (
                            <Building2 className="h-4 w-4 text-blue-400" />
                          ) : (
                            <User className="h-4 w-4 text-blue-400" />
                          )}
                        </div>
                        <div>
                          <p className="text-sm font-medium text-white">
                            {client.company && <span className="text-blue-400">{client.company} - </span>}
                            {client.first_name} {client.last_name}
                          </p>
                          <p className="text-xs text-slate-400">
                            {client.email || client.phone || 'No contact info'}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </ScrollArea>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowLinkRecurringClientDialog(false)}
              className="border-slate-600 text-white hover:bg-slate-700"
            >
              Cancel
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Recurring Client Details Dialog */}
      <Dialog open={showViewRecurringClient} onOpenChange={setShowViewRecurringClient}>
        <DialogContent className="sm:max-w-lg bg-slate-800 border-slate-700 text-slate-100" data-testid="view-recurring-client-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl text-white">
              <Users className="h-5 w-5 text-blue-500" />
              Recurring Client Details
            </DialogTitle>
          </DialogHeader>
          
          {loadingRecurringClient ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-blue-500" />
            </div>
          ) : viewingRecurringClient ? (
            <div className="space-y-4 py-2">
              {/* Client Header */}
              <div className="flex items-start gap-3 p-3 bg-slate-700/50 rounded-lg">
                <div className="w-12 h-12 bg-blue-900/50 rounded-full flex items-center justify-center flex-shrink-0">
                  {viewingRecurringClient.company ? (
                    <Building2 className="h-6 w-6 text-blue-400" />
                  ) : (
                    <User className="h-6 w-6 text-blue-400" />
                  )}
                </div>
                <div>
                  <h3 className="font-semibold text-white text-lg">
                    {viewingRecurringClient.company && (
                      <span className="text-blue-400">{viewingRecurringClient.company} - </span>
                    )}
                    {viewingRecurringClient.first_name} {viewingRecurringClient.last_name}
                  </h3>
                  <div className="flex flex-wrap gap-3 text-sm text-slate-400 mt-1">
                    {viewingRecurringClient.email && (
                      <span className="flex items-center gap-1">
                        <Mail className="h-3 w-3" /> {viewingRecurringClient.email}
                      </span>
                    )}
                    {viewingRecurringClient.phone && (
                      <span className="flex items-center gap-1">
                        <User className="h-3 w-3" /> {viewingRecurringClient.phone}
                      </span>
                    )}
                  </div>
                </div>
              </div>
              
              {/* Stats */}
              <div className="grid grid-cols-3 gap-3">
                <div className="bg-slate-700/50 p-3 rounded-lg text-center">
                  <p className="text-2xl font-bold text-white">{viewingRecurringClient.total_bookings || 0}</p>
                  <p className="text-xs text-slate-400">Bookings</p>
                </div>
                <div className="bg-slate-700/50 p-3 rounded-lg text-center">
                  <p className="text-2xl font-bold text-green-400">
                    ${(viewingRecurringClient.total_revenue || 0).toLocaleString()}
                  </p>
                  <p className="text-xs text-slate-400">Revenue</p>
                </div>
                <div className="bg-slate-700/50 p-3 rounded-lg text-center">
                  <p className="text-sm font-medium text-white">
                    {viewingRecurringClient.last_booking_date 
                      ? new Date(viewingRecurringClient.last_booking_date).toLocaleDateString()
                      : 'Never'}
                  </p>
                  <p className="text-xs text-slate-400">Last Booking</p>
                </div>
              </div>
              
              {/* Preferred Classes */}
              {viewingRecurringClient.preferred_class_ids && viewingRecurringClient.preferred_class_ids.length > 0 && (
                <div className="bg-slate-700/50 p-3 rounded-lg">
                  <p className="text-xs text-slate-400 mb-2 flex items-center gap-1">
                    <BookOpen className="h-3 w-3" /> Preferred Classes
                  </p>
                  <div className="flex flex-wrap gap-1">
                    {viewingRecurringClient.preferred_class_ids.map((classId, idx) => {
                      const classType = adminClassTypes.find(c => c.id === classId);
                      return (
                        <span key={idx} className="px-2 py-1 bg-blue-900/50 text-blue-300 text-xs rounded-full">
                          {classType ? classType.name : classId}
                        </span>
                      );
                    })}
                  </div>
                </div>
              )}
              
              {/* Notes */}
              {viewingRecurringClient.notes && viewingRecurringClient.notes.length > 0 && (
                <div className="bg-slate-700/50 p-3 rounded-lg">
                  <p className="text-xs text-slate-400 mb-2 flex items-center gap-1">
                    <FileText className="h-3 w-3" /> Notes ({viewingRecurringClient.notes.length})
                  </p>
                  <div className="space-y-2 max-h-32 overflow-y-auto">
                    {[...viewingRecurringClient.notes].reverse().slice(0, 5).map((note, idx) => (
                      <div key={idx} className="text-sm p-2 bg-slate-800 rounded">
                        <p className="text-slate-200">{note.text}</p>
                        <p className="text-xs text-slate-500 mt-1">
                          {new Date(note.created_at).toLocaleString()}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ) : null}
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                setShowViewRecurringClient(false);
                setShowRecurringClients(true);
              }}
              className="border-blue-600 text-blue-400 hover:bg-blue-900/30"
            >
              <Edit2 className="h-4 w-4 mr-2" />
              Edit in Manager
            </Button>
            <Button
              onClick={() => setShowViewRecurringClient(false)}
              className="bg-slate-700 text-white hover:bg-slate-600"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Signature View Dialog */}
      <Dialog open={!!viewSignatureData} onOpenChange={() => setViewSignatureData(null)}>
        <DialogContent className="sm:max-w-md bg-slate-800 border-slate-700 text-slate-100" data-testid="signature-view-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Edit2 className="h-5 w-5 text-teal-400" />
              Student Signature
            </DialogTitle>
          </DialogHeader>
          
          {viewSignatureData && (
            <div className="py-4">
              <div className="bg-slate-700/50 rounded-lg p-4 mb-4">
                <p className="text-sm text-white">
                  <span className="font-semibold">{viewSignatureData.name}</span>
                </p>
                {viewSignatureData.signed_at && (
                  <p className="text-xs text-slate-400 mt-1">
                    Signed at: {new Date(viewSignatureData.signed_at).toLocaleString()}
                  </p>
                )}
                {viewSignatureData.signed_in_by_admin && (
                  <p className="text-xs text-amber-400 mt-1 flex items-center gap-1">
                    <UserCheck className="h-3 w-3" /> Signed in by Admin
                  </p>
                )}
              </div>
              
              {viewSignatureData.signature && viewSignatureData.signature !== 'SIGNED_IN_BY_ADMIN' ? (
                <div className="bg-white rounded-lg p-2">
                  <img 
                    src={viewSignatureData.signature} 
                    alt="Student signature" 
                    className="w-full h-auto"
                  />
                </div>
              ) : (
                <div className="bg-slate-700 rounded-lg p-6 text-center">
                  <UserCheck className="h-12 w-12 text-amber-400 mx-auto mb-2" />
                  <p className="text-slate-300">Signed in by Admin</p>
                  <p className="text-xs text-slate-500 mt-1">No signature captured</p>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setViewSignatureData(null)}
              className="border-teal-600 text-white hover:bg-slate-700"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Sign-In Password Dialog */}
      <Dialog open={showSignInPasswordDialog} onOpenChange={(open) => { setShowSignInPasswordDialog(open); if (open) fetchSignInPassword(); }}>
        <DialogContent className="sm:max-w-md bg-slate-800 border-slate-700 text-slate-100" data-testid="signin-password-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <UserPlus className="h-5 w-5 text-emerald-400" />
              Class Sign-In Password
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Set the password students use to access the class sign-in page at /signin
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div>
              <Label className="text-slate-300">Password</Label>
              <Input
                type="text"
                value={signInPassword}
                onChange={(e) => setSignInPassword(e.target.value)}
                placeholder="Enter sign-in password"
                className="bg-slate-700 border-slate-600 text-white"
              />
              <p className="text-xs text-slate-500 mt-1">
                This password is shared with students to access the sign-in kiosk
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowSignInPasswordDialog(false)}
              className="border-slate-600 text-white hover:bg-slate-700"
            >
              Cancel
            </Button>
            <Button
              onClick={updateSignInPassword}
              disabled={signInPasswordLoading}
              className="bg-emerald-600 hover:bg-emerald-500"
            >
              {signInPasswordLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : 'Save Password'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AHA Credentials Dialog */}
      <Dialog open={showAhaCredentialsDialog} onOpenChange={setShowAhaCredentialsDialog}>
        <DialogContent className="sm:max-w-md bg-slate-800 border-slate-700 text-slate-100">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="h-5 w-5 text-red-400" />
              AHA Credentials
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Configure your AHA Instructor Network login credentials for automatic eCard inventory checking.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {ahaCredentialsConfigured && (
              <div className="bg-green-900/30 border border-green-600 rounded-lg p-3 flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-400" />
                <span className="text-green-300 text-sm">Credentials configured</span>
              </div>
            )}
            
            <div>
              <Label className="text-slate-300">Email / Username</Label>
              <Input
                type="email"
                value={ahaUsername}
                onChange={(e) => setAhaUsername(e.target.value)}
                placeholder="your.email@example.com"
                className="bg-slate-700 border-slate-600 text-white"
              />
            </div>
            
            <div>
              <Label className="text-slate-300">Password</Label>
              <Input
                type="password"
                value={ahaPassword}
                onChange={(e) => setAhaPassword(e.target.value)}
                placeholder={ahaCredentialsConfigured ? "Enter new password to change" : "Enter password"}
                className="bg-slate-700 border-slate-600 text-white"
              />
              <p className="text-xs text-slate-500 mt-1">
                Your AHA Instructor Network password
              </p>
            </div>
            
            <div className="bg-slate-700/50 rounded-lg p-3">
              <p className="text-xs text-slate-400">
                These credentials are used to automatically check your AHA eCard inventory every 10 minutes. 
                The system will login to ecards.heart.org to fetch your current inventory counts.
              </p>
            </div>
          </div>
          
          <DialogFooter className="gap-2 sm:gap-0">
            <Button
              variant="outline"
              onClick={testAhaLogin}
              disabled={ahaTestingLogin || !ahaCredentialsConfigured}
              className="border-slate-600 text-white hover:bg-slate-700"
            >
              {ahaTestingLogin ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Testing...
                </>
              ) : 'Test Login'}
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowAhaCredentialsDialog(false)}
              className="border-slate-600 text-white hover:bg-slate-700"
            >
              Cancel
            </Button>
            <Button
              onClick={saveAhaCredentials}
              disabled={ahaCredentialsLoading || !ahaUsername || !ahaPassword || ahaPassword === '********'}
              className="bg-red-600 hover:bg-red-500"
            >
              {ahaCredentialsLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Saving...
                </>
              ) : 'Save Credentials'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* AHA Product Names Dialog */}
      <Dialog open={showAhaProductLinksDialog} onOpenChange={setShowAhaProductLinksDialog}>
        <DialogContent className="sm:max-w-2xl bg-slate-800 border-slate-700 text-slate-100 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <BookOpen className="h-5 w-5 text-purple-400" />
              AHA Product Names
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Configure AHA product names for automated eBook and course assignment. Enter the exact product name as shown in the AHA eLearning portal.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {/* BLS Digital Test Link - Legacy/Fallback */}
            <div className="bg-cyan-900/30 border border-cyan-600 rounded-lg p-4">
              <div className="flex items-center gap-2 mb-2">
                <FileText className="h-5 w-5 text-cyan-400" />
                <h3 className="font-semibold text-cyan-300">BLS Digital Test (Legacy URL)</h3>
                <span className="text-xs bg-slate-600 px-2 py-0.5 rounded">Optional Fallback</span>
              </div>
              <p className="text-xs text-slate-400 mb-3">
                Legacy direct URL approach. Recommended: Use "Add New Product Mapping" below with Type="Course/Test" instead.
              </p>
              <div className="flex gap-2">
                <Input
                  value={blsTestLink}
                  onChange={(e) => setBlsTestLink(e.target.value)}
                  placeholder="https://elearning.heart.org/manage/assign_courses/..."
                  className="flex-1 bg-slate-700 border-slate-600 text-white"
                />
                <Button
                  onClick={saveBlsTestLink}
                  disabled={blsTestLinkSaving}
                  className="bg-cyan-600 hover:bg-cyan-500"
                >
                  {blsTestLinkSaving ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Save'}
                </Button>
              </div>
              {blsTestLink && (
                <p className="text-xs text-green-400 mt-2 flex items-center gap-1">
                  <CheckCircle className="h-3 w-3" />
                  BLS Test Link configured (will override product mappings)
                </p>
              )}
            </div>
            
            {/* Divider */}
            <div className="border-t border-slate-700 pt-4">
              <h3 className="font-medium text-slate-300 mb-3">AHA Product Names</h3>
              <p className="text-xs text-slate-500 mb-3">
                Configure AHA product names for automatic eBook/course assignment. These products can then be assigned to Class Types in "Manage Class Types".
              </p>
            </div>
            
            {/* Existing Links */}
            {ahaProductLinksLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-purple-400" />
              </div>
            ) : ahaProductLinks.length === 0 ? (
              <div className="bg-slate-700/50 rounded-lg p-4 text-center">
                <p className="text-slate-400">No AHA product mappings configured.</p>
                <p className="text-xs text-slate-500 mt-1">Add product names below for automatic assignment during student sign-in.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {ahaProductLinks.map((link) => (
                  <div key={link.id} className={`bg-slate-700/50 rounded-lg p-3 border ${link.is_active ? 'border-purple-600' : 'border-slate-600 opacity-50'}`}>
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2">
                          <span className={`text-xs px-2 py-0.5 rounded ${link.product_type === 'ebook' ? 'bg-amber-600' : 'bg-cyan-600'}`}>
                            {link.product_type === 'ebook' ? 'eBook' : 'Digital Test'}
                          </span>
                          <h4 className="font-medium text-white">{link.name}</h4>
                          {!link.is_active && <span className="text-xs text-slate-500">(Disabled)</span>}
                        </div>
                        <p className="text-xs text-purple-300 mt-1 break-all">AHA Product: {link.aha_product_name || <span className="text-slate-500 italic">Not configured</span>}</p>
                      </div>
                      <div className="flex gap-1 ml-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setEditingAhaLink(link)}
                          className="text-purple-400 hover:bg-purple-900/30"
                        >
                          <Edit2 className="h-4 w-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => deleteAhaProductLink(link.id)}
                          className="text-red-400 hover:bg-red-900/30"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
            
            {/* Add/Edit Form */}
            <div className="border-t border-slate-700 pt-4">
              <h4 className="font-medium text-white mb-3">
                {editingAhaLink ? 'Edit Product Mapping' : 'Add New Product Mapping'}
              </h4>
              <div className="space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label className="text-slate-300">Display Name</Label>
                    <Input
                      value={editingAhaLink?.name ?? newAhaLink.name}
                      onChange={(e) => editingAhaLink 
                        ? setEditingAhaLink({...editingAhaLink, name: e.target.value})
                        : setNewAhaLink({...newAhaLink, name: e.target.value})
                      }
                      placeholder="e.g., HS First Aid eBook, BLS Course"
                      className="bg-slate-700 border-slate-600 text-white"
                    />
                  </div>
                  <div>
                    <Label className="text-slate-300">Type</Label>
                    <select
                      value={editingAhaLink?.product_type ?? newAhaLink.product_type}
                      onChange={(e) => editingAhaLink
                        ? setEditingAhaLink({...editingAhaLink, product_type: e.target.value})
                        : setNewAhaLink({...newAhaLink, product_type: e.target.value})
                      }
                      className="w-full h-10 px-3 rounded-md bg-slate-700 border border-slate-600 text-white"
                    >
                      <option value="ebook">eBook (manage_ebooks)</option>
                      <option value="digital_test">Course/Test (manage_courses)</option>
                    </select>
                  </div>
                </div>
                
                <div>
                  <Label className="text-slate-300">AHA Product Name (Exact Match)</Label>
                  <Input
                    value={editingAhaLink?.aha_product_name ?? newAhaLink.aha_product_name}
                    onChange={(e) => editingAhaLink
                      ? setEditingAhaLink({...editingAhaLink, aha_product_name: e.target.value})
                      : setNewAhaLink({...newAhaLink, aha_product_name: e.target.value})
                    }
                    placeholder="e.g., Heartsaver® First Aid CPR AED Student eBook"
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Enter the exact product name as shown in your AHA eLearning portal. The automation will search for this name.
                  </p>
                </div>
                
                {editingAhaLink && (
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={editingAhaLink.is_active}
                      onChange={(e) => setEditingAhaLink({...editingAhaLink, is_active: e.target.checked})}
                      className="rounded border-slate-500"
                    />
                    <Label className="text-slate-300">Active</Label>
                  </div>
                )}
                
                <div className="flex gap-2">
                  {editingAhaLink && (
                    <Button
                      variant="outline"
                      onClick={() => setEditingAhaLink(null)}
                      className="border-slate-600 text-white hover:bg-slate-700"
                    >
                      Cancel Edit
                    </Button>
                  )}
                  <Button
                    onClick={() => saveAhaProductLink(editingAhaLink || newAhaLink)}
                    disabled={
                      !(editingAhaLink?.name ?? newAhaLink.name) ||
                      !(editingAhaLink?.aha_product_name ?? newAhaLink.aha_product_name)
                    }
                    className="bg-purple-600 hover:bg-purple-500"
                  >
                    {editingAhaLink ? 'Update Mapping' : 'Add Mapping'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowAhaProductLinksDialog(false)}
              className="border-slate-600 text-white hover:bg-slate-700"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* eCard Ordering Dialog */}
      <Dialog open={showEcardOrdering} onOpenChange={setShowEcardOrdering}>
        <DialogContent className="sm:max-w-2xl bg-slate-800 border-slate-700 text-slate-100 max-h-[90vh] overflow-y-auto" data-testid="ecard-order-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <CreditCard className="h-5 w-5 text-white" />
              Order eCards from CPR Suppliers
            </DialogTitle>
            <DialogDescription className="text-white">
              Order eCards for approved classes. One card per student.
            </DialogDescription>
          </DialogHeader>
          
          {/* Quick View AHA Inventory Button */}
          <div className="flex items-center justify-end gap-2 -mt-2 mb-2">
            {ahaCacheAge !== null && ahaInventory.length > 0 && (
              <span className="text-xs text-slate-500">
                AHA data: {ahaCacheAge < 1 ? '< 1' : Math.round(ahaCacheAge)} min ago
              </span>
            )}
            <Button
              onClick={async () => { 
                setShowAHAInventory(true); 
                await fetchAHAInventoryCached();
              }}
              disabled={ahaInventoryLoading}
              variant="outline"
              size="sm"
              className="border-rose-600 text-rose-400 hover:bg-rose-900/30"
              data-testid="quick-view-aha-inventory"
              title="View cached AHA inventory (click Force Refresh in dialog for fresh data)"
            >
              {ahaInventoryLoading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Eye className="h-4 w-4 mr-2" />
              )}
              View AHA Inventory
            </Button>
          </div>
          
          {/* Inventory Comparison Table */}
          {ecardLinks?.length > 0 && (
            <div className="bg-slate-700/30 rounded-lg p-4 mb-4 border border-teal-600">
              <div className="flex items-center justify-between mb-3">
                <h4 className="text-sm font-semibold text-white flex items-center gap-2">
                  <CreditCard className="h-4 w-4 text-white" />
                  Inventory Overview
                  <span className="text-xs text-slate-500 ml-2">(Edit in eCards → eCard Management)</span>
                </h4>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={cleanupEcardInventory}
                  disabled={cleaningUpInventory}
                  className="border-amber-600 text-amber-400 hover:bg-amber-600/20 text-xs"
                  data-testid="cleanup-inventory-btn"
                >
                  {cleaningUpInventory ? (
                    <>
                      <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                      Cleaning...
                    </>
                  ) : (
                    <>
                      <RefreshCw className="h-3 w-3 mr-1" />
                      Fix Inventory Data
                    </>
                  )}
                </Button>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-teal-600">
                      <th className="text-left py-2 px-3 text-white font-medium">eCard</th>
                      <th className="text-center py-2 px-3 text-white font-medium">Product #</th>
                      <th className="text-center py-2 px-3 text-white font-medium">Your Inv</th>
                      <th className="text-center py-2 px-3 text-rose-400 font-medium">AHA Qty</th>
                      <th className="text-center py-2 px-3 text-white font-medium">Order Link</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(ecardLinks || []).map(link => {
                      // Try to find inventory by product_number first, then by product_url (link.link)
                      const localInvByNumber = localEcardInventory.find(i => i.product_number === link.product_number);
                      const localInvByUrl = localEcardInventory.find(i => i.product_url === link.link);
                      const localInv = localInvByNumber || localInvByUrl;
                      const ahaInv = ahaInventory.find(i => i.product_number === link.product_number);
                      const localQty = localInv?.quantity || localInv?.available || 0;
                      const ahaQty = ahaInv?.available_quantity || 0;
                      
                      return (
                        <tr key={link.id} className="border-b border-slate-700/50">
                          <td className="py-2 px-3 text-slate-200">{link.name}</td>
                          <td className="py-2 px-3 text-center text-white font-mono text-xs">{link.product_number}</td>
                          <td className="py-2 px-3 text-center">
                            <span className="text-white font-medium">{localQty}</span>
                          </td>
                          <td className={`py-2 px-3 text-center font-medium ${ahaQty > 0 ? 'text-rose-400' : 'text-slate-500'}`}>
                            {ahaQty}
                          </td>
                          <td className="py-2 px-3 text-center">
                            <a 
                              href={link.link} 
                              target="_blank" 
                              rel="noopener noreferrer"
                              className="text-cyan-400 hover:text-cyan-300"
                            >
                              <ExternalLink className="h-4 w-4 inline" />
                            </a>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
              {ahaInventory.length === 0 && (
                <p className="text-xs text-slate-500 mt-2 text-center">
                  Click "View AHA Inventory" to load AHA quantities
                </p>
              )}
            </div>
          )}

          {ecardLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-white" />
              <span className="ml-3 text-white">Loading order data...</span>
            </div>
          ) : !ecardSupplierSettings.configured && !showEcardSettings ? (
            // Initial setup prompt
            <div className="py-6 text-center">
              <Settings className="h-12 w-12 text-slate-500 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-slate-200 mb-2">Configure Supplier Account</h3>
              <p className="text-white text-sm mb-4">
                Enter your cprsuppliers.com credentials to enable automatic cart generation.
              </p>
              <Button 
                onClick={() => setShowEcardSettings(true)}
                className="bg-teal-600 hover:bg-teal-500"
              >
                <Settings className="h-4 w-4 mr-2" />
                Setup Credentials
              </Button>
            </div>
          ) : showEcardSettings ? (
            // Settings form
            <div className="py-4 space-y-4">
              <div className="bg-slate-700/50 rounded-lg p-4">
                <h4 className="font-medium text-slate-200 mb-3">CPR Suppliers Login</h4>
                <div className="space-y-3">
                  <div>
                    <Label className="text-white text-sm">Email / Username</Label>
                    <Input
                      type="email"
                      value={ecardSettingsForm.username}
                      onChange={(e) => setEcardSettingsForm({...ecardSettingsForm, username: e.target.value})}
                      placeholder="your@email.com"
                      className="bg-slate-700 border-teal-600 text-slate-100 mt-1"
                      data-testid="ecard-supplier-username"
                    />
                  </div>
                  <div>
                    <Label className="text-white text-sm">Password</Label>
                    <Input
                      type="password"
                      value={ecardSettingsForm.password}
                      onChange={(e) => setEcardSettingsForm({...ecardSettingsForm, password: e.target.value})}
                      placeholder="••••••••"
                      className="bg-slate-700 border-teal-600 text-slate-100 mt-1"
                      data-testid="ecard-supplier-password"
                    />
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowEcardSettings(false);
                    setEcardSettingsForm({ username: '', password: '' });
                  }}
                  className="border-teal-600 text-white"
                >
                  Cancel
                </Button>
                <Button
                  onClick={saveEcardSupplierSettings}
                  disabled={!ecardSettingsForm.username || !ecardSettingsForm.password}
                  className="bg-teal-600 hover:bg-teal-500"
                >
                  Save Credentials
                </Button>
              </div>
            </div>
          ) : ecardCartResult ? (
            // Cart created - show summary and checkout options
            <div className="py-4 space-y-4">
              {/* Order Preview Header */}
              <div className="bg-blue-900/30 border border-teal-600 rounded-lg p-4">
                <div className="flex items-center gap-2 text-white mb-2">
                  <Eye className="h-5 w-5" />
                  <span className="font-medium">Order Preview</span>
                </div>
                <p className="text-white text-sm">
                  Review the items and total below. Launch CPR Suppliers to complete payment.
                </p>
              </div>

              {/* Cart Summary */}
              <div className="bg-slate-700/50 rounded-lg p-4">
                <h4 className="font-medium text-slate-200 mb-3">Order Details</h4>
                <div className="space-y-2">
                  {ecardCartResult.items_added?.map((item, idx) => (
                    <div key={idx} className="flex justify-between text-sm py-1 border-b border-teal-600/50 last:border-0">
                      <span className="text-white">
                        {item.class_type_name}
                        <span className="text-slate-500 ml-2">× {item.quantity}</span>
                      </span>
                      <span className="text-slate-200 font-medium">${item.subtotal?.toFixed(2) || '0.00'}</span>
                    </div>
                  ))}
                  <div className="border-t-2 border-slate-500 pt-3 mt-3 flex justify-between font-bold">
                    <span className="text-slate-100">Total to Pay</span>
                    <span className="text-green-400 text-xl">${ecardCartResult.cart_total?.toFixed(2) || '0.00'}</span>
                  </div>
                </div>
              </div>

              {orderResult?.success && orderResult?.order_number && !orderResult?.requires_manual_payment ? (
                // Order completed successfully - show inventory adjustment prompt
                <div className="flex flex-col gap-3">
                  <div className={`${orderResult.test_mode ? 'bg-amber-900/30 border-amber-600' : 'bg-green-900/30 border-green-600'} border rounded-lg p-4`}>
                    <div className={`flex items-center gap-2 ${orderResult.test_mode ? 'text-amber-400' : 'text-green-400'} mb-2`}>
                      <CheckCircle className="h-5 w-5" />
                      <span className="font-medium">
                        {orderResult.test_mode ? 'Test Order Simulated!' : 'Order Placed Successfully!'}
                      </span>
                    </div>
                    <p className="text-white text-lg font-bold">
                      Order #{orderResult.order_number}
                    </p>
                    <p className="text-white text-sm mt-1">
                      {orderResult.message}
                    </p>
                  </div>
                  
                  {orderResult.confirmation_screenshot && (
                    <div className="bg-slate-700/50 rounded-lg p-3">
                      <p className="text-xs text-slate-400 mb-2">Confirmation Screenshot:</p>
                      <img 
                        src={`data:image/jpeg;base64,${orderResult.confirmation_screenshot}`}
                        alt="Order Confirmation"
                        className="w-full rounded border border-slate-600"
                      />
                    </div>
                  )}
                  
                  {/* Inventory Adjustment Prompt */}
                  <div className="bg-blue-900/30 border border-blue-600 rounded-lg p-4 mt-2">
                    <div className="flex items-center gap-2 text-blue-400 mb-3">
                      <Package className="h-5 w-5" />
                      <span className="font-medium">Adjust Inventory?</span>
                    </div>
                    <p className="text-white text-sm mb-4">
                      Would you like to adjust your inventory and auto-assign eCards to students needing them?
                    </p>
                    <div className="flex gap-3">
                      <Button
                        onClick={() => handleOrderInventoryAdjustment(true)}
                        className="flex-1 bg-green-600 hover:bg-green-500"
                        data-testid="adjust-inventory-yes-btn"
                      >
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Yes, Adjust Inventory
                      </Button>
                      <Button
                        onClick={() => handleOrderInventoryAdjustment(false)}
                        variant="outline"
                        className="flex-1 border-slate-600 text-slate-300 hover:bg-slate-700"
                        data-testid="adjust-inventory-no-btn"
                      >
                        <X className="h-4 w-4 mr-2" />
                        No, Skip
                      </Button>
                    </div>
                    <p className="text-xs text-slate-500 mt-2">
                      "Yes" will update inventory counts and mark students' eCards as ordered
                    </p>
                  </div>
                </div>
              ) : !supplierLaunched ? (
                // Step 1: Show Order and Launch buttons
                <div className="flex flex-col gap-3">
                  {/* Test Mode Warning */}
                  {ecardSupplierSettings.checkout_test_mode && (
                    <div className="bg-amber-900/30 border border-amber-600 rounded-lg p-3 text-center">
                      <span className="text-amber-400 font-medium text-sm">
                        ⚠️ TEST MODE - Orders will be simulated, not placed
                      </span>
                    </div>
                  )}
                  
                  {/* Primary: Automated Order Button */}
                  <Button
                    onClick={placeAutomatedOrder}
                    disabled={placingOrder || !ecardSupplierSettings.has_payment_card}
                    className={`w-full text-lg py-6 ${ecardSupplierSettings.checkout_test_mode 
                      ? 'bg-amber-600 hover:bg-amber-500' 
                      : 'bg-green-600 hover:bg-green-500'}`}
                    data-testid="place-automated-order-btn"
                  >
                    {placingOrder ? (
                      <>
                        <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                        {ecardSupplierSettings.checkout_test_mode ? 'Simulating...' : 'Placing Order...'}
                      </>
                    ) : (
                      <>
                        <CreditCard className="h-5 w-5 mr-2" />
                        {ecardSupplierSettings.checkout_test_mode ? 'Test Order (Simulated)' : 'Place Order (Automated)'}
                      </>
                    )}
                  </Button>
                  <p className="text-xs text-slate-400 text-center">
                    {ecardSupplierSettings.checkout_test_mode 
                      ? 'Simulates order without charging card' 
                      : 'Completes checkout automatically using saved card'}
                  </p>
                  
                  <div className="relative flex items-center my-2">
                    <div className="flex-grow border-t border-slate-600"></div>
                    <span className="flex-shrink mx-4 text-slate-500 text-xs">OR</span>
                    <div className="flex-grow border-t border-slate-600"></div>
                  </div>
                  
                  {/* Secondary: Manual Launch */}
                  <Button
                    onClick={launchCprSuppliers}
                    disabled={completingCheckout || placingOrder}
                    variant="outline"
                    className="border-teal-600 text-white w-full"
                    data-testid="launch-cpr-suppliers-btn"
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Open CPR Suppliers Manually
                  </Button>
                  <p className="text-xs text-slate-500 text-center">
                    Opens checkout in browser for manual completion
                  </p>
                  
                  <Button
                    onClick={() => {
                      setEcardCartResult(null);
                      setCheckoutResult(null);
                      setOrderResult(null);
                    }}
                    variant="outline"
                    className="border-slate-600 text-slate-400 mt-2"
                  >
                    ← Go Back
                  </Button>
                </div>
              ) : (
                // Step 2: Supplier launched - show inventory adjustment options
                <div className="flex flex-col gap-3">
                  <div className="bg-amber-900/30 border border-amber-700 rounded-lg p-4">
                    <div className="flex items-center gap-2 text-amber-400 mb-2">
                      <ExternalLink className="h-5 w-5" />
                      <span className="font-medium">Complete Payment</span>
                    </div>
                    <p className="text-white text-sm">
                      Complete your payment on cprsuppliers.com, then choose an option below.
                    </p>
                  </div>
                  
                  <Button
                    onClick={() => window.open('https://cprsuppliers.com/checkout/', '_blank')}
                    variant="outline"
                    className="border-teal-600 text-white hover:bg-blue-900/30"
                  >
                    <ExternalLink className="h-4 w-4 mr-2" />
                    Open CPR Suppliers Again
                  </Button>
                  
                  <div className="border-t border-teal-600 pt-4 mt-2">
                    <p className="text-white text-sm mb-3 text-center">After completing payment:</p>
                    
                    <div className="grid grid-cols-1 gap-2">
                      <Button
                        onClick={() => confirmOrderCompleted(false)}
                        className="bg-teal-600 hover:bg-teal-500 w-full"
                        data-testid="adjust-inventory-btn"
                      >
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Adjust Inventory
                      </Button>
                      <p className="text-xs text-slate-500 text-center mb-2">
                        Mark students as having eCards ordered & update inventory
                      </p>
                      
                      <Button
                        onClick={() => confirmOrderCompleted(true)}
                        variant="outline"
                        className="border-amber-600 text-amber-400 hover:bg-amber-900/30 w-full"
                        data-testid="dont-adjust-inventory-btn"
                      >
                        Don't Adjust Inventory
                      </Button>
                      <p className="text-xs text-slate-500 text-center mb-2">
                        Record order without affecting bookings or stock count
                      </p>
                      
                      <Button
                        onClick={cancelEcardOrder}
                        variant="outline"
                        className="border-red-600 text-red-400 hover:bg-red-900/30 w-full"
                        data-testid="cancel-ecard-order-btn"
                      >
                        <X className="h-4 w-4 mr-2" />
                        Cancel
                      </Button>
                      <p className="text-xs text-slate-500 text-center">
                        Clear cart on CPR Suppliers — no inventory changes
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ) : showCardSettings ? (
            // Payment card settings form
            <div className="py-4 space-y-4">
              <div className="bg-slate-700/50 rounded-lg p-4">
                <h4 className="font-medium text-slate-200 mb-3 flex items-center gap-2">
                  <CreditCard className="h-4 w-4" />
                  Payment Card for Checkout
                </h4>
                <div className="space-y-3">
                  <div>
                    <Label className="text-white text-sm">Card Number</Label>
                    <Input
                      type="text"
                      value={cardSettingsForm.card_number}
                      onChange={(e) => setCardSettingsForm({...cardSettingsForm, card_number: e.target.value})}
                      placeholder="4111 1111 1111 1111"
                      className="bg-slate-700 border-teal-600 text-slate-100 mt-1 font-mono"
                      data-testid="ecard-card-number"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-white text-sm">Expiry (MM/YY)</Label>
                      <Input
                        type="text"
                        value={cardSettingsForm.card_expiry}
                        onChange={(e) => setCardSettingsForm({...cardSettingsForm, card_expiry: e.target.value})}
                        placeholder="03/29"
                        className="bg-slate-700 border-teal-600 text-slate-100 mt-1 font-mono"
                        maxLength={5}
                        data-testid="ecard-card-expiry"
                      />
                    </div>
                    <div>
                      <Label className="text-white text-sm">CVV</Label>
                      <Input
                        type="password"
                        value={cardSettingsForm.card_cvv}
                        onChange={(e) => setCardSettingsForm({...cardSettingsForm, card_cvv: e.target.value})}
                        placeholder="•••"
                        className="bg-slate-700 border-teal-600 text-slate-100 mt-1 font-mono"
                        maxLength={4}
                        data-testid="ecard-card-cvv"
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2 pt-2">
                    <Checkbox
                      id="tax-exempt"
                      checked={cardSettingsForm.tax_exempt}
                      onCheckedChange={(checked) => setCardSettingsForm({...cardSettingsForm, tax_exempt: checked})}
                      className="border-green-600 data-[state=checked]:bg-green-600"
                    />
                    <label htmlFor="tax-exempt" className="text-sm text-white cursor-pointer">
                      Tax Exempt
                    </label>
                  </div>
                </div>
              </div>
              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  onClick={() => {
                    setShowCardSettings(false);
                    setCardSettingsForm({ card_number: '', card_expiry: '', card_cvv: '', tax_exempt: true });
                  }}
                  className="border-teal-600 text-white"
                >
                  Cancel
                </Button>
                <Button
                  onClick={savePaymentCard}
                  disabled={!cardSettingsForm.card_number || !cardSettingsForm.card_expiry || !cardSettingsForm.card_cvv}
                  className="bg-teal-600 hover:bg-teal-500"
                >
                  Save Card Securely
                </Button>
              </div>
              <p className="text-xs text-slate-500">
                Card details are encrypted and stored securely. They will be used for automated checkout.
              </p>
            </div>
          ) : (
            // Main order view
            <div className="py-4 space-y-4">
              {/* Supplier & Payment info */}
              <div className="flex flex-wrap justify-between items-center gap-2 text-sm bg-slate-700/30 rounded-lg p-3">
                <div className="flex flex-col gap-1">
                  <span className="text-white">
                    Account: <span className="text-white">{ecardSupplierSettings.username}</span>
                  </span>
                  <span className="text-white">
                    Payment: {ecardSupplierSettings.has_payment_card ? (
                      <span className="text-green-400">
                        •••• {ecardSupplierSettings.card_last_four}
                        {ecardSupplierSettings.tax_exempt && <span className="ml-2 text-xs bg-green-900/50 px-1.5 py-0.5 rounded">Tax Exempt</span>}
                      </span>
                    ) : (
                      <span className="text-amber-400">Not configured</span>
                    )}
                  </span>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setShowEcardSettings(true);
                      setEcardSettingsForm({ username: ecardSupplierSettings.username || '', password: '' });
                    }}
                    className="text-white hover:text-slate-200"
                  >
                    <Settings className="h-4 w-4 mr-1" />
                    Account
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setShowCardSettings(true);
                      setCardSettingsForm({ ...cardSettingsForm, tax_exempt: ecardSupplierSettings.tax_exempt !== false });
                    }}
                    className="text-white hover:text-slate-200"
                  >
                    <CreditCard className="h-4 w-4 mr-1" />
                    {ecardSupplierSettings.has_payment_card ? 'Change Card' : 'Add Card'}
                  </Button>
                  {ecardSupplierSettings.has_payment_card && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={deletePaymentCard}
                      className="text-red-400 hover:text-red-300"
                    >
                      <X className="h-4 w-4" />
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={testSupplierLogin}
                    disabled={testingLogin}
                    className="text-white hover:text-blue-300"
                    data-testid="test-login-btn"
                  >
                    {testingLogin ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <RefreshCw className="h-4 w-4 mr-1" />
                        Test Login
                      </>
                    )}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={clearSupplierCart}
                    disabled={clearingCart}
                    className="text-amber-400 hover:text-amber-300"
                    data-testid="clear-cart-btn"
                    title="Clear any leftover items in cart"
                  >
                    {clearingCart ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <Trash2 className="h-4 w-4 mr-1" />
                        Clear Cart
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Order items table - Always show ALL products */}
              <div className="bg-slate-700/50 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-teal-600 bg-slate-700/70">
                      <th className="text-left p-3 text-white font-medium">eCard Product</th>
                      <th className="text-center p-3 text-white font-medium">
                        <span title="Cards in stock not committed to students">Available</span>
                      </th>
                      <th className="text-center p-3 text-white font-medium">
                        <span title="Students in approved classes needing cards">Needed</span>
                      </th>
                      <th className="text-center p-3 text-white font-medium">
                        <span title="Cached price per card">Price</span>
                      </th>
                      <th className="text-center p-3 text-white font-medium">Order</th>
                      <th className="text-right p-3 text-white font-medium">Subtotal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ecardNeeds.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="p-6 text-center text-white">
                          <p>No eCard products configured.</p>
                          <p className="text-sm mt-1">Add product URLs in Settings → Manage Class Types</p>
                        </td>
                      </tr>
                    ) : (
                      ecardNeeds.map((need) => {
                        const qty = ecardOrderQuantities[need.product_url] || 0;
                        const unitPrice = getCachedPrice(need.product_url);
                        const subtotal = unitPrice * qty;
                        return (
                          <tr key={need.product_url} className="border-b border-teal-600/50 hover:bg-slate-700/30">
                            <td className="p-3">
                              <div className="text-slate-200 font-medium">{need.product_name}</div>
                              {(need.min_order || 1) > 1 && (
                                <div className="text-xs text-yellow-400 mt-1">
                                  Min order: {need.min_order}
                                </div>
                              )}
                              {need.class_types && need.class_types.length > 1 && (
                                <div className="text-xs text-slate-500 mt-1">
                                  Used by: {need.class_types.join(', ')}
                                </div>
                              )}
                              {need.bookings && need.bookings.length > 0 && (
                                <div className="text-xs text-white mt-1">
                                  {need.bookings.length} upcoming class(es)
                                </div>
                              )}
                            </td>
                            <td className="p-3 text-center">
                              <span className={`px-2 py-1 rounded ${need.cards_in_inventory > 0 ? 'text-green-400 font-medium' : 'text-slate-500'}`}>
                                {need.cards_in_inventory}
                              </span>
                            </td>
                            <td className="p-3 text-center">
                              <span className={need.cards_needed > 0 ? 'text-amber-400 font-medium' : 'text-slate-500'}>
                                {need.cards_needed}
                              </span>
                            </td>
                            <td className="p-3 text-center">
                              <span className={unitPrice > 0 ? 'text-green-400' : 'text-slate-500'}>
                                {unitPrice > 0 ? `$${unitPrice.toFixed(2)}` : '—'}
                              </span>
                            </td>
                            <td className="p-3">
                              <div className="flex items-center justify-center gap-2">
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => adjustEcardQuantity(need.product_url, -1)}
                                  className="h-7 w-7 p-0 border-teal-600"
                                  disabled={qty <= 0}
                                >
                                  -
                                </Button>
                                <Input
                                  type="number"
                                  value={qty}
                                  onChange={(e) => setEcardQuantity(need.product_url, e.target.value)}
                                  onBlur={(e) => handleEcardQuantityBlur(need.product_url, e.target.value)}
                                  className="w-14 h-7 text-center bg-slate-700 border-teal-600 text-slate-100"
                                  min="0"
                                />
                                <Button
                                  variant="outline"
                                  size="sm"
                                  onClick={() => adjustEcardQuantity(need.product_url, 1)}
                                  className="h-7 w-7 p-0 border-teal-600"
                                >
                                  +
                                </Button>
                              </div>
                            </td>
                            <td className="p-3 text-right">
                              <span className={subtotal > 0 ? 'text-white font-medium' : 'text-slate-500'}>
                                {subtotal > 0 ? `$${subtotal.toFixed(2)}` : '—'}
                              </span>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              </div>

              {/* Totals */}
              <div className="bg-slate-700/30 rounded-lg p-4 space-y-3">
                <div className="flex justify-between items-center">
                  <div>
                    <span className="text-white text-sm">Total Cards:</span>
                    <span className="ml-2 text-lg font-medium text-slate-200">{getTotalCardsToOrder()}</span>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => {
                      // Reset all order quantities to 0
                      const zeroQuantities = {};
                      ecardNeeds.forEach(item => {
                        zeroQuantities[item.product_url] = 0;
                      });
                      setEcardOrderQuantities(zeroQuantities);
                    }}
                    className="border-teal-600 text-white hover:text-slate-200 hover:bg-slate-700"
                    data-testid="clear-order-btn"
                  >
                    <X className="h-3 w-3 mr-1" />
                    Clear Order
                  </Button>
                </div>
                
                {/* Estimated Total from Cached Prices */}
                <div className="flex justify-between items-center border-t border-slate-600 pt-3">
                  <div>
                    <span className="text-white text-sm">Estimated Total:</span>
                    <span className="ml-2 text-xl font-bold text-green-400">${getEstimatedTotal().toFixed(2)}</span>
                    {cachedPrices.last_sync && (
                      <span className="text-xs text-slate-500 ml-2">
                        (prices from {new Date(cachedPrices.last_sync).toLocaleDateString()})
                      </span>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={refreshCachedPrices}
                    disabled={refreshingPrices}
                    className="text-blue-400 hover:text-blue-300"
                    title="Sync latest prices from CPR Suppliers"
                  >
                    {refreshingPrices ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : (
                      <>
                        <RefreshCw className="h-3 w-3 mr-1" />
                        Sync Prices
                      </>
                    )}
                  </Button>
                </div>
              </div>

              {/* Test Mode Warning */}
              {ecardSupplierSettings.checkout_test_mode && (
                <div className="bg-amber-900/30 border border-amber-600 rounded-lg p-3 text-center">
                  <span className="text-amber-400 font-medium text-sm">
                    ⚠️ TEST MODE - Orders will be simulated, not placed
                  </span>
                </div>
              )}

              {/* Direct Checkout Button */}
              <Button
                onClick={placeDirectOrder}
                disabled={placingOrder || getTotalCardsToOrder() === 0 || (!ecardSupplierSettings.has_payment_card && !ecardSupplierSettings.checkout_test_mode)}
                className={`w-full text-lg py-6 ${ecardSupplierSettings.checkout_test_mode 
                  ? 'bg-amber-600 hover:bg-amber-500' 
                  : 'bg-green-600 hover:bg-green-500'}`}
                data-testid="checkout-btn"
              >
                {placingOrder ? (
                  <>
                    <Loader2 className="h-5 w-5 mr-2 animate-spin" />
                    {ecardSupplierSettings.checkout_test_mode ? 'Simulating Order...' : 'Placing Order...'}
                  </>
                ) : (
                  <>
                    <CreditCard className="h-5 w-5 mr-2" />
                    {ecardSupplierSettings.checkout_test_mode 
                      ? `Test Order • $${getEstimatedTotal().toFixed(2)}` 
                      : `Checkout • $${getEstimatedTotal().toFixed(2)}`}
                  </>
                )}
              </Button>
              {!ecardSupplierSettings.has_payment_card && !ecardSupplierSettings.checkout_test_mode && (
                <p className="text-xs text-red-400 text-center">
                  Add a payment card or enable Test Mode to proceed
                </p>
              )}
              <p className="text-xs text-slate-500 text-center">
                {placingOrder 
                  ? 'Logging in, adding items to cart, and completing checkout...' 
                  : 'Uses cached prices • Inventory auto-updated after successful order'}
              </p>
            </div>
          )}

          <DialogFooter className="border-t border-slate-700 pt-4">
            <Button
              variant="outline"
              onClick={() => {
                setShowEcardOrdering(false);
                setEcardCartResult(null);
                setShowEcardSettings(false);
              }}
              className="border-teal-600 text-white hover:bg-slate-700"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Pending Queue Sidebar */}
      <PendingQueueSidebar
        isOpen={showPendingQueue}
        onToggle={() => setShowPendingQueue(!showPendingQueue)}
        bookings={bookings}
        classTypes={adminClassTypes}
        onRefreshBookings={fetchBookings}
      />

      {/* Send Queue Panel (eBooks and Digital Tests) */}
      <QueuePanel
        isOpen={showSendQueue}
        onClose={() => setShowSendQueue(false)}
      />

      {/* Admin Tasks Dialog */}
      <Dialog open={showAdminTasks} onOpenChange={setShowAdminTasks}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto bg-slate-800 border-slate-700 text-slate-100" data-testid="admin-tasks-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-400">
              <CheckCircle className="h-5 w-5" />
              Admin Tasks
            </DialogTitle>
            <DialogDescription className="text-white">
              Manage your to-do list and tasks
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <TodoList />
          </div>
        </DialogContent>
      </Dialog>

      {/* CPR Supplier Settings Dialog */}
      <Dialog open={showSupplierSettings} onOpenChange={setShowSupplierSettings}>
        <DialogContent className="sm:max-w-lg bg-slate-800 border-slate-700 text-slate-100 max-h-[85vh] overflow-y-auto" data-testid="supplier-settings-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-cyan-400">
              <Settings className="h-5 w-5" />
              CPR Supplier Settings
            </DialogTitle>
            <DialogDescription className="text-white">
              Configure your CPR Suppliers login and payment card for auto-ordering
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Login Credentials Section */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-slate-300 flex items-center gap-2">
                <User className="h-4 w-4" />
                Login Credentials
              </h3>
              <div className="grid gap-3">
                <div>
                  <Label className="text-slate-300">Username/Email</Label>
                  <Input
                    type="text"
                    value={ecardSettingsForm.username}
                    onChange={(e) => setEcardSettingsForm({ ...ecardSettingsForm, username: e.target.value })}
                    placeholder="your@email.com"
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                </div>
                <div>
                  <Label className="text-slate-300">Password</Label>
                  <Input
                    type="password"
                    value={ecardSettingsForm.password}
                    onChange={(e) => setEcardSettingsForm({ ...ecardSettingsForm, password: e.target.value })}
                    placeholder="••••••••"
                    className="bg-slate-700 border-slate-600 text-white"
                  />
                </div>
                <Button 
                  onClick={saveEcardSupplierSettings}
                  className="bg-cyan-600 hover:bg-cyan-500"
                  disabled={!ecardSettingsForm.username || !ecardSettingsForm.password}
                >
                  <Save className="h-4 w-4 mr-2" />
                  Save Credentials
                </Button>
              </div>
              {ecardSupplierSettings?.configured && (
                <div className="text-xs text-emerald-400 flex items-center gap-1">
                  <CheckCircle className="h-3 w-3" />
                  <span>Credentials saved for: {ecardSupplierSettings.username}</span>
                  {ecardSupplierSettings.password_length > 0 && (
                    <span className="text-slate-400 ml-2">
                      (Password: {ecardSupplierSettings.password_length} chars)
                    </span>
                  )}
                </div>
              )}
            </div>

            <div className="border-t border-slate-700" />

            {/* Payment Card Section */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-slate-300 flex items-center gap-2">
                <CreditCard className="h-4 w-4" />
                Payment Card for Auto-Checkout
              </h3>
              
              {ecardSupplierSettings?.has_payment_card ? (
                <div className="p-3 bg-emerald-900/30 rounded-lg border border-emerald-700">
                  <p className="text-emerald-400 flex items-center gap-2">
                    <CheckCircle className="h-4 w-4" />
                    Payment card saved (ending in ****{ecardSupplierSettings.card_last_four || '****'})
                  </p>
                  <div className="flex gap-2 mt-2">
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setShowCardSettings(!showCardSettings)}
                      className="text-amber-400 border-amber-600 hover:bg-amber-900/30"
                    >
                      Update Card
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={deletePaymentCard}
                      className="text-red-400 border-red-600 hover:bg-red-900/30"
                    >
                      <Trash2 className="h-3 w-3 mr-1" />
                      Remove
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="p-3 bg-amber-900/30 rounded-lg border border-amber-700">
                  <p className="text-amber-400 flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4" />
                    No payment card saved - auto-ordering will not work!
                  </p>
                  <Button 
                    variant="outline" 
                    size="sm"
                    onClick={() => setShowCardSettings(true)}
                    className="mt-2 text-cyan-400 border-cyan-600 hover:bg-cyan-900/30"
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    Add Payment Card
                  </Button>
                </div>
              )}

              {showCardSettings && (
                <div className="space-y-3 p-3 bg-slate-700/50 rounded-lg">
                  <div>
                    <Label className="text-slate-300">Card Number</Label>
                    <Input
                      type="text"
                      value={cardSettingsForm.card_number}
                      onChange={(e) => setCardSettingsForm({ ...cardSettingsForm, card_number: e.target.value })}
                      placeholder="4111 1111 1111 1111"
                      className="bg-slate-700 border-slate-600 text-white font-mono"
                      maxLength={19}
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-slate-300">Expiry (MM/YY)</Label>
                      <Input
                        type="text"
                        value={cardSettingsForm.card_expiry}
                        onChange={(e) => setCardSettingsForm({ ...cardSettingsForm, card_expiry: e.target.value })}
                        placeholder="12/25"
                        className="bg-slate-700 border-slate-600 text-white font-mono"
                        maxLength={5}
                      />
                    </div>
                    <div>
                      <Label className="text-slate-300">CVV</Label>
                      <Input
                        type="password"
                        value={cardSettingsForm.card_cvv}
                        onChange={(e) => setCardSettingsForm({ ...cardSettingsForm, card_cvv: e.target.value })}
                        placeholder="123"
                        className="bg-slate-700 border-slate-600 text-white font-mono"
                        maxLength={4}
                      />
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="taxExempt"
                      checked={cardSettingsForm.tax_exempt}
                      onChange={(e) => setCardSettingsForm({ ...cardSettingsForm, tax_exempt: e.target.checked })}
                      className="rounded bg-slate-700 border-slate-600"
                    />
                    <Label htmlFor="taxExempt" className="text-slate-300 text-sm">Tax Exempt</Label>
                  </div>
                  <div className="flex gap-2">
                    <Button 
                      onClick={savePaymentCard}
                      className="bg-emerald-600 hover:bg-emerald-500"
                      disabled={!cardSettingsForm.card_number || !cardSettingsForm.card_expiry || !cardSettingsForm.card_cvv}
                    >
                      <Save className="h-4 w-4 mr-2" />
                      Save Card
                    </Button>
                    <Button 
                      variant="outline"
                      onClick={() => {
                        setShowCardSettings(false);
                        setCardSettingsForm({ card_number: '', card_expiry: '', card_cvv: '', tax_exempt: true });
                      }}
                      className="text-slate-400 border-slate-600"
                    >
                      Cancel
                    </Button>
                  </div>
                </div>
              )}
            </div>

            <div className="border-t border-slate-700" />

            {/* Test Connection */}
            <div className="space-y-2">
              <div className="flex gap-2">
                <Button 
                  onClick={testSupplierLogin}
                  disabled={testingLogin}
                  variant="outline"
                  className="flex-1 text-cyan-400 border-cyan-600 hover:bg-cyan-900/30"
                >
                  {testingLogin ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  Test Login
                </Button>
                <Button 
                  onClick={runPlaywrightDiagnostics}
                  disabled={testingLogin}
                  variant="outline"
                  className="flex-1 text-amber-400 border-amber-600 hover:bg-amber-900/30"
                >
                  {testingLogin ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <Settings className="h-4 w-4 mr-2" />
                  )}
                  Diagnostics
                </Button>
              </div>
              
              {/* View Debug Screenshots Button */}
              <Button 
                onClick={async () => {
                  try {
                    toast.loading('Loading screenshots...', { id: 'screenshots' });
                    const response = await axios.get(`${API}/admin/ecard-supplier/debug-screenshots`);
                    toast.dismiss('screenshots');
                    
                    if (response.data.screenshots && response.data.screenshots.length > 0) {
                      // Open screenshots in new window
                      const html = `
                        <!DOCTYPE html>
                        <html>
                        <head><title>Checkout Debug Screenshots</title>
                        <style>body{background:#1a1a2e;color:#fff;font-family:sans-serif;padding:20px}
                        img{max-width:100%;border:2px solid #333;margin:10px 0;border-radius:8px}
                        h3{color:#0ea5e9}</style></head>
                        <body>
                        <h1>🔍 Checkout Debug Screenshots (${response.data.count})</h1>
                        ${response.data.screenshots.map(s => `<h3>${s.filename} (${s.size_kb}KB)</h3><img src="${s.data_url}"/>`).join('')}
                        </body></html>
                      `;
                      const win = window.open('', '_blank');
                      win.document.write(html);
                      win.document.close();
                    } else {
                      toast.info('No debug screenshots found. Run a checkout attempt first.');
                    }
                  } catch (error) {
                    toast.dismiss('screenshots');
                    toast.error('Failed to load screenshots');
                  }
                }}
                variant="outline"
                className="w-full text-purple-400 border-purple-600 hover:bg-purple-900/30"
              >
                <Camera className="h-4 w-4 mr-2" />
                View Debug Screenshots
              </Button>
            <div className="border-t border-slate-700" />

            {/* Auto-Order Mode Section */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-slate-300 flex items-center gap-2">
                <ShoppingCart className="h-4 w-4" />
                eCard Auto-Order Mode
              </h3>
              <p className="text-xs text-slate-400">
                Control how eCards are ordered when a booking is created.
              </p>
              
              <div className="flex gap-2">
                <Button
                  variant={ecardSupplierSettings.auto_order_mode === 'manual' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => toggleAutoOrderMode('manual')}
                  className={ecardSupplierSettings.auto_order_mode === 'manual' 
                    ? 'bg-slate-600 hover:bg-slate-500 text-white' 
                    : 'text-slate-400 border-slate-600 hover:bg-slate-700'}
                  data-testid="auto-order-manual-btn"
                >
                  Manual
                </Button>
                <Button
                  variant={ecardSupplierSettings.auto_order_mode === 'auto' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => toggleAutoOrderMode('auto')}
                  className={ecardSupplierSettings.auto_order_mode === 'auto' 
                    ? 'bg-emerald-600 hover:bg-emerald-500 text-white' 
                    : 'text-slate-400 border-slate-600 hover:bg-slate-700'}
                  data-testid="auto-order-auto-btn"
                >
                  Auto
                </Button>
              </div>
              
              <div className={`p-3 rounded-lg text-xs ${
                ecardSupplierSettings.auto_order_mode === 'auto' 
                  ? 'bg-emerald-900/30 border border-emerald-700 text-emerald-300'
                  : 'bg-slate-700/50 border border-slate-600 text-slate-400'
              }`}>
                {ecardSupplierSettings.auto_order_mode === 'auto' ? (
                  <div className="space-y-1">
                    <p className="font-medium text-emerald-400">✓ Auto Mode Active</p>
                    <ul className="list-disc list-inside space-y-0.5">
                      <li>eCards are auto-assigned from inventory when bookings are created</li>
                      <li>If inventory is low, the system will automatically order more</li>
                      <li>Respects minimum order quantities - excess goes to inventory</li>
                    </ul>
                  </div>
                ) : (
                  <div className="space-y-1">
                    <p className="font-medium">Manual Mode (Current)</p>
                    <ul className="list-disc list-inside space-y-0.5">
                      <li>eCards are added to "My eCards Needed" for review</li>
                      <li>Use Bulk Order or eCards dialog to place orders</li>
                      <li>Full control over ordering timing and quantities</li>
                    </ul>
                  </div>
                )}
              </div>
            </div>

            <div className="border-t border-slate-700" />
            
            {/* Dry Run Test Section */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-slate-300 flex items-center gap-2">
                <Play className="h-4 w-4" />
                Test Checkout (Dry Run)
              </h3>
              <p className="text-xs text-slate-400">
                Test the entire checkout process without actually placing an order. 
                Goes through login, cart, and payment form - but stops before clicking "Place Order".
              </p>
              
              <Button
                onClick={async () => {
                  if (!ecardSupplierSettings.has_payment_card) {
                    toast.error('Please save a payment card first');
                    return;
                  }
                  try {
                    toast.loading('Running dry run test...', { id: 'dry-run' });
                    const response = await axios.post(`${API}/admin/ecard-supplier/dry-run-test`);
                    toast.dismiss('dry-run');
                    if (response.data.success) {
                      toast.success(`✅ Dry Run Complete! All ${response.data.steps_completed || 'checkout'} steps passed.`, { duration: 8000 });
                    } else {
                      toast.error(`Dry Run Failed: ${response.data.error || 'Unknown error'}`, { duration: 8000 });
                    }
                  } catch (error) {
                    toast.dismiss('dry-run');
                    toast.error(`Dry Run Error: ${error.response?.data?.detail || error.message}`);
                  }
                }}
                disabled={!ecardSupplierSettings.has_payment_card || !ecardSupplierSettings.configured}
                className="w-full bg-purple-600 hover:bg-purple-500 text-white"
                data-testid="dry-run-test-btn"
              >
                <Play className="h-4 w-4 mr-2" />
                Run Dry Test (No Real Order)
              </Button>
              
              {(!ecardSupplierSettings.has_payment_card || !ecardSupplierSettings.configured) && (
                <p className="text-xs text-amber-400">
                  Configure credentials and payment card to enable dry run testing
                </p>
              )}
            </div>

            <div className="border-t border-slate-700" />
              
              {/* Test Result Display */}
              {testLoginResult && (
                <div className={`p-3 rounded-lg text-sm ${
                  testLoginResult.success 
                    ? 'bg-emerald-900/30 border border-emerald-700 text-emerald-400' 
                    : 'bg-red-900/30 border border-red-700 text-red-400'
                }`}>
                  {testLoginResult.success ? (
                    <div className="space-y-2">
                      <p className="flex items-center gap-2">
                        <CheckCircle className="h-4 w-4" />
                        {testLoginResult.message || 'Success!'}
                      </p>
                      {testLoginResult.diagnostics && (
                        <div className="text-xs text-emerald-300 mt-2">
                          <p className="font-medium">Diagnostics passed:</p>
                          <ul className="list-disc list-inside">
                            {testLoginResult.diagnostics.checks.map((check, idx) => (
                              <li key={idx}>{check.check}: ✓</li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <p className="flex items-center gap-2 font-medium">
                        <XCircle className="h-4 w-4" />
                        {testLoginResult.error}
                      </p>
                      {testLoginResult.timeout && (
                        <p className="text-xs text-slate-400">
                          The test timed out. This might be due to slow network or the CPR Suppliers site being slow.
                        </p>
                      )}
                      {testLoginResult.diagnostics && (
                        <div className="text-xs text-slate-400 mt-2">
                          <p className="font-medium text-slate-300">Diagnostic checks:</p>
                          <ul className="list-disc list-inside">
                            {testLoginResult.diagnostics.checks.map((check, idx) => (
                              <li key={idx} className={check.status === 'success' ? 'text-emerald-400' : 'text-red-400'}>
                                {check.check}: {check.status} {check.error && `- ${check.error}`}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      {testLoginResult.steps && testLoginResult.steps.length > 0 && (
                        <div className="text-xs text-slate-400 mt-2">
                          <p className="font-medium text-slate-300">Steps completed:</p>
                          <ul className="list-disc list-inside">
                            {testLoginResult.steps.map((step, idx) => (
                              <li key={idx} className={step.status === 'success' ? 'text-emerald-400' : step.status === 'failed' ? 'text-red-400' : 'text-slate-400'}>
                                {step.step}: {step.status} {step.error && `- ${step.error}`}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Enrollware Settings Dialog */}
      <Dialog open={showEnrollwareSettings} onOpenChange={setShowEnrollwareSettings}>
        <DialogContent className="bg-slate-800 text-white border-violet-700 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-violet-400 flex items-center gap-2">
              <Upload className="h-5 w-5" />
              Enrollware Credentials
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Configure credentials for automatic Enrollware class sync and eCard issuance.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Status Banner */}
            {enrollwareSettings.configured ? (
              <div className="p-3 bg-emerald-900/30 rounded-lg border border-emerald-700">
                <p className="text-emerald-400 flex items-center gap-2">
                  <CheckCircle className="h-4 w-4" />
                  Enrollware credentials configured
                  {enrollwareSettings.source === 'environment' && 
                    <span className="text-xs text-emerald-300/70 ml-1">(from environment)</span>
                  }
                </p>
                <p className="text-xs text-emerald-300/70 mt-1">
                  Username: {enrollwareSettings.username}
                </p>
                {enrollwareSettings.last_updated && (
                  <p className="text-xs text-slate-400 mt-1">
                    Last updated: {new Date(enrollwareSettings.last_updated).toLocaleDateString()}
                  </p>
                )}
              </div>
            ) : (
              <div className="p-3 bg-amber-900/30 rounded-lg border border-amber-700">
                <p className="text-amber-400 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  Enrollware credentials not configured
                </p>
                <p className="text-xs text-amber-300/70 mt-1">
                  Configure credentials to enable automatic class sync and eCard issuance
                </p>
              </div>
            )}

            {/* Credentials Form */}
            <div className="space-y-3">
              <div>
                <Label className="text-slate-300">Username</Label>
                <Input
                  type="text"
                  value={enrollwareSettingsForm.username}
                  onChange={(e) => setEnrollwareSettingsForm({ ...enrollwareSettingsForm, username: e.target.value })}
                  placeholder="Enter Enrollware username"
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
              <div>
                <Label className="text-slate-300">Password</Label>
                <Input
                  type="password"
                  value={enrollwareSettingsForm.password}
                  onChange={(e) => setEnrollwareSettingsForm({ ...enrollwareSettingsForm, password: e.target.value })}
                  placeholder="Enter Enrollware password"
                  className="bg-slate-700 border-slate-600 text-white"
                />
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-2">
              <Button 
                onClick={saveEnrollwareSettings}
                disabled={enrollwareLoading || !enrollwareSettingsForm.username || !enrollwareSettingsForm.password}
                className="flex-1 bg-violet-600 hover:bg-violet-500"
              >
                {enrollwareLoading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <Save className="h-4 w-4 mr-2" />
                )}
                Save Credentials
              </Button>
              <Button 
                onClick={testEnrollwareConnection}
                disabled={enrollwareLoading || !enrollwareSettings.configured}
                variant="outline"
                className="flex-1 text-cyan-400 border-cyan-600 hover:bg-cyan-900/30"
              >
                {enrollwareLoading ? (
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-2" />
                )}
                Test Connection
              </Button>
            </div>

            {/* Test Result */}
            {enrollwareTestResult && (
              <div className={`p-3 rounded-lg text-sm ${
                enrollwareTestResult.success 
                  ? 'bg-emerald-900/30 border border-emerald-700 text-emerald-400' 
                  : 'bg-red-900/30 border border-red-700 text-red-400'
              }`}>
                {enrollwareTestResult.success ? (
                  <p className="flex items-center gap-2">
                    <CheckCircle className="h-4 w-4" />
                    {enrollwareTestResult.message || 'Successfully connected to Enrollware!'}
                  </p>
                ) : (
                  <p className="flex items-center gap-2">
                    <XCircle className="h-4 w-4" />
                    {enrollwareTestResult.error || 'Connection failed'}
                  </p>
                )}
              </div>
            )}

            {/* Info */}
            <div className="text-xs text-slate-400 p-3 bg-slate-700/50 rounded-lg">
              <p className="font-medium text-slate-300 mb-1">What this enables:</p>
              <ul className="list-disc list-inside space-y-1">
                <li>Automatic class sync to Enrollware</li>
                <li>Student roster upload</li>
                <li>Document attachment (rosters & AHA paperwork)</li>
                <li>eCard issuance via AHA Direct Connect</li>
              </ul>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* QuickBooks Integration Dialog */}
      <Dialog open={showQboSettings} onOpenChange={setShowQboSettings}>
        <DialogContent className="sm:max-w-lg bg-slate-800 border-slate-700 text-slate-100" data-testid="quickbooks-settings-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-400">
              <TrendingUp className="h-5 w-5" />
              QuickBooks Integration
            </DialogTitle>
            <DialogDescription className="text-white">
              Connect to QuickBooks Online to automatically sync all sales and revenue
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Connection Status */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-slate-300 flex items-center gap-2">
                <Globe className="h-4 w-4" />
                Connection Status
              </h3>
              
              {qboStatus.connected ? (
                <div className="p-4 bg-emerald-900/30 rounded-lg border border-emerald-700">
                  <div className="flex items-center gap-2 text-emerald-400">
                    <CheckCircle className="h-5 w-5" />
                    <span className="font-medium">Connected to QuickBooks</span>
                  </div>
                  <p className="text-sm text-slate-400 mt-1">
                    Company ID: {qboStatus.realm_id}
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    Environment: {qboStatus.environment || 'sandbox'}
                  </p>
                  <div className="flex flex-wrap gap-2 mt-3">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={testQboSync}
                      disabled={qboLoading}
                      className="text-cyan-400 border-cyan-600 hover:bg-cyan-900/30"
                    >
                      {qboLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <RefreshCw className="h-4 w-4 mr-2" />}
                      Test Sync
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={disconnectQuickBooks}
                      disabled={qboLoading}
                      className="text-red-400 border-red-600 hover:bg-red-900/30"
                    >
                      Disconnect
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="p-4 bg-slate-700/50 rounded-lg border border-slate-600">
                  <div className="flex items-center gap-2 text-amber-400">
                    <AlertTriangle className="h-5 w-5" />
                    <span className="font-medium">Not Connected</span>
                  </div>
                  <p className="text-sm text-slate-400 mt-1">
                    Connect to QuickBooks to start syncing sales data
                  </p>
                  <Button
                    onClick={connectToQuickBooks}
                    disabled={qboLoading}
                    className="mt-3 bg-green-600 hover:bg-green-500"
                  >
                    {qboLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ExternalLink className="h-4 w-4 mr-2" />}
                    Connect to QuickBooks
                  </Button>
                </div>
              )}
            </div>

            <div className="border-t border-slate-700" />

            {/* What Gets Synced */}
            <div className="space-y-2">
              <h3 className="text-sm font-medium text-slate-300">What Gets Synced to QuickBooks:</h3>
              <ul className="text-xs text-slate-400 space-y-1">
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-3 w-3 text-green-400" />
                  Class bookings & payments
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-3 w-3 text-green-400" />
                  Rental orders & deposits
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-3 w-3 text-green-400" />
                  Invoice payments
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-3 w-3 text-green-400" />
                  POS transactions
                </li>
                <li className="flex items-center gap-2">
                  <CheckCircle className="h-3 w-3 text-green-400" />
                  Store orders
                </li>
              </ul>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Production Sync Dialog */}
      <Dialog open={showProductionSync} onOpenChange={setShowProductionSync}>
        <DialogContent className="sm:max-w-lg bg-slate-800 border-slate-700 text-slate-100" data-testid="production-sync-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-orange-400">
              <Download className="h-5 w-5" />
              Sync from Production
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Copy configuration data (class types, pricing, settings) from production to this preview environment.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="bg-amber-900/30 border border-amber-600/50 rounded-lg p-3 text-sm">
              <div className="flex items-start gap-2">
                <AlertTriangle className="h-4 w-4 text-amber-400 mt-0.5" />
                <div>
                  <p className="text-amber-200 font-medium">This will replace preview data</p>
                  <p className="text-amber-300/70 text-xs mt-1">
                    Configuration data in preview will be replaced with production data. 
                    Customer data (bookings, orders, etc.) is NOT synced.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="space-y-3">
              <div>
                <label className="text-sm text-slate-400 block mb-1">Admin Password *</label>
                <input
                  type="password"
                  value={productionSyncForm.password}
                  onChange={(e) => setProductionSyncForm(prev => ({ ...prev, password: e.target.value }))}
                  placeholder="Enter admin password"
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white text-sm"
                  data-testid="sync-admin-password"
                />
              </div>
              
              <div>
                <label className="text-sm text-slate-400 block mb-1">Production MongoDB URL *</label>
                <input
                  type="password"
                  value={productionSyncForm.production_mongo_url}
                  onChange={(e) => setProductionSyncForm(prev => ({ ...prev, production_mongo_url: e.target.value }))}
                  placeholder="mongodb+srv://user:pass@cluster.mongodb.net"
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white text-sm"
                  data-testid="production-mongo-url"
                />
              </div>
              
              <div>
                <label className="text-sm text-slate-400 block mb-1">Database Name</label>
                <input
                  type="text"
                  value={productionSyncForm.production_db_name}
                  onChange={(e) => setProductionSyncForm(prev => ({ ...prev, production_db_name: e.target.value }))}
                  placeholder="biggestlittleclassscheduler"
                  className="w-full px-3 py-2 bg-slate-700 border border-slate-600 rounded text-white text-sm"
                  data-testid="production-db-name"
                />
              </div>
            </div>
            
            <div className="text-xs text-slate-500">
              <p className="font-medium mb-1">Collections that will be synced:</p>
              <p>class_types, instructor_ecard_prices, addon_settings, fee_settings, rental_products, instructors, email_templates, and more...</p>
            </div>
            
            {productionSyncResult && (
              <div className={`p-3 rounded-lg text-sm ${productionSyncResult.success ? 'bg-green-900/30 border border-green-600/50' : 'bg-red-900/30 border border-red-600/50'}`}>
                {productionSyncResult.success ? (
                  <div>
                    <p className="text-green-400 font-medium flex items-center gap-2">
                      <CheckCircle className="h-4 w-4" />
                      {productionSyncResult.message}
                    </p>
                    {productionSyncResult.results && (
                      <div className="mt-2 text-xs text-green-300/70">
                        <p>Synced: {productionSyncResult.results.synced?.length || 0} collections</p>
                        <p>Total documents: {productionSyncResult.results.total_documents || 0}</p>
                        {productionSyncResult.results.failed?.length > 0 && (
                          <p className="text-red-400">Failed: {productionSyncResult.results.failed.length} collections</p>
                        )}
                      </div>
                    )}
                  </div>
                ) : (
                  <p className="text-red-400 flex items-center gap-2">
                    <AlertCircle className="h-4 w-4" />
                    {productionSyncResult.error || 'Sync failed'}
                  </p>
                )}
              </div>
            )}
            
            <div className="flex gap-2 pt-2">
              <Button
                onClick={syncFromProduction}
                disabled={productionSyncing || !productionSyncForm.production_mongo_url}
                className="flex-1 bg-orange-600 hover:bg-orange-500"
                data-testid="start-production-sync-btn"
              >
                {productionSyncing ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Syncing...
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4 mr-2" />
                    Start Sync
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowProductionSync(false)}
                className="border-slate-600 text-slate-300"
              >
                Close
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Email Attachments Dialog */}
      <Dialog open={showEmailAttachments} onOpenChange={setShowEmailAttachments}>
        <DialogContent className="sm:max-w-3xl max-h-[85vh] overflow-y-auto bg-slate-800 border-slate-700 text-slate-100" data-testid="email-attachments-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-400">
              <Paperclip className="h-5 w-5" />
              Email Attachments
            </DialogTitle>
            <DialogDescription className="text-white">
              Manage document attachments sent with class approval emails. Create groups, assign to class types, and upload PDFs or Word docs.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Add Group Button */}
            <div className="flex justify-between items-center">
              <p className="text-sm text-slate-400">
                {emailAttachmentGroups.length} group(s) configured
              </p>
              <Button
                onClick={() => setShowCreateGroupDialog(true)}
                className="bg-amber-600 hover:bg-amber-500"
                size="sm"
              >
                <Plus className="h-4 w-4 mr-1" />
                New Group
              </Button>
            </div>

            {/* Loading State */}
            {emailAttachmentsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-amber-400" />
              </div>
            ) : emailAttachmentGroups.length === 0 ? (
              <div className="text-center py-8 text-slate-400">
                <Paperclip className="h-12 w-12 mx-auto mb-2 opacity-50" />
                <p>No attachment groups created yet.</p>
                <p className="text-sm">Create a group to start adding documents.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {emailAttachmentGroups.map(group => (
                  <div key={group.id} className="p-4 bg-slate-700/50 rounded-lg border border-slate-600">
                    {editingGroup === group.id ? (
                      /* Edit Mode */
                      <div className="space-y-3">
                        <Input
                          value={group.name}
                          onChange={(e) => {
                            setEmailAttachmentGroups(groups => 
                              groups.map(g => g.id === group.id ? {...g, name: e.target.value} : g)
                            );
                          }}
                          className="bg-slate-600 border-slate-500 text-white"
                          placeholder="Group name"
                        />
                        <div>
                          <Label className="text-slate-300 text-sm">Attach to Class Types:</Label>
                          <div className="flex flex-wrap gap-2 mt-2">
                            {adminClassTypes.filter(ct => ct.is_active).map(ct => (
                              <label key={ct.id} className="flex items-center gap-1 text-sm">
                                <input
                                  type="checkbox"
                                  checked={group.class_type_ids?.includes(ct.id) || false}
                                  onChange={(e) => {
                                    const newIds = e.target.checked
                                      ? [...(group.class_type_ids || []), ct.id]
                                      : (group.class_type_ids || []).filter(id => id !== ct.id);
                                    setEmailAttachmentGroups(groups => 
                                      groups.map(g => g.id === group.id ? {...g, class_type_ids: newIds} : g)
                                    );
                                  }}
                                  className="rounded border-slate-500"
                                />
                                <span className="text-slate-300">{ct.name}</span>
                              </label>
                            ))}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            onClick={() => updateAttachmentGroup(group.id, group.name, group.class_type_ids)}
                            className="bg-emerald-600 hover:bg-emerald-500"
                          >
                            <Save className="h-3 w-3 mr-1" />
                            Save
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => { setEditingGroup(null); fetchEmailAttachmentGroups(); }}
                            className="border-slate-500 text-slate-300"
                          >
                            Cancel
                          </Button>
                        </div>
                      </div>
                    ) : (
                      /* View Mode */
                      <>
                        <div className="flex items-start justify-between mb-3">
                          <div>
                            <h3 className="font-medium text-white">{group.name}</h3>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {group.class_type_ids?.length > 0 ? (
                                group.class_type_ids.map(ctId => {
                                  const ct = adminClassTypes.find(c => c.id === ctId);
                                  return ct ? (
                                    <Badge key={ctId} variant="secondary" className="bg-teal-900/50 text-teal-300 text-xs">
                                      {ct.name}
                                    </Badge>
                                  ) : null;
                                })
                              ) : (
                                <span className="text-xs text-slate-500">No class types assigned</span>
                              )}
                            </div>
                          </div>
                          <div className="flex gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setEditingGroup(group.id)}
                              className="h-7 w-7 p-0 text-slate-400 hover:text-white"
                            >
                              <Edit2 className="h-3 w-3" />
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => deleteAttachmentGroup(group.id)}
                              className="h-7 w-7 p-0 text-red-400 hover:text-red-300"
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                        </div>

                        {/* Attachments List */}
                        <div className="space-y-2">
                          {group.attachments?.length > 0 ? (
                            group.attachments.map(att => (
                              <div key={att.id} className="flex items-center justify-between p-2 bg-slate-600/50 rounded">
                                <div className="flex items-center gap-2">
                                  <FileText className="h-4 w-4 text-amber-400" />
                                  <span className="text-sm text-slate-200">{att.original_filename}</span>
                                  <span className="text-xs text-slate-500">
                                    ({(att.file_size / 1024).toFixed(1)} KB)
                                  </span>
                                </div>
                                <div className="flex gap-1">
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => window.open(`${process.env.REACT_APP_BACKEND_URL}/api/admin/email-attachments/${att.id}/download`, '_blank')}
                                    className="h-6 w-6 p-0 text-teal-400 hover:text-teal-300"
                                    title="Download"
                                  >
                                    <Download className="h-3 w-3" />
                                  </Button>
                                  <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => deleteAttachment(att.id)}
                                    className="h-6 w-6 p-0 text-red-400 hover:text-red-300"
                                    title="Delete"
                                  >
                                    <Trash2 className="h-3 w-3" />
                                  </Button>
                                </div>
                              </div>
                            ))
                          ) : (
                            <p className="text-sm text-slate-500 italic">No attachments yet</p>
                          )}
                        </div>

                        {/* Upload Button */}
                        <div className="mt-3">
                          {uploadingToGroup === group.id ? (
                            <div className="flex items-center gap-2">
                              <input
                                type="file"
                                accept=".pdf,.doc,.docx"
                                multiple
                                onChange={(e) => {
                                  if (e.target.files?.length > 0) {
                                    uploadAttachment(group.id, e.target.files);
                                  }
                                }}
                                className="text-sm text-slate-300 file:mr-2 file:py-1 file:px-3 file:rounded file:border-0 file:text-sm file:bg-amber-600 file:text-white hover:file:bg-amber-500"
                                disabled={uploadProgress}
                              />
                              {uploadProgress && <Loader2 className="h-4 w-4 animate-spin text-amber-400" />}
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => setUploadingToGroup(null)}
                                className="text-slate-400"
                              >
                                Cancel
                              </Button>
                            </div>
                          ) : (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setUploadingToGroup(group.id)}
                              className="border-amber-600 text-amber-400 hover:bg-amber-900/30"
                            >
                              <Upload className="h-3 w-3 mr-1" />
                              Upload File
                            </Button>
                          )}
                        </div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Attachment Group Dialog */}
      <Dialog open={showCreateGroupDialog} onOpenChange={setShowCreateGroupDialog}>
        <DialogContent className="sm:max-w-md bg-slate-800 border-slate-700 text-slate-100">
          <DialogHeader>
            <DialogTitle className="text-amber-400">Create Attachment Group</DialogTitle>
            <DialogDescription className="text-white">
              Create a group to organize attachments for specific class types.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-slate-300">Group Name</Label>
              <Input
                value={newGroupName}
                onChange={(e) => setNewGroupName(e.target.value)}
                placeholder="e.g., Heartsaver Documents"
                className="bg-slate-700 border-slate-600 text-white mt-1"
              />
            </div>
            <div>
              <Label className="text-slate-300">Attach to Class Types</Label>
              <div className="flex flex-wrap gap-2 mt-2 max-h-40 overflow-y-auto p-2 bg-slate-700/50 rounded">
                {adminClassTypes.filter(ct => ct.is_active).map(ct => (
                  <label key={ct.id} className="flex items-center gap-1 text-sm">
                    <input
                      type="checkbox"
                      checked={newGroupClassTypes.includes(ct.id)}
                      onChange={(e) => {
                        setNewGroupClassTypes(prev => 
                          e.target.checked 
                            ? [...prev, ct.id]
                            : prev.filter(id => id !== ct.id)
                        );
                      }}
                      className="rounded border-slate-500"
                    />
                    <span className="text-slate-300">{ct.name}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateGroupDialog(false)} className="border-slate-600 text-slate-300">
              Cancel
            </Button>
            <Button onClick={createAttachmentGroup} className="bg-amber-600 hover:bg-amber-500">
              Create Group
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Student eCard Manager Dialog */}
      <Dialog open={showStudentEcardManager} onOpenChange={setShowStudentEcardManager}>
        <DialogContent className="sm:max-w-2xl max-h-[85vh] overflow-y-auto bg-slate-800 border-slate-700 text-slate-100" data-testid="student-ecard-manager-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-teal-400">
              <Users className="h-5 w-5" />
              Student eCard Manager
            </DialogTitle>
            <DialogDescription className="text-white">
              Manage individual student eCard status for approved classes
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Students Needing eCards */}
            <div className="border border-teal-600/50 rounded-lg overflow-hidden">
              <button
                onClick={() => setShowStudentsNeedingEcards(!showStudentsNeedingEcards)}
                className="w-full flex items-center justify-between p-4 bg-slate-700/30 hover:bg-slate-700/50 transition-colors text-left"
                data-testid="toggle-students-needing-ecards"
              >
                <span className="text-base font-medium text-slate-200">
                  Students Needing eCards
                  {studentsNeedingEcards.length > 0 && (
                    <span className="ml-2 text-sm px-2 py-0.5 bg-amber-600/30 text-amber-300 rounded">
                      {studentsNeedingEcards.length} need cards
                    </span>
                  )}
                </span>
                <ChevronDown className={`h-5 w-5 text-white transition-transform ${showStudentsNeedingEcards ? 'rotate-180' : ''}`} />
              </button>
              
              {showStudentsNeedingEcards && (
                <div className="border-t border-teal-600/50">
                  {studentsNeedingEcards.length === 0 ? (
                    <div className="p-6 text-center text-white">
                      All students have eCards ordered
                    </div>
                  ) : (
                    <div className="max-h-72 overflow-y-auto">
                      {studentsNeedingEcards.map((student, idx) => (
                        <div 
                          key={`${student.booking_id}-${student.student_index}`}
                          className="flex items-center justify-between p-4 border-b border-teal-600/30 last:border-0 hover:bg-slate-700/20"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="text-sm text-slate-200 truncate font-medium">{student.student_name || 'Unknown Student'}</div>
                            <div className="text-xs text-white truncate">
                              {student.class_name}
                            </div>
                            <div className="text-xs text-slate-500">
                              {student.start_time ? new Date(student.start_time).toLocaleDateString() : 'No date'}
                            </div>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => markStudentEcardPurchased(student.booking_id, student.student_index, student.student_name, true)}
                            disabled={markingEcardStudent?.booking_id === student.booking_id && markingEcardStudent?.student_index === student.student_index}
                            className="ml-3 text-xs border-green-700 text-green-400 hover:bg-green-900/30 hover:text-green-300 whitespace-nowrap"
                            data-testid={`mark-ecard-purchased-${student.booking_id}-${student.student_index}`}
                          >
                            {markingEcardStudent?.booking_id === student.booking_id && markingEcardStudent?.student_index === student.student_index ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <>
                                <CheckCircle className="h-3 w-3 mr-1" />
                                Mark Purchased
                              </>
                            )}
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="p-3 bg-slate-700/20 text-xs text-slate-500 text-center">
                    Mark individual student cards as purchased if ordered outside the Order eCards system
                  </div>
                </div>
              )}
            </div>

            {/* Students with eCards Ordered */}
            <div className="border border-teal-600/50 rounded-lg overflow-hidden">
              <button
                onClick={() => setShowStudentsWithEcards(!showStudentsWithEcards)}
                className="w-full flex items-center justify-between p-4 bg-slate-700/30 hover:bg-slate-700/50 transition-colors text-left"
                data-testid="toggle-students-with-ecards"
              >
                <span className="text-base font-medium text-slate-200">
                  Students with eCards Ordered
                  {studentsWithEcards.length > 0 && (
                    <span className="ml-2 text-sm px-2 py-0.5 bg-green-600/30 text-green-300 rounded">
                      {studentsWithEcards.length} marked
                    </span>
                  )}
                </span>
                <ChevronDown className={`h-5 w-5 text-white transition-transform ${showStudentsWithEcards ? 'rotate-180' : ''}`} />
              </button>
              
              {showStudentsWithEcards && (
                <div className="border-t border-teal-600/50">
                  {studentsWithEcards.length === 0 ? (
                    <div className="p-6 text-center text-white">
                      No students have eCards marked as ordered
                    </div>
                  ) : (
                    <div className="max-h-72 overflow-y-auto">
                      {studentsWithEcards.map((student, idx) => (
                        <div 
                          key={`${student.booking_id}-${student.student_index}-marked`}
                          className="flex items-center justify-between p-4 border-b border-teal-600/30 last:border-0 hover:bg-slate-700/20"
                        >
                          <div className="flex-1 min-w-0">
                            <div className="text-sm text-slate-200 truncate font-medium">
                              <CheckCircle className="h-3 w-3 inline mr-1 text-green-400" />
                              {student.student_name || 'Unknown Student'}
                            </div>
                            <div className="text-xs text-white truncate">
                              {student.class_name}
                            </div>
                            <div className="text-xs text-slate-500">
                              {student.start_time ? new Date(student.start_time).toLocaleDateString() : 'No date'}
                            </div>
                          </div>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => markStudentEcardPurchased(student.booking_id, student.student_index, student.student_name, false)}
                            disabled={markingEcardStudent?.booking_id === student.booking_id && markingEcardStudent?.student_index === student.student_index}
                            className="ml-3 text-xs border-red-700 text-red-400 hover:bg-red-900/30 hover:text-red-300 whitespace-nowrap"
                            data-testid={`unmark-ecard-${student.booking_id}-${student.student_index}`}
                          >
                            {markingEcardStudent?.booking_id === student.booking_id && markingEcardStudent?.student_index === student.student_index ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <>
                                <XCircle className="h-3 w-3 mr-1" />
                                Unmark
                              </>
                            )}
                          </Button>
                        </div>
                      ))}
                    </div>
                  )}
                  <div className="p-3 bg-slate-700/20 text-xs text-slate-500 text-center">
                    Unmark students if eCard was not actually ordered
                  </div>
                </div>
              )}
            </div>
          </div>

          <DialogFooter className="border-t border-slate-700 pt-4">
            <Button
              variant="outline"
              onClick={() => setShowStudentEcardManager(false)}
              className="border-teal-600 text-white hover:bg-slate-700"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Order History Dialog */}
      <Dialog open={showOrderHistory} onOpenChange={setShowOrderHistory}>
        <DialogContent className="sm:max-w-4xl bg-slate-800 border-slate-700 text-slate-100 max-h-[90vh] overflow-y-auto" data-testid="order-history-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <History className="h-5 w-5 text-blue-400" />
              eCard Order History
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              View past eCard orders placed through CPR Suppliers (Admin & Instructor Store)
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-4">
            {/* Top Action Bar */}
            <div className="flex flex-wrap gap-2 items-center justify-between">
              {/* Search */}
              <div className="flex gap-2 flex-1 min-w-[250px]">
                <div className="relative flex-1">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                  <Input
                    placeholder="Search by order number or name..."
                    value={orderHistorySearch}
                    onChange={(e) => setOrderHistorySearch(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && fetchOrderHistory(orderHistorySearch)}
                    className="pl-9 bg-slate-700 border-slate-600 text-slate-100"
                    data-testid="order-history-search"
                  />
                </div>
                <Button
                  onClick={() => fetchOrderHistory(orderHistorySearch)}
                  disabled={orderHistoryLoading}
                  className="bg-blue-600 hover:bg-blue-500"
                >
                  {orderHistoryLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}
                </Button>
              </div>
              
              {/* Refresh Statuses Button */}
              <Button
                onClick={async () => {
                  try {
                    toast.loading('Starting status check...', { id: 'status-check', duration: 2000 });
                    const response = await axios.post(`${API}/admin/ecard-order-history/refresh-supplier-statuses`);
                    toast.dismiss('status-check');
                    if (response.data.success) {
                      toast.success(response.data.message || 'Status check started! Refresh in a moment.', { duration: 5000 });
                      // Auto-refresh after 10 seconds
                      setTimeout(() => {
                        fetchOrderHistory(orderHistorySearch);
                      }, 10000);
                    } else {
                      toast.error(response.data.error || 'Status check failed');
                    }
                  } catch (error) {
                    toast.dismiss('status-check');
                    toast.error('Failed to start status check: ' + (error.response?.data?.detail || error.message));
                  }
                }}
                variant="outline"
                className="border-green-600 text-green-400 hover:bg-green-900/30"
                data-testid="refresh-statuses-btn"
              >
                <RefreshCw className="h-4 w-4 mr-2" />
                Refresh Statuses
              </Button>
            </div>

            {/* Orders List */}
            {orderHistoryLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-blue-400" />
                <span className="ml-3 text-slate-400">Loading orders...</span>
              </div>
            ) : orderHistory.length === 0 ? (
              <div className="py-12 text-center">
                <Package className="h-12 w-12 text-slate-500 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-300 mb-2">No Orders Found</h3>
                <p className="text-slate-500 text-sm">
                  {orderHistorySearch ? 'No orders match your search' : 'Orders placed through the automated checkout will appear here'}
                </p>
              </div>
            ) : (
              <div className="space-y-3">
                {orderHistory.map(order => (
                  <div 
                    key={order.id}
                    className={`bg-slate-700/50 rounded-lg p-4 border ${
                      order.status === 'completed' ? 'border-green-600/30' : 
                      order.status === 'failed' ? 'border-red-600/30' : 
                      order.status === 'test' ? 'border-purple-600/30' : 
                      order.status === 'ready_to_fill' ? 'border-cyan-600/30' :
                      'border-slate-600'
                    }`}
                  >
                    {/* Header Row */}
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-bold text-lg text-white">
                            Order #{order.order_number}
                          </span>
                          
                          {/* Origin Badge */}
                          {order.origin === 'instructor_store' && (
                            <Badge className="bg-indigo-600/30 text-indigo-300 border-none text-xs">
                              <Users className="h-3 w-3 mr-1" />
                              Instructor Store
                            </Badge>
                          )}
                          
                          {/* Status Badge */}
                          {order.status === 'completed' ? (
                            <Badge className="bg-green-600/30 text-green-300 border-none">
                              <CheckCircle className="h-3 w-3 mr-1" />
                              Completed
                            </Badge>
                          ) : order.status === 'failed' ? (
                            <Badge className="bg-red-600/30 text-red-300 border-none">
                              <XCircle className="h-3 w-3 mr-1" />
                              Failed
                            </Badge>
                          ) : order.status === 'test' ? (
                            <Badge className="bg-purple-600/30 text-purple-300 border-none">
                              <Wrench className="h-3 w-3 mr-1" />
                              Test Mode
                            </Badge>
                          ) : order.status === 'ready_to_fill' ? (
                            <Badge className="bg-cyan-600/30 text-cyan-300 border-none">
                              <Package className="h-3 w-3 mr-1" />
                              Ready To Update Instructor Inv.
                            </Badge>
                          ) : (
                            <Badge className="bg-amber-600/30 text-amber-300 border-none">
                              <Clock className="h-3 w-3 mr-1" />
                              Pending
                            </Badge>
                          )}
                        </div>
                        
                        {/* Supplier Status */}
                        {order.supplier_status && (
                          <p className="text-xs text-slate-400 mt-1">
                            CPR Suppliers Status: <span className={`font-medium ${order.supplier_status.toLowerCase().includes('complete') ? 'text-green-400' : 'text-amber-400'}`}>
                              {order.supplier_status}
                            </span>
                          </p>
                        )}
                        
                        <p className="text-sm text-slate-400 mt-1">
                          {order.order_date ? new Date(order.order_date).toLocaleString() : 'Unknown date'}
                        </p>
                      </div>
                      <div className="text-right">
                        <span className="text-xl font-bold text-green-400">
                          ${order.total?.toFixed(2) || '0.00'}
                        </span>
                        <p className="text-xs text-slate-500">{order.billing_name}</p>
                      </div>
                    </div>
                    
                    {/* Order Items */}
                    <div className="mt-3 pt-3 border-t border-slate-600/50">
                      <p className="text-xs text-slate-500 mb-2">Items ordered:</p>
                      <div className="space-y-1">
                        {(order.items || []).map((item, idx) => (
                          <div key={idx} className="flex justify-between text-sm">
                            <span className="text-slate-300">
                              {item.product_name || item.class_type_name || 'Unknown Product'} × {item.quantity}
                            </span>
                            <span className="text-slate-400">
                              ${item.subtotal?.toFixed(2) || '0.00'}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Notes if any */}
                    {order.notes && (
                      <div className="mt-2 p-2 bg-slate-800/50 rounded text-xs text-slate-400">
                        {order.notes}
                      </div>
                    )}
                    
                    {/* Action Buttons */}
                    <div className="mt-3 flex justify-between items-center">
                      {/* Left side - View screenshot */}
                      <div>
                        {order.confirmation_screenshot && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.open(`${API}/admin/ecard-order-history/${order.id}/screenshot`, '_blank')}
                            className="border-blue-600 text-blue-400 hover:bg-blue-900/30 text-xs"
                          >
                            <Eye className="h-3 w-3 mr-1" />
                            View Confirmation
                          </Button>
                        )}
                      </div>
                      
                      {/* Right side - Filled button for ready_to_fill orders */}
                      <div>
                        {order.status === 'ready_to_fill' && (
                          <Button
                            size="sm"
                            onClick={async () => {
                              try {
                                const orderType = order.origin === 'instructor_store' ? 'instructor' : 'admin';
                                const response = await axios.post(`${API}/admin/ecard-order-history/${order.id}/mark-filled?order_type=${orderType}`);
                                if (response.data.success) {
                                  toast.success('Order marked as filled!');
                                  fetchOrderHistory(orderHistorySearch);
                                }
                              } catch (error) {
                                toast.error('Failed to mark as filled: ' + (error.response?.data?.detail || error.message));
                              }
                            }}
                            className="bg-green-600 hover:bg-green-500 text-xs"
                          >
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Filled
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          <DialogFooter className="border-t border-slate-700 pt-4">
            <Button
              variant="outline"
              onClick={() => setShowOrderHistory(false)}
              className="border-slate-600 text-slate-300"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Template Editor */}
      <EmailTemplateEditor
        isOpen={showEmailTemplateEditor}
        onClose={() => setShowEmailTemplateEditor(false)}
      />
      
      {/* POS System */}
      <POSSystem
        open={showPOS}
        onClose={() => setShowPOS(false)}
        classTypes={adminClassTypes}
      />
      
      {/* Invoice System */}
      <InvoiceSystem
        open={showInvoices}
        onClose={() => {
          setShowInvoices(false);
          setSelectedInvoiceId(null);  // Clear selected invoice on close
        }}
        classTypes={adminClassTypes}
        initialInvoiceId={selectedInvoiceId}
      />
      
      {/* Rental Management */}
      <RentalManagement
        open={showRentals}
        onClose={() => setShowRentals(false)}
      />
      
      {/* Instructor Management Dialog */}
      <Dialog open={showInstructorManagement} onOpenChange={(open) => { if (!open) setShowInstructorManagement(false); }}>
        <DialogContent className="sm:max-w-4xl bg-slate-800 border-slate-700 text-slate-100 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-teal-400">
              <Users className="h-5 w-5" />
              Instructor Management
            </DialogTitle>
            <DialogDescription className="text-white">
              Manage instructors and track certification expirations. System sends automatic reminders at 90, 60, 30, 14, and 7 days before expiration.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            {/* Add Instructor Button */}
            <div className="flex justify-between items-center mb-4">
              <p className="text-sm text-white">{instructors.filter(i => i.status === 'active').length} active instructors</p>
              <Button
                onClick={openAddInstructor}
                className="bg-teal-600 hover:bg-teal-500"
                data-testid="add-instructor-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Instructor
              </Button>
            </div>
            
            {/* Instructors List */}
            {instructorsLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-teal-400" />
              </div>
            ) : instructors.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                <Users className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>No instructors added yet</p>
                <p className="text-sm mt-1">Click "Add Instructor" to get started</p>
              </div>
            ) : (
              <div className="space-y-3">
                {instructors.map(instructor => {
                  const isRemoved = instructor.status === 'removed';
                  const credentials = instructor.credentials || [];
                  
                  // Check if any credential is expired
                  const hasExpiredCred = credentials.some(cred => {
                    if (!cred.expiration) return false;
                    const days = getDaysUntilExpiration(cred.expiration);
                    return days !== null && days < 0;
                  });
                  
                  // Check if any credential is expiring soon (within 30 days)
                  const hasExpiringSoon = credentials.some(cred => {
                    if (!cred.expiration) return false;
                    const days = getDaysUntilExpiration(cred.expiration);
                    return days !== null && days >= 0 && days <= 30;
                  });
                  
                  return (
                    <div 
                      key={instructor.id} 
                      className={`bg-slate-700/50 rounded-lg p-4 border transition-all ${
                        isRemoved ? 'border-red-600/50 opacity-60' :
                        hasExpiredCred ? 'border-red-600/50' :
                        hasExpiringSoon ? 'border-orange-600/50' :
                        'border-teal-600 hover:border-slate-500'
                      }`}
                      data-testid={`instructor-card-${instructor.id}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className={`font-medium ${
                              hasExpiredCred ? 'text-red-400' : 
                              hasExpiringSoon ? 'text-orange-400' : 
                              'text-slate-200'
                            }`}>
                              {instructor.first_name} {instructor.last_name}
                              {hasExpiredCred && <span className="ml-2 text-xs bg-red-900/50 px-1.5 py-0.5 rounded">EXPIRED CERT</span>}
                            </h4>
                            {isRemoved && (
                              <span className="text-xs px-2 py-0.5 rounded bg-red-900/50 text-red-400">REMOVED</span>
                            )}
                            {instructor.no_reminders && (
                              <span className="text-xs px-2 py-0.5 rounded bg-amber-900/50 text-amber-400" title="Expiration reminder emails disabled">NO REMINDERS</span>
                            )}
                          </div>
                          <p className="text-sm text-white">{instructor.email}</p>
                          {instructor.phone && <p className="text-sm text-slate-500">{instructor.phone}</p>}
                        </div>
                        
                        <div className="flex gap-2">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openAddCredential(instructor)}
                            className="border-teal-600/50 text-teal-400 hover:bg-teal-900/30"
                            title="Add Credential"
                          >
                            <Plus className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => openEditInstructor(instructor)}
                            className="border-teal-600 text-white hover:bg-slate-600"
                            title="Edit Info"
                          >
                            <Edit2 className="h-3 w-3" />
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => deleteInstructor(instructor)}
                            className="border-red-600/50 text-red-400 hover:bg-red-900/30"
                            title="Delete"
                          >
                            <Trash2 className="h-3 w-3" />
                          </Button>
                        </div>
                      </div>
                      
                      {/* Credentials List */}
                      {credentials.length > 0 ? (
                        <div className="mt-3 pt-3 border-t border-teal-600">
                          <p className="text-xs text-slate-500 mb-2 uppercase tracking-wide">Credentials ({credentials.length})</p>
                          <div className="grid grid-cols-2 gap-2">
                            {credentials.map(cred => {
                              const status = getExpirationStatus(cred.expiration);
                              return (
                                <div 
                                  key={cred.id} 
                                  className={`flex items-center justify-between p-2 rounded ${status.bg} border border-teal-600`}
                                >
                                  <div className="flex-1 min-w-0">
                                    <p className="text-xs font-medium text-white truncate">{cred.name}</p>
                                    <p className="text-xs text-white">Card: #{cred.number}</p>
                                    {cred.instructor_number && (
                                      <p className="text-xs text-teal-400">Instr: #{cred.instructor_number}</p>
                                    )}
                                    {cred.expiration && (
                                      <p className={`text-xs ${status.color}`}>
                                        {status.label} - {new Date(cred.expiration).toLocaleDateString()}
                                      </p>
                                    )}
                                  </div>
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => deleteCredential(instructor.id, cred.id, cred.name)}
                                    className="h-6 w-6 p-0 text-red-400 hover:bg-red-900/30"
                                    title="Remove credential"
                                  >
                                    <X className="h-3 w-3" />
                                  </Button>
                                </div>
                              );
                            })}
                          </div>
                        </div>
                      ) : (
                        <div className="mt-3 pt-3 border-t border-teal-600">
                          <p className="text-xs text-slate-500 italic">No credentials added. Click + to add certifications.</p>
                        </div>
                      )}
                      
                      {isRemoved && instructor.removal_reason && (
                        <p className="text-xs text-red-400 mt-2 italic">Removal reason: {instructor.removal_reason}</p>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Add/Edit Instructor Form Dialog */}
      <Dialog open={showInstructorForm} onOpenChange={(open) => { if (!open) setShowInstructorForm(false); }}>
        <DialogContent className="sm:max-w-md bg-slate-800 border-slate-700 text-slate-100">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-teal-400">
              <Users className="h-5 w-5" />
              {editingInstructor ? 'Edit Instructor' : 'Add Instructor'}
            </DialogTitle>
            <DialogDescription className="text-white">
              {editingInstructor ? 'Update instructor contact info.' : 'Add a new instructor. You can add certifications after creating.'}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Name */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-white">First Name *</Label>
                <Input
                  value={instructorForm.first_name}
                  onChange={(e) => setInstructorForm({...instructorForm, first_name: e.target.value})}
                  placeholder="John"
                  className="bg-slate-700 border-teal-600 text-slate-100"
                  data-testid="instructor-first-name"
                />
              </div>
              <div>
                <Label className="text-white">Last Name *</Label>
                <Input
                  value={instructorForm.last_name}
                  onChange={(e) => setInstructorForm({...instructorForm, last_name: e.target.value})}
                  placeholder="Doe"
                  className="bg-slate-700 border-teal-600 text-slate-100"
                  data-testid="instructor-last-name"
                />
              </div>
            </div>
            
            {/* Contact Info */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-white">Email *</Label>
                <Input
                  type="email"
                  value={instructorForm.email}
                  onChange={(e) => setInstructorForm({...instructorForm, email: e.target.value})}
                  placeholder="john@example.com"
                  className="bg-slate-700 border-teal-600 text-slate-100"
                  data-testid="instructor-email"
                />
              </div>
              <div>
                <Label className="text-white">Phone</Label>
                <Input
                  value={instructorForm.phone}
                  onChange={(e) => setInstructorForm({...instructorForm, phone: formatPhoneNumber(e.target.value)})}
                  placeholder="(555) 123-4567"
                  className="bg-slate-700 border-teal-600 text-slate-100"
                  data-testid="instructor-phone"
                />
              </div>
            </div>
            
            {/* No Reminders Checkbox */}
            <div className="flex items-center space-x-2 pt-2">
              <Checkbox
                id="no-reminders"
                checked={instructorForm.no_reminders}
                onCheckedChange={(checked) => setInstructorForm({...instructorForm, no_reminders: checked})}
                className="border-slate-500 data-[state=checked]:bg-amber-600 data-[state=checked]:border-amber-600"
                data-testid="instructor-no-reminders"
              />
              <Label 
                htmlFor="no-reminders" 
                className="text-white text-sm cursor-pointer"
              >
                No Reminders
              </Label>
              <span className="text-xs text-slate-500">(Skip expiration email notifications)</span>
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowInstructorForm(false)}
              className="border-teal-600 text-white hover:bg-slate-700"
            >
              Cancel
            </Button>
            <Button
              onClick={saveInstructor}
              disabled={instructorFormLoading}
              className="bg-teal-600 hover:bg-teal-500"
              data-testid="save-instructor-btn"
            >
              {instructorFormLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              {editingInstructor ? 'Update' : 'Add'} Instructor
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Add Credential Dialog */}
      <Dialog open={showAddCredential} onOpenChange={(open) => { if (!open) setShowAddCredential(false); }}>
        <DialogContent className="sm:max-w-md bg-slate-800 border-slate-700 text-slate-100">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-teal-400">
              <Plus className="h-5 w-5" />
              Add Certification
            </DialogTitle>
            <DialogDescription className="text-white">
              Add a certification for {selectedInstructorForCredential?.first_name} {selectedInstructorForCredential?.last_name}
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Credential Type */}
            <div>
              <Label className="text-white">Certification Type *</Label>
              <Select 
                value={credentialForm.credential_type} 
                onValueChange={(v) => setCredentialForm({...credentialForm, credential_type: v})}
              >
                <SelectTrigger className="bg-slate-700 border-teal-600 text-slate-200" data-testid="credential-type-select">
                  <SelectValue placeholder="Select certification type" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700 max-h-60">
                  {Object.entries(credentialTypes).map(([key, ctype]) => {
                    const existingTypes = (selectedInstructorForCredential?.credentials || []).map(c => c.credential_type);
                    const alreadyHas = existingTypes.includes(key);
                    return (
                      <SelectItem 
                        key={key} 
                        value={key} 
                        className={`text-slate-200 ${alreadyHas ? 'opacity-50' : ''}`}
                        disabled={alreadyHas}
                      >
                        {ctype.name} {alreadyHas ? '(already added)' : ''}
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>
            
            {/* Card/Certificate Number */}
            <div>
              <Label className="text-white">Card/Certificate Number *</Label>
              <Input
                value={credentialForm.number}
                onChange={(e) => setCredentialForm({...credentialForm, number: e.target.value})}
                placeholder="Enter card or certificate number"
                className="bg-slate-700 border-teal-600 text-slate-100"
                data-testid="credential-number"
              />
            </div>
            
            {/* Instructor Number - Only for instructor-type credentials */}
            {isInstructorType(credentialForm.credential_type) && (
              <div>
                <Label className="text-white">Instructor Number *</Label>
                <Input
                  value={credentialForm.instructor_number}
                  onChange={(e) => setCredentialForm({...credentialForm, instructor_number: e.target.value})}
                  placeholder="Enter AHA instructor number"
                  className="bg-slate-700 border-teal-600 text-slate-100"
                  data-testid="credential-instructor-number"
                />
                <p className="text-xs text-slate-500 mt-1">Required for instructor certifications</p>
              </div>
            )}
            
            {/* Expiration Date */}
            <div>
              <Label className="text-white">Expiration Date</Label>
              <Input
                type="date"
                value={credentialForm.expiration}
                onChange={(e) => setCredentialForm({...credentialForm, expiration: e.target.value})}
                className="bg-slate-700 border-teal-600 text-slate-100"
                data-testid="credential-expiration"
              />
              <p className="text-xs text-slate-500 mt-1">Leave blank if no expiration</p>
            </div>
            
            {/* Info about reminders */}
            <div className="p-3 bg-blue-900/30 border border-teal-600 rounded-lg text-sm text-blue-300">
              <Info className="h-4 w-4 inline mr-2" />
              Automatic email reminders will be sent at 90, 60, 30, 14, and 7 days before expiration.
            </div>
          </div>
          
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowAddCredential(false)}
              className="border-teal-600 text-white hover:bg-slate-700"
            >
              Cancel
            </Button>
            <Button
              onClick={saveCredential}
              disabled={credentialFormLoading || !credentialForm.credential_type || !credentialForm.number || (isInstructorType(credentialForm.credential_type) && !credentialForm.instructor_number)}
              className="bg-teal-600 hover:bg-teal-500"
              data-testid="save-credential-btn"
            >
              {credentialFormLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
              Add Certification
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Distance Calculator */}
      <DistanceCalculator
        open={showDistanceCalculator}
        onClose={() => setShowDistanceCalculator(false)}
      />
      
      {/* Discounts/Coupons Dialog */}
      <Dialog open={showDiscounts} onOpenChange={(open) => { setShowDiscounts(open); if (!open) setShowDiscountForm(false); }}>
        <DialogContent className="bg-slate-800 border-slate-700 text-slate-100 max-w-4xl max-h-[85vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-400">
              <Tag className="h-5 w-5" />
              Discount Codes
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Create and manage discount codes for your store and bookings
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto py-4 space-y-4">
            {/* Add New Button */}
            {!showDiscountForm && (
              <Button
                onClick={openAddDiscountForm}
                className="bg-emerald-600 hover:bg-emerald-500"
                data-testid="add-discount-btn"
              >
                <Plus className="h-4 w-4 mr-2" />
                Add Discount Code
              </Button>
            )}
            
            {/* Add/Edit Form */}
            {showDiscountForm && (
              <div className="bg-slate-900/50 p-4 rounded-lg border border-emerald-600/50 space-y-4">
                <h3 className="text-lg font-semibold text-emerald-400 flex items-center gap-2">
                  {editingDiscount ? <Edit className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
                  {editingDiscount ? 'Edit Discount' : 'New Discount Code'}
                </h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  {/* Code */}
                  <div>
                    <Label className="text-slate-300">Discount Code *</Label>
                    <Input
                      value={discountForm.code}
                      onChange={(e) => setDiscountForm({ ...discountForm, code: e.target.value.toUpperCase() })}
                      placeholder="e.g. SAVE20"
                      className="bg-slate-700 border-slate-600 text-white mt-1 uppercase"
                      disabled={!!editingDiscount}
                      data-testid="discount-code-input"
                    />
                  </div>
                  
                  {/* Discount Type */}
                  <div>
                    <Label className="text-slate-300">Discount Type *</Label>
                    <Select
                      value={discountForm.discount_type}
                      onValueChange={(val) => setDiscountForm({ ...discountForm, discount_type: val })}
                    >
                      <SelectTrigger className="bg-slate-700 border-slate-600 text-white mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-slate-800 border-slate-700">
                        <SelectItem value="percent" className="text-white">Percentage Off</SelectItem>
                        <SelectItem value="fixed_cart" className="text-white">Fixed Cart Discount ($)</SelectItem>
                        <SelectItem value="fixed_product" className="text-white">Fixed Product Discount ($)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  
                  {/* Amount */}
                  <div>
                    <Label className="text-slate-300">
                      Amount * {discountForm.discount_type === 'percent' ? '(%)' : '($)'}
                    </Label>
                    <Input
                      type="number"
                      value={discountForm.amount}
                      onChange={(e) => setDiscountForm({ ...discountForm, amount: e.target.value })}
                      placeholder={discountForm.discount_type === 'percent' ? 'e.g. 20' : 'e.g. 10.00'}
                      className="bg-slate-700 border-slate-600 text-white mt-1"
                      data-testid="discount-amount-input"
                    />
                  </div>
                  
                  {/* Description */}
                  <div>
                    <Label className="text-slate-300">Description</Label>
                    <Input
                      value={discountForm.description}
                      onChange={(e) => setDiscountForm({ ...discountForm, description: e.target.value })}
                      placeholder="Internal note about this discount"
                      className="bg-slate-700 border-slate-600 text-white mt-1"
                    />
                  </div>
                  
                  {/* Min Amount */}
                  <div>
                    <Label className="text-slate-300">Minimum Order Amount ($)</Label>
                    <Input
                      type="number"
                      value={discountForm.minimum_amount}
                      onChange={(e) => setDiscountForm({ ...discountForm, minimum_amount: e.target.value })}
                      placeholder="0 = no minimum"
                      className="bg-slate-700 border-slate-600 text-white mt-1"
                    />
                  </div>
                  
                  {/* Max Amount */}
                  <div>
                    <Label className="text-slate-300">Maximum Order Amount ($)</Label>
                    <Input
                      type="number"
                      value={discountForm.maximum_amount}
                      onChange={(e) => setDiscountForm({ ...discountForm, maximum_amount: e.target.value })}
                      placeholder="0 = no maximum"
                      className="bg-slate-700 border-slate-600 text-white mt-1"
                    />
                  </div>
                  
                  {/* Usage Limit */}
                  <div>
                    <Label className="text-slate-300">Total Usage Limit</Label>
                    <Input
                      type="number"
                      value={discountForm.usage_limit}
                      onChange={(e) => setDiscountForm({ ...discountForm, usage_limit: e.target.value })}
                      placeholder="Empty = unlimited"
                      className="bg-slate-700 border-slate-600 text-white mt-1"
                    />
                  </div>
                  
                  {/* Per User Limit */}
                  <div>
                    <Label className="text-slate-300">Per User Limit</Label>
                    <Input
                      type="number"
                      value={discountForm.usage_limit_per_user}
                      onChange={(e) => setDiscountForm({ ...discountForm, usage_limit_per_user: e.target.value })}
                      placeholder="Empty = unlimited"
                      className="bg-slate-700 border-slate-600 text-white mt-1"
                    />
                  </div>
                  
                  {/* Expiration Date */}
                  <div>
                    <Label className="text-slate-300">Expiration Date</Label>
                    <Input
                      type="date"
                      value={discountForm.date_expires}
                      onChange={(e) => setDiscountForm({ ...discountForm, date_expires: e.target.value })}
                      className="bg-slate-700 border-slate-600 text-white mt-1"
                    />
                  </div>
                  
                  {/* Active Toggle */}
                  <div className="flex items-center gap-2 pt-6">
                    <Checkbox
                      id="discount-active"
                      checked={discountForm.is_active}
                      onCheckedChange={(checked) => setDiscountForm({ ...discountForm, is_active: checked })}
                    />
                    <Label htmlFor="discount-active" className="text-slate-300 cursor-pointer">Active</Label>
                  </div>
                </div>
                
                {/* Options Checkboxes */}
                <div className="border-t border-slate-700 pt-4">
                  <Label className="text-slate-400 text-sm mb-2 block">Additional Options</Label>
                  <div className="flex flex-wrap gap-4">
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="individual-use"
                        checked={discountForm.individual_use}
                        onCheckedChange={(checked) => setDiscountForm({ ...discountForm, individual_use: checked })}
                      />
                      <Label htmlFor="individual-use" className="text-slate-300 text-sm cursor-pointer">
                        Individual Use Only
                      </Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="free-shipping"
                        checked={discountForm.free_shipping}
                        onCheckedChange={(checked) => setDiscountForm({ ...discountForm, free_shipping: checked })}
                      />
                      <Label htmlFor="free-shipping" className="text-slate-300 text-sm cursor-pointer">
                        Free Shipping
                      </Label>
                    </div>
                    <div className="flex items-center gap-2">
                      <Checkbox
                        id="exclude-sale"
                        checked={discountForm.exclude_sale_items}
                        onCheckedChange={(checked) => setDiscountForm({ ...discountForm, exclude_sale_items: checked })}
                      />
                      <Label htmlFor="exclude-sale" className="text-slate-300 text-sm cursor-pointer">
                        Exclude Sale Items
                      </Label>
                    </div>
                  </div>
                </div>
                
                {/* Form Actions */}
                <div className="flex gap-2 pt-2">
                  <Button
                    variant="outline"
                    onClick={() => { setShowDiscountForm(false); resetDiscountForm(); }}
                    className="border-slate-600 text-slate-300 hover:bg-slate-700"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={saveDiscount}
                    disabled={savingDiscount}
                    className="bg-emerald-600 hover:bg-emerald-500"
                    data-testid="save-discount-btn"
                  >
                    {savingDiscount ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
                    {editingDiscount ? 'Update Discount' : 'Create Discount'}
                  </Button>
                </div>
              </div>
            )}
            
            {/* Discounts List */}
            {!showDiscountForm && (
              <>
                {discountsLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <Loader2 className="h-6 w-6 animate-spin text-emerald-400" />
                    <span className="ml-2 text-slate-400">Loading discounts...</span>
                  </div>
                ) : discounts.length === 0 ? (
                  <div className="text-center py-8 text-slate-400">
                    <Tag className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p>No discount codes created yet</p>
                    <p className="text-sm mt-1">Click "Add Discount Code" to create your first discount</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {discounts.map((discount) => (
                      <div 
                        key={discount.id}
                        className={`bg-slate-900/50 p-4 rounded-lg border transition-colors ${
                          discount.is_active ? 'border-slate-700 hover:border-emerald-600/50' : 'border-slate-700/50 opacity-60'
                        }`}
                      >
                        {/* Discount Header */}
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <code className="bg-emerald-900/50 text-emerald-300 px-3 py-1 rounded-md font-mono text-lg font-bold">
                              {discount.code}
                            </code>
                            <Badge className={discount.is_active ? 'bg-emerald-600' : 'bg-slate-600'}>
                              {discount.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                            {discount.date_expires && new Date(discount.date_expires) < new Date() && (
                              <Badge className="bg-red-600">Expired</Badge>
                            )}
                          </div>
                          <div className="flex items-center gap-2">
                            <div className="text-right mr-4">
                              <p className="text-2xl font-bold text-emerald-400">
                                {discount.discount_type === 'percent' 
                                  ? `${discount.amount}%` 
                                  : `$${parseFloat(discount.amount).toFixed(2)}`
                                }
                              </p>
                              <p className="text-xs text-slate-500">
                                {discount.discount_type === 'percent' ? 'Percentage Off' : 
                                 discount.discount_type === 'fixed_cart' ? 'Fixed Cart Discount' :
                                 'Fixed Product Discount'}
                              </p>
                            </div>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => openEditDiscountForm(discount)}
                              className="text-blue-400 hover:text-blue-300 hover:bg-blue-900/30"
                            >
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => toggleDiscount(discount.id)}
                              className={discount.is_active ? 'text-amber-400 hover:text-amber-300 hover:bg-amber-900/30' : 'text-emerald-400 hover:text-emerald-300 hover:bg-emerald-900/30'}
                            >
                              {discount.is_active ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => deleteDiscount(discount.id, discount.code)}
                              className="text-red-400 hover:text-red-300 hover:bg-red-900/30"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                        
                        {/* Description */}
                        {discount.description && (
                          <p className="text-sm text-slate-400 mb-3 italic">"{discount.description}"</p>
                        )}
                        
                        {/* Rules Grid */}
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                          {/* Usage */}
                          <div className="bg-slate-800/50 p-2 rounded">
                            <p className="text-slate-500 text-xs">Usage</p>
                            <p className="text-slate-200">
                              {discount.usage_count || 0} / {discount.usage_limit || '∞'}
                            </p>
                          </div>
                          
                          {/* Min/Max Amount */}
                          {(discount.minimum_amount > 0 || discount.maximum_amount > 0) && (
                            <div className="bg-slate-800/50 p-2 rounded">
                              <p className="text-slate-500 text-xs">Order Amount</p>
                              <p className="text-slate-200">
                                {discount.minimum_amount > 0 ? `Min: $${discount.minimum_amount}` : ''}
                                {discount.minimum_amount > 0 && discount.maximum_amount > 0 ? ' - ' : ''}
                                {discount.maximum_amount > 0 ? `Max: $${discount.maximum_amount}` : ''}
                              </p>
                            </div>
                          )}
                          
                          {/* Expiration */}
                          {discount.date_expires && (
                            <div className="bg-slate-800/50 p-2 rounded">
                              <p className="text-slate-500 text-xs">Expires</p>
                              <p className={`${new Date(discount.date_expires) < new Date() ? 'text-red-400' : 'text-slate-200'}`}>
                                {new Date(discount.date_expires).toLocaleDateString()}
                              </p>
                            </div>
                          )}
                          
                          {/* Per User Limit */}
                          {discount.usage_limit_per_user && (
                            <div className="bg-slate-800/50 p-2 rounded">
                              <p className="text-slate-500 text-xs">Per User</p>
                              <p className="text-slate-200">{discount.usage_limit_per_user} uses</p>
                            </div>
                          )}
                          
                          {/* Flags */}
                          <div className="bg-slate-800/50 p-2 rounded col-span-2 md:col-span-1">
                            <p className="text-slate-500 text-xs">Options</p>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {discount.individual_use && (
                                <span className="text-xs bg-blue-900/50 text-blue-300 px-1.5 py-0.5 rounded">Individual Use</span>
                              )}
                              {discount.free_shipping && (
                                <span className="text-xs bg-purple-900/50 text-purple-300 px-1.5 py-0.5 rounded">Free Shipping</span>
                              )}
                              {discount.exclude_sale_items && (
                                <span className="text-xs bg-amber-900/50 text-amber-300 px-1.5 py-0.5 rounded">No Sale Items</span>
                              )}
                              {!discount.individual_use && !discount.free_shipping && !discount.exclude_sale_items && (
                                <span className="text-xs text-slate-500">None</span>
                              )}
                            </div>
                          </div>
                        </div>
                        
                        {/* Created/Updated Info */}
                        <div className="mt-3 pt-2 border-t border-slate-700/50 text-xs text-slate-500">
                          Created: {new Date(discount.created_at).toLocaleDateString()}
                          {discount.updated_at && discount.updated_at !== discount.created_at && (
                            <span> • Updated: {new Date(discount.updated_at).toLocaleDateString()}</span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
          
          <DialogFooter className="border-t border-slate-700 pt-4">
            <Button
              variant="outline"
              onClick={() => setShowDiscounts(false)}
              className="border-slate-600 text-slate-300 hover:bg-slate-700"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Reports Dashboard */}
      <ReportsDashboard
        open={showReports}
        onClose={() => setShowReports(false)}
      />
      
      {/* Reschedule Dashboard */}
      <RescheduleDashboard
        isOpen={showRescheduleDashboard}
        onClose={() => setShowRescheduleDashboard(false)}
        bookings={bookings}
        classTypes={adminClassTypes}
        onRefreshBookings={fetchBookings}
      />
      
      {/* Price List Dialog */}
      <Dialog open={showPriceList} onOpenChange={setShowPriceList}>
        <DialogContent className="bg-slate-800 border-slate-700 text-slate-100 max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white">
              <DollarSign className="h-5 w-5 text-yellow-400" />
              Class Price List
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              View all class types with their SKUs and pricing. Grouped by category.
            </DialogDescription>
          </DialogHeader>
          
          <div className="flex-1 overflow-y-auto py-4">
            {priceListLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-teal-400" />
                <span className="ml-3 text-slate-400">Loading price list...</span>
              </div>
            ) : priceListData.length === 0 ? (
              <div className="text-center py-12 text-slate-400">
                <DollarSign className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No class types found</p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Group by category */}
                {(() => {
                  const categories = {};
                  priceListData.forEach(ct => {
                    const cat = ct.course_category || 'other';
                    if (!categories[cat]) categories[cat] = [];
                    categories[cat].push(ct);
                  });
                  
                  const categoryOrder = ['bls', 'heartsaver', 'instructor', 'other'];
                  const sortedCats = Object.keys(categories).sort((a, b) => {
                    const aIdx = categoryOrder.indexOf(a.toLowerCase());
                    const bIdx = categoryOrder.indexOf(b.toLowerCase());
                    if (aIdx === -1 && bIdx === -1) return a.localeCompare(b);
                    if (aIdx === -1) return 1;
                    if (bIdx === -1) return -1;
                    return aIdx - bIdx;
                  });
                  
                  const getCategoryColor = (cat) => {
                    const c = cat.toLowerCase();
                    if (c === 'bls') return 'bg-blue-600';
                    if (c === 'heartsaver') return 'bg-orange-600';
                    if (c === 'instructor') return 'bg-emerald-600';
                    return 'bg-slate-600';
                  };
                  
                  const getCategoryDisplayName = (cat) => {
                    const c = cat.toLowerCase();
                    if (c === 'bls') return 'BLS (Basic Life Support)';
                    if (c === 'heartsaver') return 'Heartsaver';
                    if (c === 'instructor') return 'Instructor Courses';
                    return cat.charAt(0).toUpperCase() + cat.slice(1);
                  };
                  
                  return sortedCats.map(cat => (
                    <div key={cat} className="bg-slate-900/50 rounded-lg overflow-hidden border border-slate-700">
                      <div className={`${getCategoryColor(cat)} px-4 py-2`}>
                        <h3 className="font-semibold text-white text-sm uppercase tracking-wide">
                          {getCategoryDisplayName(cat)}
                        </h3>
                      </div>
                      <div className="overflow-x-auto">
                        <table className="w-full text-sm">
                          <thead>
                            <tr className="border-b border-slate-700 bg-slate-800/50">
                              <th className="text-left px-4 py-3 text-slate-400 font-medium">Class Name</th>
                              <th className="text-left px-4 py-3 text-slate-400 font-medium w-32">SKU</th>
                              <th className="text-right px-4 py-3 text-slate-400 font-medium w-28">Base Price</th>
                              <th className="text-right px-4 py-3 text-slate-400 font-medium w-28">Add'l Student</th>
                            </tr>
                          </thead>
                          <tbody>
                            {categories[cat].sort((a, b) => a.name.localeCompare(b.name)).map(ct => (
                              <tr key={ct.id} className="border-b border-slate-700/50 hover:bg-slate-700/30 transition-colors">
                                <td className="px-4 py-3 text-white">{ct.name}</td>
                                <td className="px-4 py-3 text-slate-400 font-mono text-xs">{ct.sku || '—'}</td>
                                <td className="px-4 py-3 text-right">
                                  {ct.base_price ? (
                                    <span className="text-emerald-400 font-semibold">${ct.base_price.toFixed(2)}</span>
                                  ) : (
                                    <span className="text-slate-500 italic">Not set</span>
                                  )}
                                </td>
                                <td className="px-4 py-3 text-right">
                                  {ct.additional_student_price ? (
                                    <span className="text-teal-400">${ct.additional_student_price.toFixed(2)}</span>
                                  ) : (
                                    <span className="text-slate-600">—</span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  ));
                })()}
              </div>
            )}
          </div>
          
          <DialogFooter className="border-t border-slate-700 pt-4">
            <Button 
              variant="outline" 
              onClick={() => setShowPriceList(false)}
              className="border-slate-600 text-slate-300 hover:bg-slate-700"
            >
              Close
            </Button>
            <Button 
              onClick={generatePriceListPDF}
              disabled={priceListLoading || priceListData.length === 0}
              className="bg-teal-600 hover:bg-teal-700 text-white"
              data-testid="print-price-list-btn"
            >
              <Download className="h-4 w-4 mr-2" />
              Print / Save PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Recurring Clients Management */}
      <Dialog open={showRecurringClients} onOpenChange={setShowRecurringClients}>
        <DialogContent className="bg-white text-gray-900 max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-gray-900">
              <Users className="h-5 w-5 text-blue-600" />
              Recurring Clients Management
            </DialogTitle>
            <DialogDescription className="text-gray-600">
              Manage your frequent customers and their booking history
            </DialogDescription>
          </DialogHeader>
          <RecurringClientsManager classTypes={adminClassTypes} />
        </DialogContent>
      </Dialog>
      
      {/* Admin List Management Dialog */}
      <Dialog open={showAdminList} onOpenChange={setShowAdminList}>
        <DialogContent className="bg-slate-800 border-slate-700 text-slate-100 max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white">
              <UserPlus className="h-5 w-5" />
              Admin Notification List
            </DialogTitle>
            <DialogDescription className="text-white">
              Manage who receives admin notification emails for instructor orders, bookings, and other system events.
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Add New Admin Form */}
            <div className="bg-slate-900/50 p-4 rounded-lg border border-slate-700">
              <p className="text-sm text-white mb-3">Add new admin to receive notifications:</p>
              <div className="space-y-3">
                <Input
                  placeholder="Name"
                  value={newAdminForm.name}
                  onChange={(e) => setNewAdminForm({ ...newAdminForm, name: e.target.value })}
                  className="bg-slate-700 border-teal-600 text-slate-100"
                  data-testid="admin-list-name-input"
                />
                <div className="flex gap-2">
                  <Input
                    type="email"
                    placeholder="Email address"
                    value={newAdminForm.email}
                    onChange={(e) => setNewAdminForm({ ...newAdminForm, email: e.target.value })}
                    className="flex-1 bg-slate-700 border-teal-600 text-slate-100"
                    data-testid="admin-list-email-input"
                    onKeyDown={(e) => e.key === 'Enter' && addAdmin()}
                  />
                  <Button 
                    onClick={addAdmin} 
                    disabled={adminFormLoading}
                    className="bg-teal-600 hover:bg-teal-500"
                    data-testid="add-admin-btn"
                  >
                    {adminFormLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />}
                  </Button>
                </div>
              </div>
            </div>
            
            {/* Admin List */}
            <div className="space-y-2">
              <p className="text-sm text-white">Current admin recipients:</p>
              {adminListLoading ? (
                <div className="flex items-center justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-white" />
                </div>
              ) : adminList.length === 0 ? (
                <div className="text-center py-8 text-slate-500">
                  <Mail className="h-12 w-12 mx-auto mb-2 opacity-30" />
                  <p>No admins configured</p>
                  <p className="text-xs">Add admins above to receive notifications</p>
                </div>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {adminList.map((admin) => (
                    <div 
                      key={admin.email} 
                      className={`flex items-center justify-between p-3 rounded-lg border ${
                        admin.active 
                          ? 'bg-slate-700/50 border-teal-600' 
                          : 'bg-slate-800/50 border-slate-700 opacity-50'
                      }`}
                      data-testid={`admin-item-${admin.email}`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-200 truncate">{admin.name}</p>
                        <p className="text-sm text-white truncate">{admin.email}</p>
                      </div>
                      <div className="flex items-center gap-2 ml-3">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => toggleAdminActive(admin.email)}
                          className={admin.active ? 'text-white hover:text-emerald-300' : 'text-slate-500 hover:text-white'}
                          title={admin.active ? 'Click to disable' : 'Click to enable'}
                          data-testid={`toggle-admin-${admin.email}`}
                        >
                          {admin.active ? <Eye className="h-4 w-4" /> : <EyeOff className="h-4 w-4" />}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => removeAdmin(admin.email)}
                          className="text-red-400 hover:text-red-300 hover:bg-red-900/20"
                          data-testid={`remove-admin-${admin.email}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Info Note */}
            <div className="bg-purple-900/20 border border-purple-700/50 rounded-lg p-3">
              <p className="text-xs text-purple-300">
                <Info className="h-3 w-3 inline mr-1" />
                All active admins will receive emails for: new instructor eCard orders, booking notifications, and system alerts.
              </p>
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAdminList(false)} className="border-teal-600 text-white">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* Create Public Class Dialog */}
      <Dialog open={showCreatePublicClass} onOpenChange={setShowCreatePublicClass}>
        <DialogContent className="bg-slate-800 border-slate-700 text-slate-100 max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-white">
              <Globe className="h-5 w-5" />
              Create Public Class
            </DialogTitle>
            <DialogDescription className="text-white">
              Post a public class that students can enroll in
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Class Type */}
            <div>
              <Label className="text-white">Class Type *</Label>
              <Select 
                value={publicClassForm.classTypeId} 
                onValueChange={(v) => setPublicClassForm({...publicClassForm, classTypeId: v})}
              >
                <SelectTrigger className="bg-slate-700 border-teal-600 text-slate-200" data-testid="public-class-type">
                  <SelectValue placeholder="Select class type" />
                </SelectTrigger>
                <SelectContent className="bg-slate-800 border-slate-700 max-h-60">
                  {adminClassTypes.filter(ct => ct.is_active).map(ct => (
                    <SelectItem key={ct.id} value={ct.id} className="text-slate-200">
                      {ct.name} ({ct.duration_hours || 4}h)
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Date and Time */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-white">Date *</Label>
                <Input
                  type="date"
                  value={publicClassForm.date}
                  onChange={(e) => setPublicClassForm({...publicClassForm, date: e.target.value})}
                  min={format(new Date(), 'yyyy-MM-dd')}
                  className="bg-slate-700 border-teal-600 text-slate-100"
                  data-testid="public-class-date"
                />
              </div>
              <div>
                <Label className="text-white">Start Time *</Label>
                <Input
                  type="time"
                  value={publicClassForm.startTime}
                  onChange={(e) => setPublicClassForm({...publicClassForm, startTime: e.target.value})}
                  className="bg-slate-700 border-teal-600 text-slate-100"
                  data-testid="public-class-time"
                />
              </div>
            </div>
            
            {/* Location Name and Max Students */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-white">Location Name *</Label>
                <Input
                  value={publicClassForm.locationName}
                  onChange={(e) => setPublicClassForm({...publicClassForm, locationName: e.target.value})}
                  placeholder="e.g. Reno Fire Station #3"
                  className="bg-slate-700 border-teal-600 text-slate-100"
                  data-testid="public-class-location-name"
                />
              </div>
              <div>
                <Label className="text-white">Max Students</Label>
                <Input
                  type="number"
                  min="1"
                  max="50"
                  value={publicClassForm.maxStudents}
                  onChange={(e) => setPublicClassForm({...publicClassForm, maxStudents: e.target.value === '' ? '' : e.target.value})}
                  onBlur={(e) => {
                    const val = parseInt(e.target.value);
                    if (isNaN(val) || val < 1) setPublicClassForm({...publicClassForm, maxStudents: 1});
                    else if (val > 50) setPublicClassForm({...publicClassForm, maxStudents: 50});
                  }}
                  className="bg-slate-700 border-teal-600 text-slate-100"
                  data-testid="public-class-max-students"
                />
              </div>
            </div>
            
            {/* Address (Optional) */}
            <div className="space-y-3 pt-3 border-t border-slate-700">
              <p className="text-xs text-white uppercase tracking-wide">Class Address (Optional)</p>
              
              <div>
                <Label className="text-white text-sm">Street Address</Label>
                <Input
                  value={publicClassForm.street}
                  onChange={(e) => setPublicClassForm({...publicClassForm, street: e.target.value})}
                  placeholder="123 Main St"
                  className="bg-slate-700 border-teal-600 text-slate-100"
                />
              </div>
              
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <Label className="text-white text-xs">City</Label>
                  <Input
                    value={publicClassForm.city}
                    onChange={(e) => setPublicClassForm({...publicClassForm, city: e.target.value})}
                    placeholder="Reno"
                    className="bg-slate-700 border-teal-600 text-slate-100"
                  />
                </div>
                <div>
                  <Label className="text-white text-xs">State</Label>
                  <Input
                    value={publicClassForm.state}
                    onChange={(e) => setPublicClassForm({...publicClassForm, state: e.target.value})}
                    placeholder="NV"
                    className="bg-slate-700 border-teal-600 text-slate-100"
                  />
                </div>
                <div>
                  <Label className="text-white text-xs">ZIP</Label>
                  <Input
                    value={publicClassForm.zip}
                    onChange={(e) => setPublicClassForm({...publicClassForm, zip: e.target.value})}
                    placeholder="89501"
                    className="bg-slate-700 border-teal-600 text-slate-100"
                  />
                </div>
              </div>
            </div>
            
            {/* Info Box */}
            <div className="p-3 bg-purple-900/30 border border-purple-700 rounded-lg text-sm text-purple-300">
              <Globe className="h-4 w-4 inline mr-2" />
              This class will be posted publicly and students can enroll directly.
            </div>
          </div>
          
          <DialogFooter className="flex gap-2">
            <Button 
              variant="ghost" 
              onClick={() => setShowCreatePublicClass(false)} 
              className="text-white"
            >
              Cancel
            </Button>
            <Button 
              onClick={handleCreatePublicClass}
              disabled={publicClassLoading || !publicClassForm.classTypeId || !publicClassForm.date || !publicClassForm.startTime || !publicClassForm.locationName}
              className="bg-teal-600 hover:bg-teal-500"
              data-testid="create-public-class-submit"
            >
              {publicClassLoading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Creating...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Public Class
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      
      {/* AHA eCard Inventory Dialog */}
      <Dialog open={showAHAInventory} onOpenChange={setShowAHAInventory}>
        <DialogContent className="sm:max-w-lg bg-slate-800 border-slate-700 text-slate-100">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <CreditCard className="h-5 w-5 text-rose-400" />
              AHA eCard Inventory
            </DialogTitle>
            <DialogDescription className="text-white">
              Current inventory from ecards.heart.org
              {ahaCacheAge !== null && ahaCacheAge !== undefined && (
                <span className="ml-2 text-xs text-slate-500">
                  (cached {ahaCacheAge < 1 ? '< 1' : Math.round(ahaCacheAge)} min ago • auto-refreshes every 10 min)
                </span>
              )}
            </DialogDescription>
          </DialogHeader>
          
          {ahaInventoryLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-rose-400" />
              <span className="ml-3 text-white">Fetching inventory from AHA...</span>
            </div>
          ) : ahaInventoryError ? (
            <div className="py-6 text-center">
              <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <p className="text-red-400">{ahaInventoryError}</p>
              <Button 
                onClick={fetchAHAInventory}
                className="mt-4 bg-rose-600 hover:bg-rose-500"
              >
                Try Again
              </Button>
            </div>
          ) : ahaInventory.length === 0 ? (
            <div className="py-6 text-center">
              <CreditCard className="h-12 w-12 text-slate-500 mx-auto mb-4" />
              <p className="text-white">No inventory items found</p>
            </div>
          ) : (
            <div className="py-4">
              <div className="bg-slate-700/50 rounded-lg border border-teal-600 overflow-hidden">
                <table className="w-full text-sm">
                  <thead className="bg-slate-700">
                    <tr>
                      <th className="text-left px-4 py-2 text-white font-semibold">Course</th>
                      <th className="text-center px-4 py-2 text-white font-semibold">Available</th>
                      <th className="text-left px-4 py-2 text-white font-semibold">Product #</th>
                    </tr>
                  </thead>
                  <tbody>
                    {ahaInventory.map((item, idx) => (
                      <tr key={idx} className={`border-t border-teal-600 ${item.available_quantity === 0 ? 'bg-red-900/20' : item.available_quantity <= 5 ? 'bg-amber-900/20' : ''}`}>
                        <td className="px-4 py-2 text-slate-200">{item.course}</td>
                        <td className={`px-4 py-2 text-center font-bold ${
                          item.available_quantity === 0 
                            ? 'text-red-400' 
                            : item.available_quantity <= 5 
                              ? 'text-amber-400' 
                              : 'text-white'
                        }`}>
                          {item.available_quantity}
                        </td>
                        <td className="px-4 py-2 text-white text-xs">{item.product_number}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {/* Summary */}
              <div className="flex items-center justify-between mt-4 pt-4 border-t border-slate-700">
                <div className="text-sm text-white">
                  Total: {ahaInventory.reduce((sum, item) => sum + item.available_quantity, 0)} cards
                </div>
                <Button 
                  onClick={fetchAHAInventory}
                  variant="ghost"
                  size="sm"
                  disabled={ahaInventoryLoading}
                  className="text-white hover:text-slate-200"
                >
                  <RefreshCw className={`h-4 w-4 mr-2 ${ahaInventoryLoading ? 'animate-spin' : ''}`} />
                  Force Refresh
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
      
      {/* eCard Management Dialog */}
      <Dialog open={showEcardLinks} onOpenChange={setShowEcardLinks}>
        <DialogContent className="sm:max-w-2xl bg-slate-800 border-slate-700 text-slate-100 max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <ExternalLink className="h-5 w-5 text-orange-400" />
              eCard Management
            </DialogTitle>
            <DialogDescription className="text-white">
              Manage eCard product numbers and CPR Suppliers links
            </DialogDescription>
          </DialogHeader>
          
          {/* Add New eCard Link Form */}
          <div className="bg-slate-700/50 rounded-lg p-4 border border-teal-600">
            <h4 className="font-medium text-slate-200 mb-3">Add New eCard Link</h4>
            <div className="grid grid-cols-4 gap-3">
              <div>
                <Label className="text-white text-xs">Card Name</Label>
                <Input
                  value={newEcardLink.name}
                  onChange={(e) => setNewEcardLink({...newEcardLink, name: e.target.value})}
                  placeholder="BLS Provider Course"
                  className="bg-slate-700 border-teal-600 text-slate-100 mt-1"
                />
              </div>
              <div>
                <Label className="text-white text-xs">Product Number</Label>
                <Input
                  value={newEcardLink.product_number}
                  onChange={(e) => setNewEcardLink({...newEcardLink, product_number: e.target.value})}
                  placeholder="25-3001"
                  className="bg-slate-700 border-teal-600 text-slate-100 mt-1"
                />
              </div>
              <div>
                <Label className="text-white text-xs">Min Order</Label>
                <Input
                  type="number"
                  min="1"
                  value={newEcardLink.min_order}
                  onChange={(e) => setNewEcardLink({...newEcardLink, min_order: e.target.value === '' ? '' : e.target.value})}
                  onBlur={(e) => {
                    const val = parseInt(e.target.value);
                    if (isNaN(val) || val < 1) setNewEcardLink({...newEcardLink, min_order: 1});
                  }}
                  placeholder="1"
                  className="bg-slate-700 border-teal-600 text-slate-100 mt-1"
                />
              </div>
              <div>
                <Label className="text-white text-xs">CPR Suppliers Link</Label>
                <Input
                  value={newEcardLink.link}
                  onChange={(e) => setNewEcardLink({...newEcardLink, link: e.target.value})}
                  placeholder="https://cprsuppliers.com/product/..."
                  className="bg-slate-700 border-teal-600 text-slate-100 mt-1"
                />
              </div>
            </div>
            <div className="mt-3 flex justify-end">
              <Button onClick={createEcardLink} className="bg-orange-600 hover:bg-orange-500">
                <Plus className="h-4 w-4 mr-2" />
                Add eCard Link
              </Button>
            </div>
          </div>
          
          {/* Existing eCard Links */}
          <div className="mt-4">
            <h4 className="font-medium text-slate-200 mb-3">Existing eCard Links</h4>
            {ecardLinksLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-orange-400" />
              </div>
            ) : !ecardLinks || ecardLinks.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                No eCard links configured yet
              </div>
            ) : (
              <div className="space-y-2">
                {(ecardLinks || []).map((link) => (
                  <div key={link.id} className="bg-slate-700/30 rounded-lg p-3 border border-teal-600">
                    {(() => {
                      // Try to find inventory by product_number first, then by product_url (link.link)
                      const localInvByNumber = localEcardInventory.find(i => i.product_number === link.product_number);
                      const localInvByUrl = localEcardInventory.find(i => i.product_url === link.link);
                      const localInv = localInvByNumber || localInvByUrl;
                      const localQty = localInv?.quantity || localInv?.available || 0;
                      
                      return editingEcardLink?.id === link.id ? (
                        <div className="grid grid-cols-5 gap-2">
                          <Input
                            value={editingEcardLink.name}
                            onChange={(e) => setEditingEcardLink({...editingEcardLink, name: e.target.value})}
                            className="bg-slate-700 border-teal-600 text-slate-100 text-sm"
                            placeholder="Card Name"
                          />
                          <Input
                            value={editingEcardLink.product_number}
                            onChange={(e) => setEditingEcardLink({...editingEcardLink, product_number: e.target.value})}
                            className="bg-slate-700 border-teal-600 text-slate-100 text-sm"
                            placeholder="Product #"
                          />
                          <Input
                            type="number"
                            min="1"
                            value={editingEcardLink.min_order || ''}
                            onChange={(e) => setEditingEcardLink({...editingEcardLink, min_order: e.target.value === '' ? '' : e.target.value})}
                            onBlur={(e) => {
                              const val = parseInt(e.target.value);
                              if (isNaN(val) || val < 1) setEditingEcardLink({...editingEcardLink, min_order: 1});
                            }}
                            className="bg-slate-700 border-teal-600 text-slate-100 text-sm"
                            placeholder="Min"
                          />
                          <div className="flex gap-2">
                            <Input
                              value={editingEcardLink.link}
                              onChange={(e) => setEditingEcardLink({...editingEcardLink, link: e.target.value})}
                              className="bg-slate-700 border-teal-600 text-slate-100 text-sm flex-1"
                              placeholder="CPR Suppliers Link"
                            />
                          </div>
                          <div className="flex gap-2 items-center">
                            <Button size="sm" onClick={() => updateEcardLink(editingEcardLink)} className="bg-teal-600 hover:bg-teal-500">
                              <CheckCircle className="h-4 w-4" />
                            </Button>
                            <Button size="sm" variant="ghost" onClick={() => setEditingEcardLink(null)}>
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                      ) : (
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3">
                              <p className="font-medium text-slate-200">{link.name}</p>
                              <span className="text-orange-400 font-mono text-xs bg-slate-700 px-2 py-0.5 rounded">{link.product_number}</span>
                              <span className="text-yellow-400 text-xs bg-slate-700 px-2 py-0.5 rounded">Min: {link.min_order || 1}</span>
                            </div>
                            <p className="text-xs text-white mt-1">
                              <a href={link.link} target="_blank" rel="noopener noreferrer" className="text-cyan-400 hover:underline">
                                {link.link.length > 60 ? link.link.substring(0, 60) + '...' : link.link}
                              </a>
                            </p>
                          </div>
                          <div className="flex items-center gap-3">
                            {/* Inventory Input - Track changes, save with button */}
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-white">Inv:</span>
                              <Input
                                type="number"
                                min="0"
                                defaultValue={pendingInventoryChanges[link.link] !== undefined ? pendingInventoryChanges[link.link] : localQty}
                                key={`inv-${link.product_number}-${localQty}-${pendingInventoryChanges[link.link]}`}
                                onChange={(e) => {
                                  const newVal = parseInt(e.target.value) || 0;
                                  if (newVal >= 0) {
                                    trackInventoryChange(link.link, newVal);
                                  }
                                }}
                                className={`w-16 h-7 text-center bg-slate-700 border-teal-600 text-white font-medium ${
                                  pendingInventoryChanges[link.link] !== undefined && pendingInventoryChanges[link.link] !== localQty 
                                    ? 'border-orange-500 ring-1 ring-orange-500' 
                                    : ''
                                }`}
                              />
                            </div>
                            <div className="flex gap-1">
                              <Button size="sm" variant="ghost" onClick={() => setEditingEcardLink({...link})} className="text-white hover:text-slate-200">
                                <Edit2 className="h-4 w-4" />
                              </Button>
                              <Button size="sm" variant="ghost" onClick={() => deleteEcardLink(link.id)} className="text-red-400 hover:text-red-300">
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </div>
                        </div>
                      );
                    })()}
                  </div>
                ))}
              </div>
            )}
            
            {/* Update Inventory Button */}
            {Object.keys(pendingInventoryChanges).length > 0 && (
              <div className="mt-4 p-3 bg-orange-900/30 border border-orange-600 rounded-lg">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-orange-300">
                    {Object.keys(pendingInventoryChanges).length} unsaved inventory change(s)
                  </span>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        setPendingInventoryChanges({});
                        fetchLocalEcardInventory();
                      }}
                      className="border-slate-600 text-slate-300 hover:bg-slate-700"
                    >
                      Discard
                    </Button>
                    <Button
                      size="sm"
                      onClick={saveAllInventoryChanges}
                      disabled={savingInventory}
                      className="bg-orange-600 hover:bg-orange-500"
                      data-testid="save-inventory-btn"
                    >
                      {savingInventory ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        <>
                          <Save className="h-4 w-4 mr-2" />
                          Update Inventory
                        </>
                      )}
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Instructor eCard Pricing Dialog */}
      <Dialog open={showInstructorPricing} onOpenChange={setShowInstructorPricing}>
        <DialogContent className="sm:max-w-3xl bg-slate-800 border-slate-700 text-slate-100 max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center justify-between text-xl">
              <div className="flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-yellow-400" />
                Instructor eCard Pricing
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={refreshSupplierPrices}
                disabled={refreshingSupplierPrices}
                className="border-cyan-600 text-cyan-400 hover:bg-cyan-900/30"
              >
                {refreshingSupplierPrices ? (
                  <Loader2 className="h-4 w-4 animate-spin mr-1" />
                ) : (
                  <RefreshCw className="h-4 w-4 mr-1" />
                )}
                Fetch Supplier Prices
              </Button>
            </DialogTitle>
            <DialogDescription className="text-white">
              Set supplier costs and instructor prices. Click "Fetch Supplier Prices" to auto-fill costs from CPR Suppliers (requires login credentials in eCard settings).
            </DialogDescription>
          </DialogHeader>
          
          <TooltipProvider>
          <div className="py-4">
            {instructorPricing.length === 0 ? (
              <div className="text-center py-8 text-white">
                <CreditCard className="h-12 w-12 mx-auto mb-4 text-slate-500" />
                <p>No eCard products configured.</p>
                <p className="text-sm mt-1">Add eCards in eCards → eCard Management first.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {instructorPricing.map(product => {
                  const supplierPrice = product.supplier_price;
                  const instructorPrice = product.price || 0;
                  const regularProfit = supplierPrice ? (instructorPrice - supplierPrice) : null;
                  const bulkPrice = product.bulk_quantity > 0 && product.bulk_discount_percent > 0 
                    ? instructorPrice * (1 - product.bulk_discount_percent / 100)
                    : null;
                  const bulkProfit = bulkPrice && supplierPrice ? (bulkPrice - supplierPrice) : null;
                  
                  return (
                  <div key={product.product_number} className="bg-slate-700/50 rounded-lg p-4 border border-teal-600">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex-1">
                        <p className="font-medium text-slate-200">{product.name}</p>
                        <p className="text-xs text-white">
                          Product #: {product.product_number} • Min Order: {product.min_order}
                        </p>
                      </div>
                      <div className="flex items-center gap-2">
                        <Label htmlFor={`active-${product.product_number}`} className="text-sm text-white">Active</Label>
                        <input
                          type="checkbox"
                          id={`active-${product.product_number}`}
                          checked={product.is_active}
                          onChange={(e) => updateInstructorPricing(
                            product.product_number, 
                            product.price, 
                            e.target.checked,
                            product.bulk_quantity || 0,
                            product.bulk_discount_percent || 0
                          )}
                          className="h-4 w-4 rounded border-teal-600 bg-slate-700 text-emerald-500"
                        />
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-5 gap-3">
                      {/* Supplier Cost (Manual Entry) */}
                      <div>
                        <Label className="text-xs text-cyan-400 mb-1 block">Supplier Cost</Label>
                        <div className="flex items-center gap-1">
                          <span className="text-white">$</span>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            defaultValue={supplierPrice || ''}
                            key={`supplier-${product.product_number}-${supplierPrice}`}
                            placeholder="0.00"
                            onBlur={(e) => {
                              const newSupplierCost = parseFloat(e.target.value);
                              if (!isNaN(newSupplierCost) && newSupplierCost !== supplierPrice) {
                                updateInstructorPricing(
                                  product.product_number, 
                                  product.price, 
                                  product.is_active,
                                  product.bulk_quantity || 0,
                                  product.bulk_discount_percent || 0,
                                  newSupplierCost
                                );
                              }
                            }}
                            className="w-full bg-slate-700 border-cyan-600/50 text-slate-100"
                          />
                        </div>
                      </div>
                      
                      {/* Unit Price with Profit Tooltip */}
                      <div>
                        <Label className="text-xs text-white mb-1 block flex items-center gap-1">
                          Unit Price
                          {regularProfit !== null && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className={`cursor-help ${regularProfit >= 0 ? 'text-white' : 'text-red-400'}`}>
                                  <TrendingUp className="h-3 w-3" />
                                </span>
                              </TooltipTrigger>
                              <TooltipContent className="bg-slate-900 border-slate-700 text-slate-100">
                                <div className="text-sm">
                                  <p className="font-medium">Profit Calculation</p>
                                  <p>Instructor Price: ${instructorPrice.toFixed(2)}</p>
                                  <p>Supplier Cost: ${supplierPrice.toFixed(2)}</p>
                                  <p className={`font-bold ${regularProfit >= 0 ? 'text-white' : 'text-red-400'}`}>
                                    Profit: ${regularProfit.toFixed(2)} per card
                                  </p>
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </Label>
                        <div className="flex items-center gap-1">
                          <span className="text-white">$</span>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            defaultValue={product.price || 0}
                            key={`price-${product.product_number}-${product.price}`}
                            onBlur={(e) => {
                              const newPrice = parseFloat(e.target.value) || 0;
                              if (newPrice !== product.price) {
                                updateInstructorPricing(
                                  product.product_number, 
                                  newPrice, 
                                  product.is_active,
                                  product.bulk_quantity || 0,
                                  product.bulk_discount_percent || 0
                                );
                              }
                            }}
                            className="w-full bg-slate-700 border-teal-600 text-slate-100"
                          />
                        </div>
                      </div>
                      
                      {/* Bulk Quantity Threshold */}
                      <div>
                        <Label className="text-xs text-white mb-1 block">Bulk Qty</Label>
                        <Input
                          type="number"
                          min="0"
                          defaultValue={product.bulk_quantity || 0}
                          key={`bulk-qty-${product.product_number}-${product.bulk_quantity}`}
                          onBlur={(e) => {
                            const newBulkQty = parseInt(e.target.value) || 0;
                            if (newBulkQty !== (product.bulk_quantity || 0)) {
                              updateInstructorPricing(
                                product.product_number, 
                                product.price, 
                                product.is_active,
                                newBulkQty,
                                product.bulk_discount_percent || 0
                              );
                            }
                          }}
                          placeholder="0"
                          className="w-full bg-slate-700 border-teal-600 text-slate-100"
                        />
                      </div>
                      
                      {/* Bulk Discount Percent with Profit Tooltip */}
                      <div>
                        <Label className="text-xs text-white mb-1 block flex items-center gap-1">
                          Bulk Discount
                          {bulkProfit !== null && (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <span className={`cursor-help ${bulkProfit >= 0 ? 'text-white' : 'text-red-400'}`}>
                                  <TrendingUp className="h-3 w-3" />
                                </span>
                              </TooltipTrigger>
                              <TooltipContent className="bg-slate-900 border-slate-700 text-slate-100">
                                <div className="text-sm">
                                  <p className="font-medium">Bulk Profit Calculation</p>
                                  <p>Bulk Price: ${bulkPrice.toFixed(2)}</p>
                                  <p>Supplier Cost: ${supplierPrice.toFixed(2)}</p>
                                  <p className={`font-bold ${bulkProfit >= 0 ? 'text-white' : 'text-red-400'}`}>
                                    Bulk Profit: ${bulkProfit.toFixed(2)} per card
                                  </p>
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          )}
                        </Label>
                        <div className="flex items-center gap-1">
                          <Input
                            type="number"
                            step="0.1"
                            min="0"
                            max="100"
                            defaultValue={product.bulk_discount_percent || 0}
                            key={`bulk-disc-${product.product_number}-${product.bulk_discount_percent}`}
                            onBlur={(e) => {
                              const newDiscount = parseFloat(e.target.value) || 0;
                              if (newDiscount !== (product.bulk_discount_percent || 0)) {
                                updateInstructorPricing(
                                  product.product_number, 
                                  product.price, 
                                  product.is_active,
                                  product.bulk_quantity || 0,
                                  newDiscount
                                );
                              }
                            }}
                            placeholder="0"
                            className="w-full bg-slate-700 border-teal-600 text-slate-100"
                          />
                          <span className="text-white">%</span>
                        </div>
                      </div>
                      
                      {/* Profit Summary */}
                      <div>
                        <Label className="text-xs text-white mb-1 block">Profit</Label>
                        <div className="h-10 flex items-center">
                          {regularProfit !== null ? (
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="cursor-help">
                                  <span className={`text-sm font-medium ${regularProfit >= 0 ? 'text-white' : 'text-red-400'}`}>
                                    ${regularProfit.toFixed(2)}
                                  </span>
                                  {bulkProfit !== null && (
                                    <span className={`text-xs ml-1 ${bulkProfit >= 0 ? 'text-white/70' : 'text-red-400/70'}`}>
                                      (bulk: ${bulkProfit.toFixed(2)})
                                    </span>
                                  )}
                                </div>
                              </TooltipTrigger>
                              <TooltipContent className="bg-slate-900 border-slate-700 text-slate-100">
                                <div className="text-sm space-y-2">
                                  <div>
                                    <p className="font-medium">Regular Price Profit</p>
                                    <p>Sell at: ${instructorPrice.toFixed(2)}</p>
                                    <p>Cost: ${supplierPrice.toFixed(2)}</p>
                                    <p className={`font-bold ${regularProfit >= 0 ? 'text-white' : 'text-red-400'}`}>
                                      = ${regularProfit.toFixed(2)} profit/card
                                    </p>
                                  </div>
                                  {bulkProfit !== null && (
                                    <div className="border-t border-slate-700 pt-2">
                                      <p className="font-medium">Bulk Price Profit</p>
                                      <p>Sell at: ${bulkPrice.toFixed(2)} ({product.bulk_discount_percent}% off)</p>
                                      <p>Cost: ${supplierPrice.toFixed(2)}</p>
                                      <p className={`font-bold ${bulkProfit >= 0 ? 'text-white' : 'text-red-400'}`}>
                                        = ${bulkProfit.toFixed(2)} profit/card
                                      </p>
                                    </div>
                                  )}
                                </div>
                              </TooltipContent>
                            </Tooltip>
                          ) : (
                            <span className="text-xs text-slate-500">N/A</span>
                          )}
                        </div>
                      </div>
                    </div>
                    
                    {(product.bulk_quantity > 0 && product.bulk_discount_percent > 0) && (
                      <p className="text-xs text-white mt-2">
                        Orders of {product.bulk_quantity}+ get {product.bulk_discount_percent}% off
                      </p>
                    )}
                  </div>
                )})}
              </div>
            )}
            
            <div className="mt-6 p-4 bg-slate-700/30 rounded-lg">
              <p className="text-sm text-white">
                <strong className="text-white">Store URL:</strong>{' '}
                <a 
                  href={`${window.location.origin}/instructor-ecards`} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-cyan-400 hover:underline"
                >
                  {window.location.origin}/instructor-ecards
                </a>
              </p>
              <p className="text-xs text-slate-500 mt-1">
                Share this link with instructors to access the eCard store.
              </p>
            </div>
          </div>
          </TooltipProvider>
        </DialogContent>
      </Dialog>

      {/* Bulk eCard Inventory Orders Dialog */}
      <Dialog open={showBulkOrders} onOpenChange={setShowBulkOrders}>
        <DialogContent className="sm:max-w-4xl bg-slate-800 border-slate-700 text-slate-100 max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Package className="h-5 w-5 text-green-400" />
              Bulk eCard Inventory Orders
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Order eCards to replenish your inventory. Same automated checkout as instructor orders.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {/* Create New Order Button */}
            <div className="flex justify-between items-center">
              <div className="flex gap-2">
                <Button
                  onClick={() => setShowCreateBulkOrder(true)}
                  className="bg-green-600 hover:bg-green-700"
                  data-testid="create-bulk-order-btn"
                >
                  <Plus className="h-4 w-4 mr-2" />
                  Create New Bulk Order
                </Button>
                {/* Reset Browser Session Button - useful when orders fail */}
                <Button
                  onClick={resetCprSuppliersBrowser}
                  variant="outline"
                  size="sm"
                  className="border-amber-600 text-amber-400 hover:bg-amber-900/30"
                  title="Reset browser session if orders keep failing"
                  data-testid="reset-browser-btn"
                >
                  <RefreshCw className="h-4 w-4 mr-1" />
                  Reset Browser
                </Button>
                {/* Test Browser Button - diagnose issues */}
                <Button
                  onClick={testCprSuppliersBrowser}
                  variant="outline"
                  size="sm"
                  className="border-cyan-600 text-cyan-400 hover:bg-cyan-900/30"
                  title="Test browser connection to diagnose issues"
                  disabled={browserTestRunning}
                  data-testid="test-browser-btn"
                >
                  {browserTestRunning ? <Loader2 className="h-4 w-4 mr-1 animate-spin" /> : <AlertCircle className="h-4 w-4 mr-1" />}
                  Test Browser
                </Button>
              </div>
              <Button
                onClick={fetchBulkOrders}
                variant="outline"
                size="sm"
                className="border-slate-600"
                disabled={bulkOrdersLoading}
              >
                {bulkOrdersLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
              </Button>
            </div>
            
            {/* Browser Test Results */}
            {browserTestResults && (
              <div className={`p-4 rounded-lg border ${browserTestResults.success ? 'border-green-600 bg-green-900/20' : 'border-red-600 bg-red-900/20'}`}>
                <h4 className="font-semibold text-white mb-2 flex items-center gap-2">
                  {browserTestResults.success ? (
                    <><CheckCircle className="h-4 w-4 text-green-400" /> Browser Test Passed</>
                  ) : (
                    <><AlertCircle className="h-4 w-4 text-red-400" /> Browser Test Failed</>
                  )}
                </h4>
                {browserTestResults.error && (
                  <p className="text-red-400 text-sm mb-2">{browserTestResults.error}</p>
                )}
                {browserTestResults.steps && (
                  <div className="space-y-1 text-xs font-mono">
                    {browserTestResults.steps.map((step, i) => (
                      <div key={i} className={`flex items-center gap-2 ${step.success ? 'text-green-400' : 'text-red-400'}`}>
                        <span>{step.success ? '✓' : '✗'}</span>
                        <span>{step.step}</span>
                        {step.details && <span className="text-slate-400">({JSON.stringify(step.details)})</span>}
                        {step.error && <span className="text-red-300">- {step.error}</span>}
                      </div>
                    ))}
                  </div>
                )}
                <Button
                  onClick={() => setBrowserTestResults(null)}
                  variant="ghost"
                  size="sm"
                  className="mt-2 text-slate-400 hover:text-white"
                >
                  Dismiss
                </Button>
              </div>
            )}
            
            {/* Orders List */}
            {bulkOrdersLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-8 w-8 animate-spin text-green-400" />
              </div>
            ) : bulkOrders.length === 0 ? (
              <div className="text-center py-8 text-slate-500">
                No bulk orders yet. Create one to replenish your inventory.
              </div>
            ) : (
              <div className="space-y-3">
                {bulkOrders.map((order) => (
                  <div
                    key={order.id}
                    className={`p-4 rounded-lg border ${
                      order.status === 'ordered' ? 'border-blue-600 bg-blue-900/20' :
                      order.status === 'on_hold' ? 'border-orange-600 bg-orange-900/20' :
                      order.status === 'received' ? 'border-green-600 bg-green-900/20' :
                      order.status === 'processing' ? 'border-yellow-600 bg-yellow-900/20' :
                      order.status === 'failed' ? 'border-red-600 bg-red-900/20' :
                      order.status === 'stopped' ? 'border-purple-600 bg-purple-900/20' :
                      'border-slate-600 bg-slate-900/50'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <div className="flex items-center gap-2">
                          <span className="font-mono text-sm text-slate-400">#{order.id}</span>
                          <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                            order.status === 'ordered' ? 'bg-blue-600 text-white' :
                            order.status === 'on_hold' ? 'bg-orange-600 text-white' :
                            order.status === 'received' ? 'bg-green-600 text-white' :
                            order.status === 'processing' ? 'bg-yellow-600 text-black' :
                            order.status === 'failed' ? 'bg-red-600 text-white' :
                            order.status === 'stopped' ? 'bg-purple-600 text-white' :
                            'bg-slate-600 text-white'
                          }`}>
                            {order.status === 'on_hold' ? 'ON HOLD' : order.status.toUpperCase()}
                          </span>
                          {order.supplier_order_number && (
                            <span className="text-xs text-blue-400">
                              Supplier #{order.supplier_order_number}
                            </span>
                          )}
                          {/* Status Change Dropdown */}
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className="h-6 px-2 text-xs text-slate-400 hover:text-white">
                                <ChevronDown className="h-3 w-3" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent className="bg-slate-800 border-slate-700">
                              <DropdownMenuItem 
                                onClick={() => openBulkStatusChange(order, 'pending')}
                                disabled={order.status === 'pending' || order.status === 'processing'}
                                className="text-amber-400 focus:bg-slate-700"
                              >
                                <Clock className="h-3 w-3 mr-2" />
                                Pending
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => openBulkStatusChange(order, 'ordered')}
                                disabled={order.status === 'ordered' || order.status === 'processing'}
                                className="text-blue-400 focus:bg-slate-700"
                              >
                                <Package className="h-3 w-3 mr-2" />
                                Ordered
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => openBulkStatusChange(order, 'on_hold')}
                                disabled={order.status === 'on_hold' || order.status === 'processing'}
                                className="text-orange-400 focus:bg-slate-700"
                              >
                                <Pause className="h-3 w-3 mr-2" />
                                On Hold
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => openBulkStatusChange(order, 'received')}
                                disabled={order.status === 'received' || order.status === 'processing'}
                                className="text-green-400 focus:bg-slate-700"
                              >
                                <CheckCircle className="h-3 w-3 mr-2" />
                                Received
                              </DropdownMenuItem>
                              <DropdownMenuItem 
                                onClick={() => openBulkStatusChange(order, 'failed')}
                                disabled={order.status === 'failed' || order.status === 'processing'}
                                className="text-red-400 focus:bg-slate-700"
                              >
                                <XCircle className="h-3 w-3 mr-2" />
                                Failed
                              </DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                        <div className="text-sm text-slate-300 mt-1">
                          {order.items?.map((item, idx) => (
                            <span key={idx} className="mr-2">
                              {item.quantity}x {item.product_name || item.product_number}
                            </span>
                          ))}
                        </div>
                        <div className="text-xs text-slate-500 mt-1">
                          Created: {new Date(order.created_at).toLocaleString()}
                          {order.ordered_at && ` • Ordered: ${new Date(order.ordered_at).toLocaleString()}`}
                          {order.received_at && ` • Received: ${new Date(order.received_at).toLocaleString()}`}
                        </div>
                        {/* Supplier Status Display */}
                        {order.supplier_status && (
                          <p className="text-xs mt-1">
                            <span className="text-slate-400">CPR Suppliers: </span>
                            <span className={`font-medium ${
                              order.supplier_status.toLowerCase().includes('complete') ? 'text-cyan-400' : 
                              order.supplier_status.toLowerCase().includes('hold') ? 'text-orange-400' : 'text-amber-400'
                            }`}>
                              {order.supplier_status}
                            </span>
                          </p>
                        )}
                        {order.error_message && (
                          <div className="text-xs text-red-400 mt-1">
                            Error: {order.error_message}
                          </div>
                        )}
                        {/* Change Log indicator */}
                        {order.change_log?.length > 0 && (
                          <div className="text-xs text-slate-500 mt-1">
                            {order.change_log.length} change(s) logged
                          </div>
                        )}
                      </div>
                      <div className="flex gap-2 flex-wrap">
                        {/* Edit button for pending or failed orders */}
                        {(order.status === 'pending' || order.status === 'failed') && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => startEditingBulkOrder(order)}
                            className="border-blue-600 text-blue-400 hover:bg-blue-900/30"
                          >
                            <Edit2 className="h-4 w-4 mr-1" />
                            Edit
                          </Button>
                        )}
                        {order.status === 'pending' && (
                          <>
                            <Button
                              size="sm"
                              onClick={() => processBulkOrder(order.id)}
                              disabled={processingBulkOrder === order.id}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              {processingBulkOrder === order.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <>
                                  <Play className="h-4 w-4 mr-1" />
                                  Process
                                </>
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => deleteBulkOrder(order.id)}
                              className="border-red-600 text-red-400 hover:bg-red-900/30"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                        {order.status === 'processing' && (
                          <>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => stopBulkOrder(order.id)}
                              className="border-red-600 text-red-400 hover:bg-red-900/30"
                            >
                              <Square className="h-4 w-4 mr-1" />
                              Stop
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setSelectedBulkOrder(order)}
                              className="border-yellow-600 text-yellow-400"
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              View Progress
                            </Button>
                          </>
                        )}
                        {order.status === 'ordered' && (
                          <Button
                            size="sm"
                            onClick={() => markBulkOrderReceived(order.id)}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            <Check className="h-4 w-4 mr-1" />
                            Mark Received
                          </Button>
                        )}
                        {order.status === 'on_hold' && (
                          <>
                            <Button
                              size="sm"
                              onClick={() => markBulkOrderReceived(order.id)}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              <Check className="h-4 w-4 mr-1" />
                              Mark Received
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => setSelectedBulkOrder(order)}
                              className="border-orange-600 text-orange-400"
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              View Details
                            </Button>
                          </>
                        )}
                        {order.status === 'failed' && (
                          <>
                            <Button
                              size="sm"
                              onClick={() => retryBulkOrder(order.id)}
                              disabled={processingBulkOrder === order.id}
                              className="bg-amber-600 hover:bg-amber-700"
                            >
                              {processingBulkOrder === order.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <>
                                  <RefreshCw className="h-4 w-4 mr-1" />
                                  Retry
                                </>
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => deleteBulkOrder(order.id)}
                              className="border-red-600 text-red-400 hover:bg-red-900/30"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                        {order.status === 'stopped' && (
                          <>
                            <Button
                              size="sm"
                              onClick={() => retryBulkOrder(order.id)}
                              disabled={processingBulkOrder === order.id}
                              className="bg-green-600 hover:bg-green-700"
                            >
                              {processingBulkOrder === order.id ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : (
                                <>
                                  <Play className="h-4 w-4 mr-1" />
                                  Restart
                                </>
                              )}
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => startEditingBulkOrder(order)}
                              className="border-blue-600 text-blue-400 hover:bg-blue-900/30"
                            >
                              <Edit2 className="h-4 w-4 mr-1" />
                              Edit
                            </Button>
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => deleteBulkOrder(order.id)}
                              className="border-red-600 text-red-400 hover:bg-red-900/30"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </>
                        )}
                        {(order.admin_notes_log?.length > 0 || order.change_log?.length > 0) && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => setSelectedBulkOrder(order)}
                            className="border-slate-600"
                          >
                            <FileText className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Create Bulk Order Dialog */}
      <Dialog open={showCreateBulkOrder} onOpenChange={setShowCreateBulkOrder}>
        <DialogContent className="sm:max-w-2xl bg-slate-800 border-slate-700 text-slate-100 max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Plus className="h-5 w-5 text-green-400" />
              Create Bulk Inventory Order
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Select products and quantities to order. The order will be processed using automated checkout.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {/* Product Selection */}
            <div className="space-y-3">
              <h4 className="font-medium text-slate-200">Select Products</h4>
              {ecardLinks.length === 0 ? (
                <div className="text-slate-500 text-sm">Loading products...</div>
              ) : (
                <div className="space-y-2 max-h-64 overflow-y-auto">
                  {ecardLinks.map((link) => {
                    const currentInv = localEcardInventory.find(i => i.product_number === link.product_number);
                    const currentQty = currentInv?.quantity || 0;
                    // Try to get price from by_url first, then by_product_number
                    const price = (link?.url ? cachedPrices.by_url?.[link.url]?.price : null) 
                      || cachedPrices.by_product_number?.[link.product_number]?.price 
                      || 0;
                    
                    return (
                      <div
                        key={link.product_number}
                        className="flex items-center justify-between p-3 rounded border border-slate-600 bg-slate-900/50"
                      >
                        <div className="flex-1">
                          <div className="font-medium text-slate-200">
                            {link.name}
                            {price > 0 && (
                              <span className="ml-2 text-green-400 text-sm font-normal">${price.toFixed(2)}</span>
                            )}
                          </div>
                          <div className="text-xs text-slate-500">
                            {link.product_number} • Current Inventory: {currentQty}
                            {(link.min_order || 1) > 1 && (
                              <span className="ml-2 text-yellow-400">• Min order: {link.min_order}</span>
                            )}
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 w-8 p-0 border-slate-600"
                            onClick={() => {
                              const minOrder = link.min_order || 1;
                              const currentQty = bulkOrderItems[link.product_number] || 0;
                              // When decreasing: go to 0 if at min, otherwise decrease by 1 but not below min
                              if (currentQty <= minOrder) {
                                setBulkOrderItems(prev => ({ ...prev, [link.product_number]: 0 }));
                              } else {
                                setBulkOrderItems(prev => ({ ...prev, [link.product_number]: currentQty - 1 }));
                              }
                            }}
                          >
                            <Minus className="h-4 w-4" />
                          </Button>
                          <Input
                            type="number"
                            min="0"
                            value={bulkOrderItems[link.product_number] || 0}
                            onChange={(e) => {
                              const minOrder = link.min_order || 1;
                              const val = parseInt(e.target.value) || 0;
                              // If entering a value, enforce minimum (unless clearing to 0)
                              if (val > 0 && val < minOrder) {
                                setBulkOrderItems(prev => ({ ...prev, [link.product_number]: minOrder }));
                                toast.info(`Minimum order for ${link.name} is ${minOrder}`);
                              } else {
                                setBulkOrderItems(prev => ({ ...prev, [link.product_number]: val }));
                              }
                            }}
                            className="w-16 h-8 text-center bg-slate-900 border-slate-600"
                          />
                          <Button
                            size="sm"
                            variant="outline"
                            className="h-8 w-8 p-0 border-slate-600"
                            onClick={() => {
                              const minOrder = link.min_order || 1;
                              const currentQty = bulkOrderItems[link.product_number] || 0;
                              // When increasing from 0, jump to minimum
                              if (currentQty === 0) {
                                setBulkOrderItems(prev => ({ ...prev, [link.product_number]: minOrder }));
                                if (minOrder > 1) {
                                  toast.info(`Minimum order for ${link.name} is ${minOrder}`);
                                }
                              } else {
                                setBulkOrderItems(prev => ({ ...prev, [link.product_number]: currentQty + 1 }));
                              }
                            }}
                          >
                            <Plus className="h-4 w-4" />
                          </Button>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
            
            {/* Order Summary */}
            {Object.values(bulkOrderItems).some(q => q > 0) && (
              <div className="p-3 rounded border border-green-600 bg-green-900/20">
                <h4 className="font-medium text-green-400 mb-2">Order Summary</h4>
                <div className="space-y-1 text-sm">
                  {Object.entries(bulkOrderItems)
                    .filter(([_, qty]) => qty > 0)
                    .map(([pn, qty]) => {
                      const link = ecardLinks.find(l => l.product_number === pn);
                      // Try to get price from by_url first, then by_product_number
                      const price = (link?.url ? cachedPrices.by_url?.[link.url]?.price : null) 
                        || cachedPrices.by_product_number?.[pn]?.price 
                        || 0;
                      const lineTotal = price * qty;
                      return (
                        <div key={pn} className="flex justify-between">
                          <span className="text-slate-300">{link?.name || pn}</span>
                          <span className="text-slate-400">
                            {price > 0 ? `$${price.toFixed(2)} × ` : ''}
                            <span className="text-green-400 font-medium">{qty}</span>
                            {price > 0 && <span className="text-slate-500 ml-2">= ${lineTotal.toFixed(2)}</span>}
                          </span>
                        </div>
                      );
                    })}
                  <div className="border-t border-green-600 pt-2 mt-2 space-y-1">
                    <div className="flex justify-between font-medium">
                      <span className="text-slate-200">Total Items</span>
                      <span className="text-green-400">
                        {Object.values(bulkOrderItems).reduce((sum, q) => sum + (q || 0), 0)}
                      </span>
                    </div>
                    {(() => {
                      const totalPrice = Object.entries(bulkOrderItems)
                        .filter(([_, qty]) => qty > 0)
                        .reduce((sum, [pn, qty]) => {
                          const link = ecardLinks.find(l => l.product_number === pn);
                          // Try to get price from by_url first, then by_product_number
                          const price = (link?.url ? cachedPrices.by_url?.[link.url]?.price : null) 
                            || cachedPrices.by_product_number?.[pn]?.price 
                            || 0;
                          return sum + (price * qty);
                        }, 0);
                      
                      return totalPrice > 0 ? (
                        <div className="flex justify-between font-medium text-lg">
                          <span className="text-slate-200">Estimated Total</span>
                          <span className="text-green-400">${totalPrice.toFixed(2)}</span>
                        </div>
                      ) : null;
                    })()}
                  </div>
                  {cachedPrices.last_sync && (
                    <p className="text-xs text-slate-500 mt-2">
                      Prices from {new Date(cachedPrices.last_sync).toLocaleDateString()}
                    </p>
                  )}
                </div>
              </div>
            )}
            
            {/* Notes */}
            <div>
              <label className="text-sm font-medium text-slate-300">Notes (optional)</label>
              <Input
                value={bulkOrderNotes}
                onChange={(e) => setBulkOrderNotes(e.target.value)}
                placeholder="e.g., Restocking for upcoming busy season"
                className="mt-1 bg-slate-900 border-slate-600"
              />
            </div>
            
            {/* Actions */}
            <div className="flex justify-end gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowCreateBulkOrder(false);
                  setBulkOrderItems({});
                  setBulkOrderNotes('');
                }}
                className="border-slate-600"
              >
                Cancel
              </Button>
              <Button
                onClick={createBulkOrder}
                disabled={creatingBulkOrder || !Object.values(bulkOrderItems).some(q => q > 0)}
                className="bg-green-600 hover:bg-green-700"
              >
                {creatingBulkOrder ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-2" />
                    Create Order
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Bulk Order Details/Log Dialog */}
      <Dialog open={!!selectedBulkOrder} onOpenChange={(open) => !open && setSelectedBulkOrder(null)}>
        <DialogContent className="sm:max-w-2xl bg-slate-800 border-slate-700 text-slate-100 max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-blue-400" />
              Bulk Order #{selectedBulkOrder?.id}
            </DialogTitle>
          </DialogHeader>
          
          {selectedBulkOrder && (
            <div className="py-4 space-y-4">
              {/* Status */}
              <div className="flex items-center gap-2">
                <span className="text-slate-400">Status:</span>
                <span className={`px-2 py-0.5 rounded text-xs font-medium ${
                  selectedBulkOrder.status === 'ordered' ? 'bg-blue-600 text-white' :
                  selectedBulkOrder.status === 'on_hold' ? 'bg-orange-600 text-white' :
                  selectedBulkOrder.status === 'received' ? 'bg-green-600 text-white' :
                  selectedBulkOrder.status === 'processing' ? 'bg-yellow-600 text-black' :
                  selectedBulkOrder.status === 'failed' ? 'bg-red-600 text-white' :
                  'bg-slate-600 text-white'
                }`}>
                  {selectedBulkOrder.status === 'on_hold' ? 'ON HOLD' : selectedBulkOrder.status.toUpperCase()}
                </span>
                {selectedBulkOrder.supplier_order_number && (
                  <span className="text-blue-400">
                    Supplier Order #{selectedBulkOrder.supplier_order_number}
                  </span>
                )}
              </div>
              
              {/* Supplier Status */}
              {selectedBulkOrder.supplier_status && (
                <div className="flex items-center gap-2">
                  <span className="text-slate-400">CPR Suppliers:</span>
                  <span className={`font-medium ${
                    selectedBulkOrder.supplier_status.toLowerCase().includes('complete') ? 'text-cyan-400' : 
                    selectedBulkOrder.supplier_status.toLowerCase().includes('hold') ? 'text-orange-400' : 'text-amber-400'
                  }`}>
                    {selectedBulkOrder.supplier_status}
                  </span>
                </div>
              )}
              
              {/* Items */}
              <div>
                <h4 className="font-medium text-slate-200 mb-2">Items</h4>
                <div className="space-y-1">
                  {selectedBulkOrder.items?.map((item, idx) => (
                    <div key={idx} className="flex justify-between text-sm">
                      <span className="text-slate-300">{item.product_name || item.product_number}</span>
                      <span className="text-slate-400">×{item.quantity}</span>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Change Log */}
              {selectedBulkOrder.change_log?.length > 0 && (
                <div>
                  <h4 className="font-medium text-slate-200 mb-2">Change Log</h4>
                  <div className="bg-slate-900 rounded border border-slate-700 p-3 max-h-48 overflow-y-auto text-xs space-y-2">
                    {selectedBulkOrder.change_log.slice().reverse().map((entry, idx) => (
                      <div key={idx} className="pb-2 border-b border-slate-700 last:border-0">
                        <div className="text-slate-500">{new Date(entry.timestamp).toLocaleString()}</div>
                        <div className="text-slate-300">
                          {entry.type === 'status_change' && entry.changes?.map((c, i) => (
                            <span key={i}>
                              Status: <span className="text-amber-400">{c.from_status}</span> → <span className="text-green-400">{c.to_status}</span>
                              {c.reason && <span className="text-slate-500 ml-2">({c.reason})</span>}
                            </span>
                          ))}
                          {entry.type === 'items_edited' && (
                            <span>
                              Items edited ({entry.changes?.length} changes): {entry.old_total} → {entry.new_total} items
                              {entry.reason && <span className="text-slate-500 ml-2">({entry.reason})</span>}
                            </span>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Progress Log */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-slate-200">Progress Log</h4>
                  <div className="flex gap-2">
                    {/* Emergency Stop Button */}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={emergencyStopBrowser}
                      disabled={emergencyStopLoading}
                      className="border-red-600 text-red-400 hover:bg-red-900/30"
                      data-testid="emergency-stop-btn"
                    >
                      {emergencyStopLoading ? (
                        <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                      ) : (
                        <StopCircle className="h-3 w-3 mr-1" />
                      )}
                      Emergency Stop
                    </Button>
                    {selectedBulkOrder.admin_notes_log?.length > 0 && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => clearBulkOrderLog(selectedBulkOrder.id)}
                        className="border-slate-600 text-xs"
                      >
                        Clear Log
                      </Button>
                    )}
                  </div>
                </div>
                <div className="bg-slate-900 rounded border border-slate-700 p-3 max-h-64 overflow-y-auto font-mono text-xs">
                  {selectedBulkOrder.admin_notes_log?.length > 0 ? (
                    selectedBulkOrder.admin_notes_log.map((log, idx) => (
                      <div
                        key={idx}
                        className={`py-1 ${log.includes('ERROR') ? 'text-red-400' : 'text-slate-300'}`}
                      >
                        {log}
                      </div>
                    ))
                  ) : (
                    <div className="text-slate-500">No log entries</div>
                  )}
                </div>
              </div>
              
              {/* Actions */}
              <div className="flex justify-end gap-2 pt-2">
                {(selectedBulkOrder.status === 'pending' || selectedBulkOrder.status === 'failed') && (
                  <Button
                    onClick={() => { setSelectedBulkOrder(null); startEditingBulkOrder(selectedBulkOrder); }}
                    variant="outline"
                    className="border-blue-600 text-blue-400"
                  >
                    <Edit2 className="h-4 w-4 mr-2" />
                    Edit Items
                  </Button>
                )}
                {selectedBulkOrder.status === 'processing' && (
                  <Button
                    onClick={async () => {
                      const orderRes = await axios.get(`${API}/admin/bulk-ecard-orders/${selectedBulkOrder.id}`);
                      setSelectedBulkOrder(orderRes.data);
                    }}
                    variant="outline"
                    className="border-slate-600"
                  >
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Refresh
                  </Button>
                )}
                <Button
                  onClick={() => setSelectedBulkOrder(null)}
                  variant="outline"
                  className="border-slate-600"
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Edit Bulk Order Dialog */}
      <Dialog open={!!editingBulkOrder} onOpenChange={(open) => !open && cancelEditingBulkOrder()}>
        <DialogContent className="sm:max-w-2xl bg-slate-800 border-slate-700 text-slate-100 max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Edit2 className="h-5 w-5 text-blue-400" />
              Edit Bulk Order #{editingBulkOrder?.id}
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              Modify items and quantities. Changes will be logged.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {/* Current Items */}
            <div>
              <h4 className="font-medium text-slate-200 mb-2">Order Items</h4>
              {editedBulkItems.length === 0 ? (
                <div className="text-slate-500 text-sm py-4">No items. Add products below.</div>
              ) : (
                <div className="space-y-2">
                  {editedBulkItems.map((item, idx) => (
                    <div key={idx} className="flex items-center gap-2 p-2 bg-slate-900 rounded border border-slate-700">
                      <div className="flex-1 text-sm text-slate-300">{item.product_name || item.product_number}</div>
                      <Input
                        type="number"
                        value={item.quantity}
                        onChange={(e) => updateBulkItemQuantity(idx, e.target.value)}
                        className="w-20 h-8 bg-slate-700 border-slate-600 text-white text-center"
                        min={1}
                      />
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => removeBulkItem(idx)}
                        className="h-8 w-8 p-0 text-red-400 hover:text-red-300 hover:bg-red-900/30"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
              <div className="text-sm text-slate-400 mt-2">
                Total: {editedBulkItems.reduce((sum, i) => sum + (i.quantity || 0), 0)} items
              </div>
            </div>
            
            {/* Add Product */}
            <div>
              <h4 className="font-medium text-slate-200 mb-2">Add Product</h4>
              <div className="flex flex-wrap gap-2">
                {availableEcardProducts.map((product) => (
                  <Button
                    key={product.product_number}
                    size="sm"
                    variant="outline"
                    onClick={() => addBulkItem(product)}
                    className="border-slate-600 text-slate-300 hover:bg-slate-700"
                  >
                    <Plus className="h-3 w-3 mr-1" />
                    {product.product_name || product.product_number}
                  </Button>
                ))}
              </div>
            </div>
            
            {/* Edit Reason */}
            <div>
              <Label className="text-white">Reason for Edit *</Label>
              <Textarea
                value={bulkOrderEditReason}
                onChange={(e) => setBulkOrderEditReason(e.target.value)}
                placeholder="Enter reason for changes..."
                className="mt-2 bg-slate-700 border-teal-600 text-slate-100 min-h-[60px]"
              />
              <p className="text-xs text-slate-500 mt-1">This will be saved in the change log</p>
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={cancelEditingBulkOrder}
              className="border-teal-600 text-white hover:bg-slate-700"
            >
              Cancel
            </Button>
            <Button
              onClick={saveBulkOrderEdits}
              disabled={savingBulkOrderEdit || !bulkOrderEditReason || bulkOrderEditReason.trim().length < 3 || editedBulkItems.length === 0}
              className="bg-blue-600 hover:bg-blue-500"
            >
              {savingBulkOrderEdit ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save Changes
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Order Status Change Dialog */}
      <Dialog open={!!bulkStatusChangeOrder} onOpenChange={(open) => { if (!open) { setBulkStatusChangeOrder(null); setBulkStatusChangeReason(''); } }}>
        <DialogContent className="sm:max-w-md bg-slate-800 border-slate-700 text-slate-100">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {bulkStatusChangeNew === 'pending' && <Clock className="h-5 w-5 text-amber-400" />}
              {bulkStatusChangeNew === 'ordered' && <Package className="h-5 w-5 text-blue-400" />}
              {bulkStatusChangeNew === 'on_hold' && <Pause className="h-5 w-5 text-orange-400" />}
              {bulkStatusChangeNew === 'received' && <CheckCircle className="h-5 w-5 text-green-400" />}
              {bulkStatusChangeNew === 'failed' && <XCircle className="h-5 w-5 text-red-400" />}
              Change Bulk Order Status
            </DialogTitle>
            <DialogDescription className="text-white">
              {bulkStatusChangeOrder && (
                <>
                  Changing order <span className="font-mono text-white">#{bulkStatusChangeOrder.id}</span> from{' '}
                  <span className="font-medium text-white">{bulkStatusChangeOrder.status}</span> to{' '}
                  <span className={`font-medium ${
                    bulkStatusChangeNew === 'pending' ? 'text-amber-400' :
                    bulkStatusChangeNew === 'ordered' ? 'text-blue-400' :
                    bulkStatusChangeNew === 'on_hold' ? 'text-orange-400' :
                    bulkStatusChangeNew === 'received' ? 'text-green-400' :
                    'text-red-400'
                  }`}>{bulkStatusChangeNew === 'on_hold' ? 'On Hold' : bulkStatusChangeNew}</span>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div>
              <Label className="text-white">Reason for change *</Label>
              <Textarea
                value={bulkStatusChangeReason}
                onChange={(e) => setBulkStatusChangeReason(e.target.value)}
                placeholder="Enter reason for status change..."
                className="mt-2 bg-slate-700 border-teal-600 text-slate-100 min-h-[80px]"
              />
              <p className="text-xs text-slate-500 mt-1">This will be saved in the change log</p>
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => { setBulkStatusChangeOrder(null); setBulkStatusChangeReason(''); }}
              className="border-teal-600 text-white hover:bg-slate-700"
            >
              Cancel
            </Button>
            <Button
              onClick={submitBulkStatusChange}
              disabled={savingBulkStatusChange || !bulkStatusChangeReason || bulkStatusChangeReason.trim().length < 3}
              className={`${
                bulkStatusChangeNew === 'pending' ? 'bg-amber-600 hover:bg-amber-500' :
                bulkStatusChangeNew === 'ordered' ? 'bg-blue-600 hover:bg-blue-500' :
                bulkStatusChangeNew === 'on_hold' ? 'bg-orange-600 hover:bg-orange-500' :
                bulkStatusChangeNew === 'received' ? 'bg-green-600 hover:bg-green-500' :
                'bg-red-600 hover:bg-red-500'
              }`}
            >
              {savingBulkStatusChange ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
              Confirm Change
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Instructor eCard Orders Dialog */}
      <Dialog open={showInstructorOrders} onOpenChange={(open) => { setShowInstructorOrders(open); if (!open) { setSelectedOrderIds([]); setOrderStatusFilter('all'); } }}>
        <DialogContent className="sm:max-w-4xl bg-slate-800 border-slate-700 text-slate-100 max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <ShoppingBag className="h-5 w-5 text-yellow-400" />
              Instructor eCard Orders
            </DialogTitle>
            <DialogDescription className="text-white">
              Manage and fulfill instructor eCard orders. Sorted by status (Pending → Ordered → Complete), then by date.
            </DialogDescription>
          </DialogHeader>
          
          {/* EMERGENCY STOP - Prominent at top for quick access */}
          <div className="bg-red-950/50 border border-red-700 rounded-lg p-3 flex items-center justify-between">
            <div className="flex items-center gap-2">
              <StopCircle className="h-5 w-5 text-red-400" />
              <span className="text-red-300 text-sm font-medium">Browser Automation Controls</span>
            </div>
            <Button
              onClick={emergencyStopBrowser}
              disabled={emergencyStopLoading}
              className="bg-red-700 hover:bg-red-600 text-white font-bold"
              data-testid="emergency-stop-top-btn"
            >
              {emergencyStopLoading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <StopCircle className="h-4 w-4 mr-2" />
              )}
              EMERGENCY STOP
            </Button>
          </div>
          
          <div className="py-4">
            {/* Filter tabs and actions */}
            <div className="flex items-center justify-between mb-4">
              <div className="flex gap-2 flex-wrap">
                <Button
                  variant={orderStatusFilter === 'all' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => { fetchInstructorOrders(); setSelectedOrderIds([]); }}
                  className={orderStatusFilter === 'all' 
                    ? 'bg-slate-600 text-white' 
                    : 'border-teal-600 text-white hover:bg-slate-700'}
                  data-testid="filter-all-orders"
                >
                  All Orders
                </Button>
                <Button
                  variant={orderStatusFilter === 'pending' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => { fetchInstructorOrders('pending'); setSelectedOrderIds([]); }}
                  className={orderStatusFilter === 'pending' 
                    ? 'bg-amber-600 text-white' 
                    : 'border-amber-600 text-amber-400 hover:bg-amber-900/30'}
                  data-testid="filter-pending-orders"
                >
                  Pending
                </Button>
                <Button
                  variant={orderStatusFilter === 'ordered' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => { fetchInstructorOrders('ordered'); setSelectedOrderIds([]); }}
                  className={orderStatusFilter === 'ordered' 
                    ? 'bg-blue-600 text-white' 
                    : 'border-teal-600 text-white hover:bg-blue-900/30'}
                  data-testid="filter-ordered-orders"
                >
                  Ordered From TC
                </Button>
                <Button
                  variant={orderStatusFilter === 'fulfilled' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => { fetchInstructorOrders('fulfilled'); setSelectedOrderIds([]); }}
                  className={orderStatusFilter === 'fulfilled' 
                    ? 'bg-emerald-600 text-white' 
                    : 'border-teal-600 text-white hover:bg-emerald-900/30'}
                  data-testid="filter-complete-orders"
                >
                  Complete
                </Button>
                <Button
                  variant={orderStatusFilter === 'ready_to_fill' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => { fetchInstructorOrders('ready_to_fill'); setSelectedOrderIds([]); }}
                  className={orderStatusFilter === 'ready_to_fill' 
                    ? 'bg-cyan-600 text-white' 
                    : 'border-cyan-600 text-cyan-400 hover:bg-cyan-900/30'}
                  data-testid="filter-ready-to-fill-orders"
                >
                  Ready to Fill
                </Button>
                <Button
                  variant={orderStatusFilter === 'cancelled' ? 'default' : 'outline'}
                  size="sm"
                  onClick={() => { fetchInstructorOrders('cancelled'); setSelectedOrderIds([]); }}
                  className={orderStatusFilter === 'cancelled' 
                    ? 'bg-red-600 text-white' 
                    : 'border-red-600 text-red-400 hover:bg-red-900/30'}
                  data-testid="filter-cancelled-orders"
                >
                  Cancelled
                </Button>
              </div>
              
              {/* Auto-refresh toggle */}
              <div className="flex items-center gap-2">
                <label className="flex items-center gap-2 text-xs text-slate-400 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={autoRefreshOrders}
                    onChange={(e) => setAutoRefreshOrders(e.target.checked)}
                    className="w-4 h-4 rounded border-slate-600 bg-slate-700 text-cyan-500 focus:ring-cyan-500"
                  />
                  <span className={autoRefreshOrders ? 'text-cyan-400' : ''}>
                    Live Updates {autoRefreshOrders && '(every 5s)'}
                  </span>
                </label>
              </div>
              
              {/* Bulk actions */}
              {instructorOrders.some(o => o.order_status === 'pending' || o.order_status === 'ordered') && (
                <div className="flex gap-2">
                  {/* Refresh Supplier Statuses */}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={async () => {
                      try {
                        toast.loading('Starting status check...', { id: 'supplier-status', duration: 2000 });
                        const response = await axios.post(`${API}/admin/ecard-order-history/refresh-supplier-statuses`);
                        toast.dismiss('supplier-status');
                        if (response.data.success) {
                          toast.success(response.data.message || 'Status check started! Refresh in a moment.', { duration: 5000 });
                          // Auto-refresh after 10 seconds to show updated statuses
                          setTimeout(() => {
                            fetchInstructorOrders(orderStatusFilter === 'all' ? null : orderStatusFilter);
                          }, 10000);
                        } else {
                          toast.error(response.data.error || 'Status check failed');
                        }
                      } catch (error) {
                        toast.dismiss('supplier-status');
                        toast.error('Failed to start status check');
                      }
                    }}
                    className="border-green-600 text-green-400 hover:bg-green-900/30"
                    data-testid="refresh-supplier-statuses-btn"
                  >
                    <RefreshCw className="h-4 w-4 mr-1" />
                    Refresh Supplier Status
                  </Button>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={selectAllPendingOrders}
                    className="border-teal-600 text-white hover:bg-slate-700"
                  >
                    Select All
                  </Button>
                  {selectedOrderIds.length > 0 && (
                    <>
                      <Button
                        size="sm"
                        onClick={generateSupplierCart}
                        disabled={supplierCartLoading}
                        className="bg-teal-600 hover:bg-teal-500"
                      >
                        {supplierCartLoading ? (
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                        ) : (
                          <ShoppingCart className="h-4 w-4 mr-2" />
                        )}
                        Order from Supplier ({selectedOrderIds.length})
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => updateOrdersStatus('fulfilled')}
                        className="bg-teal-600 hover:bg-teal-500"
                      >
                        <CheckCircle className="h-4 w-4 mr-2" />
                        Mark Complete ({selectedOrderIds.length})
                      </Button>
                    </>
                  )}
                </div>
              )}
            </div>

            {instructorOrdersLoading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="h-8 w-8 animate-spin text-yellow-400" />
              </div>
            ) : instructorOrders.length === 0 ? (
              <div className="text-center py-8 text-white">
                <ShoppingBag className="h-12 w-12 mx-auto mb-4 text-slate-500" />
                <p>No orders found</p>
              </div>
            ) : (
              <div className="space-y-3">
                {/* Render orders with section headers when viewing all */}
                {(() => {
                  let lastSection = null;
                  const statusLabels = {
                    'ready_to_fill': { label: 'Ready to Fill', color: 'text-cyan-400', bg: 'bg-cyan-900/30', isOpen: true },
                    'pending': { label: 'Pending Orders', color: 'text-amber-400', bg: 'bg-amber-900/30', isOpen: true },
                    'ordered': { label: 'Ordered From TC', color: 'text-white', bg: 'bg-blue-900/30', isOpen: true },
                    'fulfilled': { label: 'Completed Orders', color: 'text-white', bg: 'bg-emerald-900/30', isOpen: false },
                    'cancelled': { label: 'Cancelled Orders', color: 'text-red-400', bg: 'bg-red-900/30', isOpen: false }
                  };
                  
                  // Helper to determine section
                  const getSection = (order) => {
                    // First check for explicit fulfilled/cancelled status - these are closed
                    if (order.order_status === 'fulfilled' || order.order_status === 'cancelled') {
                      return order.order_status;
                    }
                    // Check for ready_to_fill: explicit status OR ordered with supplier complete
                    if (order.order_status === 'ready_to_fill' ||
                        (order.order_status === 'ordered' && 
                         order.supplier_status && 
                         order.supplier_status.toLowerCase().includes('complete'))) {
                      return 'ready_to_fill';
                    }
                    return order.order_status;
                  };
                  
                  // Check if section is collapsed (closed orders: fulfilled, cancelled)
                  const isSectionCollapsed = (section) => {
                    return collapsedSections[section] === true;
                  };
                  
                  // Toggle section collapse
                  const toggleSection = (section) => {
                    setCollapsedSections(prev => ({ ...prev, [section]: !prev[section] }));
                  };
                  
                  return instructorOrders.map((order, idx) => {
                    const currentSection = getSection(order);
                    const showHeader = orderStatusFilter === 'all' && currentSection !== lastSection;
                    const isFirstOfSection = currentSection !== lastSection;
                    lastSection = currentSection;
                    const statusInfo = statusLabels[currentSection] || { label: order.order_status, color: 'text-white', bg: 'bg-slate-700', isOpen: true };
                    const isCollapsed = isSectionCollapsed(currentSection);
                    const isClosedSection = currentSection === 'fulfilled' || currentSection === 'cancelled';
                    
                    return (
                      <React.Fragment key={order.id}>
                        {/* Section header when status changes */}
                        {showHeader && (
                          <>
                            {/* Divider before closed orders */}
                            {isClosedSection && (
                              <div className="border-t-2 border-slate-600 my-4 pt-2">
                                <span className="text-xs text-slate-400 uppercase tracking-wider">Closed Orders</span>
                              </div>
                            )}
                            <div 
                              className={`px-3 py-2 rounded-lg ${statusInfo.bg} flex items-center justify-between cursor-pointer hover:opacity-80`}
                              onClick={() => isClosedSection && toggleSection(currentSection)}
                            >
                              <div className="flex items-center gap-2">
                                {isClosedSection && (
                                  <ChevronDown className={`h-4 w-4 ${statusInfo.color} transition-transform ${isCollapsed ? '-rotate-90' : ''}`} />
                                )}
                                <span className={`font-semibold text-sm ${statusInfo.color}`}>
                                  {statusInfo.label}
                                </span>
                              </div>
                              <span className="text-xs text-white">
                                {instructorOrders.filter(o => getSection(o) === currentSection).length} order(s)
                              </span>
                            </div>
                          </>
                        )}
                        
                        {/* Only render order if section is not collapsed */}
                        {(!isClosedSection || !isCollapsed) && (
                        <div 
                          data-testid={`order-row-${order.id}`}
                          className={`bg-slate-700/50 rounded-lg p-4 border transition-colors ${
                            selectedOrderIds.includes(order.id) ? 'border-blue-500 bg-blue-900/20' :
                            currentSection === 'ready_to_fill' ? 'border-cyan-500 bg-cyan-900/20 hover:border-cyan-400' :
                            order.order_status === 'pending' ? 'border-amber-600/50 hover:border-slate-500' :
                            order.order_status === 'ordered' ? 'border-teal-600/50 hover:border-slate-500' :
                            order.order_status === 'on_hold' ? 'border-orange-600/50 hover:border-slate-500' :
                            order.order_status === 'fulfilled' ? 'border-teal-600/50' :
                            'border-teal-600'
                          }`}
                        >
                    <div className="flex items-center gap-4">
                      {/* Checkbox for pending, ordered, or on_hold orders */}
                      {(order.order_status === 'pending' || order.order_status === 'ordered' || order.order_status === 'on_hold') && (
                        <input
                          type="checkbox"
                          checked={selectedOrderIds.includes(order.id)}
                          onChange={() => toggleOrderSelection(order.id)}
                          className="h-5 w-5 rounded border-teal-600 bg-slate-700 text-blue-500 cursor-pointer"
                        />
                      )}
                      
                      <div 
                        className="flex-1 cursor-pointer"
                        onClick={() => setSelectedInstructorOrder(order)}
                      >
                        <div className="flex items-center justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <span className="font-mono text-sm text-white">{order.id}</span>
                              <span className={`text-xs px-2 py-0.5 rounded ${
                                order.order_status === 'pending' ? 'bg-amber-900/50 text-amber-400' :
                                order.order_status === 'ordered' ? 'bg-blue-900/50 text-white' :
                                order.order_status === 'on_hold' ? 'bg-orange-900/50 text-orange-400' :
                                order.order_status === 'fulfilled' ? 'bg-emerald-900/50 text-white' :
                                'bg-red-900/50 text-red-400'
                              }`}>
                                {order.order_status === 'ordered' ? 'Ordered From TC' : order.order_status === 'on_hold' ? 'On Hold' : order.order_status}
                              </span>
                              {/* Retry Auto-Order button - visible for pending, disabled for others */}
                              <button
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  if (order.order_status !== 'pending') return;
                                  try {
                                    toast.loading('Retrying auto-order...', { id: `retry-${order.id}` });
                                    await axios.post(`${API}/admin/instructor-ecard-orders/${order.id}/retry-auto-order`);
                                    toast.dismiss(`retry-${order.id}`);
                                    toast.success('Auto-order retry started! Check status in a moment.');
                                    setTimeout(() => fetchInstructorOrders(orderStatusFilter === 'all' ? null : orderStatusFilter), 5000);
                                  } catch (error) {
                                    toast.dismiss(`retry-${order.id}`);
                                    toast.error(error.response?.data?.detail || 'Failed to retry auto-order');
                                  }
                                }}
                                disabled={order.order_status !== 'pending'}
                                className={`text-xs px-2 py-0.5 rounded flex items-center gap-1 ${
                                  order.order_status === 'pending' 
                                    ? 'bg-orange-600 hover:bg-orange-500 text-white cursor-pointer' 
                                    : 'bg-slate-700 text-slate-500 cursor-not-allowed'
                                }`}
                              >
                                <RotateCcw className="h-3 w-3" />
                                Retry TC Order
                              </button>
                              {order.woo_order_id && (
                                <span className="text-xs px-2 py-0.5 rounded bg-purple-900/50 text-white">
                                  Woo #{order.woo_order_id}
                                </span>
                              )}
                              {order.supplier_order_number && (
                                <span className="text-xs px-2 py-0.5 rounded bg-indigo-900/50 text-indigo-300">
                                  TC #{order.supplier_order_number}
                                </span>
                              )}
                            </div>
                            <p className="text-sm text-white mt-1">{order.instructor_name} • {order.instructor_email}</p>
                            <p className="text-xs text-slate-500">{new Date(order.created_at).toLocaleString()}</p>
                            {/* Supplier Status Display */}
                            {order.supplier_status && (
                              <p className="text-xs mt-1">
                                <span className="text-slate-400">CPR Suppliers: </span>
                                <span className={`font-medium ${
                                  order.supplier_status.toLowerCase().includes('complete') ? 'text-cyan-400' : 
                                  order.supplier_status.toLowerCase().includes('hold') ? 'text-orange-400' : 'text-amber-400'
                                }`}>
                                  {order.supplier_status}
                                  {order.supplier_status.toLowerCase().includes('complete') && order.order_status === 'ordered' && (
                                    <span className="ml-2 text-cyan-300">(Ready to fill inventory)</span>
                                  )}
                                  {order.supplier_status.toLowerCase().includes('hold') && (
                                    <span className="ml-2 text-orange-300">(Order on hold at supplier)</span>
                                  )}
                                </span>
                              </p>
                            )}
                            {/* Check Status from CPR Suppliers */}
                            {order.supplier_order_number && order.order_status !== 'fulfilled' && order.order_status !== 'cancelled' && (
                              <button
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  try {
                                    toast.loading('Checking CPR Suppliers website...', { id: `check-${order.id}` });
                                    const response = await axios.post(`${API}/admin/instructor-ecard-orders/check-single-order/${order.supplier_order_number}`);
                                    toast.dismiss(`check-${order.id}`);
                                    if (response.data.success) {
                                      toast.success(`Status: ${response.data.supplier_status || 'Updated'}`, { duration: 5000 });
                                      fetchInstructorOrders(orderStatusFilter === 'all' ? null : orderStatusFilter);
                                    } else {
                                      toast.error(response.data.error || 'Could not check status');
                                    }
                                  } catch (error) {
                                    toast.dismiss(`check-${order.id}`);
                                    toast.error(error.response?.data?.detail || 'Failed to check status');
                                  }
                                }}
                                className="text-xs mt-1 px-2 py-0.5 bg-blue-600 hover:bg-blue-500 text-white rounded mr-1"
                                data-testid={`check-status-btn-${order.id}`}
                              >
                                🔄 Check Status
                              </button>
                            )}
                            {/* TEMP: Force refresh button for order 148383 - REMOVE NEXT DEPLOYMENT */}
                            {order.supplier_order_number && order.order_status === 'ordered' && !order.supplier_status?.toLowerCase().includes('complete') && (
                              <button
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  try {
                                    toast.loading('Force updating...', { id: `force-${order.id}` });
                                    await axios.post(`${API}/admin/instructor-ecard-orders/force-update-supplier-status/${order.supplier_order_number}?status=Completed`);
                                    toast.dismiss(`force-${order.id}`);
                                    toast.success('Status updated to Completed!');
                                    fetchInstructorOrders(orderStatusFilter === 'all' ? null : orderStatusFilter);
                                  } catch (error) {
                                    toast.dismiss(`force-${order.id}`);
                                    toast.error('Failed to update status');
                                  }
                                }}
                                className="text-xs mt-1 px-2 py-0.5 bg-orange-600 hover:bg-orange-500 text-white rounded"
                              >
                                ⚡ Force Complete
                              </button>
                            )}
                            {order.status_change_reason && (
                              <p className="text-xs text-slate-500 mt-1 italic">
                                Reason: {order.status_change_reason}
                              </p>
                            )}
                            {/* Admin Notes Display */}
                            {order.admin_notes && (
                              <div className="mt-1">
                                <p className={`text-xs p-1 rounded ${
                                  order.admin_notes.includes('SUCCESS') ? 'bg-emerald-900/30 text-emerald-400' :
                                  order.admin_notes.includes('ERROR') || order.admin_notes.includes('FAILED') ? 'bg-red-900/30 text-red-400' :
                                  'bg-slate-700 text-slate-400'
                                }`}>
                                  {order.admin_notes}
                                </p>
                                {/* Full Log Display with Clear button */}
                                {order.admin_notes_log && order.admin_notes_log.length > 0 && (
                                  <div className="flex items-center gap-2 mt-1">
                                    <details className="flex-1" onClick={(e) => e.stopPropagation()}>
                                      <summary className="text-xs text-cyan-400 cursor-pointer hover:text-cyan-300">
                                        View full log ({order.admin_notes_log.length} entries)
                                      </summary>
                                      <div className="mt-1 p-2 bg-slate-900 rounded text-xs text-slate-400 max-h-40 overflow-y-auto">
                                        {[...order.admin_notes_log].reverse().map((log, idx) => (
                                          <div key={idx} className={`py-0.5 ${log.includes('ERROR') ? 'text-red-400' : log.includes('SUCCESS') || log.includes('✓') ? 'text-emerald-400' : ''}`}>
                                            {log}
                                          </div>
                                        ))}
                                      </div>
                                    </details>
                                    <button
                                      onClick={async (e) => {
                                        e.stopPropagation();
                                        try {
                                          await axios.post(`${API}/admin/instructor-ecard-orders/${order.id}/clear-log`);
                                          toast.success('Log cleared');
                                          fetchInstructorOrders(orderStatusFilter === 'all' ? null : orderStatusFilter);
                                        } catch (error) {
                                          toast.error('Failed to clear log');
                                        }
                                      }}
                                      className="text-xs px-2 py-0.5 bg-slate-700 hover:bg-slate-600 text-slate-300 rounded flex items-center gap-1"
                                      title="Clear log entries"
                                    >
                                      <Trash2 className="h-3 w-3" />
                                      Clear
                                    </button>
                                  </div>
                                )}
                              </div>
                            )}
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold text-white">${order.total?.toFixed(2)}</p>
                            <p className="text-xs text-slate-500">{order.items?.length || 0} items</p>
                          </div>
                        </div>
                      </div>
                      
                      {/* Status Change Dropdown and Filled Button - Outside clickable area */}
                      <div onClick={(e) => e.stopPropagation()} className="flex items-center gap-2">
                        {/* Filled Button - Shows when order is ready_to_fill (explicit status OR ordered with supplier complete) */}
                        {(order.order_status === 'ready_to_fill' || (order.supplier_status && order.supplier_status.toLowerCase().includes('complete') && order.order_status === 'ordered')) && (
                          <Button
                            size="sm"
                            onClick={async (e) => {
                              e.stopPropagation();
                              try {
                                toast.loading('Marking as filled...', { id: `fill-${order.id}` });
                                const response = await axios.post(`${API}/admin/ecard-order-history/${order.id}/mark-filled?order_type=instructor`);
                                toast.dismiss(`fill-${order.id}`);
                                if (response.data.success) {
                                  toast.success('Order marked as filled! Instructor notified.');
                                  fetchInstructorOrders(orderStatusFilter === 'all' ? null : orderStatusFilter);
                                }
                              } catch (error) {
                                toast.dismiss(`fill-${order.id}`);
                                toast.error('Failed to mark as filled');
                              }
                            }}
                            className="bg-cyan-600 hover:bg-cyan-500 text-xs h-8"
                            data-testid={`filled-btn-${order.id}`}
                          >
                            <CheckCircle className="h-3 w-3 mr-1" />
                            Filled
                          </Button>
                        )}
                        <DropdownMenu>
                          <DropdownMenuTrigger asChild>
                            <Button variant="outline" size="sm" className="h-8 px-2 text-xs border-teal-600 text-white hover:text-slate-200 hover:bg-slate-700">
                              Change
                              <ChevronDown className="h-3 w-3 ml-1" />
                            </Button>
                          </DropdownMenuTrigger>
                          <DropdownMenuContent className="bg-slate-800 border-slate-700">
                            <DropdownMenuItem 
                              onClick={(e) => { e.stopPropagation(); openStatusChange(order, 'pending'); }}
                              className={`focus:bg-slate-700 ${order.order_status === 'pending' ? 'text-amber-400 bg-amber-900/30' : 'text-amber-400 focus:text-amber-300'}`}
                              disabled={order.order_status === 'pending'}
                            >
                              <Clock className="h-3 w-3 mr-2" />
                              Pending {order.order_status === 'pending' && '(current)'}
                            </DropdownMenuItem>
                            {/* TEMP: Retry Auto-Order button for pending orders - REMOVE NEXT DEPLOYMENT */}
                            {order.order_status === 'pending' && (
                              <DropdownMenuItem 
                                onClick={async (e) => {
                                  e.stopPropagation();
                                  try {
                                    toast.loading('Retrying auto-order...', { id: `retry-${order.id}` });
                                    await axios.post(`${API}/admin/instructor-ecard-orders/${order.id}/retry-auto-order`);
                                    toast.dismiss(`retry-${order.id}`);
                                    toast.success('Auto-order retry started! Check status in a moment.');
                                    setTimeout(() => fetchInstructorOrders(orderStatusFilter === 'all' ? null : orderStatusFilter), 5000);
                                  } catch (error) {
                                    toast.dismiss(`retry-${order.id}`);
                                    toast.error(error.response?.data?.detail || 'Failed to retry auto-order');
                                  }
                                }}
                                className="focus:bg-slate-700 text-orange-400 focus:text-orange-300"
                              >
                                <RotateCcw className="h-3 w-3 mr-2" />
                                Retry Auto-Order from TC
                              </DropdownMenuItem>
                            )}
                            {/* Manual TC Order Entry - for pending orders without a supplier order number */}
                            {order.order_status === 'pending' && !order.supplier_order_number && (
                              <DropdownMenuItem 
                                onClick={(e) => { e.stopPropagation(); openManualOrderDialog(order); }}
                                className="focus:bg-slate-700 text-emerald-400 focus:text-emerald-300"
                              >
                                <Edit className="h-3 w-3 mr-2" />
                                Enter TC Order # Manually
                              </DropdownMenuItem>
                            )}
                            <DropdownMenuItem 
                              onClick={(e) => { e.stopPropagation(); openStatusChange(order, 'ordered'); }}
                              className={`focus:bg-slate-700 ${order.order_status === 'ordered' ? 'text-white bg-blue-900/30' : 'text-white focus:text-blue-300'}`}
                              disabled={order.order_status === 'ordered'}
                            >
                              <Package className="h-3 w-3 mr-2" />
                              Ordered From TC {order.order_status === 'ordered' && '(current)'}
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={(e) => { e.stopPropagation(); openStatusChange(order, 'on_hold'); }}
                              className={`focus:bg-slate-700 ${order.order_status === 'on_hold' ? 'text-orange-400 bg-orange-900/30' : 'text-orange-400 focus:text-orange-300'}`}
                              disabled={order.order_status === 'on_hold'}
                            >
                              <Pause className="h-3 w-3 mr-2" />
                              On Hold {order.order_status === 'on_hold' && '(current)'}
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={(e) => { e.stopPropagation(); openStatusChange(order, 'ready_to_fill'); }}
                              className={`focus:bg-slate-700 ${order.supplier_status?.toLowerCase().includes('complete') && order.order_status === 'ordered' ? 'text-cyan-400 bg-cyan-900/30' : 'text-cyan-400 focus:text-cyan-300'}`}
                            >
                              <Inbox className="h-3 w-3 mr-2" />
                              Ready to Fill {order.supplier_status?.toLowerCase().includes('complete') && order.order_status === 'ordered' && '(current)'}
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={(e) => { e.stopPropagation(); openStatusChange(order, 'fulfilled'); }}
                              className={`focus:bg-slate-700 ${order.order_status === 'fulfilled' ? 'text-white bg-emerald-900/30' : 'text-white focus:text-emerald-300'}`}
                              disabled={order.order_status === 'fulfilled'}
                            >
                              <CheckCircle className="h-3 w-3 mr-2" />
                              Complete {order.order_status === 'fulfilled' && '(current)'}
                            </DropdownMenuItem>
                            <DropdownMenuItem 
                              onClick={(e) => { e.stopPropagation(); openStatusChange(order, 'cancelled'); }}
                              className={`focus:bg-slate-700 ${order.order_status === 'cancelled' ? 'text-red-400 bg-red-900/30' : 'text-red-400 focus:text-red-300'}`}
                              disabled={order.order_status === 'cancelled'}
                            >
                              <XCircle className="h-3 w-3 mr-2" />
                              Cancelled {order.order_status === 'cancelled' && '(current)'}
                            </DropdownMenuItem>
                          </DropdownMenuContent>
                        </DropdownMenu>
                      </div>
                    </div>
                  </div>
                        )}
                </React.Fragment>
              );
            });
          })()}
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Status Change Confirmation Dialog */}
      <Dialog open={!!statusChangeOrder} onOpenChange={(open) => { if (!open) { setStatusChangeOrder(null); setStatusChangeReason(''); } }}>
        <DialogContent className="sm:max-w-md bg-slate-800 border-slate-700 text-slate-100">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              {statusChangeNewStatus === 'pending' && <RotateCcw className="h-5 w-5 text-amber-400" />}
              {statusChangeNewStatus === 'ordered' && <Package className="h-5 w-5 text-white" />}
              {statusChangeNewStatus === 'on_hold' && <Pause className="h-5 w-5 text-orange-400" />}
              {statusChangeNewStatus === 'ready_to_fill' && <Inbox className="h-5 w-5 text-cyan-400" />}
              {statusChangeNewStatus === 'fulfilled' && <CheckCircle className="h-5 w-5 text-white" />}
              {statusChangeNewStatus === 'cancelled' && <XCircle className="h-5 w-5 text-red-400" />}
              Change Order Status
            </DialogTitle>
            <DialogDescription className="text-white">
              {statusChangeOrder && (
                <>
                  Changing order <span className="font-mono text-white">{statusChangeOrder.id}</span> from{' '}
                  <span className="font-medium text-white">{statusChangeOrder.order_status === 'ordered' ? 'Ordered From TC' : statusChangeOrder.order_status === 'on_hold' ? 'On Hold' : statusChangeOrder.order_status}</span> to{' '}
                  <span className={`font-medium ${
                    statusChangeNewStatus === 'pending' ? 'text-amber-400' :
                    statusChangeNewStatus === 'ordered' ? 'text-white' :
                    statusChangeNewStatus === 'on_hold' ? 'text-orange-400' :
                    statusChangeNewStatus === 'ready_to_fill' ? 'text-cyan-400' :
                    statusChangeNewStatus === 'fulfilled' ? 'text-white' :
                    'text-red-400'
                  }`}>{statusChangeNewStatus === 'ordered' ? 'Ordered From TC' : statusChangeNewStatus === 'ready_to_fill' ? 'Ready to Fill' : statusChangeNewStatus === 'on_hold' ? 'On Hold' : statusChangeNewStatus}</span>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div>
              <Label className="text-white">Reason for change *</Label>
              <Textarea
                value={statusChangeReason}
                onChange={(e) => setStatusChangeReason(e.target.value)}
                placeholder="Enter reason for status change..."
                className="mt-2 bg-slate-700 border-teal-600 text-slate-100 min-h-[80px]"
              />
              <p className="text-xs text-slate-500 mt-1">This will be saved with the order history</p>
            </div>
          </div>
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => { setStatusChangeOrder(null); setStatusChangeReason(''); }}
              className="border-teal-600 text-white hover:bg-slate-700"
            >
              Cancel
            </Button>
            <Button
              onClick={submitStatusChange}
              disabled={statusChangeLoading || !statusChangeReason || statusChangeReason.trim().length < 3}
              className={`${
                statusChangeNewStatus === 'pending' ? 'bg-teal-600 hover:bg-teal-500' :
                statusChangeNewStatus === 'fulfilled' ? 'bg-teal-600 hover:bg-teal-500' :
                statusChangeNewStatus === 'on_hold' ? 'bg-orange-600 hover:bg-orange-500' :
                'bg-red-600 hover:bg-red-500'
              }`}
            >
              {statusChangeLoading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : null}
              Confirm Change
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Manual Supplier Order Entry Dialog */}
      <Dialog open={manualOrderDialogOpen} onOpenChange={(open) => { if (!open) { setManualOrderDialogOpen(false); setManualOrderTarget(null); } }}>
        <DialogContent className="sm:max-w-md bg-slate-800 border-slate-700 text-slate-100">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <Edit className="h-5 w-5 text-emerald-400" />
              Enter TC Order Number
            </DialogTitle>
            <DialogDescription className="text-slate-400">
              {manualOrderTarget && (
                <>
                  Manually link order <span className="font-mono text-white">{manualOrderTarget.id}</span> to a CPR Suppliers order.
                  <br />
                  <span className="text-amber-400 text-xs mt-2 block">
                    Use this when you've manually placed the order on CPR Suppliers and want to track it here.
                  </span>
                </>
              )}
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            <div>
              <Label className="text-white">TC Order Number *</Label>
              <Input
                value={manualSupplierNumber}
                onChange={(e) => setManualSupplierNumber(e.target.value)}
                placeholder="e.g. 148500"
                className="mt-2 bg-slate-700 border-teal-600 text-slate-100 font-mono"
                autoFocus
              />
              <p className="text-xs text-slate-500 mt-1">Enter the order number from CPR Suppliers</p>
            </div>
            
            <div>
              <Label className="text-white">Notes (optional)</Label>
              <Textarea
                value={manualOrderNotes}
                onChange={(e) => setManualOrderNotes(e.target.value)}
                placeholder="e.g. Ordered manually due to auto-order timeout"
                className="mt-2 bg-slate-700 border-teal-600 text-slate-100 min-h-[60px]"
              />
            </div>

            {manualOrderTarget && (
              <div className="bg-slate-700/50 rounded-lg p-3 border border-slate-600">
                <p className="text-xs text-slate-400 mb-2">Order Summary:</p>
                <p className="text-sm text-white">{manualOrderTarget.instructor_name}</p>
                <p className="text-xs text-slate-400">{manualOrderTarget.instructor_email}</p>
                <p className="text-sm text-emerald-400 mt-1">${manualOrderTarget.total?.toFixed(2)} - {manualOrderTarget.items?.length || 0} items</p>
              </div>
            )}
          </div>
          
          <DialogFooter className="gap-2">
            <Button
              variant="outline"
              onClick={() => { setManualOrderDialogOpen(false); setManualOrderTarget(null); }}
              className="border-teal-600 text-white hover:bg-slate-700"
              disabled={savingManualOrder}
            >
              Cancel
            </Button>
            <Button
              onClick={submitManualSupplierOrder}
              disabled={savingManualOrder || !manualSupplierNumber.trim()}
              className="bg-emerald-600 hover:bg-emerald-500"
            >
              {savingManualOrder ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Syncing & Saving...
                </>
              ) : (
                <>
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Link to TC Order
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Supplier Cart Confirmation Dialog */}
      <Dialog open={showSupplierCartConfirm} onOpenChange={(open) => { if (!open) closeSupplierCartNoChange(); }}>
        <DialogContent className="sm:max-w-lg bg-slate-800 border-slate-700 text-slate-100">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <ShoppingCart className="h-5 w-5 text-white" />
              Supplier Cart Created
            </DialogTitle>
            <DialogDescription className="text-white">
              A cart has been created at CPR Suppliers. Complete the checkout in the new tab, then choose how to update the orders.
            </DialogDescription>
          </DialogHeader>
          
          {supplierCartData && (
            <div className="py-4 space-y-4">
              <div className="bg-slate-700/50 rounded-lg p-4 border border-teal-600">
                <h4 className="font-medium text-slate-200 mb-3">Cart Summary</h4>
                <div className="space-y-2">
                  {supplierCartData.cart_items?.map((item, idx) => (
                    <div key={idx} className="flex justify-between text-sm">
                      <span className="text-white">{item.name || item.description}</span>
                      <span className="text-slate-100">x{item.quantity}</span>
                    </div>
                  ))}
                </div>
                <div className="mt-3 pt-3 border-t border-teal-600">
                  <div className="flex justify-between text-sm font-medium">
                    <span className="text-white">Total Items</span>
                    <span className="text-white">{supplierCartData.total_items}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-white">Orders Selected</span>
                    <span className="text-white">{supplierCartData.order_ids?.length}</span>
                  </div>
                </div>
              </div>
              
              <div className="bg-blue-900/30 border border-teal-600/50 rounded-lg p-4">
                <p className="text-blue-300 text-sm">
                  <strong>Next Steps:</strong> Complete the checkout at CPR Suppliers in the new tab, 
                  then return here and select the appropriate status for these orders.
                </p>
              </div>
              
              <div className="flex gap-3">
                <Button
                  onClick={() => window.open(supplierCartData.checkout_url, '_blank')}
                  variant="outline"
                  className="flex-1 border-teal-600 text-white hover:bg-purple-900/30"
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  Open Checkout
                </Button>
              </div>
              
              <div className="border-t border-teal-600 pt-4">
                <p className="text-white text-sm mb-3">Update order status:</p>
                <div className="grid grid-cols-1 gap-2">
                  <Button
                    onClick={() => updateOrdersStatus('ordered')}
                    className="w-full bg-teal-600 hover:bg-teal-500 text-white"
                    data-testid="mark-ordered-btn"
                  >
                    <Package className="h-4 w-4 mr-2" />
                    Mark as Ordered From TC
                  </Button>
                  <Button
                    onClick={() => updateOrdersStatus('fulfilled')}
                    className="w-full bg-teal-600 hover:bg-teal-500 text-white"
                    data-testid="mark-complete-btn"
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Mark as Complete (Fulfilled)
                  </Button>
                  <Button
                    onClick={closeSupplierCartNoChange}
                    variant="outline"
                    className="w-full border-teal-600 text-white hover:bg-slate-700"
                    data-testid="no-change-btn"
                  >
                    No Change (Keep as Pending)
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Selected Instructor Order Detail Dialog */}
      <Dialog open={!!selectedInstructorOrder} onOpenChange={(open) => { if (!open) { setSelectedInstructorOrder(null); setNewNote(''); setNoteType('admin'); setEditingOrderItems(false); setEditedItems([]); setInventoryAvailability(null); setPriceChangeWarning(null); } }}>
        <DialogContent className="sm:max-w-2xl bg-slate-800 border-slate-700 text-slate-100 max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-slate-100 flex items-center gap-2">
              Order Details
              <span className={`text-xs px-2 py-0.5 rounded ${
                selectedInstructorOrder?.order_status === 'pending' ? 'bg-amber-900/50 text-amber-400' :
                selectedInstructorOrder?.order_status === 'ordered' ? 'bg-blue-900/50 text-white' :
                selectedInstructorOrder?.order_status === 'fulfilled' ? 'bg-emerald-900/50 text-white' :
                'bg-red-900/50 text-red-400'
              }`}>
                {selectedInstructorOrder?.order_status === 'ordered' ? 'Ordered From TC' : selectedInstructorOrder?.order_status}
              </span>
            </DialogTitle>
            <DialogDescription className="text-white">
              Order ID: {selectedInstructorOrder?.id}
            </DialogDescription>
          </DialogHeader>
          
          {selectedInstructorOrder && (
            <div className="py-4 space-y-4">
              {/* Customer Info */}
              <div className="bg-slate-700/50 rounded-lg p-4">
                <h4 className="font-medium text-slate-200 mb-2">Instructor Information</h4>
                <p className="text-sm text-white">{selectedInstructorOrder.instructor_name}</p>
                <p className="text-sm text-white">{selectedInstructorOrder.instructor_email}</p>
                {selectedInstructorOrder.instructor_phone && (
                  <p className="text-sm text-white">{selectedInstructorOrder.instructor_phone}</p>
                )}
                {selectedInstructorOrder.instructor_number && (
                  <p className="text-sm text-white">Instructor #: {selectedInstructorOrder.instructor_number}</p>
                )}
              </div>

              {/* Order Items */}
              <div className="bg-slate-700/50 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <h4 className="font-medium text-slate-200">Order Items</h4>
                  {selectedInstructorOrder.order_status === 'pending' && !editingOrderItems && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={startEditingOrderItems}
                      className="text-cyan-400 hover:text-cyan-300 h-7 px-2"
                      data-testid="edit-order-items-btn"
                    >
                      <Edit2 className="h-3 w-3 mr-1" />
                      Edit
                    </Button>
                  )}
                  {selectedInstructorOrder.order_status !== 'pending' && (
                    <span className="text-xs text-slate-500 italic">
                      (Items locked - status: {selectedInstructorOrder.order_status})
                    </span>
                  )}
                </div>
                
                {editingOrderItems ? (
                  <div className="space-y-3">
                    {editedItems.map((item, idx) => {
                      const isProductChanged = item.original_product_number && item.original_product_number !== item.product_number;
                      return (
                        <div key={idx} className="bg-slate-800/50 p-3 rounded space-y-2">
                          {/* Show original product with strikethrough if changed */}
                          {isProductChanged && (
                            <div className="text-xs text-red-400/70 flex items-center gap-1">
                              <span className="line-through">{item.original_product_name}</span>
                              <span className="text-slate-500">→</span>
                            </div>
                          )}
                          
                          <div className="flex items-center gap-2">
                            {/* Product dropdown */}
                            <select
                              value={item.product_number}
                              onChange={(e) => changeItemProduct(idx, e.target.value)}
                              className="flex-1 h-8 text-sm bg-slate-700 border border-teal-600 text-slate-100 rounded px-2"
                              data-testid={`edit-item-product-${idx}`}
                            >
                              {availableEcardProducts.map(product => (
                                <option key={product.product_number} value={product.product_number}>
                                  {product.product_name} (${product.price?.toFixed(2)})
                                </option>
                              ))}
                            </select>
                            
                            {/* Quantity input */}
                            <Input
                              type="number"
                              min="1"
                              value={item.quantity}
                              onChange={(e) => updateItemQuantity(idx, e.target.value)}
                              className="w-16 h-8 text-center bg-slate-700 border-teal-600 text-slate-100"
                              data-testid={`edit-item-qty-${idx}`}
                            />
                            
                            {/* Subtotal */}
                            <span className="text-sm text-slate-200 w-20 text-right">${item.subtotal?.toFixed(2)}</span>
                            
                            {/* Remove button */}
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeOrderItem(idx)}
                              className="text-red-400 hover:text-red-300 h-8 w-8 p-0"
                              disabled={editedItems.length <= 1}
                              data-testid={`remove-item-${idx}`}
                            >
                              <Trash2 className="h-3 w-3" />
                            </Button>
                          </div>
                          
                          {/* Show price difference for this item if product changed */}
                          {isProductChanged && item.original_price !== item.price && (
                            <div className="text-xs text-amber-400 flex items-center gap-1">
                              <AlertCircle className="h-3 w-3" />
                              Price change: ${item.original_price?.toFixed(2)} → ${item.price?.toFixed(2)}
                            </div>
                          )}
                        </div>
                      );
                    })}
                    
                    {/* Price change warning */}
                    {priceChangeWarning && (
                      <div className="bg-amber-900/30 border border-amber-600/50 rounded-lg p-3">
                        <div className="flex items-start gap-2">
                          <AlertCircle className="h-4 w-4 text-amber-400 mt-0.5" />
                          <div className="text-sm">
                            <p className="text-amber-300 font-medium">Price Change Warning</p>
                            <p className="text-amber-400/80 text-xs mt-1">
                              Original total: ${priceChangeWarning.originalTotal?.toFixed(2)} → 
                              New total: ${priceChangeWarning.newTotal?.toFixed(2)}
                              <span className={priceChangeWarning.difference > 0 ? 'text-red-400' : 'text-green-400'}>
                                {' '}({priceChangeWarning.difference > 0 ? '+' : ''}${priceChangeWarning.difference?.toFixed(2)})
                              </span>
                            </p>
                          </div>
                        </div>
                      </div>
                    )}
                    
                    {/* Add Item Button */}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={addOrderItem}
                      className="w-full border-dashed border-slate-500 text-white hover:text-slate-200 hover:border-slate-400"
                      data-testid="add-order-item-btn"
                    >
                      <Plus className="h-4 w-4 mr-2" />
                      Add Item
                    </Button>
                    
                    <div className="border-t border-teal-600 pt-2 mt-2 flex justify-between font-semibold">
                      <span className="text-slate-200">New Total</span>
                      <span className="text-cyan-400">${editedItems.reduce((sum, i) => sum + (i.subtotal || 0), 0).toFixed(2)}</span>
                    </div>
                    <div className="flex gap-2 pt-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={cancelEditingOrderItems}
                        className="border-teal-600 text-white"
                        disabled={savingOrderItems}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        onClick={saveOrderItems}
                        disabled={savingOrderItems}
                        className="bg-cyan-600 hover:bg-cyan-500"
                        data-testid="save-order-items-btn"
                      >
                        {savingOrderItems ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : null}
                        Save Changes
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    {selectedInstructorOrder.items?.map((item, idx) => {
                      const isProductChanged = item.original_product_number && item.original_product_number !== item.product_number;
                      return (
                        <div key={idx} className="text-sm">
                          {/* Show original with strikethrough if changed */}
                          {isProductChanged && (
                            <div className="flex justify-between text-red-400/60">
                              <span className="line-through">{item.original_product_name} x{item.quantity}</span>
                              <span className="line-through">${((item.original_price || 0) * item.quantity).toFixed(2)}</span>
                            </div>
                          )}
                          <div className="flex justify-between">
                            <span className={`text-white ${isProductChanged ? 'text-white' : ''}`}>
                              {isProductChanged && '→ '}{item.product_name} x{item.quantity}
                            </span>
                            <span className="text-slate-100">${item.subtotal?.toFixed(2)}</span>
                          </div>
                        </div>
                      );
                    })}
                    
                    {/* Show original total if items were modified */}
                    {selectedInstructorOrder.original_total && selectedInstructorOrder.original_total !== selectedInstructorOrder.total && (
                      <div className="border-t border-teal-600 pt-2 mt-2">
                        <div className="flex justify-between text-sm text-slate-500">
                          <span className="line-through">Original Total</span>
                          <span className="line-through">${selectedInstructorOrder.original_total?.toFixed(2)}</span>
                        </div>
                      </div>
                    )}
                    
                    <div className={`${selectedInstructorOrder.original_total && selectedInstructorOrder.original_total !== selectedInstructorOrder.total ? '' : 'border-t border-teal-600 pt-2 mt-2'} flex justify-between font-semibold`}>
                      <span className="text-slate-200">Total</span>
                      <span className="text-white">${selectedInstructorOrder.total?.toFixed(2)}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Fill from Inventory Option */}
              {(selectedInstructorOrder.order_status === 'pending' || selectedInstructorOrder.order_status === 'ordered') && (
                <div className="bg-purple-900/30 rounded-lg p-4 border border-purple-700/50">
                  <div className="flex items-center justify-between mb-3">
                    <div>
                      <h4 className="font-medium text-purple-300 flex items-center gap-2">
                        <Package className="h-4 w-4" />
                        Fill from Inventory
                      </h4>
                      <p className="text-xs text-white mt-1">
                        Use available eCard inventory to fulfill this order instantly
                      </p>
                    </div>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={checkInventoryForOrder}
                      disabled={checkingInventory}
                      className="border-teal-600 text-purple-300 hover:bg-purple-900/50"
                      data-testid="check-inventory-btn"
                    >
                      {checkingInventory ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                    </Button>
                  </div>
                  
                  {inventoryAvailability && (
                    <div className="space-y-2 mt-3">
                      <div className="text-sm space-y-1">
                        {inventoryAvailability.items?.map((item, idx) => (
                          <div key={idx} className={`flex justify-between py-1 px-2 rounded ${item.can_fill ? 'bg-emerald-900/30' : 'bg-red-900/30'}`}>
                            <span className={item.can_fill ? 'text-emerald-300' : 'text-red-300'}>
                              {item.product_name}
                            </span>
                            <span className={item.can_fill ? 'text-white' : 'text-red-400'}>
                              {item.can_fill ? '✓' : '✗'} Need {item.needed}, Have {item.available}
                              {!item.can_fill && <span className="text-red-500 ml-1">(Short {item.short})</span>}
                            </span>
                          </div>
                        ))}
                      </div>
                      
                      {inventoryAvailability.can_fill_all ? (
                        <Button
                          onClick={fillOrderFromInventory}
                          disabled={fillingFromInventory}
                          className="w-full bg-teal-600 hover:bg-teal-500 mt-2"
                          data-testid="fill-from-inventory-btn"
                        >
                          {fillingFromInventory ? (
                            <>
                              <Loader2 className="h-4 w-4 animate-spin mr-2" />
                              Filling Order...
                            </>
                          ) : (
                            <>
                              <CheckCircle className="h-4 w-4 mr-2" />
                              Fill Order & Mark Complete
                            </>
                          )}
                        </Button>
                      ) : (
                        <div className="bg-red-900/30 border border-red-700/50 rounded p-2 mt-2">
                          <p className="text-red-400 text-sm flex items-center gap-2">
                            <AlertCircle className="h-4 w-4" />
                            Cannot fill - insufficient inventory for some items
                          </p>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Payment Info */}
              <div className="bg-slate-700/50 rounded-lg p-4">
                <h4 className="font-medium text-slate-200 mb-2">Payment</h4>
                <p className="text-sm text-white">
                  Status: <span className={selectedInstructorOrder.payment_status === 'completed' || selectedInstructorOrder.payment_status === 'COMPLETED' ? 'text-white' : 'text-amber-400'}>
                    {selectedInstructorOrder.payment_status}
                  </span>
                </p>
                {selectedInstructorOrder.payment_id && (
                  <p className="text-xs text-slate-500 mt-1">Stripe ID: {selectedInstructorOrder.payment_id}</p>
                )}
                {selectedInstructorOrder.woo_order_id && (
                  <p className="text-xs text-slate-500 mt-1">WooCommerce Order: #{selectedInstructorOrder.woo_order_id}</p>
                )}
                <p className="text-xs text-slate-500 mt-1">Created: {new Date(selectedInstructorOrder.created_at).toLocaleString()}</p>
              </div>

              {/* Status History */}
              {selectedInstructorOrder.status_history && selectedInstructorOrder.status_history.length > 0 && (
                <div className="bg-slate-700/50 rounded-lg p-4">
                  <h4 className="font-medium text-slate-200 mb-2">Status History</h4>
                  <div className="space-y-2 max-h-40 overflow-y-auto">
                    {selectedInstructorOrder.status_history.map((entry, idx) => (
                      <div key={idx} className="text-sm border-l-2 border-teal-600 pl-3 py-1">
                        <div className="flex items-center gap-2">
                          <span className={`px-1.5 py-0.5 rounded text-xs ${
                            entry.to_status === 'pending' ? 'bg-amber-900/50 text-amber-400' :
                            entry.to_status === 'ordered' ? 'bg-blue-900/50 text-white' :
                            entry.to_status === 'fulfilled' ? 'bg-emerald-900/50 text-white' :
                            'bg-red-900/50 text-red-400'
                          }`}>{entry.from_status} → {entry.to_status}</span>
                          <span className="text-slate-500 text-xs">{new Date(entry.changed_at).toLocaleString()}</span>
                        </div>
                        {entry.reason && (
                          <p className="text-white text-xs mt-1 italic">Reason: {entry.reason}</p>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Change Log - Item Modifications */}
              {selectedInstructorOrder.change_log && selectedInstructorOrder.change_log.length > 0 && (
                <div className="bg-slate-700/50 rounded-lg p-4">
                  <h4 className="font-medium text-slate-200 mb-2 flex items-center gap-2">
                    <History className="h-4 w-4 text-cyan-400" />
                    Change Log
                  </h4>
                  <div className="space-y-3 max-h-60 overflow-y-auto">
                    {[...selectedInstructorOrder.change_log].reverse().map((entry, idx) => (
                      <div key={idx} className="text-sm border-l-2 border-cyan-600/50 pl-3 py-1">
                        <div className="flex items-center justify-between mb-1">
                          <span className={`px-1.5 py-0.5 rounded text-xs ${
                            entry.type === 'items_modified' ? 'bg-cyan-900/50 text-cyan-400' :
                            entry.type === 'status_change' ? 'bg-purple-900/50 text-white' :
                            'bg-slate-600 text-white'
                          }`}>
                            {entry.type === 'items_modified' ? 'Items Modified' : 
                             entry.type === 'status_change' ? 'Status Change' : entry.type}
                          </span>
                          <span className="text-slate-500 text-xs">{new Date(entry.timestamp).toLocaleString()}</span>
                        </div>
                        
                        {/* Show total change if items modified */}
                        {entry.type === 'items_modified' && entry.old_total !== entry.new_total && (
                          <p className="text-xs text-white mb-1">
                            Total: ${entry.old_total?.toFixed(2)} → ${entry.new_total?.toFixed(2)}
                            <span className={entry.new_total > entry.old_total ? 'text-red-400 ml-1' : 'text-green-400 ml-1'}>
                              ({entry.new_total > entry.old_total ? '+' : ''}${(entry.new_total - entry.old_total).toFixed(2)})
                            </span>
                          </p>
                        )}
                        
                        {/* Show individual changes */}
                        <div className="space-y-1">
                          {entry.changes?.map((change, cIdx) => (
                            <div key={cIdx} className="text-xs text-white flex items-center gap-1">
                              {change.action === 'added' && (
                                <>
                                  <Plus className="h-3 w-3 text-green-400" />
                                  <span className="text-green-400">Added:</span> {change.item} × {change.quantity} @ ${change.price?.toFixed(2)}
                                </>
                              )}
                              {change.action === 'removed' && (
                                <>
                                  <Trash2 className="h-3 w-3 text-red-400" />
                                  <span className="text-red-400">Removed:</span> {change.item} × {change.quantity}
                                </>
                              )}
                              {change.action === 'changed' && (
                                <>
                                  <RefreshCw className="h-3 w-3 text-amber-400" />
                                  <span className="text-amber-400">Changed:</span> {change.from_item} → {change.to_item}
                                </>
                              )}
                              {change.action === 'quantity_changed' && (
                                <>
                                  <Edit className="h-3 w-3 text-cyan-400" />
                                  <span className="text-cyan-400">Qty Changed:</span> {change.item}: {change.from_qty} → {change.to_qty}
                                </>
                              )}
                              {change.action === 'status_changed' && (
                                <>
                                  <RefreshCw className="h-3 w-3 text-white" />
                                  <span className="text-white">Status:</span> {change.from_status} → {change.to_status}
                                  {change.reason && <span className="italic ml-1">({change.reason})</span>}
                                </>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Order Notes */}
              <div className="bg-slate-700/50 rounded-lg p-4">
                <h4 className="font-medium text-slate-200 mb-2">Order Notes</h4>
                
                {/* Existing Notes */}
                {selectedInstructorOrder.notes && selectedInstructorOrder.notes.length > 0 ? (
                  <div className="space-y-2 mb-4 max-h-40 overflow-y-auto">
                    {selectedInstructorOrder.notes.map((note, idx) => (
                      <div key={idx} className={`text-sm p-2 rounded ${
                        note.note_type === 'customer' ? 'bg-blue-900/30 border border-teal-600/30' : 'bg-slate-600/50'
                      }`}>
                        <div className="flex items-center justify-between mb-1">
                          <span className={`text-xs px-1.5 py-0.5 rounded ${
                            note.note_type === 'customer' ? 'bg-blue-600 text-blue-100' : 'bg-slate-500 text-slate-200'
                          }`}>
                            {note.note_type === 'customer' ? 'Customer Visible' : 'Admin Only'}
                          </span>
                          <span className="text-slate-500 text-xs">{new Date(note.created_at).toLocaleString()}</span>
                        </div>
                        <p className="text-white">{note.note}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-slate-500 text-sm mb-4">No notes yet</p>
                )}
                
                {/* Add Note Form */}
                <div className="space-y-2 border-t border-teal-600 pt-3">
                  <div className="flex gap-2">
                    <select
                      value={noteType}
                      onChange={(e) => setNoteType(e.target.value)}
                      className="px-2 py-1 text-sm rounded bg-slate-700 border border-teal-600 text-slate-200"
                    >
                      <option value="admin">Admin Note</option>
                      <option value="customer">Customer Note</option>
                    </select>
                    {noteType === 'customer' && (
                      <span className="text-xs text-white self-center">Visible on status check page</span>
                    )}
                  </div>
                  <Textarea
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder="Enter note..."
                    className="bg-slate-700 border-teal-600 text-slate-100 text-sm min-h-[60px]"
                  />
                  <Button
                    onClick={addNoteToOrder}
                    disabled={addingNote || !newNote.trim()}
                    size="sm"
                    className="bg-teal-600 hover:bg-teal-500"
                  >
                    {addingNote ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
                    Add Note
                  </Button>
                </div>
              </div>

              {/* Quick Actions based on status */}
              <div className="flex gap-2 flex-wrap">
                {/* Emergency Stop Button - Always visible */}
                <Button
                  onClick={emergencyStopBrowser}
                  disabled={emergencyStopLoading}
                  variant="outline"
                  className="border-red-600 text-red-400 hover:bg-red-900/30"
                  data-testid="emergency-stop-btn-instructor"
                >
                  {emergencyStopLoading ? (
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <StopCircle className="h-4 w-4 mr-2" />
                  )}
                  Emergency Stop
                </Button>
                
                {(selectedInstructorOrder.order_status === 'pending' || selectedInstructorOrder.order_status === 'ordered') && (
                  <>
                    <Button
                      onClick={() => {
                        window.open('https://cprsuppliers.com/cart', '_blank');
                      }}
                      variant="outline"
                      className="border-teal-600 text-white hover:bg-purple-900/30"
                    >
                      <ExternalLink className="h-4 w-4 mr-2" />
                      Open CPR Suppliers
                    </Button>
                    <Button
                      onClick={() => openStatusChange(selectedInstructorOrder, 'fulfilled')}
                      className="bg-teal-600 hover:bg-teal-500"
                    >
                      <CheckCircle className="h-4 w-4 mr-2" />
                      Mark Complete
                    </Button>
                    <Button
                      onClick={() => openStatusChange(selectedInstructorOrder, 'cancelled')}
                      variant="outline"
                      className="border-red-600 text-red-400 hover:bg-red-900/30"
                    >
                      <XCircle className="h-4 w-4 mr-2" />
                      Cancel
                    </Button>
                  </>
                )}
                
                {selectedInstructorOrder.order_status === 'fulfilled' && (
                  <div className="w-full text-center text-white py-2">
                    <CheckCircle className="h-8 w-8 mx-auto mb-2" />
                    <p>Order completed on {selectedInstructorOrder.fulfilled_at ? new Date(selectedInstructorOrder.fulfilled_at).toLocaleString() : 'N/A'}</p>
                  </div>
                )}
                
                {selectedInstructorOrder.order_status === 'cancelled' && (
                  <div className="w-full text-center text-red-400 py-2">
                    <XCircle className="h-8 w-8 mx-auto mb-2" />
                    <p>Order cancelled</p>
                    {selectedInstructorOrder.status_change_reason && (
                      <p className="text-sm text-white mt-1">Reason: {selectedInstructorOrder.status_change_reason}</p>
                    )}
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* AHA Verification Modal */}
      <Dialog open={ahaVerifyModal.show} onOpenChange={(open) => !open && setAhaVerifyModal({ ...ahaVerifyModal, show: false })}>
        <DialogContent className="sm:max-w-md bg-slate-900 border-2 border-amber-500/50">
          <DialogHeader className="text-center">
            <div className="mx-auto mb-3 w-16 h-16 rounded-full bg-amber-500/20 flex items-center justify-center">
              <AlertTriangle className="h-10 w-10 text-amber-400" />
            </div>
            <DialogTitle className="text-xl font-bold text-white text-center">
              AHA Verification Review
            </DialogTitle>
            <DialogDescription className="text-slate-400 text-center pt-2">
              Review the student information and confirm verification status.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4 space-y-4">
            {/* Name Comparison */}
            <div className="bg-slate-800/50 rounded-lg p-4 space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-slate-400 text-sm">Student Name (Entered):</span>
                <span className="text-white font-semibold">{ahaVerifyModal.enteredName}</span>
              </div>
              <div className="border-t border-slate-600/50"></div>
              <div className="flex justify-between items-center">
                <span className="text-slate-400 text-sm">AHA Card Name:</span>
                <span className={`font-semibold ${
                  ahaVerifyModal.ahaName === 'Not available' 
                    ? 'text-slate-500 italic' 
                    : ahaVerifyModal.ahaName.toLowerCase() === ahaVerifyModal.enteredName.toLowerCase()
                      ? 'text-emerald-400'
                      : 'text-amber-400'
                }`}>
                  {ahaVerifyModal.ahaName}
                </span>
              </div>
            </div>
            
            {/* eCard Info if available */}
            {ahaVerifyModal.student?.aha_ecard?.ecard_code && (
              <div className="bg-slate-800/30 rounded-lg p-3 text-sm">
                <p className="text-slate-400">
                  <span className="text-slate-500">eCard Code:</span>{' '}
                  <span className="text-slate-200 font-mono">{ahaVerifyModal.student.aha_ecard.ecard_code}</span>
                </p>
                {ahaVerifyModal.student.aha_ecard.expiration_date && (
                  <p className="text-slate-400 mt-1">
                    <span className="text-slate-500">Expires:</span>{' '}
                    <span className="text-slate-200">{ahaVerifyModal.student.aha_ecard.expiration_date}</span>
                  </p>
                )}
              </div>
            )}
            
            {/* Current Status */}
            <div className="text-center">
              <span className="text-sm text-slate-500">Current Status: </span>
              <span className={`text-sm font-medium ${ahaVerifyModal.currentStatus ? 'text-emerald-400' : 'text-red-400'}`}>
                {ahaVerifyModal.currentStatus ? 'Verified' : 'Not Verified'}
              </span>
            </div>
            
            <p className="text-center text-lg font-semibold text-white pt-2">
              Is this student verified?
            </p>
          </div>
          
          <DialogFooter className="flex gap-3 sm:justify-center">
            <Button
              onClick={() => handleAhaVerifyResponse(false)}
              className="flex-1 bg-red-600 hover:bg-red-500 text-white font-semibold"
            >
              <XCircle className="w-4 h-4 mr-2" />
              No - Unverified
            </Button>
            <Button
              onClick={() => handleAhaVerifyResponse(true)}
              className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold"
            >
              <CheckCircle className="w-4 h-4 mr-2" />
              Yes - Verified
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Waitlist Manager */}
      <WaitlistManager 
        open={showWaitlist} 
        onOpenChange={setShowWaitlist} 
      />

      {/* Store Manager */}
      <StoreManager 
        open={showStore} 
        onOpenChange={setShowStore} 
      />
    </div>
  );
};

export default AdminDashboard;
